import { billingRequest, loadBillingConfig, BillingRequestError, type AccessSummary, type BillingConfig } from './data/billing';
import stylesText from './styles.css?inline';
import motionPitchLogo from '../../motionpitch.png';
import { initializeAnalytics } from './analytics';
import {
  getMotionColorMatteLabel,
  getTemplateInputCount,
  getDuoInputAspect,
  getDuoInputLabel,
  isMotionColorMatteChannel,
  normalizeMotionColorMatte,
  resolveStaticTemplateCapabilities,
  type MotionColorMatteChannel
} from '@motionpitch/template-contract';
import { getCategoryAspect } from './categoryAspect';
import { escapeHtml } from './components/dom';
import {
  showVideoSavedModal,
  type SavedVideoConfirmation
} from './components/videoSavedModal';
import { loadCatalog } from './data/catalog';
import { loadCollections, loadCollectionPurposes } from './data/collections';
import { scopeCollectionPurposeId, type CollectionPurpose } from '@motionpitch/template-contract';
import { getCollectionPurposeId, renderCollectionHierarchy } from './components/collectionHierarchy';
import { loadCachedPreviewUrls, syncPreviewCache } from './data/previewCache';
import { APP_URL } from './data/api';
import {
  loadUserAccountOverview,
  type AccountContentType,
  type AccountOverview
} from './data/account';
import {
  getCurrentAuthUser,
  getSupabaseAuthConfigStatus,
  isSupabaseAuthConfigured,
  onAuthStateChange,
  signInWithEmail,
  signInWithGoogle,
  signOut,
  type AuthUser
} from './data/auth';
import {
  addUserFavoriteTemplateId,
  loadUserFavoriteTemplateIds,
  removeUserFavoriteTemplateId
} from './data/favorites';
import {
  deleteGradientPreset,
  GRADIENT_PRESET_LIMIT,
  loadGradientPresets,
  saveGradientPreset,
  type GradientPreset
} from './data/gradientPresets';
import { generateHarmoniousGradientPalette } from './gradientPalette';
import { recordExportEvent, type ExportEventType } from './data/exportEvents';
import {
  DEFAULT_RENDER_ACCESS_SETTINGS,
  loadRenderAccessSettings,
  type RenderAccessSettings
} from './data/renderAccess';
import {
  createMotionExportSession,
  loadMotionExportSession,
  type MotionExportSessionSettings
} from './data/motionExportSession';
import { matchesTemplateSearchQuery } from './data/tags';
import {
  drawImageWithDiffuseAdjustments,
  loadMockupAssets,
  releaseDeviceShadowCache,
  renderMaster,
  type LoadedMockupAssets,
  type DiffuseAdjustments,
  type BackgroundAdjustments,
  type DeviceShadowAdjustments,
  type DeviceShadowPreset,
  type ReflectionAdjustments,
  type ScreenOverlayAdjustments
} from './render/composite';
import {
  downloadCanvasPng,
  downloadVideoExportArtifact,
  getStaticExportSize,
  renderStaticExport
} from './render/export';
import {
  isVideoExportFilePickerCancellation,
  requestVideoExportFileHandle
} from './render/videoExportStorage';
import {
  getCameraMasterSize,
  isStaticVideoExportSupported,
  isStaticVideoWebCodecsSupported,
  renderStaticVideoExport
} from './render/staticVideoExport';
import {
  createMeshPreviewSession,
  isStaticBackgroundActive,
  renderStaticBackground,
  type MeshPreviewSession,
  type StaticBackgroundComposition,
  type StaticBackgroundImageFit,
  type StaticBackgroundMode
} from './render/staticBackground';
import {
  createDynamicZoomState,
  getMaxZoom,
  getViewportAt,
  normalizeBackgroundMotion,
  DYNAMIC_ZOOM_IMAGE_DURATIONS,
  DYNAMIC_ZOOM_SPEED_SECONDS,
  type DynamicZoomState,
  type DynamicZoomViewport
} from './render/dynamicZoom';
import { drawDynamicZoomFrame } from './render/dynamicZoomBackground';
import { withCanvasMemoryScope } from './render/canvasMemory';
import { StaticRenderCache } from './render/StaticRenderCache';
import { DynamicZoomEditor, renderDynamicZoomEditorMarkup } from './components/dynamicZoomEditor';
import { EditorDismissGuard, editorValuesEqual } from './components/editorDismiss';
import { EditorSnapSession, paintSnapGuides, renderSnapGuides, snapPlacementMove, renderSnapsButton, getEditorSnapsEnabled, setEditorSnapsEnabled } from './components/editorSnapping';
import { FootagePlayback } from './render/footagePlayback';
import { formatPreviewTime, renderStaticPreviewControls } from './components/staticPreviewControls';
import { AdaptivePreviewQuality, getPausedFootageSize } from './render/adaptivePreviewQuality';
import { getFramedCameraRect, getStaticFrameRect, getStaticFormatAspect, type StaticFraming } from './render/staticFraming';
import {
  appendKeyframe,
  createBackgroundAnimationState,
  getAnimationProgress,
  removeKeyframe,
  sampleKeyframes,
  sampleMeshOrbitPoints,
  BACKGROUND_ANIMATION_DURATIONS,
  BACKGROUND_ANIMATION_MAX_KEYFRAMES,
  type BackgroundAnimationState
} from './render/backgroundAnimation';
import {
  clampMeshPoint,
  seededMeshPoints,
  MESH_POINT_LIMIT,
  MESH_POINT_MAX,
  MESH_POINT_MIN,
  type MeshPoint
} from './render/meshGradientPoints';
import {
  angleFromAxis,
  axisFromAngle,
  clampAxisPoint,
  clampStopBetweenNeighbours,
  defaultStopPositions,
  normalizeStopPositions,
  pointOnAxis,
  projectOntoAxis,
  rotateAxis,
  AXIS_POINT_MAX,
  AXIS_POINT_MIN,
  type AxisPoint
} from './render/gradientAxis';
import {
  canvasToBlob,
  canvasToPngBytes,
  createPreviewImageCopy,
  loadImageFromBlob,
  loadImageFromUrl,
  loadVideoFromFile,
  loadVideoFromUrl,
  revokeManagedMediaUrl,
  seekVideo
} from './render/loadImage';
import {
  releaseStaticVideoStoryboardSamples,
  sampleStaticVideoStoryboardFrames,
  type StaticVideoStoryboardSample
} from './render/staticVideoStoryboard';
import {
  createMotionViewportCanvas,
  getEffectiveMotionPlaybackDuration,
  getMotionAtlasFrameTime,
  getMotionFrameIndex,
  getMotionFrameTime,
  getMotionSequencePlaybackTime,
  loadMotionAssets,
  ensureMotionExportColorAssets,
  ensureMotionPreviewColorAssets,
  ensureNeutralAtlas,
  getActiveAtlasVideo,
  renderFrame as renderMotionFrame,
  seekMotionColorMatte,
  type LoadedMotionAssets
} from './render/motion';
import {
  isBackgroundTintActive,
  resolveActiveMatteChannels,
  type MotionColorMatteAdjustments
} from './render/colorMatte';
import { isMotionVideoExportSupported, renderMotionExport } from './render/motionExport';
import {
  getRenderAbortError,
  RenderStallWatchdog,
  waitForRenderOperation
} from './render/RenderTask';
import {
  ExportFrameTimeEstimator,
  formatEstimatedTimeRemaining
} from './render/exportTimeEstimate';
import type {
  Category,
  ContentType,
  CustomPlacement,
  DomainCategory,
  ExportResolution,
  MockupCollection,
  MockupTemplate,
  MotionResizeMode,
  SafeRect,
  SourceFilter,
  StaticExportFormat,
  StaticExportMode,
  TemplateType
} from './types';

declare const __MOCKUP_APP_URL__: string | undefined;

type PaddleGlobal = {
  Environment?: { set(environment: string): void };
  Initialize(options: { token: string; eventCallback?: (event: { name: string }) => void }): void;
  Checkout: { open(options: { transactionId: string; settings?: { successUrl?: string } }): void };
};

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

injectStyles(stylesText);

window.addEventListener('error', (event) => {
  showFatalError(event.error instanceof Error ? event.error.message : event.message);
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  showFatalError(reason instanceof Error ? reason.message : String(reason));
});

type CategoryFilter = 'All' | Category;
type QuickCatalogFilter = 'collections' | CategoryFilter;
type UsePaneView = 'home' | 'collections' | 'templates' | 'detail' | 'account';
interface NewTemplatesReturnState {
  selectedPurposeId?: string;
  quickCatalogFilter: QuickCatalogFilter;
  usePaneView: UsePaneView;
  selectedCollectionId: string;
  category: CategoryFilter;
  favoritesOnly: boolean;
  catalogSearch: string;
}
type StaticInteractiveExportMode = Extract<StaticExportMode, 'fit' | 'custom'>;
type NonCustomStaticExportMode = Exclude<StaticInteractiveExportMode, 'custom'>;
type UploadedMediaType = 'image' | 'video' | 'unknown';
type StaticRenderMode = 'static_image' | 'static_video';
type StaticVideoChromaPreset = 'green' | 'blue' | 'custom';
type StaticBackgroundImagePlacementMode = 'fit' | 'custom';
type CustomEditorTarget = 'screen' | 'background';
const STATIC_VIDEO_EXPORT_RESOLUTION: ExportResolution = '2k';
const STATIC_VIDEO_CHROMA_GREEN = '#00ff00';
const STATIC_VIDEO_CHROMA_BLUE = '#0000ff';
const NEW_TEMPLATES_BATCH_SIZE = 50;
const GRADIENT_PALETTE_HISTORY_LIMIT = 30;
type CollectionQueryType = TemplateType | 'MOTION';
type AccountMenuView = 'favorites' | 'exports' | 'stats' | 'subscription';
type AccountFavoriteFilter = 'ALL' | AccountContentType;
type PublicPageRoute =
  | 'pricing'
  | 'terms'
  | 'privacy'
  | 'refund'
  | 'refund-policy'
  | 'payment-success'
  | 'payment-cancel'
  | 'billing'
  | 'contact';

interface StaticExportState {
  format: StaticExportFormat;
  resolution: ExportResolution;
  mode: StaticInteractiveExportMode;
  mode2: StaticInteractiveExportMode;
  mode3: StaticInteractiveExportMode;
  renderMode: StaticRenderMode;
  customPlacement: CustomPlacement | null;
  customDraftPlacement: CustomPlacement | null;
  customPlacement2: CustomPlacement | null;
  customDraftPlacement2: CustomPlacement | null;
  customPlacement3: CustomPlacement | null;
  customDraftPlacement3: CustomPlacement | null;
  customEditorScreen: 1 | 2 | 3;
  customEditorTarget: CustomEditorTarget;
  customEditorOpen: boolean;
  customModeFallback: NonCustomStaticExportMode;
  customModeFallback2: NonCustomStaticExportMode;
  customModeFallback3: NonCustomStaticExportMode;
}

interface MotionExportState {
  resizeMode: MotionResizeMode;
}

type CustomPlacementEditorSource = {
  kind: 'image' | 'video';
  src: string;
  width: number;
  height: number;
  currentTime?: number;
};

interface UploadedMediaState {
  file: File | null;
  type: Exclude<UploadedMediaType, 'unknown'>;
  duration?: number;
  width?: number;
  height?: number;
}

interface StaticVideoStoryboardFrame {
  time: number;
  previewUrl: string;
}

interface StaticVideoStoryboardState {
  frames: StaticVideoStoryboardFrame[];
  selectedIndex: number;
  loading: boolean;
  error: string;
}

interface StaticVideoStoryboardSampleCache {
  key: string;
  samples: StaticVideoStoryboardSample[];
}

type SegmentedControlSize = 'sm' | 'md';

interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface SliderRowOptions {
  id: string;
  label: string;
  min: number | string;
  max: number | string;
  step?: number | string;
  value: number | string;
  valueId: string;
  valueLabel: string;
}

const EXPORT_RESOLUTION_OPTIONS: Array<SegmentedControlOption<ExportResolution>> = [
  { value: '4k', label: '4K' },
  { value: '2k', label: '2K' }
];

const STATIC_EXPORT_FORMAT_OPTIONS: Array<SegmentedControlOption<StaticExportFormat>> = [
  { value: '1:1', label: '1:1' },
  { value: '1:1z', label: '1:1 Zoom' },
  { value: '16:9', label: '16:9' },
  { value: '9:16', label: '9:16' },
  { value: '2:3', label: '2:3' }
];

const STATIC_EXPORT_MODE_OPTIONS: Array<SegmentedControlOption<StaticInteractiveExportMode>> = [
  { value: 'fit', label: 'Fit' },
  { value: 'custom', label: 'Custom' }
];

const STATIC_BACKGROUND_IMAGE_MODE_OPTIONS: Array<SegmentedControlOption<StaticBackgroundImagePlacementMode>> = [
  { value: 'fit', label: 'Fit' },
  { value: 'custom', label: 'Custom' }
];

const DEVICE_SHADOW_PRESET_OPTIONS: Array<SegmentedControlOption<DeviceShadowPreset>> = [
  { value: 'soft', label: 'Soft' },
  { value: 'close', label: 'Close' },
  { value: 'floating', label: 'Floating' },
  { value: 'long', label: 'Long' }
];

const MOTION_RESIZE_OPTIONS: Array<SegmentedControlOption<MotionResizeMode>> = [
  { value: '1:1', label: '1:1' },
  { value: '16:9', label: '16:9' },
  { value: '9:16', label: '9:16' }
];

function renderSegmentedControl<T extends string>(
  options: Array<SegmentedControlOption<T>>,
  value: T,
  dataAttribute: string,
  ariaLabel: string,
  size: SegmentedControlSize = 'md',
  className = ''
): string {
  const classes = ['segmented-control', `segmented-control-${size}`, className].filter(Boolean).join(' ');
  return `
    <div
      class="${classes}"
      role="group"
      aria-label="${escapeHtml(ariaLabel)}"
      data-segmented="${escapeHtml(dataAttribute)}"
      style="grid-template-columns: repeat(${options.length}, minmax(0, auto));"
    >
      <span class="segmented-thumb" aria-hidden="true"></span>
      ${options
        .map((option) => {
          const active = option.value === value;
          return `<button
            type="button"
            class="segmented-option ${active ? 'active' : ''}"
            data-${dataAttribute}="${escapeHtml(option.value)}"
            aria-pressed="${active ? 'true' : 'false'}"
            ${option.disabled ? 'disabled' : ''}
          >${escapeHtml(option.label)}</button>`;
        })
        .join('')}
    </div>
  `;
}

function renderSliderRow(options: SliderRowOptions): string {
  const stepAttribute = options.step === undefined ? '' : ` step="${escapeHtml(String(options.step))}"`;
  const min = Number(options.min);
  const max = Number(options.max);
  const range = max - min;
  const rangeProgress =
    range > 0 ? clamp(((Number(options.value) - min) / range) * 100, 0, 100) : 0;
  return `
    <label class="slider-control" for="${escapeHtml(options.id)}" style="--range-progress: ${rangeProgress}%;">
      <div class="slider-control-head">
        <span>${escapeHtml(options.label)}</span>
        <strong id="${escapeHtml(options.valueId)}">${escapeHtml(options.valueLabel)}</strong>
      </div>
      <input
        id="${escapeHtml(options.id)}"
        type="range"
        min="${escapeHtml(String(options.min))}"
        max="${escapeHtml(String(options.max))}"
        value="${escapeHtml(String(options.value))}"${stepAttribute}
      />
    </label>
  `;
}

function syncSliderProgress(input: HTMLInputElement | null): void {
  if (!input) {
    return;
  }

  const min = Number(input.min || 0);
  const max = Number(input.max || 100);
  const value = Number(input.value || min);
  const range = max - min;
  const progress = range > 0 ? clamp(((value - min) / range) * 100, 0, 100) : 0;
  input.style.setProperty('--range-progress', `${progress}%`);
}

function cleanupMobileStickyPreviewBehavior(): void {
  if (!mobileStickyPreviewCleanup) {
    return;
  }
  mobileStickyPreviewCleanup();
  mobileStickyPreviewCleanup = null;
}

function bindMobileStickyPreviewBehavior(): void {
  cleanupMobileStickyPreviewBehavior();

  const pane = document.querySelector<HTMLElement>('.pane-detail');
  if (pane?.classList.contains('pane-detail-motion')) {
    return;
  }

  const toolbar = pane?.querySelector<HTMLElement>('.detail-toolbar-row') || null;
  const previewColumn = pane?.querySelector<HTMLElement>('.detail-preview-column') || null;
  const previewDropzone = pane?.querySelector<HTMLElement>('.preview-dropzone') || null;
  const editorColumn = pane?.querySelector<HTMLElement>('.detail-editor-column') || null;
  if (!pane || !toolbar || !previewColumn || !previewDropzone || !editorColumn) {
    return;
  }

  const mediaQuery = window.matchMedia('(max-width: 899px) and (orientation: portrait)');
  let rafId = 0;
  let expandedWidth = 0;
  let collapsedWidth = 0;
  let lastScaleValue = '';
  let lastStuck: boolean | null = null;

  const clearMobilePreviewState = () => {
    pane.classList.remove('is-mobile-preview-stuck');
    pane.style.removeProperty('--mobile-preview-progress');
    pane.style.removeProperty('--mobile-preview-scale');
    pane.style.removeProperty('--mobile-preview-visual-width');
    pane.style.removeProperty('--mobile-preview-toggle-width');
    previewDropzone.style.removeProperty('transform');
    previewDropzone.style.removeProperty('-webkit-transform');
    lastScaleValue = '';
    lastStuck = null;
  };

  const measure = () => {
    const columnStyle = window.getComputedStyle(previewColumn);
    const columnPadding =
      Number.parseFloat(columnStyle.paddingLeft || '0') + Number.parseFloat(columnStyle.paddingRight || '0');
    expandedWidth = Math.max(0, previewColumn.clientWidth - columnPadding);
    const collapsedLimit = window.innerWidth <= 420 ? 190 : 220;
    collapsedWidth = Math.min(expandedWidth, window.innerWidth * 0.35, collapsedLimit);
  };

  const update = () => {
    rafId = 0;

    if (!mediaQuery.matches) {
      clearMobilePreviewState();
      return;
    }

    if (expandedWidth <= 0) {
      measure();
    }

    const appTop = appRoot.getBoundingClientRect().top;
    const toolbarTop = toolbar.getBoundingClientRect().top;
    const previewTop = previewDropzone.getBoundingClientRect().top;
    const editorTop = editorColumn.getBoundingClientRect().top;
    const contactGap = 12;
    const visualWidth = clamp(editorTop - previewTop - contactGap, collapsedWidth, expandedWidth);
    const scale = expandedWidth > 0 ? clamp(visualWidth / expandedWidth, 0.1, 1) : 1;
    const scaleValue = scale.toFixed(4);
    const stuck = toolbarTop <= appTop + 0.5;

    if (scaleValue !== lastScaleValue) {
      const transformValue = `translate3d(0, 0, 0) scale(${scaleValue})`;
      previewDropzone.style.transform = transformValue;
      previewDropzone.style.setProperty('-webkit-transform', transformValue);
      lastScaleValue = scaleValue;
    }

    if (stuck !== lastStuck) {
      pane.classList.toggle('is-mobile-preview-stuck', stuck);
      lastStuck = stuck;
    }
  };

  const schedule = () => {
    if (rafId) {
      return;
    }
    rafId = window.requestAnimationFrame(update);
  };

  const refreshAndSchedule = () => {
    measure();
    schedule();
  };

  measure();
  appRoot.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', refreshAndSchedule);
  mediaQuery.addEventListener('change', refreshAndSchedule);
  mobileStickyPreviewCleanup = () => {
    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = 0;
    }
    appRoot.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', refreshAndSchedule);
    mediaQuery.removeEventListener('change', refreshAndSchedule);
    clearMobilePreviewState();
  };

  schedule();
}

function isExportResolution(value: string | undefined): value is ExportResolution {
  return value === '4k' || value === '2k';
}

function isStaticExportFormat(value: string | undefined): value is StaticExportFormat {
  return value === '1:1' || value === '1:1z' || value === '16:9' || value === '9:16' || value === '2:3';
}

function isStaticInteractiveExportMode(value: string | undefined): value is StaticInteractiveExportMode {
  return value === 'fit' || value === 'custom';
}

function getUploadedMediaType(file: File): UploadedMediaType {
  const mimeType = (file.type || '').toLowerCase();
  const lowerName = file.name.trim().toLowerCase();

  if (mimeType === 'image/png' || mimeType === 'image/jpeg') {
    return 'image';
  }
  if (mimeType === 'video/mp4' || mimeType === 'video/quicktime') {
    return 'video';
  }
  if (lowerName.endsWith('.png') || lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) {
    return 'image';
  }
  if (lowerName.endsWith('.mp4') || lowerName.endsWith('.mov')) {
    return 'video';
  }

  return 'unknown';
}
type AuthPromptReason =
  | 'account'
  | 'favorite'
  | 'favorites'
  | 'gradient-preset'
  | 'export'
  | 'browser-export';

type PendingAuthAction =
  | {
      type: 'favorite';
      templateId: string;
    }
  | {
      type: 'favorites';
    }
  | {
      type: 'gradient-presets';
    }
  | {
      type: 'export';
      mode: 'download' | 'browser';
    };

interface AuthPromptState {
  reason: AuthPromptReason;
  pendingAction: PendingAuthAction | null;
}

type MainToUiMessage =
  | {
      type: 'REMOTE_READY';
    }
  | {
      type: 'READY';
      payload?: {
        favoriteTemplateIds?: string[];
      };
    }
  | {
      type: 'ERROR';
      payload: {
        message: string;
      };
    }
  | {
      type: 'INSERT_DONE';
    }
  | {
      type: 'IMPORT_FROM_FIGMA_DONE';
      payload: {
        bytes: ArrayBuffer | Uint8Array | number[];
        name?: string;
      };
    };

interface AppState {
  selectedPurposeId: string;
  collectionPurposes: CollectionPurpose[];
  collectionsError: string;
  category: CategoryFilter;
  templateType: TemplateType;
  catalog: MockupTemplate[];
  collections: MockupCollection[];
  selectedId: string;
  selectedCollectionId: string;
  favoritesOnly: boolean;
  favoriteTemplateIds: string[];
  heroFileName: string;
  heroDragActive: boolean;
  heroExporting: boolean;
  authConfigured: boolean;
  authReady: boolean;
  authUser: AuthUser | null;
  accountMenuOpen: boolean;
  accountMenuView: AccountMenuView;
  accountFavoriteFilter: AccountFavoriteFilter;
  accountOverview: AccountOverview | null;
  accountAccess: AccessSummary | null;
  accountPublicId: string | null;
  accountOverviewLoading: boolean;
  accountOverviewError: string;
  authPrompt: AuthPromptState | null;
  authEmail: string;
  authBusy: boolean;
  authMessage: {
    type: 'info' | 'error' | 'success';
    message: string;
  } | null;
  favoritesLoading: boolean;
  gradientPresets: GradientPreset[];
  gradientPresetsOpen: boolean;
  gradientPresetsLoading: boolean;
  gradientPresetBusy: boolean;
  loadingCatalog: boolean;
  loadingCollections: boolean;
  rendering: boolean;
  exporting: boolean;
  motionExportProgress: { completed: number; total: number } | null;
  openingBrowserExport: boolean;
  renderAccessSettings: RenderAccessSettings;
  inserting: boolean;
  importingFromFigma: boolean;
  previewUrls: Record<string, string>;
  detailPreviewReadyKey: string;
  detailTransitionOverlayUrl: string;
  error: string;
  notice: {
    type: 'info' | 'error' | 'success';
    message: string;
  } | null;
  userImage: HTMLImageElement | null;
  userImagePreview: HTMLImageElement | null;
  userImageSrc: string;
  userImagePreviewSrc: string;
  userImageName: string;
  userVideo: HTMLVideoElement | null;
  userVideoSrc: string;
  userVideoName: string;
  uploadedMedia: UploadedMediaState | null;
  userImage2: HTMLImageElement | null;
  userImagePreview2: HTMLImageElement | null;
  userImageSrc2: string;
  userImagePreviewSrc2: string;
  userImageName2: string;
  userVideo2: HTMLVideoElement | null;
  userVideoSrc2: string;
  userVideoName2: string;
  uploadedMedia2: UploadedMediaState | null;
  userImage3: HTMLImageElement | null;
  userImagePreview3: HTMLImageElement | null;
  userImageSrc3: string;
  userImagePreviewSrc3: string;
  userImageName3: string;
  userVideo3: HTMLVideoElement | null;
  userVideoSrc3: string;
  userVideoName3: string;
  uploadedMedia3: UploadedMediaState | null;
  staticVideoStoryboard: StaticVideoStoryboardState;
  staticUseOriginalAudio: boolean;
  motionPlaying: boolean;
  motionFrameIndex: number;
  motionExport: MotionExportState;
  staticExport: StaticExportState;
  usePaneView: UsePaneView;
  quickCatalogFilter: QuickCatalogFilter;
  newTemplatesMode: boolean;
  newTemplatesVisibleCount: number;
  newTemplatesReturnState: NewTemplatesReturnState | null;
  selectedContentType: ContentType;
  selectedDomainCategory: DomainCategory;
  selectedSourceFilter: SourceFilter;
  catalogSearch: string;
  diffuseControlsOpen: boolean;
  backgroundControlsOpen: boolean;
  motionColorMatteOpen: boolean;
  /**
   * Puzzle Matte. One master switch for the whole panel, plus the colour picked
   * per channel. It starts off, so a template opens in its original colours
   * until the viewer asks otherwise.
   */
  motionColorMatte: MotionColorMatteAdjustments;
  diffuseHue: number;
  diffuseSaturation: number;
  diffuseBrightness: number;
  diffuseContrast: number;
  diffuseMonochrome: boolean;
  dynamicIsland: boolean;
  backgroundMode: BackgroundAdjustments['mode'];
  backgroundTransparent: boolean;
  staticVideoChromaPreset: StaticVideoChromaPreset;
  staticVideoChromaCustomColor: string;
  backgroundShadowColor: string;
  backgroundMidColor: string;
  backgroundHighlightColor: string;
  staticBackgroundMode: StaticBackgroundMode;
  staticBackgroundSourceOpen: boolean;
  staticBackgroundAnimation: BackgroundAnimationState;
  dynamicZoom: DynamicZoomState;
  dynamicZoomOpen: boolean;
  dynamicZoomEditorOpen: boolean;
  /** Edited copy — Cancel is simply dropping it. */
  dynamicZoomDraft: DynamicZoomState | null;
  dynamicZoomSelectedKey: string | null;
  /** Editor playback, and the framing the next key will take. */
  dynamicZoomPlaying: boolean;
  staticBackgroundColors: string[];
  staticBackgroundAngle: number;
  staticBackgroundAxisStart: AxisPoint;
  staticBackgroundAxisEnd: AxisPoint;
  staticBackgroundStopPositions: number[];
  /** Which colour slots take part in a linear or radial ramp. The two ends are
   *  always in; the slots between them are opt-in. */
  staticBackgroundGradientSlots: boolean[];
  staticBackgroundAxisDraft: { start: AxisPoint; end: AxisPoint; stops: number[] } | null;
  staticBackgroundAxisEditorOpen: boolean;
  staticBackgroundMeshSeed: number;
  staticBackgroundMeshPoints: MeshPoint[];
  staticBackgroundMeshDraftPoints: MeshPoint[] | null;
  staticBackgroundMeshEditorOpen: boolean;
  staticBackgroundMeshMix: number;
  staticBackgroundMeshDistortion: number;
  staticBackgroundMeshGrain: number;
  staticBackgroundMeshFlowCenter: MeshPoint;
  staticBackgroundMeshFlowDraft: MeshPoint | null;
  staticBackgroundImage: HTMLImageElement | null;
  staticBackgroundImagePreviewSrc: string;
  staticBackgroundImageName: string;
  staticBackgroundImageRevision: number;
  staticBackgroundImageFit: StaticBackgroundImageFit;
  staticBackgroundImagePlacementMode: StaticBackgroundImagePlacementMode;
  staticBackgroundImagePlacement: CustomPlacement | null;
  staticBackgroundImageDraftPlacement: CustomPlacement | null;
  deviceShadowEnabled: boolean;
  deviceShadowPreset: DeviceShadowPreset;
  deviceShadowAngle: number;
  deviceShadowOpacity: number;
  deviceShadowColor: string;
  reflectionVisible: boolean;
  reflectionOpacity: number;
  reflectionBlur: number;
  screenOverlayOpacity: number;
  screenBlur: number;
}

const STORAGE_KEYS = {
  favorites: 'mockup-web-app.favorite-template-ids',
  pendingAuthAction: 'mockup-web-app.pending-auth-action',
  returnNavHash: 'mp-auth-return-hash',
  theme: 'mp-theme'
};
const CATEGORIES: Category[] = ['Phone', 'Tablet', 'Laptop', 'Desktop', 'Scenes', 'Frame', 'Icon', 'Longread'];
const APP_CATEGORIES: Category[] = ['Phone', 'Tablet', 'Laptop', 'Desktop', 'Clock', 'Duo', 'Frame', 'Icon', 'Longread'];
const PROMO_CATEGORIES: Category[] = ['Billboards', 'Presentation Screens', 'Digital Signage', 'Urban Scenes', 'Cafe Scenes', 'Outdoor Promo'];
const BRAND_CATEGORIES: Category[] = ['Stationery', 'Business Cards', 'Logo Placements', 'Identity Pack', 'Office Branding', 'Minimal Brand Mockups'];
const CATEGORY_FILTERS: CategoryFilter[] = ['All', ...CATEGORIES];
const CONTENT_TYPES: ContentType[] = ['STATIC', 'MOTION'];
const DOMAIN_CATEGORIES: DomainCategory[] = ['App', 'Promo', 'Brand'];
const DEFAULT_DOMAIN_CATEGORY: DomainCategory = 'App';
const SHOW_DOMAIN_CATEGORY_TABS = false;
const PUBLIC_SITE_URL = (__MOCKUP_APP_URL__ || window.location.origin || 'https://motionpitch.app').trim().replace(/\/$/, '');
const SUPPORT_EMAIL = 'support@motionpitch.app';
const LEGAL_LAST_UPDATED = 'May 19, 2026';
const PRIVACY_LAST_UPDATED = 'August 19, 2026';
const PADDLE_SCRIPT_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';
const PADDLE_SUCCESS_URL = `${window.location.origin}/payment/success`;
let billingConfig: BillingConfig | null = null;
let billingBusy = false;
let motionAccessChecking = false;
let billingStatus = '';


const PREVIEW_RENDER_SIZE = 1024;
const STATIC_BACKGROUND_EDITOR_OVERLAY_SIZE = 768;
const STATIC_VIDEO_STORYBOARD_FRAME_COUNT = 10;
const STATIC_VIDEO_STORYBOARD_SOURCE_SIZE_DESKTOP = 960;
const STATIC_VIDEO_STORYBOARD_SOURCE_SIZE_MOBILE = 720;
const STATIC_VIDEO_STORYBOARD_RENDER_SIZE_DESKTOP = 224;
const STATIC_VIDEO_STORYBOARD_RENDER_SIZE_MOBILE = 176;
const STATIC_VIDEO_STORYBOARD_GRID_SIZE = 8;
const ZOOM_FACTOR_1_1Z = 1.5;
const STATIC_BACKGROUND_CUSTOM_MIN_SCALE = 0.05;
const SCREEN_CUSTOM_MIN_SCALE = 0.05;
const PREVIEW_GRID_SIZE = 10;
const MOTION_PREVIEW_GRID_DESKTOP = 14;
const MOTION_PREVIEW_GRID_MOBILE = 10;
const MOTION_PLAYBACK_GRID_DESKTOP = 10;
const MOTION_PLAYBACK_GRID_MOBILE = 8;
const MOTION_PLAYBACK_RENDER_MAX_DESKTOP = 768;
const MOTION_PLAYBACK_RENDER_MAX_MOBILE = 640;
const MOTION_WIDESCREEN_EXPORT_WIDTH = 1920;
const MOTION_WIDESCREEN_EXPORT_HEIGHT = 1080;
const MOTION_VERTICAL_EXPORT_WIDTH = 1080;
const MOTION_VERTICAL_EXPORT_HEIGHT = 1920;
const previewAssetCache = new Map<string, Promise<LoadedMockupAssets>>();
const exportAssetCache = new Map<string, Promise<LoadedMockupAssets>>();
const previewMotionAssetCache = new Map<string, Promise<LoadedMotionAssets>>();
const exportMotionAssetCache = new Map<string, Promise<LoadedMotionAssets>>();
const homeCompositePreviewCache = new Map<string, Promise<string>>();
const staticBackgroundSourceCache = new Map<string, Promise<HTMLCanvasElement | null>>();
// Declared up here with the rest of the module state: render() is called during
// start-up, well before the point in the file where these are used, and a `let`
// below its first use is a TDZ crash before anything is ever drawn.
let lastRenderScreenKey = '';
let pendingRenderScrollTop: number | null = null;
let pendingEditorScrollTop: number | null = null;
let renderScrollToken = 0;
let preservedPreviewCanvas: HTMLCanvasElement | null = null;
/** Playback clock for the animated background, in seconds. */
let backgroundAnimationTime = 0;
/** Playback clock for the Dynamic Zoom preview, in seconds. */
let dynamicZoomTime = 0;
let dynamicZoomRaf: number | null = null;
let dynamicZoomEditor: DynamicZoomEditor | null = null;
let editorDismissGuards: EditorDismissGuard[] = [];
let editorRenderDeferred = false;
let dynamicZoomEditorInitial: { zoom: DynamicZoomState; format: StaticExportFormat; audio: boolean } | null = null;
let axisEditorInitial: { axis: { start: AxisPoint; end: AxisPoint; stops: number[] }; colors: string[]; slots: boolean[] } | null = null;
let meshEditorInitial: { points: MeshPoint[]; flow: MeshPoint; colors: string[] } | null = null;
let customEditorInitial: { placement: CustomPlacement; screenMode?: StaticInteractiveExportMode; backgroundMode?: StaticBackgroundImagePlacementMode } | null = null;
let staticPreviewFootage: FootagePlayback | null = null;
const staticFootagePreviewQuality = new AdaptivePreviewQuality();
let staticPreviewOwner = '';
let staticPreviewTime = 0;
let staticPreviewPlaying = false;
let staticPreviewVersion = 0;
let staticPreviewResumeAfterSeek = false;
let staticPreviewSeekVersion = 0;
let staticPreviewSeekPromise: Promise<void> | null = null;
let staticPreviewVisibilityBound = false;
let dynamicZoomPreviewMaster: { templateId: string; image: HTMLCanvasElement } | null = null;
// Declared with the rest of the module state: template switches read it, and
// init() can open a template before the export code further down is evaluated.
let activeVideoExportTask: ActiveVideoExportTask | null = null;
let backgroundAnimationRaf: number | null = null;
// Declared above the state initializer that reads it — a const below its first
// use crashes the page with a TDZ error before anything is ever drawn.
const STATIC_BACKGROUND_DEFAULT_COLORS = ['#0f5bff', '#4a6cff', '#6b7cff', '#b9c8ff', '#eef4ff'];
const COLOR_SLOT_INDEXES = STATIC_BACKGROUND_DEFAULT_COLORS.map((_, index) => index);
const MESH_EDITOR_PREVIEW_SIZE = 420;

/** Variation carries a decimal now, so 42 stays "42" and 42.3 keeps its tenth. */
function formatSeed(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
/** Two colours to begin with — a ramp from one end to the other. The middle
 *  slots are there when an author wants them, not by default. */
const STATIC_BACKGROUND_DEFAULT_SLOTS = STATIC_BACKGROUND_DEFAULT_COLORS.map(
  (_, index, all) => index === 0 || index === all.length - 1
);
const STATIC_BACKGROUND_DEFAULT_ANGLE = 135;
const STATIC_BACKGROUND_DEFAULT_FOCUS = { x: 35, y: 30 };

/** The radial ramp used to reach the farthest corner; the rim handle starts
 *  there so switching to handles did not shrink anyone's existing background. */
function defaultRadialAxis(focusX: number, focusY: number): { start: AxisPoint; end: AxisPoint } {
  const x = clamp(focusX, 0, 100) / 100;
  const y = clamp(focusY, 0, 100) / 100;
  const radius = Math.hypot(Math.max(x, 1 - x), Math.max(y, 1 - y));
  return { start: { x, y }, end: { x: x + radius, y } };
}
const MOTION_EXPORT_SIZE = 1080;
type MotionCardHoverPreviewState = {
  hoverTarget: HTMLElement;
  card: HTMLButtonElement;
  image: HTMLImageElement | null;
  video: HTMLVideoElement;
  token: number;
};

const MOTION_CARD_HOVER_PREVIEW_DELAY_MS = 140;
let activeMotionCardHoverPreview: MotionCardHoverPreviewState | null = null;
let motionCardHoverPreviewToken = 0;
let homeCompositePreviewToken = 0;
let pendingMotionCardHoverPreviewTimer: number | null = null;
let pendingMotionCardHoverPreviewCard: HTMLButtonElement | null = null;
let pendingMotionCardHoverPreviewTarget: HTMLElement | null = null;

function normalizeDomainCategory(domain: DomainCategory): DomainCategory {
  return SHOW_DOMAIN_CATEGORY_TABS ? domain : DEFAULT_DOMAIN_CATEGORY;
}
const isEmbeddedPlugin = window.parent !== window;
const isStandaloneWebApp = !isEmbeddedPlugin;

function readStoredThemePreference(): 'dark' | 'light' | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEYS.theme);
    return value === 'dark' || value === 'light' ? value : null;
  } catch {
    return null;
  }
}

function getInitialDarkTheme(): boolean {
  const storedTheme = readStoredThemePreference();
  return storedTheme === 'dark';
}

let isDarkTheme = getInitialDarkTheme();

function syncThemeToggleButton(): void {
  const button = document.getElementById('theme-toggle');
  if (!button) {
    return;
  }

  const title = isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode';
  button.setAttribute('title', title);
  button.setAttribute('aria-label', title);
  button.querySelector<HTMLElement>('[data-theme-icon="sun"]')?.classList.toggle('is-visible', isDarkTheme);
  button.querySelector<HTMLElement>('[data-theme-icon="moon"]')?.classList.toggle('is-visible', !isDarkTheme);
}

function applyThemePreference(dark: boolean, persist = false): void {
  isDarkTheme = dark;
  if (dark) {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }

  if (persist) {
    try {
      window.localStorage.setItem(STORAGE_KEYS.theme, dark ? 'dark' : 'light');
    } catch {
      // Ignore storage failures in private browsing or restricted embeds.
    }
  }

  syncThemeToggleButton();
}

applyThemePreference(isDarkTheme);

const state: AppState = {
  category: 'All',
  templateType: 'CG',
  catalog: [],
  collections: [],
  selectedId: '',
  selectedCollectionId: '',
  selectedPurposeId: '',
  collectionPurposes: [],
  collectionsError: '',
  favoritesOnly: false,
  favoriteTemplateIds: isStandaloneWebApp ? [] : loadFavoriteTemplateIds(),
  heroFileName: '',
  heroDragActive: false,
  heroExporting: false,
  authConfigured: isSupabaseAuthConfigured(),
  authReady: !isStandaloneWebApp,
  authUser: null,
  accountMenuOpen: false,
  accountMenuView: 'favorites',
  accountFavoriteFilter: 'ALL',
  accountOverview: null,
  accountAccess: null,
  accountPublicId: null,
  accountOverviewLoading: false,
  accountOverviewError: '',
  authPrompt: null,
  authEmail: '',
  authBusy: false,
  authMessage: null,
  favoritesLoading: false,
  gradientPresets: [],
  gradientPresetsOpen: false,
  gradientPresetsLoading: false,
  gradientPresetBusy: false,
  loadingCatalog: false,
  loadingCollections: true,
  rendering: false,
  exporting: false,
  motionExportProgress: null,
  openingBrowserExport: false,
  renderAccessSettings: { ...DEFAULT_RENDER_ACCESS_SETTINGS },
  inserting: false,
  importingFromFigma: false,
  previewUrls: {},
  detailPreviewReadyKey: '',
  detailTransitionOverlayUrl: '',
  error: '',
  notice: null,
  userImage: null,
  userImagePreview: null,
  userImageSrc: '',
  userImagePreviewSrc: '',
  userImageName: '',
  userVideo: null,
  userVideoSrc: '',
  userVideoName: '',
  uploadedMedia: null,
  userImage2: null,
  userImagePreview2: null,
  userImageSrc2: '',
  userImagePreviewSrc2: '',
  userImageName2: '',
  userVideo2: null,
  userVideoSrc2: '',
  userVideoName2: '',
  uploadedMedia2: null,
  userImage3: null,
  userImagePreview3: null,
  userImageSrc3: '',
  userImagePreviewSrc3: '',
  userImageName3: '',
  userVideo3: null,
  userVideoSrc3: '',
  userVideoName3: '',
  uploadedMedia3: null,
  staticVideoStoryboard: {
    frames: [],
    selectedIndex: 0,
    loading: false,
    error: ''
  },
  staticUseOriginalAudio: true,
  motionPlaying: false,
  motionFrameIndex: 0,
  motionExport: {
    resizeMode: '1:1'
  },
  staticExport: {
    format: '1:1',
    resolution: '4k',
    mode: 'fit',
    mode2: 'fit',
    mode3: 'fit',
    renderMode: 'static_image',
    customPlacement: null,
    customDraftPlacement: null,
    customPlacement2: null,
    customDraftPlacement2: null,
    customPlacement3: null,
    customDraftPlacement3: null,
    customEditorScreen: 1,
    customEditorTarget: 'screen',
    customEditorOpen: false,
    customModeFallback: 'fit',
    customModeFallback2: 'fit',
    customModeFallback3: 'fit'
  },
  quickCatalogFilter: 'All',
  newTemplatesMode: false,
  newTemplatesVisibleCount: NEW_TEMPLATES_BATCH_SIZE,
  newTemplatesReturnState: null,
  selectedContentType: 'STATIC',
  selectedDomainCategory: DEFAULT_DOMAIN_CATEGORY,
  selectedSourceFilter: 'ALL',
  catalogSearch: '',
  diffuseControlsOpen: false,
  backgroundControlsOpen: false,
  motionColorMatteOpen: false,
  motionColorMatte: createDefaultMotionColorMatteState(),
  diffuseHue: 0,
  diffuseSaturation: 100,
  diffuseBrightness: 100,
  diffuseContrast: 100,
  diffuseMonochrome: false,
  dynamicIsland: false,
  backgroundMode: 'original',
  backgroundTransparent: false,
  staticVideoChromaPreset: 'green',
  staticVideoChromaCustomColor: STATIC_VIDEO_CHROMA_GREEN,
  backgroundShadowColor: '#17203b',
  backgroundMidColor: '#4f73ff',
  backgroundHighlightColor: '#ffffff',
  staticBackgroundMode: 'none',
  staticBackgroundSourceOpen: false,
  staticBackgroundAnimation: createBackgroundAnimationState(),
  dynamicZoom: createDynamicZoomState(),
  dynamicZoomOpen: false,
  dynamicZoomEditorOpen: false,
  dynamicZoomDraft: null,
  dynamicZoomSelectedKey: null,
  dynamicZoomPlaying: false,
  // Five stops along the ramp the three-colour default already described, so
  // raising the count did not move the look anyone was used to.
  staticBackgroundColors: [...STATIC_BACKGROUND_DEFAULT_COLORS],
  staticBackgroundAngle: STATIC_BACKGROUND_DEFAULT_ANGLE,
  staticBackgroundAxisStart: axisFromAngle(STATIC_BACKGROUND_DEFAULT_ANGLE).start,
  staticBackgroundAxisEnd: axisFromAngle(STATIC_BACKGROUND_DEFAULT_ANGLE).end,
  staticBackgroundStopPositions: defaultStopPositions(STATIC_BACKGROUND_DEFAULT_COLORS.length),
  staticBackgroundGradientSlots: [...STATIC_BACKGROUND_DEFAULT_SLOTS],
  staticBackgroundAxisDraft: null,
  staticBackgroundAxisEditorOpen: false,
  staticBackgroundMeshSeed: 42,
  staticBackgroundMeshPoints: seededMeshPoints(42, MESH_POINT_LIMIT),
  staticBackgroundMeshDraftPoints: null,
  staticBackgroundMeshEditorOpen: false,
  staticBackgroundMeshMix: 72,
  staticBackgroundMeshDistortion: 38,
  staticBackgroundMeshGrain: 0,
  staticBackgroundMeshFlowCenter: { x: 0.5, y: 0.5 },
  staticBackgroundMeshFlowDraft: null,
  staticBackgroundImage: null,
  staticBackgroundImagePreviewSrc: '',
  staticBackgroundImageName: '',
  staticBackgroundImageRevision: 0,
  staticBackgroundImageFit: 'cover',
  staticBackgroundImagePlacementMode: 'fit',
  staticBackgroundImagePlacement: null,
  staticBackgroundImageDraftPlacement: null,
  deviceShadowEnabled: false,
  deviceShadowPreset: 'soft',
  deviceShadowAngle: 45,
  deviceShadowOpacity: 100,
  deviceShadowColor: '#000000',
  reflectionVisible: true,
  reflectionOpacity: 50,
  reflectionBlur: 50,
  screenOverlayOpacity: 50,
  screenBlur: 50,
  usePaneView: isStandaloneWebApp ? 'home' : 'templates'
};

const appRoot = document.getElementById('app');
if (!appRoot) {
  throw new Error('Root element #app not found');
}

let noticeTimer: number | null = null;
let newTemplatesLoadObserver: IntersectionObserver | null = null;
let pendingInputFocus: { id: string; start: number; end: number } | null = null;
let previewSyncGeneration = 0;
let collectionsRequestGeneration = 0;
let previewDrawGeneration = 0;
let previewRenderScheduled = false;
let previewRenderInFlight = false;
let previewRenderPending = false;
let sliderDragActive = false;
let sliderDragResetTimer: number | null = null;
let previewNavTouchHandledUntil = 0;
let previewUpgradeTimer: number | null = null;
let previewUpgradeToken = 0;
let staticVideoStoryboardGeneration = 0;
let staticVideoStoryboardAbortController: AbortController | null = null;
let staticVideoStoryboardSeekGeneration = 0;
let staticVideoStoryboardSeekAbortController: AbortController | null = null;
let staticVideoStoryboardSampleCache: StaticVideoStoryboardSampleCache | null = null;
let staticVideoStoryboardSamplePromise: Promise<StaticVideoStoryboardSample[]> | null = null;
let staticVideoStoryboardSamplePromiseKey = '';
let staticVideoStoryboardSampleAbortController: AbortController | null = null;
let authStateUnsubscribe: (() => void) | null = null;
let accountMenuOutsideClickCleanup: (() => void) | null = null;
let gradientPresetsOutsideClickCleanup: (() => void) | null = null;
let gradientPaletteHistory: string[][] = [];
let gradientColorEditSnapshot: string[] | null = null;
let motionPlaybackRequestId: number | null = null;
let motionPlaybackToken = 0;
let motionPlaybackStartAt = 0;
let motionPlaybackStartTime = 0;
let motionPlaybackCurrentTime = 0;
let motionPlaybackDuration = 0;
let motionPlaybackLastRenderedFrameIndex = -1;
let motionPlaybackLastUserFrameIndex = -1;
let motionPlaybackRenderInvalidated = true;
let motionTimelineResumeAfterSeek = false;
let motionTimelineSeekGeneration = 0;
let motionTimelineSeekPromise: Promise<void> | null = null;
let pendingMotionDetailAutoplayTemplateId = '';
let detailTransitionCleanupTimer: number | null = null;
let staticBackgroundObjectOverlayGeneration = 0;
let staticBackgroundObjectOverlaySrc = '';

/**
 * Geometry of the segmented pill as it stood before the pane was rebuilt.
 * Declared here rather than beside its functions because the first render runs
 * while the module is still evaluating, and a `let` further down would not be
 * initialised yet.
 */
type SegmentedThumbSnapshot = { key: string; left: number; width: number };
let segmentedThumbSnapshot: SegmentedThumbSnapshot | null = null;
let headerThumbSnapshots = new Map<string, SegmentedThumbSnapshot>();
let catalogPaneExitAnimation: Animation | null = null;
let catalogPaneSwapReady = false;
let catalogPaneRevealStartedAt: number | null = null;
let catalogPaneRevealFromOpacity = 0;
let mobileStickyPreviewCleanup: (() => void) | null = null;
const motionExportSessionId = new URLSearchParams(window.location.search).get('motionExportSession')?.trim() || '';
const shouldAutoExportMotionSession = new URLSearchParams(window.location.search).get('motionAutoExport') === '1';

// ===== Hash-based routing =====
// Format: #/{contentType}/{sourceFilter}/{domain}[/collections|/c/{collectionId}|/t/{templateId}|/templates]
// Examples:
//   #/static/all/app              → STATIC, ALL, App, all templates view
//   #/static/all/app/collections  → STATIC, ALL, App, collections view
//   #/static/cg/promo/c/coll-id  → STATIC CG, Promo, templates in collection
//   #/motion/cg/app/t/tpl-id     → MOTION CG, App, template detail

let _suppressHashPush = false;

type NavView = 'home' | 'collections' | 'templates' | 'detail';

interface NavHashState {
  purposeId?: string;
  contentType: ContentType;
  sourceFilter: SourceFilter;
  domain: DomainCategory;
  view: NavView;
  collectionId?: string;
  templateId?: string;
}

function encodeNavHash(nav: NavHashState): string {
  if (nav.view === 'home') {
    return '#/home';
  }
  const ct = nav.contentType.toLowerCase();
  const sf = nav.sourceFilter.toLowerCase();
  const domain = nav.domain.toLowerCase();
  let path = `/${ct}/${sf}/${domain}`;
  if (nav.purposeId) path += `/p/${encodeURIComponent(nav.purposeId)}`;
  if (nav.view === 'detail' && nav.templateId) {
    path += `/t/${encodeURIComponent(nav.templateId)}`;
  } else if (nav.view === 'collections') {
    path += '/collections';
  } else if (nav.view === 'templates') {
    if (nav.collectionId) {
      path += `/c/${encodeURIComponent(nav.collectionId)}`;
    } else {
      path += '/templates';
    }
  }
  return `#${path}`;
}

function decodeNavHash(hash: string): NavHashState | null {
  if (!hash || hash === '#' || hash === '#/') return null;
  const parts = hash.replace(/^#\//, '').split('/');
  if (parts[0]?.toLowerCase() === 'home') {
    return {
      contentType: state.selectedContentType,
      sourceFilter: state.selectedSourceFilter,
      domain: normalizeDomainCategory(state.selectedDomainCategory),
      view: 'home'
    };
  }
  const ctRaw = parts[0]?.toLowerCase();
  const contentType: ContentType = ctRaw === 'motion' ? 'MOTION' : 'STATIC';
  const sfRaw = parts[1]?.toLowerCase();
  const sourceFilter: SourceFilter = sfRaw === 'cg' ? 'CG' : sfRaw === 'ai' ? 'AI' : 'ALL';
  const domainRaw = parts[2]?.toLowerCase();
  const parsedDomain: DomainCategory = domainRaw === 'promo' ? 'Promo' : domainRaw === 'brand' ? 'Brand' : DEFAULT_DOMAIN_CATEGORY;
  const domain = normalizeDomainCategory(parsedDomain);
  const purposeId = parts[3] === 'p' && parts[4] ? scopeCollectionPurposeId(decodeURIComponent(parts[4]),contentType) : undefined;
  const offset = purposeId ? 5 : 3;
  const viewType = parts[offset] || (purposeId ? 'collections' : 'templates');
  const viewId = parts[offset+1] ? decodeURIComponent(parts[offset+1]) : '';
  if (viewType === 't' && viewId) {
    return { contentType, sourceFilter, domain, purposeId, view: 'detail', templateId: viewId };
  }
  if (viewType === 'c' && viewId) {
    return { contentType, sourceFilter, domain, purposeId, view: 'templates', collectionId: viewId };
  }
  if (viewType === 'collections') {
    return { contentType, sourceFilter, domain, purposeId, view: 'collections' };
  }
  if (viewType === 'templates') {
    return { contentType, sourceFilter, domain, purposeId, view: 'templates' };
  }
  return { contentType, sourceFilter, domain, purposeId, view: 'templates' };
}

function getCurrentNavState(): NavHashState {
  const collection = state.usePaneView === 'detail' ? getTemplateCollection(getSelectedTemplate()) : getSelectedCollection();
  return {
    purposeId: state.favoritesOnly ? undefined : collection ? getCollectionPurposeId(collection,state.collectionPurposes) : state.selectedPurposeId || undefined,
    contentType: state.selectedContentType,
    sourceFilter: state.selectedSourceFilter,
    domain: normalizeDomainCategory(state.selectedDomainCategory),
    view:
      state.usePaneView === 'home'
        ? 'home'
        : state.usePaneView === 'detail'
          ? 'detail'
          : state.usePaneView === 'templates'
            ? 'templates'
            : 'collections',
    collectionId: state.selectedCollectionId || undefined,
    templateId: state.selectedId || undefined
  };
}

function pushNavHash(): void {
  if (_suppressHashPush || isEmbeddedPlugin) return;
  const hash = encodeNavHash(getCurrentNavState());
  if (window.location.hash !== hash) {
    history.pushState(null, '', hash);
  }
}

function replaceNavHash(): void {
  if (_suppressHashPush || isEmbeddedPlugin) return;
  const hash = encodeNavHash(getCurrentNavState());
  if (window.location.hash !== hash) {
    history.replaceState(null, '', hash);
  }
}

function getCurrentTemplateUrl(): string {
  const url = new URL(window.location.href);
  url.hash = encodeNavHash(getCurrentNavState());
  return url.toString();
}

function applyNavHash(hash: string): void {
  const nav = decodeNavHash(hash);
  const collectionQueryChanged = nav && (nav.contentType !== state.selectedContentType || nav.sourceFilter !== state.selectedSourceFilter);
  discardNewTemplatesMode();
  if (!nav) {
    if (!isEmbeddedPlugin) {
      state.usePaneView = 'home';
      render();
    }
    return;
  }
  _suppressHashPush = true;
  try {
    if (nav.view === 'home') {
      state.usePaneView = 'home';
      render();
      return;
    }
    // Apply content type if changed
    if (nav.contentType !== state.selectedContentType) {
      applyContentTypeFilter(nav.contentType);
    }
    // Apply source filter if changed
    if (nav.sourceFilter !== state.selectedSourceFilter) {
      applySourceFilterChange(nav.sourceFilter);
    }
    // Apply domain if changed
    if (nav.domain !== state.selectedDomainCategory) {
      applyDomainCategoryFilter(nav.domain);
    }
    state.selectedPurposeId = state.collectionPurposes.some(p=>p.id===nav.purposeId&&p.contentTypes.includes(nav.contentType)) ? nav.purposeId! : '';
    // Apply view
    if (nav.view === 'detail' && nav.templateId) {
      const template = state.catalog.find((item) => item.id === nav.templateId);
      if (template) {
        state.selectedCollectionId =
          template.collectionId || getDefaultCollectionIdForType(template.templateType);
        const collection = getTemplateCollection(template);
        state.selectedPurposeId = collection ? getCollectionPurposeId(collection, state.collectionPurposes) : nav.purposeId || '';
        state.quickCatalogFilter = 'collections';
        state.favoritesOnly = false;
        state.category = 'All';
        state.catalogSearch = '';
        openTemplateDetail(nav.templateId);
        return;
      }
    }
    if (nav.view === 'templates') {
      if (nav.collectionId) {
        state.selectedCollectionId = nav.collectionId;
        const collection = getSelectedCollection();
        state.selectedPurposeId = collection ? getCollectionPurposeId(collection, state.collectionPurposes) : nav.purposeId || '';
        state.quickCatalogFilter = 'collections';
        state.favoritesOnly = false;
      } else {
        state.selectedCollectionId = '';
        state.quickCatalogFilter = 'All';
        state.favoritesOnly = false;
        state.category = 'All';
      }
      state.usePaneView = 'templates';
    } else {
      state.selectedCollectionId = '';
      state.quickCatalogFilter = 'collections';
      state.favoritesOnly = false;
      state.category = 'All';
      state.usePaneView = 'collections';
    }
    render();
  } finally {
    _suppressHashPush = false;
    if (collectionQueryChanged && nav?.view !== 'home') {
      void refreshCollections({ quiet: true }).then(() => {
        if (window.location.hash === hash) applyNavHash(hash);
      });
    }
  }
}

const SHARP_PREVIEW_IDLE_MS = 1200;
let scrollChromeHideTimer: number | null = null;
let scrollChromeFrameId: number | null = null;
let scrollChromeBound = false;
let scrollThumbDrag:
  | {
      pointerId: number;
      startY: number;
      startScrollTop: number;
    }
  | null = null;

init().catch((error) => {
  setError(error instanceof Error ? error.message : 'Unknown init error');
  render();
});

async function init(): Promise<void> {
  if (isStandaloneWebApp) {
    initializeAnalytics();
  }

  window.addEventListener('beforeunload', () => {
    revokePreviewUrlMap(state.previewUrls);
    clearStaticVideoStoryboard();
    revokeUserImageSrc();
    revokeUserVideoSrc();
    authStateUnsubscribe?.();
  });

  // Browser back/forward: react to hash changes
  window.addEventListener('popstate', () => {
    if (renderPublicRouteIfNeeded()) {
      return;
    }
    pauseMotionPlayback({ preserveFrame: true });
    applyNavHash(window.location.hash);
  });

  if (renderPublicRouteIfNeeded()) {
    if (['pricing', 'billing', 'payment-success'].includes(getPublicPageRoute() || '')) {
      await refreshPublicBilling();
      if (/^txn_[a-z0-9]{26}$/.test(new URLSearchParams(window.location.search).get('_ptxn') || '')) {
        try { await initializePaddleCheckout(); }
        catch (error) { showBillingStatus(error instanceof Error ? error.message : 'Unable to open payment link.'); }
      }
      if (getPublicPageRoute() === 'payment-success') {
        let attempts = 0;
        const poll = window.setInterval(() => {
          if (state.accountAccess?.unlimited || ++attempts > 12 || getPublicPageRoute() !== 'payment-success') { window.clearInterval(poll); return; }
          void refreshPublicBilling();
        }, 2500);
      }
    }
    return;
  }
  void loadBillingConfig().then((config) => { billingConfig = config; }).catch(() => undefined);

  // Peek at hash before the first render to avoid the home-page flash on reload
  // and so refreshCollections() below fetches the right (MOTION/STATIC) set —
  // otherwise STATIC collections leak into MOTION tab until user toggles it.
  if (!isEmbeddedPlugin) {
    const peekedHash = window.location.hash || safeStorageGet(STORAGE_KEYS.returnNavHash);
    if (peekedHash && peekedHash !== '#') {
      const peekedNav = decodeNavHash(peekedHash);
      if (peekedNav && peekedNav.view !== 'home') {
        // Apply filter state from hash BEFORE catalog/collections fetch
        state.selectedContentType = peekedNav.contentType;
        state.selectedSourceFilter = peekedNav.sourceFilter;
        state.selectedDomainCategory = normalizeDomainCategory(peekedNav.domain);
        state.templateType = getEffectiveTemplateType();
        state.selectedPurposeId = peekedNav.purposeId || '';
        // For detail view show templates list while catalog loads (closest match)
        state.usePaneView = peekedNav.view === 'detail' ? 'templates' : peekedNav.view;
        if (peekedNav.view === 'templates' && peekedNav.collectionId) {
          state.selectedCollectionId = peekedNav.collectionId;
          state.quickCatalogFilter = 'collections';
        } else if (peekedNav.view === 'templates') {
          state.selectedCollectionId = '';
          state.quickCatalogFilter = 'All';
          state.category = 'All';
        } else if (peekedNav.view === 'collections') {
          state.selectedCollectionId = '';
          state.quickCatalogFilter = 'collections';
          state.category = 'All';
        }
      }
    }
  }

  render();
  ensureScrollChromeBindings();
  bindGlobalMessages();
  bindGlobalKeyboardNavigation();
  bindSliderDragTracking();
  notifyShellReady();
  await initializeStandaloneAuth();
  await Promise.all([
    refreshRenderAccessSettings(),
    refreshCatalog('', { quiet: true }),
    refreshCollections({ quiet: true })
  ]);
  if (!isEmbeddedPlugin && motionExportSessionId) {
    await hydrateMotionExportSession(motionExportSessionId, { autoExport: shouldAutoExportMotionSession });
  }

  if (new URLSearchParams(window.location.search).has('subscription')) {
    openAccountPage('subscription');
    return;
  }

  // Restore state from URL hash on initial load
  if (!isEmbeddedPlugin && window.location.hash && window.location.hash !== '#') {
    applyNavHash(window.location.hash);
    return;
  }

  // Restore hash saved before Google OAuth redirect (mobile redirect flow)
  const returnNavHash = safeStorageGet(STORAGE_KEYS.returnNavHash);
  if (!isEmbeddedPlugin && returnNavHash && returnNavHash !== '#') {
    safeStorageSet(STORAGE_KEYS.returnNavHash, '');
    applyNavHash(returnNavHash);
    return;
  }

  // Empty URL is the clean landing page. Deep links keep using hash routes.
  if (state.usePaneView !== 'home') {
    replaceNavHash();
  }
  render();
}

function injectStyles(cssText: string): void {
  if (document.getElementById('mockup-plugin-styles')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'mockup-plugin-styles';
  style.textContent = cssText;
  document.head.appendChild(style);
}

function showFatalError(message: string): void {
  const host = document.getElementById('app') || document.body;
  host.innerHTML = `<pre style="margin:12px;padding:12px;background:#ffecec;color:#9a1e1e;border:1px solid #f2b8b8;border-radius:8px;white-space:pre-wrap">UI fatal error:\n${escapeHtml(
    message
  )}</pre>`;
}

function bindGlobalMessages(): void {
  window.addEventListener('message', (event: MessageEvent<{ pluginMessage?: MainToUiMessage }>) => {
    const message = event.data?.pluginMessage;
    if (!message) {
      return;
    }

    if (message.type === 'READY') {
      const incomingFavorites = normalizeFavoriteTemplateIds(message.payload?.favoriteTemplateIds);
      state.favoriteTemplateIds = incomingFavorites;
      saveFavoriteTemplateIds(incomingFavorites);
      render();
      setNotice('Plugin ready', 'info');
      return;
    }

    if (message.type === 'ERROR') {
      state.importingFromFigma = false;
      setNotice(message.payload.message, 'error');
      render();
      return;
    }

    if (message.type === 'INSERT_DONE') {
      state.inserting = false;
      setNotice('PNG inserted into Figma project', 'success');
      render();
      return;
    }

    if (message.type === 'IMPORT_FROM_FIGMA_DONE') {
      state.importingFromFigma = false;
      void applyLoadedUserImageFromFigma(message.payload.bytes, message.payload.name || 'Figma Selection')
        .then(async () => {
          const selected = getSelectedTemplate();
          if (selected) {
            if (selected.isVideo) {
              await ensurePreviewMotionAssets(selected);
            } else {
              await ensurePreviewAssets(selected);
            }
          }
          setNotice('Selection imported from Figma', 'success');
          render();
        })
        .catch((error) => {
          setNotice(error instanceof Error ? error.message : 'Failed to import selection from Figma', 'error');
          render();
        });
    }
  });
}

function bindGlobalKeyboardNavigation(): void {
  window.addEventListener('keydown', (event) => {
    if (state.usePaneView !== 'detail') {
      return;
    }
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    if (shouldIgnoreGlobalArrowNavigation(event.target)) {
      return;
    }

    const selectedTemplate = getSelectedTemplate();
    if (!selectedTemplate) {
      return;
    }

    const navigation = getTemplateNavigation(filteredCatalog(), selectedTemplate.id);
    const nextId = event.key === 'ArrowLeft' ? navigation.previousId : navigation.nextId;
    if (!nextId) {
      return;
    }

    event.preventDefault();
    openTemplateDetail(nextId);
  });
}

function shouldIgnoreGlobalArrowNavigation(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable || target.closest('[contenteditable="true"]')) {
    return true;
  }

  const formControl = target.closest('input, textarea, select');
  if (!(formControl instanceof HTMLElement)) {
    return false;
  }

  if (formControl instanceof HTMLInputElement) {
    const type = (formControl.type || '').toLowerCase();
    return ['text', 'search', 'url', 'tel', 'password', 'email', 'number', 'range', 'color'].includes(type);
  }

  return true;
}

function notifyShellReady(): void {
  if (window.parent === window) {
    return;
  }

  window.parent.postMessage(
    {
      pluginMessage: {
        type: 'REMOTE_READY'
      }
    },
    '*'
  );
}

async function initializeStandaloneAuth(): Promise<void> {
  if (!isStandaloneWebApp) {
    return;
  }

  state.authConfigured = isSupabaseAuthConfigured();
  state.authReady = false;
  render();

  if (!state.authConfigured) {
    state.authReady = true;
    return;
  }

  // Popup detection: when Google OAuth popup lands back on our app, close it
  // and notify the opener so it can update the session without page refresh.
  const isAuthPopup = Boolean(window.opener) && (() => {
    try { return localStorage.getItem('mp-auth-popup') === '1'; } catch { return false; }
  })();

  if (isAuthPopup) {
    try {
      const user = await getCurrentAuthUser();
      if (user) {
        (window.opener as Window).postMessage({ type: 'MP_AUTH_DONE' }, window.location.origin);
      }
    } catch {
      // best-effort
    }
    try { localStorage.removeItem('mp-auth-popup'); } catch {}
    window.close();
    return;
  }

  // Listen for popup-based auth completion in the main window
  const handlePopupAuthMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if ((event.data as { type?: string } | null)?.type !== 'MP_AUTH_DONE') return;
    void getCurrentAuthUser().then(user =>
      applyAuthUser(user, { migrateLocalFavorites: true, renderAfter: true })
    );
  };
  window.addEventListener('message', handlePopupAuthMessage);

  try {
    // Subscribe BEFORE getSession so we catch PKCE code exchange events properly
    authStateUnsubscribe = onAuthStateChange((user) => {
      void applyAuthUser(user, { migrateLocalFavorites: true, renderAfter: true });
    });

    const user = await getCurrentAuthUser();
    await applyAuthUser(user, { migrateLocalFavorites: true, renderAfter: false });
  } catch (error) {
    state.authReady = true;
    setNotice(error instanceof Error ? error.message : 'Failed to initialize auth', 'error');
  }

  state.authReady = true;
}

async function refreshRenderAccessSettings(): Promise<void> {
  if (!isStandaloneWebApp) {
    return;
  }

  try {
    state.renderAccessSettings = await loadRenderAccessSettings();
  } catch {
    state.renderAccessSettings = { ...DEFAULT_RENDER_ACCESS_SETTINGS };
  }
}

async function applyAuthUser(
  user: AuthUser | null,
  options: { migrateLocalFavorites: boolean; renderAfter: boolean }
): Promise<void> {
  const previousUserId = state.authUser?.id || '';
  const nextUserId = user?.id || '';

  state.authUser = user;
  state.authReady = true;
  if (previousUserId !== nextUserId) { state.accountOverview = null; state.accountAccess = null; state.accountPublicId = null; }

  if (!user) {
    state.favoriteTemplateIds = [];
    state.favoritesOnly = false;
    state.gradientPresets = [];
    state.gradientPresetsOpen = false;
    state.gradientPresetsLoading = false;
    state.gradientPresetBusy = false;
    state.accountMenuOpen = false;
    state.accountOverview = null;
    state.accountAccess = null; state.accountPublicId = null;
    state.accountOverviewError = '';
    state.accountOverviewLoading = false;
    if (options.renderAfter) {
      render();
    }
    return;
  }

  await refreshAuthenticatedFavorites({
    migrateLocalFavorites: options.migrateLocalFavorites && previousUserId !== nextUserId,
    renderWhileLoading: options.renderAfter
  });
  await refreshGradientPresets({ renderWhileLoading: false });
  await refreshAccountOverview();
  await consumePendingAuthAction();
  if (new URLSearchParams(window.location.search).has('subscription')) {
    state.accountMenuView = 'subscription';
    state.usePaneView = 'account';
  }

  if (state.authPrompt) {
    state.authPrompt = null;
    state.authMessage = null;
  }

  if (options.renderAfter) {
    render();
  }
}

async function refreshAuthenticatedFavorites(options: {
  migrateLocalFavorites: boolean;
  renderWhileLoading: boolean;
}): Promise<void> {
  if (!isStandaloneWebApp || !state.authUser) {
    return;
  }

  state.favoritesLoading = true;
  if (options.renderWhileLoading) {
    render();
  }

  try {
    let remoteIds = normalizeFavoriteTemplateIds(await loadUserFavoriteTemplateIds());
    const localIds = options.migrateLocalFavorites ? normalizeFavoriteTemplateIds(loadFavoriteTemplateIds()) : [];

    if (localIds.length > 0) {
      const mergedIds = mergeFavoriteTemplateIds(remoteIds, localIds);
      await Promise.all(localIds.map((id) => addUserFavoriteTemplateId(id)));
      saveFavoriteTemplateIds([]);
      remoteIds = mergedIds;
    }

    state.favoriteTemplateIds = remoteIds;
  } catch (error) {
    setNotice(error instanceof Error ? error.message : 'Failed to load favorites', 'error');
  } finally {
    state.favoritesLoading = false;
  }
}

async function refreshGradientPresets(options: { renderWhileLoading: boolean }): Promise<void> {
  if (!isStandaloneWebApp || !state.authUser) {
    state.gradientPresets = [];
    state.gradientPresetsLoading = false;
    return;
  }

  state.gradientPresetsLoading = true;
  if (options.renderWhileLoading) {
    render();
  }

  try {
    state.gradientPresets = await loadGradientPresets();
  } catch (error) {
    setNotice(error instanceof Error ? error.message : 'Failed to load gradient presets', 'error');
  } finally {
    state.gradientPresetsLoading = false;
  }
}

function requireStandaloneAuth(reason: AuthPromptReason, pendingAction: PendingAuthAction | null): boolean {
  if (!isStandaloneWebApp || state.authUser) {
    return false;
  }

  openAuthPrompt(reason, pendingAction);
  return true;
}

function shouldRequireAuthForDownload(template: MockupTemplate, staticVideoRenderActive: boolean): boolean {
  if (template.isVideo || staticVideoRenderActive) {
    return state.renderAccessSettings.requireAuthForVideoRender;
  }

  return state.renderAccessSettings.requireAuthForImageRender;
}

function shouldRequireAuthForVideoRender(): boolean {
  return state.renderAccessSettings.requireAuthForVideoRender;
}

function shouldRequireAuthForImageRender(): boolean {
  return state.renderAccessSettings.requireAuthForImageRender;
}

function openAuthPrompt(reason: AuthPromptReason, pendingAction: PendingAuthAction | null = null): void {
  state.authPrompt = {
    reason,
    pendingAction
  };
  state.authMessage = null;

  if (pendingAction) {
    savePendingAuthAction(pendingAction);
  }

  render();
}

function closeAuthPrompt(): void {
  state.authPrompt = null;
  state.authBusy = false;
  state.authMessage = null;
  clearPendingAuthAction();
  render();
}

async function handleGoogleSignIn(): Promise<void> {
  if (!state.authConfigured) {
    state.authMessage = {
      type: 'error',
      message: buildMissingAuthConfigMessage()
    };
    render();
    return;
  }

  state.authBusy = true;
  state.authMessage = null;
  render();

  try {
    await signInWithGoogle();
    state.authBusy = false;
    state.authMessage = {
      type: 'success',
      message: 'Complete Google sign-in in the opened window.'
    };
    render();
  } catch (error) {
    state.authBusy = false;
    state.authMessage = {
      type: 'error',
      message: error instanceof Error ? error.message : 'Google login failed'
    };
    render();
  }
}

async function handleEmailSignIn(email: string): Promise<void> {
  state.authEmail = email;

  if (!state.authConfigured) {
    state.authMessage = {
      type: 'error',
      message: buildMissingAuthConfigMessage()
    };
    render();
    return;
  }

  state.authBusy = true;
  state.authMessage = null;
  render();

  try {
    await signInWithEmail(email);
    state.authBusy = false;
    state.authMessage = {
      type: 'success',
      message: 'Check your email for the login link.'
    };
    render();
  } catch (error) {
    state.authBusy = false;
    state.authMessage = {
      type: 'error',
      message: error instanceof Error ? error.message : 'Email login failed'
    };
    render();
  }
}

async function handleSignOut(): Promise<void> {
  try {
    await signOut();
    state.authUser = null;
    state.favoriteTemplateIds = [];
    state.favoritesOnly = false;
    state.gradientPresets = [];
    state.gradientPresetsOpen = false;
    state.gradientPresetsLoading = false;
    state.gradientPresetBusy = false;
    state.accountMenuOpen = false;
    state.accountOverview = null;
    state.accountAccess = null; state.accountPublicId = null;
    state.accountOverviewError = '';
    state.accountOverviewLoading = false;
    setNotice('Signed out', 'info');
  } catch (error) {
    setNotice(error instanceof Error ? error.message : 'Sign out failed', 'error');
  }
}

async function consumePendingAuthAction(): Promise<void> {
  const pendingAction = loadPendingAuthAction();
  if (!pendingAction || !state.authUser) {
    return;
  }

  clearPendingAuthAction();

  if (pendingAction.type === 'favorite') {
    await setFavoriteTemplateFavorite(pendingAction.templateId, true, { skipAuthPrompt: true });
    setNotice('Template added to your favorites', 'success');
    return;
  }

  if (pendingAction.type === 'favorites') {
    state.favoritesOnly = true;
    state.quickCatalogFilter = 'collections';
    state.usePaneView = 'templates';
    state.selectedCollectionId = '';
    state.category = 'All';
    setNotice('Favorites unlocked', 'success');
    return;
  }

  if (pendingAction.type === 'gradient-presets') {
    state.gradientPresetsOpen = true;
    setNotice('Your gradient presets are ready.', 'success');
    return;
  }

  setNotice('Export unlocked. Choose a file and export again.', 'success');
}

function savePendingAuthAction(action: PendingAuthAction): void {
  safeStorageSet(STORAGE_KEYS.pendingAuthAction, JSON.stringify(action));
}

function loadPendingAuthAction(): PendingAuthAction | null {
  const raw = safeStorageGet(STORAGE_KEYS.pendingAuthAction);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<PendingAuthAction>;
    if (parsed.type === 'favorite' && typeof parsed.templateId === 'string' && parsed.templateId.trim()) {
      return {
        type: 'favorite',
        templateId: parsed.templateId.trim()
      };
    }
    if (parsed.type === 'favorites') {
      return { type: 'favorites' };
    }
    if (parsed.type === 'gradient-presets') {
      return { type: 'gradient-presets' };
    }
    if (parsed.type === 'export' && (parsed.mode === 'download' || parsed.mode === 'browser')) {
      return {
        type: 'export',
        mode: parsed.mode
      };
    }
  } catch {
    return null;
  }

  return null;
}

function clearPendingAuthAction(): void {
  safeStorageSet(STORAGE_KEYS.pendingAuthAction, '');
}

function buildMissingAuthConfigMessage(): string {
  const missing = getSupabaseAuthConfigStatus().missing.join(', ');
  return `Supabase auth is not configured: ${missing}`;
}

async function refreshCatalog(preferredId = '', options: { quiet?: boolean } = {}): Promise<void> {
  if (!options.quiet) {
    state.loadingCatalog = true;
    state.error = '';
    render();
  }

  try {
    const catalog = await loadCatalog();
    state.catalog = catalog;
    trimPreviewUrlsToCatalog(catalog);

    if (catalog.length === 0) {
      state.selectedId = '';
      clearPreviewUrls();
      if (state.usePaneView !== 'collections') {
        setError('Catalog is empty. Check the server connection or make sure mockups exist.');
      }
      return;
    }

    const scopedCatalog = getTypeScopedCatalog();
    const existing = scopedCatalog.find((item) => item.id === state.selectedId);
    if (preferredId && scopedCatalog.some((item) => item.id === preferredId)) {
      state.selectedId = preferredId;
    } else if (!existing) {
      state.selectedId = scopedCatalog[0]?.id || '';
      resetTemplateControlsToDefaults();
    }

    await hydratePreviewUrls(catalog, { allowRender: !options.quiet });
  } catch (error) {
    setError(error instanceof Error ? error.message : 'Failed to load catalog');
  } finally {
    state.loadingCatalog = false;
    if (!options.quiet) {
      render();
    }
  }
}

async function refreshCollections(options: { quiet?: boolean } = {}): Promise<void> {
  const request = ++collectionsRequestGeneration;
  state.loadingCollections = true;
  state.collectionsError = '';
  if (!options.quiet) {
    render();
  }

  const collectionQueryType = getCollectionQueryType();

  try {
    // Attach both requests to the same promise immediately: failures must not
    // leave a detached request or render collections before visibility arrives.
    const [purposes, collections] = await Promise.all([
      loadCollectionPurposes(),
      state.selectedContentType === 'STATIC' && state.selectedSourceFilter === 'ALL'
        ? Promise.all([loadCollections(undefined, 'CG'), loadCollections(undefined, 'AI')]).then(([cgCols, aiCols]) => {
            const existingIds = new Set(cgCols.map(c=>c.id));
            return [...cgCols, ...aiCols.filter(c=>!existingIds.has(c.id))];
          })
        : loadCollections(undefined, collectionQueryType)
    ]);
    if (request !== collectionsRequestGeneration) return;
    state.collections = collections;
    state.collectionPurposes = purposes;
    if (!state.collectionPurposes.some(p=>p.id===state.selectedPurposeId&&p.contentTypes.includes(state.selectedContentType))) state.selectedPurposeId='';

    const isCollectionScoped =
      Boolean(state.selectedCollectionId) &&
      (state.usePaneView === 'detail' ||
        (state.usePaneView === 'templates' && state.quickCatalogFilter === 'collections'));

    if (isCollectionScoped) {
      if (!state.collections.some((item) => item.id === state.selectedCollectionId)) {
        state.selectedCollectionId = state.collections[0]?.id || getDefaultCollectionIdForType(collectionQueryType);
      }
      state.selectedPurposeId = getCollectionPurposeId(getSelectedCollection(), state.collectionPurposes);
    } else {
      state.selectedCollectionId = '';
    }
  } catch {
    if (request !== collectionsRequestGeneration) return;
    state.collections = [];
    state.collectionPurposes = [];
    state.collectionsError = 'Could not load collections. Please refresh the page to try again.';
  } finally {
    if (request === collectionsRequestGeneration) {
      state.loadingCollections = false;
      if (!options.quiet) render();
    }
  }
}

function setError(message: string): void {
  state.error = message;
}

function getTemplatePreviewUrl(template: MockupTemplate): string {
  return state.previewUrls[template.id] || template.preview;
}

function supportsMotionCardHoverPreview(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function clearPendingMotionCardHoverPreview(): void {
  if (pendingMotionCardHoverPreviewTimer !== null) {
    window.clearTimeout(pendingMotionCardHoverPreviewTimer);
    pendingMotionCardHoverPreviewTimer = null;
  }
  pendingMotionCardHoverPreviewCard = null;
  pendingMotionCardHoverPreviewTarget = null;
}

function disposeActiveMotionCardHoverPreview(): void {
  clearPendingMotionCardHoverPreview();
  motionCardHoverPreviewToken += 1;
  const active = activeMotionCardHoverPreview;
  activeMotionCardHoverPreview = null;
  if (!active) {
    return;
  }
  active.image?.classList.remove('is-hover-hidden');
  active.video.pause();
  active.video.removeAttribute('src');
  active.video.load();
  active.video.remove();
}

function startMotionCardHoverPreview(card: HTMLButtonElement, hoverTarget: HTMLElement = card): void {
  if (!supportsMotionCardHoverPreview()) {
    return;
  }

  const previewUrl = card.dataset.motionPreviewUrl;
  if (!previewUrl) {
    return;
  }

  if (activeMotionCardHoverPreview?.card === card) {
    return;
  }

  disposeActiveMotionCardHoverPreview();

  const token = motionCardHoverPreviewToken + 1;
  motionCardHoverPreviewToken = token;
  const atlasLayout = card.dataset.motionAtlasLayout === 'extended-6' ? 'extended-6' : 'standard-4';
  const image = card.querySelector('img');
  const video = document.createElement('video');
  video.className = `mockup-card-motion-video ${atlasLayout === 'extended-6' ? 'layout-extended-6' : 'layout-standard-4'}`;
  video.muted = true;
  video.loop = true;
  video.playsInline = true;
  video.preload = 'metadata';
  video.disablePictureInPicture = true;
  video.setAttribute('aria-hidden', 'true');

  activeMotionCardHoverPreview = { hoverTarget, card, image, video, token };
  card.append(video);

  const showVideo = () => {
    if (
      !activeMotionCardHoverPreview ||
      activeMotionCardHoverPreview.token !== token ||
      !hoverTarget.matches(':hover')
    ) {
      return;
    }
    video.classList.add('is-visible');
    window.requestAnimationFrame(() => {
      if (!activeMotionCardHoverPreview || activeMotionCardHoverPreview.token !== token) {
        return;
      }
      image?.classList.add('is-hover-hidden');
    });
    void video.play().catch(() => {
      if (activeMotionCardHoverPreview?.token === token) {
        disposeActiveMotionCardHoverPreview();
      }
    });
  };

  video.addEventListener('loadeddata', showVideo, { once: true });
  video.addEventListener(
    'error',
    () => {
      if (activeMotionCardHoverPreview?.token === token) {
        disposeActiveMotionCardHoverPreview();
      }
    },
    { once: true }
  );

  video.src = previewUrl;
  video.load();
}

function getTemplateDetailPreviewUrl(template: MockupTemplate): string {
  if (!template.isVideo) {
    return template.previewLarge || getTemplatePreviewUrl(template);
  }
  return template.detailPreview || getTemplatePreviewUrl(template);
}

type MotionMockupTemplate = MockupTemplate & { isVideo: true };

function isMotionTemplate(template: MockupTemplate | null | undefined): template is MotionMockupTemplate {
  return Boolean(template?.isVideo);
}

function isDualScreenTemplate(template: MockupTemplate | null | undefined): boolean {
  return !template?.flexibleDisplay && template?.screenProfile?.id === 'dual-screen';
}

function isTripleScreenTemplate(template: MockupTemplate | null | undefined): boolean {
  return !template?.flexibleDisplay && template?.screenProfile?.id === 'triple-screen';
}

function getTemplateScreenCount(template: MockupTemplate | null | undefined): 1 | 2 | 3 {
  return getTemplateInputCount(template || {});
}

function hasTemplateScreen(template: MockupTemplate | null | undefined, screen: 1 | 2 | 3): boolean {
  return screen <= getTemplateScreenCount(template);
}

function hasDynamicIslandControl(template: MockupTemplate | null | undefined): boolean {
  if (!template || template.flexibleDisplay) {
    return false;
  }
  return template.isVideo
    ? template.motionDynamicIslandToggle === true
    : resolveStaticTemplateCapabilities(template).dynamicIsland;
}

function getDefaultDynamicIslandState(template: MockupTemplate | null | undefined): boolean {
  return Boolean(template?.isVideo && template.motionDynamicIslandToggle === true);
}

function shouldUseAlternateMask(template: MockupTemplate | null = getSelectedTemplate()): boolean {
  if (template?.isVideo && template.motionDynamicIslandToggle === true) {
    return !state.dynamicIsland;
  }
  return state.dynamicIsland;
}

function getActiveUserMediaName(template: MockupTemplate | null | undefined, screen: 1 | 2 | 3 = 1): string {
  if (screen === 3) {
    return state.userVideoName3 || state.userImageName3;
  }
  if (screen === 2) {
    return state.userVideoName2 || state.userImageName2;
  }
  if (isMotionTemplate(template)) {
    return state.userVideoName || state.userImageName;
  }
  return state.staticExport.renderMode === 'static_video' ? state.userVideoName : state.userImageName;
}

function hasActiveUserMedia(template: MockupTemplate | null | undefined): boolean {
  const primary = isMotionTemplate(template)
    ? Boolean(state.userVideo || state.userImage)
    : Boolean(state.userImage || (state.staticExport.renderMode === 'static_video' && state.userVideo));
  return primary ||
    (hasTemplateScreen(template, 2) && Boolean(state.userVideo2 || state.userImage2)) ||
    (hasTemplateScreen(template, 3) && Boolean(state.userVideo3 || state.userImage3));
}

function hasRequiredUserMedia(template: MockupTemplate | null | undefined): boolean {
  if (isMotionTemplate(template)) {
    return Boolean(
      state.userVideo ||
      state.userImage ||
      (hasTemplateScreen(template, 2) && (state.userVideo2 || state.userImage2)) ||
      (hasTemplateScreen(template, 3) && (state.userVideo3 || state.userImage3))
    );
  }
  return Boolean(
    state.userImage ||
    (state.staticExport.renderMode === 'static_video' && state.userVideo) ||
    (hasTemplateScreen(template, 2) && (state.userImage2 || state.userVideo2)) ||
    (hasTemplateScreen(template, 3) && (state.userImage3 || state.userVideo3))
  );
}

function getDetailPreviewKey(template: MockupTemplate): string {
  if (isMotionTemplate(template)) {
    return [
      'interactive-motion',
      template.id,
      state.motionExport.resizeMode,
      state.staticExport.mode,
      state.staticExport.mode === 'custom' ? JSON.stringify(state.staticExport.customPlacement || createDefaultCustomPlacement()) : 'fit',
      state.userVideoSrc || state.userImagePreviewSrc || state.userImageSrc || state.userVideoName || state.userImageName || 'user-media',
      hasTemplateScreen(template, 2) ? state.staticExport.mode2 : 'single-screen',
      hasTemplateScreen(template, 2) && state.staticExport.mode2 === 'custom'
        ? JSON.stringify(state.staticExport.customPlacement2 || createDefaultCustomPlacement())
        : 'fit',
      hasTemplateScreen(template, 2)
        ? state.userVideoSrc2 || state.userImagePreviewSrc2 || state.userImageSrc2 || state.userVideoName2 || state.userImageName2 || 'screen-2-media'
        : '',
      hasTemplateScreen(template, 3) ? state.staticExport.mode3 : 'single-screen',
      hasTemplateScreen(template, 3) && state.staticExport.mode3 === 'custom'
        ? JSON.stringify(state.staticExport.customPlacement3 || createDefaultCustomPlacement())
        : 'fit',
      hasTemplateScreen(template, 3)
        ? state.userVideoSrc3 || state.userImagePreviewSrc3 || state.userImageSrc3 || state.userVideoName3 || state.userImageName3 || 'screen-3-media'
        : '',
      shouldUseAlternateMask(template) ? 'mask2' : 'mask1',
      String(state.motionFrameIndex)
    ].join('|');
  }

  if (state.userImage || state.userImage2 || state.userImage3 || state.userVideo || state.userVideo2 || state.userVideo3) {
    return [
      'interactive',
      template.id,
      state.staticExport.renderMode,
      state.userVideoSrc || state.userImagePreviewSrc || state.userImageSrc || state.userVideoName || state.userImageName || 'user-media',
      state.staticExport.mode,
      state.staticExport.mode === 'custom' ? JSON.stringify(state.staticExport.customPlacement || createDefaultCustomPlacement()) : 'fit',
      hasTemplateScreen(template, 2)
        ? state.userVideoSrc2 || state.userImagePreviewSrc2 || state.userImageSrc2 || state.userVideoName2 || state.userImageName2 || 'screen-2-media'
        : '',
      hasTemplateScreen(template, 2) ? state.staticExport.mode2 : 'single-screen',
      hasTemplateScreen(template, 2) && state.staticExport.mode2 === 'custom'
        ? JSON.stringify(state.staticExport.customPlacement2 || createDefaultCustomPlacement())
        : 'fit',
      hasTemplateScreen(template, 3)
        ? state.userVideoSrc3 || state.userImagePreviewSrc3 || state.userImageSrc3 || state.userVideoName3 || state.userImageName3 || 'screen-3-media'
        : '',
      hasTemplateScreen(template, 3) ? state.staticExport.mode3 : 'single-screen',
      hasTemplateScreen(template, 3) && state.staticExport.mode3 === 'custom'
        ? JSON.stringify(state.staticExport.customPlacement3 || createDefaultCustomPlacement())
        : 'fit',
      shouldUseAlternateMask(template) ? 'mask2' : 'mask1'
    ].join('|');
  }

  return ['empty', template.id, getTemplateDetailPreviewUrl(template)].join('|');
}

function clearDetailTransitionCleanupTimer(): void {
  if (detailTransitionCleanupTimer !== null) {
    window.clearTimeout(detailTransitionCleanupTimer);
    detailTransitionCleanupTimer = null;
  }
}

function captureCurrentDetailPreviewOverlay(): string {
  if (state.usePaneView !== 'detail') {
    return '';
  }

  const previewCanvas = document.getElementById('preview-canvas') as HTMLCanvasElement | null;
  if (previewCanvas) {
    try {
      return previewCanvas.toDataURL('image/png');
    } catch {
      return '';
    }
  }

  const previewImage = document.getElementById('preview-image') as HTMLImageElement | null;
  return previewImage?.currentSrc || previewImage?.src || '';
}

function getTemplateNavigationOverlayUrl(templateId: string): string {
  const currentTemplate = getSelectedTemplate();
  const nextTemplate = state.catalog.find((item) => item.id === templateId);

  if (state.usePaneView === 'detail' && currentTemplate?.isVideo && nextTemplate?.isVideo) {
    // Encoding the live Motion canvas with toDataURL blocks navigation for
    // hundreds of milliseconds. The target poster gives immediate feedback
    // while its atlas is prepared without touching the export pipeline.
    return getTemplateDetailPreviewUrl(nextTemplate);
  }

  return captureCurrentDetailPreviewOverlay();
}

function markDetailPreviewReady(key: string): void {
  state.detailPreviewReadyKey = key;
  const previewDropzone = document.getElementById('preview-dropzone') as HTMLDivElement | null;
  if (previewDropzone?.dataset.previewKey === key) {
    previewDropzone.classList.remove('preview-loading');
  }
  const transitionOverlay = document.getElementById('detail-preview-transition-overlay');
  if (state.detailTransitionOverlayUrl && transitionOverlay instanceof HTMLImageElement) {
    transitionOverlay.classList.add('is-fading-out');
    clearDetailTransitionCleanupTimer();
    detailTransitionCleanupTimer = window.setTimeout(() => {
      detailTransitionCleanupTimer = null;
      state.detailTransitionOverlayUrl = '';
      transitionOverlay.remove();
    }, 240);
  }
}

async function hydratePreviewUrls(
  catalog: MockupTemplate[],
  options: {
    allowRender: boolean;
  }
): Promise<void> {
  const generation = ++previewSyncGeneration;
  const cachedUrls = await loadCachedPreviewUrls(catalog);

  if (generation !== previewSyncGeneration) {
    revokePreviewUrlMap(cachedUrls);
    return;
  }

  const cachedChanged = mergePreviewUrls(cachedUrls);
  if (options.allowRender && cachedChanged) {
    render();
  }

  void revalidatePreviewUrls(catalog, generation);
}

async function revalidatePreviewUrls(catalog: MockupTemplate[], generation: number): Promise<void> {
  const refreshedUrls = await syncPreviewCache(catalog);

  if (generation !== previewSyncGeneration) {
    revokePreviewUrlMap(refreshedUrls);
    return;
  }

  const changed = mergePreviewUrls(refreshedUrls);
  if (changed) {
    render();
  }
}

function mergePreviewUrls(incomingUrls: Record<string, string>): boolean {
  let changed = false;

  for (const [id, nextUrl] of Object.entries(incomingUrls)) {
    const currentUrl = state.previewUrls[id];
    if (currentUrl === nextUrl) {
      continue;
    }

    if (currentUrl && currentUrl.startsWith('blob:')) {
      URL.revokeObjectURL(currentUrl);
    }

    state.previewUrls[id] = nextUrl;
    changed = true;
  }

  return changed;
}

function trimPreviewUrlsToCatalog(catalog: MockupTemplate[]): void {
  const validIds = new Set(catalog.map((item) => item.id));
  const nextPreviewUrls: Record<string, string> = {};

  for (const [id, url] of Object.entries(state.previewUrls)) {
    if (validIds.has(id)) {
      nextPreviewUrls[id] = url;
      continue;
    }

    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  state.previewUrls = nextPreviewUrls;
}

function clearPreviewUrls(): void {
  revokePreviewUrlMap(state.previewUrls);
  state.previewUrls = {};
}

function revokeUserImageSrc(nextSrc = '', nextPreviewSrc = ''): void {
  if (state.userImageSrc && state.userImageSrc !== nextSrc && state.userImageSrc.startsWith('blob:')) {
    URL.revokeObjectURL(state.userImageSrc);
  }
  if (
    state.userImagePreviewSrc &&
    state.userImagePreviewSrc !== nextSrc &&
    state.userImagePreviewSrc !== nextPreviewSrc &&
    state.userImagePreviewSrc.startsWith('blob:')
  ) {
    URL.revokeObjectURL(state.userImagePreviewSrc);
  }
}

function revokeUserVideoSrc(nextSrc = ''): void {
  if (state.userVideo) {
    revokeManagedMediaUrl(state.userVideo);
  }
  if (state.userVideoSrc && state.userVideoSrc !== nextSrc && state.userVideoSrc.startsWith('blob:')) {
    URL.revokeObjectURL(state.userVideoSrc);
  }
}

function revokeUserImageSrc2(nextSrc = '', nextPreviewSrc = ''): void {
  if (state.userImageSrc2 && state.userImageSrc2 !== nextSrc && state.userImageSrc2.startsWith('blob:')) {
    URL.revokeObjectURL(state.userImageSrc2);
  }
  if (
    state.userImagePreviewSrc2 &&
    state.userImagePreviewSrc2 !== nextSrc &&
    state.userImagePreviewSrc2 !== nextPreviewSrc &&
    state.userImagePreviewSrc2.startsWith('blob:')
  ) {
    URL.revokeObjectURL(state.userImagePreviewSrc2);
  }
}

function revokeUserVideoSrc2(nextSrc = ''): void {
  if (state.userVideo2) {
    revokeManagedMediaUrl(state.userVideo2);
  }
  if (state.userVideoSrc2 && state.userVideoSrc2 !== nextSrc && state.userVideoSrc2.startsWith('blob:')) {
    URL.revokeObjectURL(state.userVideoSrc2);
  }
}

function revokeUserImageSrc3(nextSrc = '', nextPreviewSrc = ''): void {
  if (state.userImageSrc3 && state.userImageSrc3 !== nextSrc && state.userImageSrc3.startsWith('blob:')) {
    URL.revokeObjectURL(state.userImageSrc3);
  }
  if (
    state.userImagePreviewSrc3 &&
    state.userImagePreviewSrc3 !== nextSrc &&
    state.userImagePreviewSrc3 !== nextPreviewSrc &&
    state.userImagePreviewSrc3.startsWith('blob:')
  ) {
    URL.revokeObjectURL(state.userImagePreviewSrc3);
  }
}

function revokeUserVideoSrc3(nextSrc = ''): void {
  if (state.userVideo3) {
    revokeManagedMediaUrl(state.userVideo3);
  }
  if (state.userVideoSrc3 && state.userVideoSrc3 !== nextSrc && state.userVideoSrc3.startsWith('blob:')) {
    URL.revokeObjectURL(state.userVideoSrc3);
  }
}

function revokeStaticBackgroundImagePreviewSrc(nextSrc = ''): void {
  const currentSrc = state.staticBackgroundImagePreviewSrc;
  if (currentSrc && currentSrc !== nextSrc && currentSrc.startsWith('blob:')) {
    URL.revokeObjectURL(currentSrc);
  }
}

function replaceStaticBackgroundObjectOverlaySrc(nextSrc = ''): void {
  if (
    staticBackgroundObjectOverlaySrc &&
    staticBackgroundObjectOverlaySrc !== nextSrc &&
    staticBackgroundObjectOverlaySrc.startsWith('blob:')
  ) {
    URL.revokeObjectURL(staticBackgroundObjectOverlaySrc);
  }
  staticBackgroundObjectOverlaySrc = nextSrc;
}

function resetStaticBackgroundObjectOverlay(): void {
  staticBackgroundObjectOverlayGeneration += 1;
  replaceStaticBackgroundObjectOverlaySrc();
}

function revokePreviewUrlMap(previewUrls: Record<string, string>): void {
  for (const url of Object.values(previewUrls)) {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }
}

function safeStorageGet(key: string): string {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures in sandboxed contexts.
  }
}

function queueInputFocusRestore(input: HTMLInputElement | null): void {
  if (!input) {
    pendingInputFocus = null;
    return;
  }

  pendingInputFocus = {
    id: input.id,
    start: input.selectionStart ?? input.value.length,
    end: input.selectionEnd ?? input.value.length
  };
}

function restoreQueuedInputFocus(): void {
  if (!pendingInputFocus) {
    return;
  }

  const target = document.getElementById(pendingInputFocus.id) as HTMLInputElement | null;
  const snapshot = pendingInputFocus;
  pendingInputFocus = null;
  if (!target) {
    return;
  }

  target.focus();
  const max = target.value.length;
  const start = Math.max(0, Math.min(snapshot.start, max));
  const end = Math.max(start, Math.min(snapshot.end, max));
  target.setSelectionRange(start, end);
}

function loadFavoriteTemplateIds(): string[] {
  const raw = safeStorageGet(STORAGE_KEYS.favorites);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
  } catch {
    return [];
  }
}

function saveFavoriteTemplateIds(ids: string[]): void {
  safeStorageSet(STORAGE_KEYS.favorites, JSON.stringify(ids));
}

function normalizeFavoriteTemplateIds(ids: unknown): string[] {
  if (!Array.isArray(ids)) {
    return [];
  }

  return Array.from(
    new Set(
      ids
        .filter((id): id is string => typeof id === 'string')
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
    )
  );
}

function mergeFavoriteTemplateIds(primaryIds: string[], secondaryIds: string[]): string[] {
  return Array.from(new Set([...primaryIds, ...secondaryIds]));
}

function syncFavoriteTemplateIdsToMain(ids: string[]): void {
  if (!isEmbeddedPlugin) {
    return;
  }

  parent.postMessage(
    {
      pluginMessage: {
        type: 'SET_FAVORITES',
        payload: {
          ids
        }
      }
    },
    '*'
  );
}

function isFavoriteTemplate(id: string): boolean {
  return state.favoriteTemplateIds.includes(id);
}

async function toggleFavoriteTemplate(id: string): Promise<void> {
  const templateId = id.trim();
  if (!templateId) {
    return;
  }

  if (
    requireStandaloneAuth('favorite', {
      type: 'favorite',
      templateId
    })
  ) {
    return;
  }

  await setFavoriteTemplateFavorite(templateId, !isFavoriteTemplate(templateId));
}

async function setFavoriteTemplateFavorite(
  id: string,
  favorite: boolean,
  options: { skipAuthPrompt?: boolean } = {}
): Promise<void> {
  const templateId = id.trim();
  if (!templateId) {
    return;
  }

  if (
    !options.skipAuthPrompt &&
    requireStandaloneAuth('favorite', {
      type: 'favorite',
      templateId
    })
  ) {
    return;
  }

  const previousIds = state.favoriteTemplateIds;
  const nextIds = favorite
    ? mergeFavoriteTemplateIds(previousIds, [templateId])
    : previousIds.filter((item) => item !== templateId);

  state.favoriteTemplateIds = nextIds;
  render();

  try {
    if (isStandaloneWebApp) {
      if (favorite) {
        await addUserFavoriteTemplateId(templateId);
      } else {
        await removeUserFavoriteTemplateId(templateId);
      }
      return;
    }

    saveFavoriteTemplateIds(state.favoriteTemplateIds);
    syncFavoriteTemplateIdsToMain(state.favoriteTemplateIds);
  } catch (error) {
    state.favoriteTemplateIds = previousIds;
    if (!options.skipAuthPrompt && /sign in|required|401/i.test(error instanceof Error ? error.message : '')) {
      openAuthPrompt('favorite', {
        type: 'favorite',
        templateId
      });
      return;
    }
    setNotice(error instanceof Error ? error.message : 'Failed to update favorite', 'error');
  } finally {
    render();
  }
}

async function refreshAccountOverview(options: { renderWhileLoading?: boolean } = {}): Promise<void> {
  if (!isStandaloneWebApp || !state.authUser) {
    return;
  }

  state.accountOverviewLoading = true;
  state.accountOverviewError = '';
  if (options.renderWhileLoading) {
    render();
  }

  try {
    const userId = state.authUser.id;
    const overview = await loadUserAccountOverview();
    if (state.authUser?.id !== userId) return;
    state.accountOverview = overview;
    state.accountAccess = overview.access;
    state.accountPublicId = overview.publicId;
  } catch (error) {
    state.accountOverviewError = error instanceof Error ? error.message : 'Failed to load account data';
  } finally {
    state.accountOverviewLoading = false;
    render();
  }
}

function openAccountMenu(view: AccountMenuView = state.accountMenuView): void {
  if (!state.authUser) {
    openAuthPrompt('account');
    return;
  }

  state.accountMenuOpen = true;
  state.accountMenuView = view;
  render();

  if (!state.accountOverviewLoading) {
    void refreshAccountOverview({ renderWhileLoading: true });
  }
}

function openAccountPage(view: AccountMenuView = state.accountMenuView): void {
  if (!state.authUser) {
    openAuthPrompt('account');
    return;
  }

  pauseMotionPlayback({ preserveFrame: true });
  state.accountMenuView = view;
  state.accountMenuOpen = false;
  state.usePaneView = 'account';
  state.favoritesOnly = false;
  render();

  if (!state.accountOverviewLoading) {
    void refreshAccountOverview({ renderWhileLoading: true });
  }
}

function closeAccountMenu(): void {
  if (!state.accountMenuOpen) {
    return;
  }
  state.accountMenuOpen = false;
  render();
}

function cleanupAccountMenuOutsideClick(): void {
  if (!accountMenuOutsideClickCleanup) {
    return;
  }
  accountMenuOutsideClickCleanup();
  accountMenuOutsideClickCleanup = null;
}

function bindAccountMenuOutsideClick(): void {
  cleanupAccountMenuOutsideClick();
  if (!state.accountMenuOpen) {
    return;
  }

  const handleDocumentClick = (event: MouseEvent) => {
    const target = event.target;
    if (target instanceof Element && target.closest('.account-control')) {
      return;
    }
    closeAccountMenu();
  };

  document.addEventListener('click', handleDocumentClick);
  accountMenuOutsideClickCleanup = () => {
    document.removeEventListener('click', handleDocumentClick);
  };
}

function cleanupGradientPresetsOutsideClick(): void {
  gradientPresetsOutsideClickCleanup?.();
  gradientPresetsOutsideClickCleanup = null;
}

function bindGradientPresetsOutsideClick(container: HTMLElement): void {
  cleanupGradientPresetsOutsideClick();
  if (!state.gradientPresetsOpen) {
    return;
  }

  const handleDocumentClick = (event: MouseEvent) => {
    if (container.contains(event.target as Node)) {
      return;
    }
    state.gradientPresetsOpen = false;
    cleanupGradientPresetsOutsideClick();
    render();
  };
  document.addEventListener('click', handleDocumentClick);
  gradientPresetsOutsideClickCleanup = () => {
    document.removeEventListener('click', handleDocumentClick);
  };
}

function getAccountAccessLabel(): string {
  if (state.accountAccess?.source === 'vip') return 'VIP UNLIMITED';
  if (state.accountAccess?.unlimited) return 'UNLIMITED';
  return state.accountAccess ? 'Free' : 'Account';
}

function getAccountSubscriptionMenuLabel(): string { return 'Subscription'; }

function getAccountFavoriteTemplates(): MockupTemplate[] {
  const favoriteIds = new Set(state.favoriteTemplateIds);
  return state.catalog.filter((template) => favoriteIds.has(template.id));
}

function filterAccountFavoriteTemplates(templates: MockupTemplate[], filter: AccountFavoriteFilter): MockupTemplate[] {
  if (filter === 'ALL') {
    return templates;
  }
  return templates.filter((template) => (isMotionTemplateType(template.templateType) ? 'MOTION' : 'STATIC') === filter);
}

function renderTopbarNoticeSlot(): void {
  const slot = document.querySelector<HTMLElement>('.topbar-status-slot');
  if (!slot) {
    return;
  }

  slot.innerHTML = state.notice
    ? `<p class="topbar-status ${state.notice.type}" title="${escapeHtml(state.notice.message)}">${escapeHtml(state.notice.message)}</p>`
    : '<span class="topbar-status-placeholder" aria-hidden="true"></span>';
}

function isStaticBackgroundMode(value: string): value is Exclude<StaticBackgroundMode, 'none'> {
  return value === 'solid' || value === 'linear' || value === 'radial' || value === 'mesh' || value === 'image';
}

async function loadStaticBackgroundImage(file: File): Promise<void> {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  const supported = file.type === 'image/png' ||
    file.type === 'image/jpeg' ||
    file.type === 'image/webp' ||
    extension === 'png' ||
    extension === 'jpg' ||
    extension === 'jpeg' ||
    extension === 'webp';
  if (!file.size) {
    setNotice('The background image is empty.', 'error');
    return;
  }
  if (!supported) {
    setNotice('Choose a PNG, JPG or WebP background image.', 'error');
    return;
  }

  try {
    const image = await loadImageFromBlob(file);
    const previewCopy = await createPreviewImageCopy(image, PREVIEW_RENDER_SIZE);
    revokeStaticBackgroundImagePreviewSrc(previewCopy.src);
    state.staticBackgroundImage = image;
    state.staticBackgroundImagePreviewSrc = previewCopy.src;
    state.staticBackgroundImageName = file.name;
    state.staticBackgroundImageRevision += 1;
    state.staticBackgroundMode = 'image';
    state.staticBackgroundImagePlacementMode = 'fit';
    state.staticBackgroundImagePlacement = null;
    state.staticBackgroundImageDraftPlacement = null;
    state.backgroundMode = 'original';
    state.backgroundTransparent = false;
    invalidateStaticBackgroundSource();
    render();
  } catch (error) {
    setNotice(error instanceof Error ? error.message : 'The background image could not be opened.', 'error');
  }
}

function setNotice(
  message: string,
  type: 'info' | 'error' | 'success',
  options: { render?: boolean } = {}
): void {
  const shouldRender = options.render !== false;
  state.notice = { message, type };
  if (shouldRender) {
    render();
  } else {
    renderTopbarNoticeSlot();
  }

  if (noticeTimer) {
    window.clearTimeout(noticeTimer);
  }

  noticeTimer = window.setTimeout(() => {
    state.notice = null;
    if (shouldRender) {
      render();
    } else {
      renderTopbarNoticeSlot();
    }
  }, 3500);
}

function buildMotionExportSessionSettings(): MotionExportSessionSettings {
  const warpMode = getCurrentStaticExportMode();
  return {
    resizeMode: state.motionExport.resizeMode,
    warpMode,
    customPlacement: warpMode === 'custom' ? getEffectiveCustomPlacement() : null,
    diffuse: getDiffuseAdjustments(),
    background: getBackgroundAdjustments(),
    reflection: getReflectionAdjustments(),
    useAlternateMask: shouldUseAlternateMask()
  };
}

function getSelectedTemplate(): MockupTemplate | null {
  return state.catalog.find((item) => item.id === state.selectedId) || null;
}

function getSelectedCollection(): MockupCollection | null {
  return state.collections.find((item) => item.id === state.selectedCollectionId) || null;
}

function getTemplateCollection(template: MockupTemplate | null | undefined): MockupCollection | null {
  if (!template) return null;
  const collectionId = template.collectionId || getDefaultCollectionIdForType(template.templateType);
  return state.collections.find((item) => item.id === collectionId) || null;
}

function getContentCollectionsLabel(contentType: ContentType = state.selectedContentType): string {
  return contentType === 'MOTION' ? 'Video Collections' : 'Static Collections';
}

function renderPurposeBreadcrumb(collection?: MockupCollection | null): string {
  const id = collection?.id ? getCollectionPurposeId(collection, state.collectionPurposes) : state.selectedPurposeId;
  const purpose = state.collectionPurposes?.find(p=>p.id===id);
  if (!purpose || state.favoritesOnly) return '';
  const href = encodeNavHash({contentType:state.selectedContentType,sourceFilter:state.selectedSourceFilter,
    domain:normalizeDomainCategory(state.selectedDomainCategory),view:'collections',purposeId:purpose.id});
  return `<span class="detail-breadcrumb-separator" aria-hidden="true">/</span><a class="detail-breadcrumb-link" href="${escapeHtml(href)}" data-breadcrumb-target="purpose" data-purpose-id="${escapeHtml(purpose.id)}">${escapeHtml(purpose.name)}</a>`;
}

function renderDetailBreadcrumb(template: MockupTemplate, collection: MockupCollection | null): string {
  const contentLabel = getContentCollectionsLabel(template.isVideo ? 'MOTION' : 'STATIC');
  const collectionId = template.collectionId || getDefaultCollectionIdForType(template.templateType);
  const collectionName = collection?.name || 'Collection';
  const contentHref = encodeNavHash({
    contentType: state.selectedContentType,
    sourceFilter: state.selectedSourceFilter,
    domain: normalizeDomainCategory(state.selectedDomainCategory),
    view: 'collections'
  });
  const collectionHref = encodeNavHash({
    purposeId: collection ? getCollectionPurposeId(collection,state.collectionPurposes) : state.selectedPurposeId || undefined,
    contentType: state.selectedContentType,
    sourceFilter: state.selectedSourceFilter,
    domain: normalizeDomainCategory(state.selectedDomainCategory),
    view: 'templates',
    collectionId
  });

  return `
    <nav class="detail-breadcrumb" aria-label="Template navigation">
      <a class="detail-breadcrumb-link" href="${escapeHtml(contentHref)}" data-breadcrumb-target="collections">${contentLabel}</a>
      ${renderPurposeBreadcrumb(collection)}
      <span class="detail-breadcrumb-separator" aria-hidden="true">/</span>
      <a
        class="detail-breadcrumb-link"
        href="${escapeHtml(collectionHref)}"
        data-breadcrumb-target="collection"
        data-collection-id="${escapeHtml(collectionId)}"
        title="${escapeHtml(collectionName)}"
      >${escapeHtml(collectionName)}</a>
      <span class="detail-breadcrumb-separator" aria-hidden="true">/</span>
      <span class="detail-breadcrumb-current" title="${escapeHtml(template.name)}">${escapeHtml(template.name)}</span>
    </nav>
  `;
}

function renderTemplatesBreadcrumb(collection: MockupCollection | null): string {
  const contentLabel = getContentCollectionsLabel();
  const contentHref = encodeNavHash({
    contentType: state.selectedContentType,
    sourceFilter: state.selectedSourceFilter,
    domain: normalizeDomainCategory(state.selectedDomainCategory),
    view: 'collections'
  });
  const currentLabel = state.favoritesOnly
    ? 'Favorites'
    : state.selectedCollectionId
      ? collection?.name || 'Collection'
      : state.category === 'All'
        ? 'All Templates'
        : state.category;

  return `
    <nav class="detail-breadcrumb catalog-breadcrumb" aria-label="Catalog navigation">
      <a class="detail-breadcrumb-link" href="${escapeHtml(contentHref)}" data-breadcrumb-target="collections">${contentLabel}</a>
      ${renderPurposeBreadcrumb(collection)}
      <span class="detail-breadcrumb-separator" aria-hidden="true">/</span>
      <span class="detail-breadcrumb-current" title="${escapeHtml(currentLabel)}">${escapeHtml(currentLabel)}</span>
    </nav>
  `;
}

function renderCollectionsLocationLabel(): string {
  if (state.selectedPurposeId) {
    const root = encodeNavHash({contentType:state.selectedContentType,sourceFilter:state.selectedSourceFilter,
      domain:normalizeDomainCategory(state.selectedDomainCategory),view:'collections'});
    return `<nav class="detail-breadcrumb catalog-breadcrumb" aria-label="Collection navigation"><a class="detail-breadcrumb-link" href="${escapeHtml(root)}" data-breadcrumb-target="collections">${getContentCollectionsLabel()}</a>${renderPurposeBreadcrumb()}</nav>`;
  }
  return `
    <div class="detail-breadcrumb catalog-breadcrumb" aria-label="Catalog location">
      <span class="detail-breadcrumb-current">${getContentCollectionsLabel()}</span>
    </div>
  `;
}

function getActiveMotionResizeMode(template: MockupTemplate | null | undefined): MotionResizeMode {
  return template?.isVideo ? state.motionExport.resizeMode : '1:1';
}

function isMotionResizeMode(value: string | undefined): value is MotionResizeMode {
  return value === '1:1' || value === '16:9' || value === '9:16';
}

function getMotionPreviewAspectRatio(template: MockupTemplate | null | undefined): number {
  if (template && !template.isVideo) return getStaticFormatAspect(state.staticExport.format);
  const resizeMode = getActiveMotionResizeMode(template);
  if (resizeMode === '16:9') {
    return 16 / 9;
  }
  if (resizeMode === '9:16') {
    return 9 / 16;
  }
  return 1;
}

function getMotionExportOutputSize(): { width: number; height: number; renderSize: number } {
  if (state.motionExport.resizeMode === '16:9') {
    return {
      width: MOTION_WIDESCREEN_EXPORT_WIDTH,
      height: MOTION_WIDESCREEN_EXPORT_HEIGHT,
      renderSize: MOTION_WIDESCREEN_EXPORT_WIDTH
    };
  }
  if (state.motionExport.resizeMode === '9:16') {
    return {
      width: MOTION_VERTICAL_EXPORT_WIDTH,
      height: MOTION_VERTICAL_EXPORT_HEIGHT,
      renderSize: MOTION_VERTICAL_EXPORT_HEIGHT
    };
  }

  return {
    width: MOTION_EXPORT_SIZE,
    height: MOTION_EXPORT_SIZE,
    renderSize: MOTION_EXPORT_SIZE
  };
}

function getMotionResizeInlineStyle(template: MockupTemplate | null | undefined): string {
  if (template && !template.isVideo) return `style="aspect-ratio: ${getStaticFormatAspect(state.staticExport.format)};"`;
  const resizeMode = getActiveMotionResizeMode(template);
  if (resizeMode === '16:9') {
    return 'style="aspect-ratio: 16 / 9;"';
  }
  if (resizeMode === '9:16') {
    return 'style="aspect-ratio: 9 / 16;"';
  }
  return '';
}

function getMotionLoopCycleDuration(template: MockupTemplate): number | null {
  const duration = Number(template.duration);
  if (Number.isFinite(duration) && duration > 0) {
    return duration;
  }

  const frameCount = Number(template.frameCount);
  const fps = Number(template.fps);
  if (Number.isFinite(frameCount) && frameCount > 0 && Number.isFinite(fps) && fps > 0) {
    return frameCount / fps;
  }

  return null;
}

function formatMotionLoopCycleDuration(duration: number): string {
  const rounded = Math.round(duration * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function renderMotionLoopInfo(template: MockupTemplate): string {
  if (!template.isVideo || template.motionLoop !== true) {
    return '';
  }

  const duration = getMotionLoopCycleDuration(template);
  if (duration === null) {
    return `
      <section class="motion-loop-info" aria-label="Loop template information">
        <div class="motion-loop-badges">
          <span class="motion-loop-badge motion-loop-badge-primary">Loop</span>
        </div>
      </section>
    `;
  }

  const durationLabel = formatMotionLoopCycleDuration(duration);
  const durationUnit = durationLabel === '1' ? 'second' : 'seconds';
  return `
    <section class="motion-loop-info" aria-label="Loop template information">
      <div class="motion-loop-badges">
        <span class="motion-loop-badge motion-loop-badge-primary">Loop</span>
        <span class="motion-loop-badge">${durationLabel} sec</span>
      </div>
      <p class="motion-loop-recommendation">For seamless loops, use a video duration that is a multiple of ${durationLabel} ${durationUnit}.</p>
    </section>
  `;
}

function getMotionExportFileSuffix(): string {
  if (state.motionExport.resizeMode === '16:9') {
    return '16x9';
  }
  if (state.motionExport.resizeMode === '9:16') {
    return '9x16';
  }
  return '1x1';
}

function getMotionExportLabel(template: MockupTemplate | null): string {
  const size = getMotionExportOutputSize();
  return `${size.width} × ${size.height} MP4 · ${Math.round(template?.fps || 30)} FPS`;
}

function getDiffuseAdjustments(): DiffuseAdjustments {
  return {
    hue: state.diffuseHue,
    saturation: state.diffuseSaturation,
    brightness: state.diffuseBrightness,
    contrast: state.diffuseContrast,
    monochrome: state.diffuseMonochrome
  };
}

function getBackgroundAdjustments(): BackgroundAdjustments {
  return {
    mode: state.backgroundMode,
    transparent: state.backgroundTransparent,
    shadowColor: state.backgroundShadowColor,
    midColor: state.backgroundMidColor,
    highlightColor: state.backgroundHighlightColor
  };
}

function isDeviceShadowAvailable(
  template: MockupTemplate | null | undefined = getSelectedTemplate()
): boolean {
  if (!template || template.isVideo || !supportsStaticBackgroundComposer(template)) {
    return false;
  }
  if (state.backgroundTransparent || state.staticBackgroundMode !== 'none') {
    return true;
  }
  return Boolean(template.transparency && state.backgroundMode === 'original');
}

function getDeviceShadowAdjustments(
  template: MockupTemplate | null | undefined = getSelectedTemplate()
): DeviceShadowAdjustments {
  return {
    enabled: state.deviceShadowEnabled && isDeviceShadowAvailable(template),
    preset: state.deviceShadowPreset,
    angle: state.deviceShadowAngle,
    opacity: state.deviceShadowOpacity,
    color: state.deviceShadowColor
  };
}

function isDeviceShadowActive(
  template: MockupTemplate | null | undefined = getSelectedTemplate()
): boolean {
  return getDeviceShadowAdjustments(template).enabled;
}

function supportsStaticBackgroundComposer(template: MockupTemplate | null | undefined): boolean {
  return Boolean(template && !template.isVideo && (template.backgroundMask || template.transparency));
}

/** The editor's live drag wins while the dialog is open, so the preview behind
 *  it tracks the handle being moved. */
function getEditedAxis(): { start: AxisPoint; end: AxisPoint; stops: number[] } {
  const draft = state.staticBackgroundAxisDraft;
  if (draft) {
    return draft;
  }
  return {
    start: state.staticBackgroundAxisStart,
    end: state.staticBackgroundAxisEnd,
    stops: state.staticBackgroundStopPositions
  };
}

/** The ends are structural — a ramp needs somewhere to start and finish — so
 *  only the slots between them can be switched off. */
function isGradientSlotActive(slot: number): boolean {
  if (slot === 0 || slot === STATIC_BACKGROUND_DEFAULT_COLORS.length - 1) {
    return true;
  }
  return Boolean(state.staticBackgroundGradientSlots[slot]);
}

function getActiveGradientSlots(): number[] {
  return COLOR_SLOT_INDEXES.filter((slot) => isGradientSlotActive(slot));
}

/**
 * The colours a linear or radial ramp actually uses, with their positions.
 *
 * Positions stay indexed by slot in state, so switching a middle colour off and
 * back on returns it to where it was rather than to an even split.
 */
function getActiveGradientRamp(): { colors: string[]; positions: number[] } {
  const stops = getEditedAxis().stops;
  const active = getActiveGradientSlots();
  return {
    colors: active.map(
      (slot) => state.staticBackgroundColors[slot] || STATIC_BACKGROUND_DEFAULT_COLORS[slot]
    ),
    positions: active.map((slot) => stops[slot])
  };
}

/** The colour set a mode animates through, and what a fresh keyframe copies. */
function getAnimationBaseColours(): string[] {
  if (state.staticBackgroundMode === 'solid') {
    return [state.staticBackgroundColors[0] || STATIC_BACKGROUND_DEFAULT_COLORS[0]];
  }
  if (state.staticBackgroundMode === 'linear' || state.staticBackgroundMode === 'radial') {
    return getActiveGradientRamp().colors;
  }
  return [...state.staticBackgroundColors];
}

function isBackgroundAnimationActive(
  animation: BackgroundAnimationState = state.staticBackgroundAnimation
): boolean {
  return Boolean(
    animation.enabled &&
      state.staticBackgroundMode !== 'none' &&
      state.staticBackgroundMode !== 'image' &&
      !state.backgroundTransparent
  );
}

function getStaticBackgroundComposition(
  timeSeconds = backgroundAnimationTime,
  animation: BackgroundAnimationState = state.staticBackgroundAnimation
): StaticBackgroundComposition {
  const axis = getEditedAxis();
  // Only the ramp modes honour the slot switches; mesh spreads every colour it
  // is given, and solid never looks past the first one.
  const usesRamp = state.staticBackgroundMode === 'linear' || state.staticBackgroundMode === 'radial';
  const ramp = usesRamp
    ? getActiveGradientRamp()
    : { colors: [...state.staticBackgroundColors], positions: [...axis.stops] };

  const animating = isBackgroundAnimationActive(animation);
  const progress = animating ? getAnimationProgress(animation, timeSeconds) : 0;
  // Mesh keeps colour and movement on separate switches; the other modes only
  // ever animate colour, so there is nothing to gate there.
  const animatesColour = animating && (state.staticBackgroundMode !== 'mesh' || animation.meshColor);
  const animatesMove = animating && state.staticBackgroundMode === 'mesh' && animation.meshMove;
  const sampled = animatesColour ? sampleKeyframes(animation.keyframes, progress, animation.loop) : null;
  const colours = sampled && sampled.length > 0 ? sampled : ramp.colors;
  const authoredMeshPoints = state.staticBackgroundMeshDraftPoints ?? state.staticBackgroundMeshPoints;
  const meshPoints = animatesMove
    ? sampleMeshOrbitPoints(authoredMeshPoints, animation, timeSeconds)
    : authoredMeshPoints;

  return {
    mode: state.staticBackgroundMode,
    colors: colours,
    axisStart: axis.start,
    axisEnd: axis.end,
    stopPositions: ramp.positions,
    angle: state.staticBackgroundAngle,
    // The editor's live drag wins while it is open, so the preview behind the
    // modal tracks the point being moved.
    meshPoints: meshPoints.map((point) => ({ ...point })),
    meshMix: state.staticBackgroundMeshMix,
    meshDistortion: state.staticBackgroundMeshDistortion,
    meshGrain: state.staticBackgroundMeshGrain,
    meshFlowCenter: { ...(state.staticBackgroundMeshFlowDraft ?? state.staticBackgroundMeshFlowCenter) },
    image: state.staticBackgroundImage,
    imageFit: state.staticBackgroundImageFit,
    imagePlacement: state.staticBackgroundMode === 'image' && state.staticBackgroundImagePlacementMode === 'custom'
      ? getEffectiveBackgroundImagePlacement()
      : null
  };
}

function createStaticBackgroundAnimationTimeline(): {
  durationSeconds: number;
  compositionAtTime: (timeSeconds: number) => StaticBackgroundComposition;
} | undefined {
  if (!isBackgroundAnimationActive()) {
    return undefined;
  }

  const animation: BackgroundAnimationState = {
    ...state.staticBackgroundAnimation,
    keyframes: state.staticBackgroundAnimation.keyframes.map((frame) => [...frame])
  };
  return {
    durationSeconds: Math.max(0.001, animation.duration),
    compositionAtTime: (timeSeconds) => getStaticBackgroundComposition(timeSeconds, animation)
  };
}

function invalidateStaticBackgroundSource(): void {
  staticBackgroundSourceCache.clear();
}

/** Seeds the keyframes from whatever the mode currently shows, so turning the
 *  animation on starts from the background the author already built. */
function ensureAnimationKeyframes(): void {
  const animation = state.staticBackgroundAnimation;
  const base = getAnimationBaseColours();
  const wanted = state.staticBackgroundMode === 'mesh' ? 1 : 2;
  const shapeChanged =
    animation.keyframes.length > 0 && animation.keyframes[0].length !== base.length;
  if (animation.keyframes.length === 0 || shapeChanged) {
    animation.keyframes = Array.from({ length: wanted }, () => [...base]);
  }
}

/**
 * Preview playback.
 *
 * Capped well below display refresh on purpose: a frame costs a full composite
 * of the mockup, not just the gradient, and a background is judged by its drift
 * rather than its frame rate. The export renders at its own rate and is not
 * bound by this.
 */
const BACKGROUND_ANIMATION_PREVIEW_FPS = 20;

function stopBackgroundAnimationLoop(): void {
  if (backgroundAnimationRaf !== null) {
    window.cancelAnimationFrame(backgroundAnimationRaf);
    backgroundAnimationRaf = null;
  }
}

function syncBackgroundAnimationLoop(): void {
  if (state.exporting || state.dynamicZoomEditorOpen || isStaticPreviewAvailable() || !isBackgroundAnimationActive()) {
    stopBackgroundAnimationLoop();
    return;
  }
  if (backgroundAnimationRaf !== null) {
    return;
  }

  let last = performance.now();
  let carried = 0;
  const step = (now: number) => {
    backgroundAnimationRaf = null;
    if (state.exporting || state.dynamicZoomEditorOpen || isStaticPreviewAvailable() || !isBackgroundAnimationActive()) {
      return;
    }
    const delta = Math.min(0.25, Math.max(0, (now - last) / 1000));
    last = now;
    carried += delta;
    if (carried >= 1 / BACKGROUND_ANIMATION_PREVIEW_FPS) {
      const elapsed = carried;
      carried = 0;
      backgroundAnimationTime += elapsed;
      const duration = Math.max(0.001, state.staticBackgroundAnimation.duration);
      if (backgroundAnimationTime > duration) {
        // Preview playback always repeats so the editor never freezes on its
        // last frame. Loop still controls the animation itself: with it off,
        // export remains a one-shot start-to-end motion and the preview makes
        // a visible jump back to the beginning instead of blending the seam.
        backgroundAnimationTime -= Math.floor(backgroundAnimationTime / duration) * duration;
      }
      invalidateStaticBackgroundSource();
      schedulePreviewRender();
    }
    backgroundAnimationRaf = window.requestAnimationFrame(step);
  };
  backgroundAnimationRaf = window.requestAnimationFrame(step);
}

function openGradientAxisEditor(): void {
  axisEditorInitial = {
    axis: { start: { ...state.staticBackgroundAxisStart }, end: { ...state.staticBackgroundAxisEnd }, stops: [...state.staticBackgroundStopPositions] },
    colors: [...state.staticBackgroundColors], slots: [...state.staticBackgroundGradientSlots]
  };
  state.staticBackgroundAxisDraft = {
    start: { ...state.staticBackgroundAxisStart },
    end: { ...state.staticBackgroundAxisEnd },
    stops: [...state.staticBackgroundStopPositions]
  };
  state.staticBackgroundAxisEditorOpen = true;
  render();
  prepareEditorObjectOverlay();
}

function prepareEditorObjectOverlay(): void {
  const template = getSelectedTemplate();
  if (!template) {
    return;
  }
  resetStaticBackgroundObjectOverlay();
  void prepareStaticBackgroundObjectOverlay(template);
}

function closeGradientAxisEditor(commit: boolean): void {
  const draft = state.staticBackgroundAxisDraft;
  if (commit && draft) {
    state.staticBackgroundAxisStart = { ...draft.start };
    state.staticBackgroundAxisEnd = { ...draft.end };
    state.staticBackgroundStopPositions = [...draft.stops];
    // The angle slider is a readout of the handles, so it follows them rather
    // than sitting on a stale value the picture no longer matches.
    state.staticBackgroundAngle = angleFromAxis(draft.start, draft.end);
  }
  if (!commit && axisEditorInitial) {
    state.staticBackgroundColors = [...axisEditorInitial.colors];
    state.staticBackgroundGradientSlots = [...axisEditorInitial.slots];
  }
  axisEditorInitial = null;
  state.staticBackgroundAxisDraft = null;
  state.staticBackgroundAxisEditorOpen = false;
  invalidateStaticBackgroundSource();
  render();
  schedulePreviewRender();
}

let axisEditorPreviewToken = 0;

function refreshGradientAxisPreview(): void {
  const target = getEditorPreviewCanvas('axis-editor-preview');
  if (!target) {
    return;
  }
  // Canvas2D gradients are cheap enough to redraw straight from the pointer
  // handler; only the mesh path needs a mounted session.
  const token = ++axisEditorPreviewToken;
  void renderStaticBackground(getStaticBackgroundComposition(), MESH_EDITOR_PREVIEW_SIZE).then((canvas) => {
    if (!canvas || token !== axisEditorPreviewToken) {
      return;
    }
    const ctx = target.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, target.width, target.height);
    ctx.drawImage(canvas, 0, 0, target.width, target.height);
  });
}

function openMeshEditor(): void {
  meshEditorInitial = {
    points: state.staticBackgroundMeshPoints.map(point => ({ ...point })),
    flow: { ...state.staticBackgroundMeshFlowCenter }, colors: [...state.staticBackgroundColors]
  };
  // The draft is what the dialog edits, so Cancel is simply dropping it.
  state.staticBackgroundMeshDraftPoints = state.staticBackgroundMeshPoints.map((point) => ({ ...point }));
  state.staticBackgroundMeshFlowDraft = { ...state.staticBackgroundMeshFlowCenter };
  state.staticBackgroundMeshEditorOpen = true;
  render();
  void startMeshEditorSession();
  prepareEditorObjectOverlay();
}

function closeMeshEditor(commit: boolean): void {
  if (commit && state.staticBackgroundMeshDraftPoints) {
    state.staticBackgroundMeshPoints = state.staticBackgroundMeshDraftPoints.map((point) => ({ ...point }));
  }
  if (commit && state.staticBackgroundMeshFlowDraft) {
    state.staticBackgroundMeshFlowCenter = { ...state.staticBackgroundMeshFlowDraft };
  }
  if (!commit && meshEditorInitial) state.staticBackgroundColors = [...meshEditorInitial.colors];
  meshEditorInitial = null;
  state.staticBackgroundMeshDraftPoints = null;
  state.staticBackgroundMeshFlowDraft = null;
  state.staticBackgroundMeshEditorOpen = false;
  disposeMeshEditorSession();
  invalidateStaticBackgroundSource();
  render();
  schedulePreviewRender();
}

// A mesh frame costs a WebGL mount and two paints, so a drag cannot ask for one
// per pointer move. Requests coalesce: whatever the points say when the current
// render finishes is what gets drawn next.
let meshEditorSession: MeshPreviewSession | null = null;
let meshEditorSessionToken = 0;

/**
 * The canvas the editors draw into.
 *
 * Kept and redrawn rather than replaced. Swapping in a fresh canvas node on
 * every update is what made the dialog blink: the old frame is removed before
 * the new one has painted.
 */
function getEditorPreviewCanvas(hostId: string): HTMLCanvasElement | null {
  const host = document.getElementById(hostId);
  if (!host) {
    return null;
  }
  const existing = host.querySelector('canvas');
  if (existing) {
    return existing as HTMLCanvasElement;
  }
  const canvas = document.createElement('canvas');
  canvas.width = MESH_EDITOR_PREVIEW_SIZE;
  canvas.height = MESH_EDITOR_PREVIEW_SIZE;
  canvas.className = 'mesh-editor-preview-canvas';
  host.prepend(canvas);
  return canvas;
}

function refreshMeshEditorPreview(): void {
  const canvas = getEditorPreviewCanvas('mesh-editor-preview');
  if (!canvas || !meshEditorSession) {
    return;
  }
  meshEditorSession.draw({ ...getStaticBackgroundComposition(), mode: 'mesh' }, canvas);
}

async function startMeshEditorSession(): Promise<void> {
  const token = ++meshEditorSessionToken;
  disposeMeshEditorSession();
  try {
    const session = await createMeshPreviewSession(MESH_EDITOR_PREVIEW_SIZE, {
      ...getStaticBackgroundComposition(),
      mode: 'mesh'
    });
    if (token !== meshEditorSessionToken) {
      session?.dispose();
      return;
    }
    meshEditorSession = session;
    refreshMeshEditorPreview();
  } catch {
    // Without a session the dialog still works; it just shows nothing until the
    // gradient is applied, which beats a broken modal.
  }
}

function disposeMeshEditorSession(): void {
  meshEditorSession?.dispose();
  meshEditorSession = null;
}

async function ensureStaticBackgroundComposition(
  template: MockupTemplate,
  renderSize: number
): Promise<(StaticBackgroundComposition & { source: HTMLCanvasElement }) | undefined> {
  if (!supportsStaticBackgroundComposer(template)) {
    return undefined;
  }
  if (state.backgroundTransparent) {
    return undefined;
  }

  const composition = getStaticBackgroundComposition();
  if (!isStaticBackgroundActive(composition)) {
    return undefined;
  }

  const key = JSON.stringify({
    templateId: template.id,
    renderSize,
    mode: composition.mode,
    colors: composition.colors,
    angle: composition.angle,
    axisStart: composition.axisStart,
    axisEnd: composition.axisEnd,
    stopPositions: composition.stopPositions,
    gradientSlots: state.staticBackgroundGradientSlots,
    meshPoints: composition.meshPoints,
    meshMix: composition.meshMix,
    meshDistortion: composition.meshDistortion,
    meshGrain: composition.meshGrain,
    meshFlowCenter: composition.meshFlowCenter,
    imageRevision: state.staticBackgroundImageRevision,
    imageFit: composition.imageFit,
    imagePlacement: composition.imagePlacement
  });
  let pending = staticBackgroundSourceCache.get(key);
  if (!pending) {
    pending = renderStaticBackground(composition, renderSize);
    staticBackgroundSourceCache.set(key, pending);
    while (staticBackgroundSourceCache.size > 3) {
      const oldestKey = staticBackgroundSourceCache.keys().next().value as string | undefined;
      if (!oldestKey || oldestKey === key) break;
      staticBackgroundSourceCache.delete(oldestKey);
    }
  }

  const source = await pending;
  return source ? { ...composition, source } : undefined;
}

function getStaticVideoChromaKeyColor(
  template: MockupTemplate | null | undefined = getSelectedTemplate()
): string | null {
  if (
    !template ||
    template.isVideo ||
    !template.backgroundMask ||
    !state.backgroundTransparent ||
    !isStaticVideoRenderActive(template)
  ) {
    return null;
  }

  if (state.staticVideoChromaPreset === 'blue') {
    return STATIC_VIDEO_CHROMA_BLUE;
  }
  if (state.staticVideoChromaPreset === 'custom') {
    return /^#[0-9a-f]{6}$/i.test(state.staticVideoChromaCustomColor)
      ? state.staticVideoChromaCustomColor.toLowerCase()
      : STATIC_VIDEO_CHROMA_GREEN;
  }
  return STATIC_VIDEO_CHROMA_GREEN;
}

function applyStaticVideoChromaMatte(
  canvas: HTMLCanvasElement,
  template: MockupTemplate | null | undefined = getSelectedTemplate()
): void {
  const chromaKeyColor = getStaticVideoChromaKeyColor(template);
  if (!chromaKeyColor) {
    return;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  ctx.save();
  ctx.globalCompositeOperation = 'destination-over';
  ctx.fillStyle = chromaKeyColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
}

function refreshStaticVideoChromaPreview(refreshStoryboard = false): void {
  schedulePreviewRender();
  if (refreshStoryboard && shouldUseStaticVideoStoryboard()) {
    void refreshStaticVideoStoryboard({ preserveSelection: true });
  }
}

function getReflectionAdjustments(): ReflectionAdjustments {
  return {
    enabled: state.reflectionVisible,
    opacity: state.reflectionOpacity,
    blur: state.reflectionBlur
  };
}

function getScreenOverlayAdjustments(): ScreenOverlayAdjustments {
  return {
    opacity: state.screenOverlayOpacity,
    blur: state.screenBlur
  };
}

function shouldShowTransparencyPreview(template: MockupTemplate | null): boolean {
  if (!template) {
    return false;
  }

  if (getStaticVideoChromaKeyColor(template)) {
    return false;
  }
  return template.transparency || (Boolean(template.backgroundMask) && state.backgroundTransparent);
}

function drawPreviewBackdrop(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  template: MockupTemplate
): void {
  const chromaKeyColor = getStaticVideoChromaKeyColor(template);
  if (chromaKeyColor) {
    ctx.fillStyle = chromaKeyColor;
    ctx.fillRect(0, 0, width, height);
    return;
  }
  if (shouldShowTransparencyPreview(template)) {
    drawTransparencyPreviewBackground(ctx, width, height);
  }
}

function drawTransparencyPreviewBackground(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
): void {
  const cellSize = 12;
  ctx.save();
  ctx.fillStyle = '#f1f3f7';
  ctx.fillRect(0, 0, width, height);

  for (let y = 0; y < height; y += cellSize) {
    for (let x = 0; x < width; x += cellSize) {
      const isDarkCell = ((x / cellSize) + (y / cellSize)) % 2 === 0;
      if (!isDarkCell) {
        continue;
      }

      ctx.fillStyle = 'rgba(150, 159, 175, 0.12)';
      ctx.fillRect(x, y, cellSize, cellSize);
    }
  }

  ctx.restore();
}

function resetDiffuseAdjustments(): void {
  state.diffuseHue = 0;
  state.diffuseSaturation = 100;
  state.diffuseBrightness = 100;
  state.diffuseContrast = 100;
  state.diffuseMonochrome = false;
  state.screenOverlayOpacity = 50;
  state.screenBlur = 50;
}

function resetBackgroundAdjustments(): void {
  state.backgroundMode = 'original';
  state.backgroundTransparent = false;
  state.staticVideoChromaPreset = 'green';
  state.staticVideoChromaCustomColor = STATIC_VIDEO_CHROMA_GREEN;
  state.backgroundShadowColor = '#17203b';
  state.backgroundMidColor = '#4f73ff';
  state.backgroundHighlightColor = '#ffffff';
  state.staticBackgroundMode = 'none';
  state.staticBackgroundSourceOpen = false;
  state.gradientPresetsOpen = false;
  state.staticBackgroundAnimation = createBackgroundAnimationState();
  state.dynamicZoom = createDynamicZoomState();
  state.dynamicZoomEditorOpen = false;
  state.dynamicZoomDraft = null;
  state.dynamicZoomSelectedKey = null;
  state.dynamicZoomPlaying = false;
  dynamicZoomTime = 0;
  staticPreviewTime = 0;
  backgroundAnimationTime = 0;
  state.staticBackgroundColors = [...STATIC_BACKGROUND_DEFAULT_COLORS];
  gradientPaletteHistory = [];
  gradientColorEditSnapshot = null;
  state.staticBackgroundAngle = STATIC_BACKGROUND_DEFAULT_ANGLE;
  state.staticBackgroundAxisStart = axisFromAngle(STATIC_BACKGROUND_DEFAULT_ANGLE).start;
  state.staticBackgroundAxisEnd = axisFromAngle(STATIC_BACKGROUND_DEFAULT_ANGLE).end;
  state.staticBackgroundStopPositions = defaultStopPositions(STATIC_BACKGROUND_DEFAULT_COLORS.length);
  state.staticBackgroundGradientSlots = [...STATIC_BACKGROUND_DEFAULT_SLOTS];
  state.staticBackgroundAxisDraft = null;
  state.staticBackgroundAxisEditorOpen = false;
  state.staticBackgroundMeshSeed = 42;
  state.staticBackgroundMeshPoints = seededMeshPoints(42, MESH_POINT_LIMIT);
  state.staticBackgroundMeshDraftPoints = null;
  state.staticBackgroundMeshEditorOpen = false;
  state.staticBackgroundMeshMix = 72;
  state.staticBackgroundMeshDistortion = 38;
  state.staticBackgroundMeshGrain = 0;
  state.staticBackgroundMeshFlowCenter = { x: 0.5, y: 0.5 };
  state.staticBackgroundMeshFlowDraft = null;
  revokeStaticBackgroundImagePreviewSrc();
  state.staticBackgroundImage = null;
  state.staticBackgroundImagePreviewSrc = '';
  state.staticBackgroundImageName = '';
  state.staticBackgroundImageRevision += 1;
  state.staticBackgroundImageFit = 'cover';
  state.staticBackgroundImagePlacementMode = 'fit';
  state.staticBackgroundImagePlacement = null;
  state.staticBackgroundImageDraftPlacement = null;
  state.deviceShadowEnabled = false;
  state.deviceShadowPreset = 'soft';
  state.deviceShadowAngle = 45;
  state.deviceShadowOpacity = 100;
  state.deviceShadowColor = '#000000';
  resetStaticBackgroundObjectOverlay();
  invalidateStaticBackgroundSource();
  state.reflectionVisible = true;
  state.reflectionOpacity = 50;
  state.reflectionBlur = 50;
}

/**
 * Starting state for the Puzzle Matte controls: every channel off, each holding a
 * colour the picker can open with. Those colours are inlined rather than read
 * from a module constant because this runs while `state` itself is being built,
 * before any `const` declared further down the file exists.
 */
function createDefaultMotionColorMatteState(): MotionColorMatteAdjustments {
  return {
    enabled: false,
    colors: { r: '#4f73ff', g: '#ff7a4f', b: '#33c48d' },
    background: { enabled: false, dark: '#17203b', light: '#ffffff' }
  };
}

function resetMotionColorMatteAdjustments(): void {
  state.motionColorMatte = createDefaultMotionColorMatteState();
}

/** Channels this template actually exposes. Empty for everything without a matte. */
function getMotionColorMatteChannels(
  template: MockupTemplate | null | undefined
): MotionColorMatteChannel[] {
  const matte = template ? normalizeMotionColorMatte(template.motionColorMatte) : null;
  return matte ? matte.channels : [];
}

function getMotionColorMatteAdjustments(): MotionColorMatteAdjustments {
  return state.motionColorMatte;
}

/**
 * Whether either matte video needs seeking this frame.
 *
 * Covers both the colour matte and the background separator, because either one
 * being on means a matte will be sampled. Every control starts switched off, so
 * the common case costs exactly what it cost before the feature existed rather
 * than adding video seeks to every frame.
 *
 * Not the same question as which atlas to draw from — that is decided by
 * getActiveAtlasVideo, and only the object colours swap the atlas. Using this
 * predicate for the atlas is what once left the drawn atlas unseeked and frozen
 * while the neutral one advanced.
 */
function isMotionColorMatteActive(template: MockupTemplate | null | undefined): boolean {
  const adjustments = getMotionColorMatteAdjustments();
  return (
    resolveActiveMatteChannels(getMotionColorMatteChannels(template), adjustments).length > 0 ||
    isBackgroundTintActive(Boolean(template?.backgroundMatteUrl), adjustments)
  );
}

function getCurrentMotionAtlasTime(assets: LoadedMotionAssets): number {
  const trackingTime = getMotionSequencePlaybackTime(
    assets.tracking,
    motionPlaybackCurrentTime,
    assets.template.motionLoop === true
  );
  return getMotionAtlasFrameTime(
    assets.tracking,
    getMotionFrameIndex(assets.tracking, trackingTime)
  );
}

/**
 * Active Motion playback already renders the latest control state on its next
 * frame. Starting drawPreview at the same time would make two independent seek
 * loops compete for the same atlas and matte videos.
 */
function refreshMotionPreviewAfterControlChange(): void {
  if (state.motionPlaying && getSelectedTemplate()?.isVideo) {
    motionPlaybackRenderInvalidated = true;
    return;
  }
  schedulePreviewRender();
}

/**
 * "Custom colors" — the user-facing half of the Puzzle Matte. Renders nothing at
 * all for templates that do not declare the capability, which is every template
 * published before it existed.
 */
function renderMotionColorMatteControls(selected: MockupTemplate | null | undefined): string {
  const channels = getMotionColorMatteChannels(selected);
  const matte = normalizeMotionColorMatte(selected?.motionColorMatte);
  const hasBackground = Boolean(selected?.backgroundMatteUrl);

  // Either capability is worth the panel on its own: a template may ship only a
  // background separator and no colour channels.
  if ((channels.length === 0 || !matte) && !hasBackground) {
    return '';
  }

  const enabled = state.motionColorMatte.enabled;
  const rows = (matte ? channels : [])
    .map((channel) => {
      const label = getMotionColorMatteLabel(matte!, channel);
      return `
            <label class="matte-color-row ${enabled ? 'enabled' : ''}">
              <span class="matte-color-name">${escapeHtml(label)}</span>
              <input
                class="color-input"
                type="color"
                data-motion-color-matte-color="${channel}"
                value="${escapeHtml(state.motionColorMatte.colors[channel] || '#ffffff')}"
                ${enabled ? '' : 'disabled'}
                aria-label="${escapeHtml(label)}"
              />
            </label>`;
    })
    .join('');

  return `
    <section class="control-disclosure ${state.motionColorMatteOpen ? 'open' : ''}">
      <button id="motion-color-matte-toggle" class="control-disclosure-toggle" type="button" aria-expanded="${state.motionColorMatteOpen ? 'true' : 'false'}">
        <span class="control-disclosure-title">Custom colors</span>
        <span class="control-disclosure-chevron">›</span>
      </button>
      <div class="control-disclosure-body">
        <div class="control-disclosure-inner">
          <div class="control-disclosure-content color-controls compact">
            <div class="matte-group ${enabled ? 'enabled' : ''}">
              <div class="matte-master-row">
                <label class="admin-toggle matte-master">
                  <input id="motion-color-matte-enabled" type="checkbox" ${enabled ? 'checked' : ''} />
                  <span>On/Off</span>
                </label>
                <button id="motion-color-matte-reset" type="button">Default</button>
              </div>
              <div class="matte-color-grid">${rows}
              </div>
            </div>
            ${
              hasBackground
                ? `
            <div class="matte-background ${state.motionColorMatte.background.enabled ? 'enabled' : ''}">
              <label class="admin-toggle matte-background-switch">
                <input
                  id="motion-color-matte-background"
                  type="checkbox"
                  ${state.motionColorMatte.background.enabled ? 'checked' : ''}
                />
                <span>Background</span>
              </label>
              <div class="matte-background-colors">
                <input class="color-input" type="color" data-motion-color-matte-background="dark"
                  value="${escapeHtml(state.motionColorMatte.background.dark)}"
                  ${state.motionColorMatte.background.enabled ? '' : 'disabled'}
                  aria-label="Background dark" />
                <input class="color-input" type="color" data-motion-color-matte-background="light"
                  value="${escapeHtml(state.motionColorMatte.background.light)}"
                  ${state.motionColorMatte.background.enabled ? '' : 'disabled'}
                  aria-label="Background light" />
              </div>
            </div>`
                : ''
            }
          </div>
        </div>
      </div>
    </section>`;
}

function formatReflectionBlurValue(value: number): string {
  return `${value.toFixed(2)}%`;
}

function formatScreenBlurValue(value: number): string {
  return `${value.toFixed(2)}%`;
}

function getSubCategoriesForDomain(domain: DomainCategory): Category[] {
  if (domain === 'Promo') return PROMO_CATEGORIES;
  if (domain === 'Brand') return BRAND_CATEGORIES;
  return APP_CATEGORIES;
}

function getEffectiveTemplateType(): TemplateType {
  if (state.selectedContentType === 'MOTION') {
    return state.selectedSourceFilter === 'AI' ? 'MOTION-AI' : 'MOTION-CG';
  }
  if (state.selectedSourceFilter === 'AI') return 'AI';
  return 'CG';
}

function getCollectionQueryType(): CollectionQueryType {
  if (state.selectedContentType === 'MOTION' && state.selectedSourceFilter === 'ALL') {
    return 'MOTION';
  }
  return getEffectiveTemplateType();
}

function isMotionTemplateType(templateType: TemplateType): boolean {
  return templateType === 'MOTION-CG' || templateType === 'MOTION-AI';
}

function getTypeScopedCatalog(): MockupTemplate[] {
  const isStatic = state.selectedContentType === 'STATIC';
  const filtered = state.catalog.filter((item) => {
    const itemIsMotion = isMotionTemplateType(item.templateType);
    const itemIsStatic = !itemIsMotion;
    if (isStatic !== itemIsStatic) return false;
    if (isStatic && state.selectedSourceFilter !== 'ALL' && item.templateType !== state.selectedSourceFilter) return false;
    if (!isStatic && state.selectedSourceFilter === 'AI' && item.templateType !== 'MOTION-AI') return false;
    if (!isStatic && state.selectedSourceFilter === 'CG' && item.templateType !== 'MOTION-CG') return false;
    const itemDomain = item.domainCategory ?? 'App';
    if (itemDomain !== state.selectedDomainCategory) return false;
    return true;
  });
  return filtered.sort((a, b) => {
    const aCreatedAt = Date.parse(a.createdAt || '');
    const bCreatedAt = Date.parse(b.createdAt || '');
    const aCreatedTime = Number.isFinite(aCreatedAt) ? aCreatedAt : 0;
    const bCreatedTime = Number.isFinite(bCreatedAt) ? bCreatedAt : 0;
    if (aCreatedTime !== bCreatedTime) {
      return bCreatedTime - aCreatedTime;
    }
    const aO = a.order ?? Number.MAX_SAFE_INTEGER;
    const bO = b.order ?? Number.MAX_SAFE_INTEGER;
    if (aO !== bO) {
      return aO - bO;
    }
    return a.id.localeCompare(b.id);
  });
}

function getFavoriteCountForCurrentType(): number {
  const scopedIds = new Set(getTypeScopedCatalog().map((item) => item.id));
  return state.favoriteTemplateIds.filter((id) => scopedIds.has(id)).length;
}

function getDefaultCollectionIdForType(templateType: CollectionQueryType): string {
  const concreteType = templateType === 'MOTION' ? 'MOTION-CG' : templateType;
  if (concreteType === 'AI') return 'default-ai';
  if (concreteType === 'MOTION-AI') return 'default-motion-ai';
  if (concreteType === 'MOTION-CG') return 'default-motion-cg';
  return 'default-cg';
}

function createDefaultCollectionForType(templateType: CollectionQueryType): MockupCollection {
  const concreteType = templateType === 'MOTION' ? 'MOTION-CG' : templateType;
  return {
    id: getDefaultCollectionIdForType(templateType),
    templateType: concreteType,
    name: 'General',
    order: 0,
    published: true,
    background: '',
    fontColor: '#ffffff'
  };
}

function resetTemplateControlsToDefaults(): void {
  resetTemplateControls({ preserveOutputSettings: false });
}

function resetTemplateControls(options: { preserveOutputSettings: boolean; preserveCustomPlacement?: boolean }): void {
  pauseMotionPlayback();
  const preservedMotionResizeMode = state.motionExport.resizeMode;
  const preservedExportFormat = state.staticExport.format;
  const preservedExportResolution = state.staticExport.resolution;
  const preservedExportMode = state.staticExport.mode;
  const preservedExportMode2 = state.staticExport.mode2;
  const preservedExportMode3 = state.staticExport.mode3;
  const preservedStaticRenderMode = state.staticExport.renderMode;
  const preservedCustomModeFallback = state.staticExport.customModeFallback;
  const preservedCustomModeFallback2 = state.staticExport.customModeFallback2;
  const preservedCustomModeFallback3 = state.staticExport.customModeFallback3;
  const preservedCustomPlacement = options.preserveCustomPlacement
    ? cloneCustomPlacement(state.staticExport.customPlacement)
    : null;
  const preservedCustomDraftPlacement = options.preserveCustomPlacement
    ? cloneCustomPlacement(state.staticExport.customDraftPlacement)
    : null;
  const preservedCustomPlacement2 = options.preserveCustomPlacement
    ? cloneCustomPlacement(state.staticExport.customPlacement2)
    : null;
  const preservedCustomDraftPlacement2 = options.preserveCustomPlacement
    ? cloneCustomPlacement(state.staticExport.customDraftPlacement2)
    : null;
  const preservedCustomPlacement3 = options.preserveCustomPlacement
    ? cloneCustomPlacement(state.staticExport.customPlacement3)
    : null;
  const preservedCustomDraftPlacement3 = options.preserveCustomPlacement
    ? cloneCustomPlacement(state.staticExport.customDraftPlacement3)
    : null;
  const preservedCustomEditorScreen = state.staticExport.customEditorScreen;
  const preservedCustomEditorOpen = Boolean(
    options.preserveCustomPlacement &&
    state.staticExport.customEditorOpen &&
    state.staticExport.customEditorTarget === 'screen'
  );

  state.motionExport.resizeMode = '1:1';
  state.staticExport.format = '1:1';
  state.staticExport.resolution = '4k';
  state.staticExport.mode = 'fit';
  state.staticExport.mode2 = 'fit';
  state.staticExport.mode3 = 'fit';
  state.staticExport.renderMode = 'static_image';
  state.staticExport.customModeFallback = 'fit';
  state.staticExport.customModeFallback2 = 'fit';
  state.staticExport.customModeFallback3 = 'fit';
  state.dynamicIsland = getDefaultDynamicIslandState(getSelectedTemplate());
  state.staticExport.customPlacement = null;
  state.staticExport.customDraftPlacement = null;
  state.staticExport.customPlacement2 = null;
  state.staticExport.customDraftPlacement2 = null;
  state.staticExport.customPlacement3 = null;
  state.staticExport.customDraftPlacement3 = null;
  state.staticExport.customEditorScreen = 1;
  state.staticExport.customEditorTarget = 'screen';
  state.staticExport.customEditorOpen = false;
  state.diffuseControlsOpen = false;
  state.backgroundControlsOpen = false;
  state.motionColorMatteOpen = false;
  resetDiffuseAdjustments();
  resetBackgroundAdjustments();
  resetMotionColorMatteAdjustments();

  if (options.preserveOutputSettings) {
    state.motionExport.resizeMode = preservedMotionResizeMode;
    state.staticExport.format = preservedExportFormat;
    state.staticExport.resolution = preservedExportResolution;
    state.staticExport.mode = preservedExportMode;
    state.staticExport.mode2 = preservedExportMode2;
    state.staticExport.mode3 = preservedExportMode3;
    state.staticExport.renderMode = preservedStaticRenderMode;
    state.staticExport.customModeFallback = preservedCustomModeFallback;
    state.staticExport.customModeFallback2 = preservedCustomModeFallback2;
    state.staticExport.customModeFallback3 = preservedCustomModeFallback3;
  }

  if (options.preserveCustomPlacement) {
    state.staticExport.customPlacement = preservedCustomPlacement;
    state.staticExport.customDraftPlacement = preservedCustomDraftPlacement;
    state.staticExport.customPlacement2 = preservedCustomPlacement2;
    state.staticExport.customDraftPlacement2 = preservedCustomDraftPlacement2;
    state.staticExport.customPlacement3 = preservedCustomPlacement3;
    state.staticExport.customDraftPlacement3 = preservedCustomDraftPlacement3;
    state.staticExport.customEditorScreen = preservedCustomEditorScreen;
    state.staticExport.customEditorTarget = 'screen';
    state.staticExport.customEditorOpen = preservedCustomEditorOpen;
  }
}

function openTemplateDetail(templateId: string, options: { preserveCustomPlacement?: boolean } = {}): void {
  clearDetailTransitionCleanupTimer();
  state.detailTransitionOverlayUrl = getTemplateNavigationOverlayUrl(templateId);
  pauseMotionPlayback();
  state.selectedId = templateId;
  releaseOtherTemplateResources(templateId);
  if (state.usePaneView === 'collections') {
    const template = state.catalog.find((item) => item.id === templateId);
    if (template) {
      state.selectedCollectionId = template.collectionId || getDefaultCollectionIdForType(template.templateType);
    }
  }
  pendingMotionDetailAutoplayTemplateId = state.catalog.find((item) => item.id === templateId && item.isVideo) ? templateId : '';
  resetTemplateControls({
    preserveOutputSettings: true,
    preserveCustomPlacement: options.preserveCustomPlacement
  });
  syncStaticRenderModeFromMedia();
  state.usePaneView = 'detail';
  pushNavHash();
  void refreshStaticVideoStoryboard({ preserveSelection: true });
  render();
}

function applyTemplateTypeFilter(nextType: TemplateType): void {
  state.selectedPurposeId = '';
  state.templateType = nextType;
  if (isMotionTemplateType(nextType)) {
    state.selectedContentType = 'MOTION';
    state.selectedSourceFilter = 'ALL';
  } else {
    state.selectedContentType = 'STATIC';
    state.selectedSourceFilter = nextType as SourceFilter;
  }
  state.selectedCollectionId = '';
  const scopedTemplates = getTemplateScope();

  if (scopedTemplates.length === 0) {
    state.selectedId = '';
    return;
  }

  if (!scopedTemplates.some((item) => item.id === state.selectedId)) {
    state.selectedId = scopedTemplates[0].id;
  }
}

function applyContentTypeFilter(nextContentType: ContentType): void {
  state.selectedPurposeId = '';
  discardNewTemplatesMode();
  state.selectedContentType = nextContentType;
  if (nextContentType === 'MOTION') {
    state.selectedSourceFilter = 'ALL';
    state.templateType = getEffectiveTemplateType();
  } else {
    state.selectedSourceFilter = 'ALL';
    state.templateType = getEffectiveTemplateType();
  }
  state.selectedCollectionId = '';
  state.quickCatalogFilter = 'collections';
  state.favoritesOnly = false;
  state.catalogSearch = '';
  state.category = 'All';
  state.usePaneView = 'collections';
  const scopedTemplates = getTemplateScope();

  if (scopedTemplates.length === 0) {
    state.selectedId = '';
    pushNavHash();
    return;
  }

  if (!scopedTemplates.some((item) => item.id === state.selectedId)) {
    state.selectedId = scopedTemplates[0].id;
  }
  pushNavHash();
}

function cleanupNewTemplatesLoadObserver(): void {
  newTemplatesLoadObserver?.disconnect();
  newTemplatesLoadObserver = null;
}

function discardNewTemplatesMode(): void {
  cleanupNewTemplatesLoadObserver();
  state.newTemplatesMode = false;
  state.newTemplatesVisibleCount = NEW_TEMPLATES_BATCH_SIZE;
  state.newTemplatesReturnState = null;
}

function activateNewTemplatesMode(): void {
  if (state.newTemplatesMode) return;

  state.newTemplatesReturnState = {
    selectedPurposeId: state.selectedPurposeId,
    quickCatalogFilter: state.quickCatalogFilter,
    usePaneView: state.usePaneView,
    selectedCollectionId: state.selectedCollectionId,
    category: state.category,
    favoritesOnly: state.favoritesOnly,
    catalogSearch: state.catalogSearch
  };
  state.newTemplatesMode = true;
  state.newTemplatesVisibleCount = NEW_TEMPLATES_BATCH_SIZE;
  state.quickCatalogFilter = 'All';
  state.usePaneView = 'templates';
  state.selectedCollectionId = '';
  state.category = 'All';
  state.favoritesOnly = false;
  state.catalogSearch = '';
}

function deactivateNewTemplatesMode(options: { restore?: boolean } = {}): void {
  const returnState = state.newTemplatesReturnState;
  const shouldRestore = options.restore !== false && returnState !== null;
  discardNewTemplatesMode();

  if (!shouldRestore || !returnState) return;

  state.quickCatalogFilter = returnState.quickCatalogFilter;
  state.usePaneView = returnState.usePaneView;
  state.selectedPurposeId = returnState.selectedPurposeId || '';
  state.selectedCollectionId = returnState.selectedCollectionId;
  state.category = returnState.category;
  state.favoritesOnly = returnState.favoritesOnly;
  state.catalogSearch = returnState.catalogSearch;
}

function bindNewTemplatesPagination(): void {
  cleanupNewTemplatesLoadObserver();
  if (!state.newTemplatesMode) return;

  const sentinel = document.getElementById('new-templates-load-sentinel');
  if (!sentinel || typeof IntersectionObserver === 'undefined') return;

  newTemplatesLoadObserver = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting) || !state.newTemplatesMode) return;

      const total = filteredCatalog().length;
      const nextCount = Math.min(total, state.newTemplatesVisibleCount + NEW_TEMPLATES_BATCH_SIZE);
      if (nextCount <= state.newTemplatesVisibleCount) {
        cleanupNewTemplatesLoadObserver();
        return;
      }

      cleanupNewTemplatesLoadObserver();
      state.newTemplatesVisibleCount = nextCount;
      render();
    },
    { rootMargin: '500px 0px' }
  );
  newTemplatesLoadObserver.observe(sentinel);
}

function applySourceFilterChange(nextFilter: SourceFilter): void {
  discardNewTemplatesMode();
  const keepCollectionScope = state.usePaneView === 'templates' && Boolean(state.selectedCollectionId) && !state.favoritesOnly;
  const keepCollectionsView = state.usePaneView === 'collections' && state.quickCatalogFilter === 'collections';
  state.selectedSourceFilter = nextFilter;
  state.templateType = getEffectiveTemplateType();
  if (!keepCollectionScope) {
    state.selectedCollectionId = '';
    state.quickCatalogFilter = keepCollectionsView ? 'collections' : 'All';
    state.category = 'All';
    state.usePaneView = keepCollectionsView ? 'collections' : 'templates';
  }
  const scopedTemplates = getTemplateScope();
  if (!scopedTemplates.some((item) => item.id === state.selectedId)) {
    state.selectedId = scopedTemplates[0]?.id || '';
  }
  pushNavHash();
}

function applyDomainCategoryFilter(nextDomain: DomainCategory): void {
  state.selectedPurposeId = '';
  discardNewTemplatesMode();
  state.selectedDomainCategory = normalizeDomainCategory(nextDomain);
  state.category = 'All';
  state.selectedCollectionId = '';
  state.quickCatalogFilter = 'All';
  state.usePaneView = 'templates';
  const scopedTemplates = getTemplateScope();
  if (!scopedTemplates.some((item) => item.id === state.selectedId)) {
    state.selectedId = scopedTemplates[0]?.id || '';
  }
  pushNavHash();
}

function getTemplateNavigation(
  templates: MockupTemplate[],
  selectedId: string
): { previousId: string | null; nextId: string | null } {
  const index = templates.findIndex((item) => item.id === selectedId);
  if (index < 0) {
    return {
      previousId: null,
      nextId: null
    };
  }

  return {
    previousId: index > 0 ? templates[index - 1]?.id || null : null,
    nextId: index < templates.length - 1 ? templates[index + 1]?.id || null : null
  };
}

function getStaticPreviewFormatMaskStyle(format: StaticExportFormat): string {
  if (format === '1:1' || format === '1:1z') {
    return '--preview-mask-top:0%;--preview-mask-right:0%;--preview-mask-bottom:0%;--preview-mask-left:0%;';
  }

  if (format === '16:9') {
    return '--preview-mask-top:21.875%;--preview-mask-right:0%;--preview-mask-bottom:21.875%;--preview-mask-left:0%;';
  }

  if (format === '9:16') {
    return '--preview-mask-top:0%;--preview-mask-right:21.875%;--preview-mask-bottom:0%;--preview-mask-left:21.875%;';
  }

  return '--preview-mask-top:0%;--preview-mask-right:16.6667%;--preview-mask-bottom:0%;--preview-mask-left:16.6667%;';
}

function getStaticBackgroundEditorFormatMaskStyle(format: StaticExportFormat): string {
  if (format === '1:1z') {
    return '--preview-mask-top:16.6667%;--preview-mask-right:16.6667%;--preview-mask-bottom:16.6667%;--preview-mask-left:16.6667%;';
  }

  return getStaticPreviewFormatMaskStyle(format);
}

function cloneCustomPlacement(placement: CustomPlacement | null): CustomPlacement | null {
  if (!placement) {
    return null;
  }

  return {
    scale: placement.scale,
    offsetX: placement.offsetX,
    offsetY: placement.offsetY
  };
}

function getSelectedScreenAspect(template: MockupTemplate, screen: 1 | 2 | 3 = 1): number {
  const duoAspect = getDuoInputAspect(template, screen);
  if (duoAspect) return duoAspect;
  const categoryAspect = getCategoryAspect(template.category);
  if (categoryAspect) {
    return categoryAspect;
  }

  const quad = screen === 3 && template.screenQuad3?.length === 4
    ? template.screenQuad3
    : screen === 2 && template.screenQuad2?.length === 4
      ? template.screenQuad2
      : template.screenQuad;
  const top = edgeLength(quad[0], quad[1]);
  const bottom = edgeLength(quad[3], quad[2]);
  const left = edgeLength(quad[0], quad[3]);
  const right = edgeLength(quad[1], quad[2]);
  const width = (top + bottom) / 2;
  const height = (left + right) / 2;
  const aspect = width / Math.max(1e-6, height);

  if (!Number.isFinite(aspect) || aspect <= 0) {
    return 1;
  }

  return Math.max(0.05, Math.min(20, aspect));
}

function createDefaultCustomPlacement(): CustomPlacement {
  return {
    scale: 1,
    offsetX: 0,
    offsetY: 0
  };
}

function normalizeCustomPlacement(
  placement: CustomPlacement,
  source: { width: number; height: number },
  viewportWidth: number,
  viewportHeight: number,
  options: { minScale?: number; freePosition?: boolean } = {}
): CustomPlacement {
  const safeViewportWidth =
    Number.isFinite(viewportWidth) && viewportWidth > 0 ? viewportWidth : 1;
  const safeViewportHeight =
    Number.isFinite(viewportHeight) && viewportHeight > 0 ? viewportHeight : 1;
  const minScale = clamp(options.minScale ?? 1, 0.01, 6);
  const scale = clamp(Number.isFinite(placement.scale) ? placement.scale : 1, minScale, 6);
  const offsetX = Number.isFinite(placement.offsetX) ? placement.offsetX : 0;
  const offsetY = Number.isFinite(placement.offsetY) ? placement.offsetY : 0;
  if (options.freePosition) {
    return { scale, offsetX, offsetY };
  }

  const sourceWidth = Number.isFinite(source.width) && source.width > 0 ? source.width : 1;
  const sourceHeight = Number.isFinite(source.height) && source.height > 0 ? source.height : 1;
  const coverScale = Math.max(safeViewportWidth / sourceWidth, safeViewportHeight / sourceHeight);
  const drawWidth = sourceWidth * coverScale * scale;
  const drawHeight = sourceHeight * coverScale * scale;
  const maxOffsetX = Math.max(0, (drawWidth - safeViewportWidth) / 2) / safeViewportWidth;
  const maxOffsetY = Math.max(0, (drawHeight - safeViewportHeight) / 2) / safeViewportHeight;

  return {
    scale,
    offsetX: clamp(offsetX, -maxOffsetX, maxOffsetX),
    offsetY: clamp(offsetY, -maxOffsetY, maxOffsetY)
  };
}

function getEffectiveCustomPlacement(screen: 1 | 2 | 3 = 1): CustomPlacement {
  const placement = screen === 3
    ? state.staticExport.customPlacement3
    : screen === 2
      ? state.staticExport.customPlacement2
      : state.staticExport.customPlacement;
  return cloneCustomPlacement(placement) || createDefaultCustomPlacement();
}

function getEffectiveBackgroundImagePlacement(): CustomPlacement {
  return cloneCustomPlacement(state.staticBackgroundImagePlacement) || createDefaultCustomPlacement();
}

function getScreenExportMode(screen: 1 | 2 | 3 = 1): StaticInteractiveExportMode {
  return screen === 3 ? state.staticExport.mode3 : screen === 2 ? state.staticExport.mode2 : state.staticExport.mode;
}

function setScreenExportMode(mode: StaticInteractiveExportMode, screen: 1 | 2 | 3 = 1): void {
  if (screen === 3) {
    state.staticExport.mode3 = mode;
  } else if (screen === 2) {
    state.staticExport.mode2 = mode;
  } else {
    state.staticExport.mode = mode;
  }
}

function getScreenCustomModeFallback(screen: 1 | 2 | 3 = 1): NonCustomStaticExportMode {
  return screen === 3
    ? state.staticExport.customModeFallback3
    : screen === 2
      ? state.staticExport.customModeFallback2
      : state.staticExport.customModeFallback;
}

function setScreenCustomModeFallback(mode: NonCustomStaticExportMode, screen: 1 | 2 | 3 = 1): void {
  if (screen === 3) {
    state.staticExport.customModeFallback3 = mode;
  } else if (screen === 2) {
    state.staticExport.customModeFallback2 = mode;
  } else {
    state.staticExport.customModeFallback = mode;
  }
}

function getCustomDraftPlacement(screen: 1 | 2 | 3 = state.staticExport.customEditorScreen): CustomPlacement | null {
  return screen === 3
    ? state.staticExport.customDraftPlacement3
    : screen === 2
      ? state.staticExport.customDraftPlacement2
      : state.staticExport.customDraftPlacement;
}

function setCustomDraftPlacement(
  placement: CustomPlacement | null,
  screen: 1 | 2 | 3 = state.staticExport.customEditorScreen
): void {
  if (screen === 3) {
    state.staticExport.customDraftPlacement3 = placement;
  } else if (screen === 2) {
    state.staticExport.customDraftPlacement2 = placement;
  } else {
    state.staticExport.customDraftPlacement = placement;
  }
}

function getActiveCustomDraftPlacement(): CustomPlacement | null {
  return state.staticExport.customEditorTarget === 'background'
    ? state.staticBackgroundImageDraftPlacement
    : getCustomDraftPlacement();
}

function setActiveCustomDraftPlacement(placement: CustomPlacement | null): void {
  if (state.staticExport.customEditorTarget === 'background') {
    state.staticBackgroundImageDraftPlacement = placement;
    return;
  }
  setCustomDraftPlacement(placement);
}

function getActiveEffectiveCustomPlacement(): CustomPlacement {
  return state.staticExport.customEditorTarget === 'background'
    ? getEffectiveBackgroundImagePlacement()
    : getEffectiveCustomPlacement(state.staticExport.customEditorScreen);
}

function normalizeInteractiveStaticExportMode(mode: StaticInteractiveExportMode): NonCustomStaticExportMode {
  return mode === 'custom' ? 'fit' : 'fit';
}

function getCustomPlacementEditorSource(screen: 1 | 2 | 3 = state.staticExport.customEditorScreen): CustomPlacementEditorSource | null {
  const video = screen === 3 ? state.userVideo3 : screen === 2 ? state.userVideo2 : state.userVideo;
  const videoSrc = screen === 3 ? state.userVideoSrc3 : screen === 2 ? state.userVideoSrc2 : state.userVideoSrc;
  if (video) {
    return {
      kind: 'video',
      src: videoSrc || video.currentSrc || video.src,
      width: video.videoWidth || 1,
      height: video.videoHeight || 1,
      currentTime: Number.isFinite(video.currentTime) ? Math.max(0, video.currentTime) : 0
    };
  }

  const image = screen === 3
    ? state.userImagePreview3 || state.userImage3
    : screen === 2
      ? state.userImagePreview2 || state.userImage2
      : getInteractiveUserImage();
  if (!image) {
    return null;
  }

  return {
    kind: 'image',
    src: screen === 3
      ? state.userImagePreviewSrc3 || state.userImageSrc3 || image.src
      : screen === 2
        ? state.userImagePreviewSrc2 || state.userImageSrc2 || image.src
        : state.userImagePreviewSrc || state.userImageSrc || image.src,
    width: image.naturalWidth || image.width || 1,
    height: image.naturalHeight || image.height || 1
  };
}

function getBackgroundPlacementEditorSource(): CustomPlacementEditorSource | null {
  const image = state.staticBackgroundImage;
  if (!image) {
    return null;
  }

  return {
    kind: 'image',
    src: state.staticBackgroundImagePreviewSrc || image.currentSrc || image.src,
    width: image.naturalWidth || image.width || 1,
    height: image.naturalHeight || image.height || 1
  };
}

async function prepareStaticBackgroundObjectOverlay(template: MockupTemplate): Promise<void> {
  const generation = ++staticBackgroundObjectOverlayGeneration;

  try {
    const assets = await ensurePreviewAssets(template);
    if (generation !== staticBackgroundObjectOverlayGeneration) {
      return;
    }

    const renderSize = Math.min(
      STATIC_BACKGROUND_EDITOR_OVERLAY_SIZE,
      Math.max(1, template.editSize || PREVIEW_RENDER_SIZE)
    );
    const foreground = renderMaster(assets, getInteractiveStaticSource(), {
      renderSize,
      gridX: PREVIEW_GRID_SIZE,
      gridY: PREVIEW_GRID_SIZE,
      diffuse: getDiffuseAdjustments(),
      background: {
        ...getBackgroundAdjustments(),
        transparent: true
      },
      reflection: getReflectionAdjustments(),
      screenOverlay: getScreenOverlayAdjustments(),
      useAlternateMask: shouldUseAlternateMask(template),
      customPlacement: state.staticExport.mode === 'custom' ? getEffectiveCustomPlacement() : null,
      userImage2: hasTemplateScreen(template, 2) ? getInteractiveStaticSource2() : null,
      userImage3: hasTemplateScreen(template, 3) ? getInteractiveStaticSource3() : null,
      customPlacement2:
        state.staticExport.mode2 === 'custom' ? getEffectiveCustomPlacement(2) : null,
      customPlacement3:
        state.staticExport.mode3 === 'custom' ? getEffectiveCustomPlacement(3) : null,
      warpMode: state.staticExport.mode,
      warpMode2: state.staticExport.mode2,
      warpMode3: state.staticExport.mode3,
      realtime: true
    });
    const blob = await canvasToBlob(foreground, 'image/webp', 0.9);
    foreground.width = 1;
    foreground.height = 1;

    if (generation !== staticBackgroundObjectOverlayGeneration) {
      return;
    }

    const nextSrc = URL.createObjectURL(blob);
    replaceStaticBackgroundObjectOverlaySrc(nextSrc);
    const overlayIsVisible =
      (state.staticExport.customEditorOpen && state.staticExport.customEditorTarget === 'background') ||
      state.staticBackgroundMeshEditorOpen ||
      state.staticBackgroundAxisEditorOpen;
    if (overlayIsVisible && getSelectedTemplate()?.id === template.id) {
      render();
    }
  } catch {
    if (generation === staticBackgroundObjectOverlayGeneration) {
      replaceStaticBackgroundObjectOverlaySrc();
    }
  }
}

function getActiveCustomPlacementEditorSource(): CustomPlacementEditorSource | null {
  return state.staticExport.customEditorTarget === 'background'
    ? getBackgroundPlacementEditorSource()
    : getCustomPlacementEditorSource();
}

function openCustomPlacementEditor(fallbackMode: NonCustomStaticExportMode, screen: 1 | 2 | 3 = 1): void {
  state.staticExport.customEditorTarget = 'screen';
  state.staticExport.customEditorScreen = screen;
  if (!getCustomPlacementEditorSource(screen)) {
    setScreenExportMode(fallbackMode, screen);
    setNotice(
      isMotionTemplate(getSelectedTemplate()) ? 'Upload MP4 or PNG/JPG first' : 'Upload PNG, JPG, MP4 or MOV first',
      'error'
    );
    return;
  }

  setScreenCustomModeFallback(fallbackMode, screen);
  customEditorInitial = { placement: getEffectiveCustomPlacement(screen), screenMode: getScreenExportMode(screen) };
  setCustomDraftPlacement(getEffectiveCustomPlacement(screen), screen);
  setScreenExportMode('custom', screen);
  state.staticExport.customEditorOpen = true;
  render();
}

function openBackgroundPlacementEditor(): void {
  if (!getBackgroundPlacementEditorSource()) {
    setNotice('Choose a background image first.', 'error');
    return;
  }

  const template = getSelectedTemplate();
  if (!template) {
    return;
  }

  resetStaticBackgroundObjectOverlay();
  state.staticExport.customEditorTarget = 'background';
  customEditorInitial = { placement: getEffectiveBackgroundImagePlacement(), backgroundMode: state.staticBackgroundImagePlacementMode };
  state.staticBackgroundImageDraftPlacement = getEffectiveBackgroundImagePlacement();
  state.staticBackgroundImagePlacementMode = 'custom';
  state.staticExport.customEditorOpen = true;
  render();
  void prepareStaticBackgroundObjectOverlay(template);
}

function closeCustomPlacementEditor(apply: boolean): void {
  const initial = customEditorInitial;
  customEditorInitial = null;
  if (state.staticExport.customEditorTarget === 'background') {
    const draft = state.staticBackgroundImageDraftPlacement;
    if (apply && draft) {
      state.staticBackgroundImagePlacement = cloneCustomPlacement(draft);
      state.staticBackgroundImagePlacementMode = 'custom';
    } else if (!state.staticBackgroundImagePlacement) {
      state.staticBackgroundImagePlacementMode = 'fit';
    }
    if (!apply && initial?.backgroundMode) state.staticBackgroundImagePlacementMode = initial.backgroundMode;

    state.staticBackgroundImageDraftPlacement = null;
    state.staticExport.customEditorOpen = false;
    invalidateStaticBackgroundSource();
    render();
    return;
  }

  const screen = state.staticExport.customEditorScreen;
  const draft = getCustomDraftPlacement(screen);
  const current = screen === 3
    ? state.staticExport.customPlacement3
    : screen === 2
      ? state.staticExport.customPlacement2
      : state.staticExport.customPlacement;
  if (apply && draft) {
    if (screen === 3) {
      state.staticExport.customPlacement3 = cloneCustomPlacement(draft);
    } else if (screen === 2) {
      state.staticExport.customPlacement2 = cloneCustomPlacement(draft);
    } else {
      state.staticExport.customPlacement = cloneCustomPlacement(draft);
    }
    setScreenExportMode('custom', screen);
  } else if (!current) {
    setScreenExportMode(getScreenCustomModeFallback(screen), screen);
  }
  if (!apply && initial?.screenMode) setScreenExportMode(initial.screenMode, screen);

  setCustomDraftPlacement(null, screen);
  state.staticExport.customEditorOpen = false;
  render();
}

function edgeLength(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function shouldShowScreenReflectionControl(template: MockupTemplate | null | undefined): boolean {
  if (!template) {
    return false;
  }
  if (template.isVideo) {
    return true;
  }
  return resolveStaticTemplateCapabilities(template).screenOverlayOpacity;
}

function shouldShowScreenBlurControl(template: MockupTemplate | null | undefined): boolean {
  // Static "Blur Screen" profile only. Motion shares the render path but carries
  // its blur mask inside the atlas, so it has no per-template slider here.
  if (!template || template.isVideo) {
    return false;
  }
  return resolveStaticTemplateCapabilities(template).screenBlur === true;
}

/**
 * One colour slot.
 *
 * On a ramp the middle slots carry a tick that brings them into the gradient;
 * the two ends never do, because a ramp cannot be missing them. Anywhere else
 * — solid, mesh — every slot is simply on, so the tick would be a control that
 * does nothing.
 */
function renderPaletteSwatch(
  slot: number,
  colorAttribute: string,
  slotAttribute: string,
  label: string
): string {
  const isRamp = state.staticBackgroundMode === 'linear' || state.staticBackgroundMode === 'radial';
  const isEnd = slot === 0 || slot === STATIC_BACKGROUND_DEFAULT_COLORS.length - 1;
  const optional = isRamp && !isEnd;
  const active = !optional || isGradientSlotActive(slot);
  const color = state.staticBackgroundColors[slot] || STATIC_BACKGROUND_DEFAULT_COLORS[slot];
  const toggle = optional
    ? `<input
         class="palette-swatch-toggle"
         type="checkbox"
         ${active ? 'checked' : ''}
         ${slotAttribute}="${slot}"
         aria-label="Use colour ${slot + 1}"
       />`
    : '';
  // The editors number their slots because the number points at something in
  // the frame — a pin, a stop on the line. The panel's palette has no such
  // referent, so its head collapses to whatever is actually left.
  const head = toggle || label ? `<span class="palette-swatch-head">${toggle}${escapeHtml(label)}</span>` : '';

  return `
    <label class="field palette-swatch ${active ? '' : 'is-off'}">
      ${head}
      <input
        class="color-input"
        type="color"
        value="${escapeHtml(color)}"
        ${active ? '' : 'disabled'}
        ${colorAttribute}="${slot}"
      />
    </label>
  `;
}

function getGradientPaletteKey(colors: string[]): string {
  return colors.map((color) => color.trim().toLowerCase()).join(',');
}

function syncGradientUndoButton(): void {
  const button = document.getElementById('gradient-palette-undo') as HTMLButtonElement | null;
  if (button) {
    button.disabled = gradientPaletteHistory.length === 0;
  }
}

function rememberGradientPalette(colors: string[]): void {
  const snapshot = colors.slice(0, STATIC_BACKGROUND_DEFAULT_COLORS.length);
  if (snapshot.length !== STATIC_BACKGROUND_DEFAULT_COLORS.length) {
    return;
  }
  const last = gradientPaletteHistory[gradientPaletteHistory.length - 1];
  if (last && getGradientPaletteKey(last) === getGradientPaletteKey(snapshot)) {
    return;
  }
  gradientPaletteHistory = [...gradientPaletteHistory, snapshot].slice(-GRADIENT_PALETTE_HISTORY_LIMIT);
  syncGradientUndoButton();
}

function syncAnimatedGradientBasePalette(): void {
  if (!state.staticBackgroundAnimation.enabled) {
    return;
  }
  ensureAnimationKeyframes();
  const keyframes = state.staticBackgroundAnimation.keyframes.map((frame) => [...frame]);
  const base = getAnimationBaseColours();
  state.staticBackgroundAnimation.keyframes = keyframes.length > 0 ? [base, ...keyframes.slice(1)] : [base];
  backgroundAnimationTime = 0;
}

function replaceGradientPalette(colors: string[], rememberCurrent = true): boolean {
  const next = colors.slice(0, STATIC_BACKGROUND_DEFAULT_COLORS.length);
  if (
    next.length !== STATIC_BACKGROUND_DEFAULT_COLORS.length ||
    getGradientPaletteKey(next) === getGradientPaletteKey(state.staticBackgroundColors)
  ) {
    return false;
  }
  if (rememberCurrent) {
    rememberGradientPalette(state.staticBackgroundColors);
  }
  state.staticBackgroundColors = next;
  syncAnimatedGradientBasePalette();
  invalidateStaticBackgroundSource();
  return true;
}

function beginGradientColorEdit(): void {
  if (!gradientColorEditSnapshot) {
    gradientColorEditSnapshot = [...state.staticBackgroundColors];
  }
}

function commitGradientColorEdit(): void {
  const snapshot = gradientColorEditSnapshot;
  gradientColorEditSnapshot = null;
  if (snapshot && getGradientPaletteKey(snapshot) !== getGradientPaletteKey(state.staticBackgroundColors)) {
    rememberGradientPalette(snapshot);
  }
}

function bindGradientColorHistory(input: HTMLInputElement): void {
  input.addEventListener('pointerdown', beginGradientColorEdit);
  input.addEventListener('focus', beginGradientColorEdit);
  input.addEventListener('input', beginGradientColorEdit);
  input.addEventListener('change', commitGradientColorEdit);
  input.addEventListener('blur', commitGradientColorEdit);
}

function renderGradientQuickActions(): string {
  return `
    <button
      id="gradient-palette-undo"
      class="custom-mode-edit gradient-quick-action gradient-undo-action"
      type="button"
      title="Undo"
      aria-label="Undo"
      ${gradientPaletteHistory.length === 0 ? 'disabled' : ''}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
        <path d="M9 14 4 9l5-5" />
        <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
      </svg>
    </button>
    <button
      id="gradient-palette-random"
      class="custom-mode-edit gradient-quick-action"
      type="button"
      title="Generate a harmonious palette"
      aria-label="Generate a harmonious palette"
    >Random</button>
  `;
}

function renderGradientPresetsControl(): string {
  if (!isStandaloneWebApp) {
    return '';
  }

  const currentKey = getGradientPaletteKey(state.staticBackgroundColors);
  const currentSaved = state.gradientPresets.some(
    (preset) => getGradientPaletteKey(preset.colors) === currentKey
  );
  const atLimit = state.gradientPresets.length >= GRADIENT_PRESET_LIMIT;
  const addDisabled = state.gradientPresetsLoading || state.gradientPresetBusy || atLimit || currentSaved;
  const addTitle = currentSaved
    ? 'This palette is already saved'
    : atLimit
      ? `Maximum ${GRADIENT_PRESET_LIMIT} presets reached`
      : 'Save the current five colors';
  const rows = state.gradientPresets
    .map((preset, index) => {
      const active = getGradientPaletteKey(preset.colors) === currentKey;
      return `
        <div class="gradient-preset-item ${active ? 'is-active' : ''}" role="group" aria-label="Palette ${index + 1}">
          <button
            type="button"
            class="gradient-preset-apply"
            data-gradient-preset-apply="${escapeHtml(preset.id)}"
            aria-pressed="${active}"
            title="Apply palette ${index + 1}"
          >
            ${preset.colors
              .map(
                (color) =>
                  `<span class="gradient-preset-dot" style="--preset-color:${escapeHtml(color)}" aria-hidden="true"></span>`
              )
              .join('')}
          </button>
          <button
            type="button"
            class="gradient-preset-delete"
            data-gradient-preset-delete="${escapeHtml(preset.id)}"
            aria-label="Delete palette ${index + 1}"
            title="Delete palette"
            ${state.gradientPresetBusy ? 'disabled' : ''}
          >×</button>
        </div>
      `;
    })
    .join('');

  return `
    <div class="gradient-presets ${state.gradientPresetsOpen ? 'is-open' : ''}">
      <button
        id="gradient-presets-trigger"
        class="custom-mode-edit gradient-presets-trigger"
        type="button"
        aria-expanded="${state.gradientPresetsOpen}"
        aria-haspopup="dialog"
      >Presets</button>
      <div class="gradient-presets-popover" role="dialog" aria-label="Gradient presets">
        <button
          id="gradient-preset-add"
          class="gradient-preset-add"
          type="button"
          title="${escapeHtml(addTitle)}"
          ${addDisabled ? 'disabled' : ''}
        ><span aria-hidden="true">+</span> Add Preset</button>
        <div class="gradient-preset-list">
          ${
            state.gradientPresetsLoading
              ? '<p class="gradient-presets-empty">Loading...</p>'
              : rows || '<p class="gradient-presets-empty">No saved palettes</p>'
          }
        </div>
        <span class="gradient-presets-count">${state.gradientPresets.length} / ${GRADIENT_PRESET_LIMIT}</span>
      </div>
    </div>
  `;
}

function applyGradientPreset(preset: GradientPreset): void {
  replaceGradientPalette(preset.colors);
  state.gradientPresetsOpen = false;
  render();
  schedulePreviewRender();
}

function randomizeGradientPalette(): void {
  gradientColorEditSnapshot = null;
  if (replaceGradientPalette(generateHarmoniousGradientPalette())) {
    state.gradientPresetsOpen = false;
    render();
    schedulePreviewRender();
  }
}

function undoGradientPalette(): void {
  const previous = gradientPaletteHistory.pop();
  if (!previous) {
    return;
  }
  gradientColorEditSnapshot = null;
  replaceGradientPalette(previous, false);
  state.gradientPresetsOpen = false;
  render();
  schedulePreviewRender();
}

async function addCurrentGradientPreset(): Promise<void> {
  if (
    requireStandaloneAuth('gradient-preset', {
      type: 'gradient-presets'
    })
  ) {
    return;
  }
  if (!state.authUser || state.gradientPresetBusy) {
    return;
  }
  if (state.gradientPresets.length >= GRADIENT_PRESET_LIMIT) {
    setNotice(`You can save up to ${GRADIENT_PRESET_LIMIT} gradient presets.`, 'error');
    return;
  }

  state.gradientPresetBusy = true;
  render();
  try {
    const result = await saveGradientPreset([...state.staticBackgroundColors]);
    state.gradientPresets = [
      result.preset,
      ...state.gradientPresets.filter((preset) => preset.id !== result.preset.id)
    ].slice(0, GRADIENT_PRESET_LIMIT);
    setNotice(result.created ? 'Gradient preset saved.' : 'This gradient preset is already saved.', 'success');
  } catch (error) {
    setNotice(error instanceof Error ? error.message : 'Failed to save gradient preset', 'error');
  } finally {
    state.gradientPresetBusy = false;
    render();
  }
}

async function removeGradientPreset(presetId: string): Promise<void> {
  if (!state.authUser || state.gradientPresetBusy) {
    return;
  }
  state.gradientPresetBusy = true;
  render();
  try {
    await deleteGradientPreset(presetId);
    state.gradientPresets = state.gradientPresets.filter((preset) => preset.id !== presetId);
    setNotice('Gradient preset deleted.', 'success');
  } catch (error) {
    setNotice(error instanceof Error ? error.message : 'Failed to delete gradient preset', 'error');
  } finally {
    state.gradientPresetBusy = false;
    render();
  }
}

function toggleGradientSlot(slot: number, enabled: boolean): void {
  if (slot <= 0 || slot >= STATIC_BACKGROUND_DEFAULT_COLORS.length - 1) {
    return;
  }
  const slots = [...state.staticBackgroundGradientSlots];
  slots[slot] = enabled;
  state.staticBackgroundGradientSlots = slots;
  invalidateStaticBackgroundSource();
}

function getStaticBackgroundSourceValue(): string {
  return state.staticBackgroundMode === 'none' ? state.backgroundMode : state.staticBackgroundMode;
}

/**
 * Background source picker.
 *
 * A dropdown hid every option but the chosen one, which is the wrong shape for
 * a choice that is entirely about appearance — you cannot read "mesh gradient"
 * and know what it looks like. The tiles show it instead.
 *
 * The thumbnails are painted with CSS gradients rather than images or shader
 * frames: they cost nothing to load, they scale, and they follow the theme.
 * The marks on the three gradient tiles echo their editors — a line with two
 * ends, a centre with a rim, scattered points — so the tile also says how that
 * gradient is shaped.
 */
function renderStaticBackgroundSourcePicker(sourceValue: string, hasBackgroundTreatment: boolean): string {
  const disabled = state.backgroundTransparent;

  const tile = (
    value: string,
    label: string,
    art: string,
    options: { wide?: boolean; trailing?: string } = {}
  ): string => `
    <button
      type="button"
      class="source-tile${options.wide ? ' is-wide' : ''}${sourceValue === value ? ' is-active' : ''}"
      data-static-background-source="${value}"
      aria-pressed="${sourceValue === value}"
      ${disabled ? 'disabled' : ''}
    >
      ${art}
      <span class="source-tile-label">${escapeHtml(label)}</span>
      ${options.trailing || ''}
    </button>
  `;

  const art = (kind: string, marks = '') => `<span class="source-art is-${kind}" aria-hidden="true">${marks}</span>`;
  // Viewbox matches the tile so the marks land where the art suggests.
  const svg = (body: string) => `<svg class="source-art-marks" viewBox="0 0 32 32" aria-hidden="true">${body}</svg>`;
  const linearMarks = svg(
    '<line x1="7" y1="7" x2="25" y2="25" /><circle cx="7" cy="7" r="2.6" /><circle cx="25" cy="25" r="2.6" />'
  );
  const radialMarks = svg('<circle cx="12" cy="13" r="7" fill="none" /><circle cx="12" cy="13" r="2.6" /><circle cx="19" cy="13" r="2.2" />');
  const meshMarks = svg('<circle cx="9" cy="10" r="2.4" /><circle cx="22" cy="9" r="2.4" /><circle cx="16" cy="18" r="2.4" /><circle cx="9" cy="24" r="2.4" />');

  const imageThumb = state.staticBackgroundImagePreviewSrc
    ? `<span class="source-tile-thumb"><img src="${escapeHtml(state.staticBackgroundImagePreviewSrc)}" alt="" /></span>`
    : `<span class="source-tile-thumb is-empty" aria-hidden="true">${svg(
        '<rect x="6" y="8" width="20" height="16" rx="3" fill="none" /><circle cx="12" cy="14" r="2" /><path d="M8 22l6-6 5 5 3-3 4 4" fill="none" />'
      )}</span>`;

  const labels: Record<string, string> = {
    original: hasBackgroundTreatment ? 'Original' : 'Transparent',
    tint: 'Tint',
    tritone: 'Tritone',
    solid: 'Solid',
    linear: 'Linear',
    radial: 'Radial',
    mesh: 'Mesh',
    image: 'User image'
  };
  const currentArt: Record<string, string> = {
    tint: art('tint'),
    tritone: art('tritone'),
    solid: art('solid'),
    linear: art('linear', linearMarks),
    radial: art('radial', radialMarks),
    mesh: art('mesh', meshMarks)
  };

  // Still a dropdown: closed it costs one row, and the panel only appears when
  // the choice is actually being made. What changed is that the panel shows the
  // options instead of naming them.
  return `
    <div class="source-select ${state.staticBackgroundSourceOpen ? 'is-open' : ''}">
      <button
        type="button"
        id="static-background-source-trigger"
        class="source-select-trigger"
        aria-expanded="${state.staticBackgroundSourceOpen}"
        aria-haspopup="true"
        ${disabled ? 'disabled' : ''}
      >
        <span class="source-select-current">
          ${currentArt[sourceValue] || ''}
          <span>${escapeHtml(labels[sourceValue] || labels.original)}</span>
        </span>
        <span class="source-select-caret" aria-hidden="true">▾</span>
      </button>
      <div class="source-picker" role="group" aria-label="Background source">
        ${tile('original', labels.original, '', { wide: true })}
        ${hasBackgroundTreatment ? tile('tint', labels.tint, currentArt.tint) : ''}
        ${hasBackgroundTreatment ? tile('tritone', labels.tritone, currentArt.tritone) : ''}
        ${tile('solid', labels.solid, currentArt.solid)}
        ${tile('linear', labels.linear, currentArt.linear)}
        ${tile('radial', labels.radial, currentArt.radial)}
        ${tile('mesh', labels.mesh, currentArt.mesh)}
        ${tile('image', labels.image, '', { wide: true, trailing: imageThumb })}
      </div>
    </div>
  `;
}

function renderStaticBackgroundSourceControls(hasBackgroundTreatment: boolean): string {
  const sourceValue = getStaticBackgroundSourceValue();
  const gradientMode = state.staticBackgroundMode === 'linear' ||
    state.staticBackgroundMode === 'radial' ||
    state.staticBackgroundMode === 'mesh';

  return `
    ${renderStaticBackgroundSourcePicker(sourceValue, hasBackgroundTreatment)}
    ${
      state.backgroundTransparent
        ? '<p class="hint background-hint">The area covered by the background mask will be exported as transparent.</p>'
        : state.staticBackgroundMode === 'none' && state.backgroundMode === 'original'
          ? `<p class="hint background-hint">${hasBackgroundTreatment ? 'Choose a source to replace or recolor the masked background.' : 'Choose a source to place behind the transparent mockup.'}</p>`
          : state.staticBackgroundMode === 'none'
            ? `
                <div class="background-color-grid ${state.backgroundMode === 'tritone' ? 'tritone' : 'tint'}">
                  <label class="field">
                    <span>${state.backgroundMode === 'tritone' ? 'Shadow' : 'Dark'}</span>
                    <input id="background-shadow-color" class="color-input" type="color" value="${escapeHtml(state.backgroundShadowColor)}" />
                  </label>
                  ${
                    state.backgroundMode === 'tritone'
                      ? `
                        <label class="field">
                          <span>Mid</span>
                          <input id="background-mid-color" class="color-input" type="color" value="${escapeHtml(state.backgroundMidColor)}" />
                        </label>
                      `
                      : ''
                  }
                  <label class="field">
                    <span>${state.backgroundMode === 'tritone' ? 'Highlight' : 'Light'}</span>
                    <input id="background-highlight-color" class="color-input" type="color" value="${escapeHtml(state.backgroundHighlightColor)}" />
                  </label>
                </div>
              `
            : state.staticBackgroundMode === 'image'
              ? `
                  <div id="static-background-image-dropzone" class="static-background-image-picker">
                    <div class="static-background-image-copy">
                      <strong>${escapeHtml(state.staticBackgroundImageName || 'No background selected')}</strong>
                      <span>PNG, JPG or WebP</span>
                    </div>
                    <div class="static-background-image-actions">
                      <button id="static-background-image-button" type="button">Choose image</button>
                      ${state.staticBackgroundImage ? '<button id="static-background-image-clear" class="icon-clear-button" type="button" aria-label="Remove background image" title="Remove background image">×</button>' : ''}
                    </div>
                    <input id="static-background-image-input" type="file" accept="image/png,image/jpeg,image/webp" hidden />
                  </div>
                  ${state.staticBackgroundImage ? `
                    <div class="static-background-placement-control">
                      <span class="static-background-placement-label">Placement</span>
                      ${renderSegmentedControl(
                        STATIC_BACKGROUND_IMAGE_MODE_OPTIONS,
                        state.staticBackgroundImagePlacementMode,
                        'static-background-image-mode',
                        'Background image placement',
                        'sm'
                      )}
                    </div>
                    ${state.staticBackgroundImagePlacementMode === 'fit' ? `
                      <label class="field">
                        <span>Image fit</span>
                        <select id="static-background-image-fit">
                          <option value="cover" ${state.staticBackgroundImageFit === 'cover' ? 'selected' : ''}>Cover</option>
                          <option value="contain" ${state.staticBackgroundImageFit === 'contain' ? 'selected' : ''}>Contain</option>
                        </select>
                      </label>
                    ` : `
                      <button id="static-background-image-edit" class="custom-mode-edit static-background-image-edit" type="button">Edit Background</button>
                    `}
                  ` : ''}
                `
              : `
                  <div class="static-background-palette ${state.staticBackgroundMode === 'solid' ? 'single' : ''}">
                    ${(state.staticBackgroundMode === 'solid' ? [0] : COLOR_SLOT_INDEXES)
                      .map((slot) =>
                        renderPaletteSwatch(
                          slot,
                          'data-static-background-color',
                          'data-static-background-slot',
                          state.staticBackgroundMode === 'solid' ? 'Color' : ''
                        )
                      )
                      .join('')}
                  </div>
                  ${state.staticBackgroundMode === 'linear' || state.staticBackgroundMode === 'radial' ? `
                    <div class="static-background-placement-control">
                      <span class="static-background-placement-label">Gradient</span>
                      <div class="static-background-placement-actions gradient-toolbar">
                        ${renderGradientQuickActions()}
                        <button id="static-background-axis-edit" class="custom-mode-edit gradient-editor-button" type="button">Edit Gradient</button>
                        ${renderGradientPresetsControl()}
                      </div>
                    </div>
                  ` : ''}
                  ${state.staticBackgroundMode === 'linear' ? renderSliderRow({
                    id: 'static-background-angle',
                    label: 'Angle',
                    min: 0,
                    max: 360,
                    step: 1,
                    value: state.staticBackgroundAngle,
                    valueId: 'static-background-angle-value',
                    valueLabel: `${state.staticBackgroundAngle}°`
                  }) : ''}
                  ${state.staticBackgroundMode === 'mesh' ? `
                    <div class="static-background-placement-control">
                      <span class="static-background-placement-label">Colour points</span>
                      <div class="static-background-placement-actions gradient-toolbar">
                        ${renderGradientQuickActions()}
                        <button id="static-background-mesh-edit" class="custom-mode-edit gradient-editor-button mesh-editor-button" type="button">Edit Mesh</button>
                        ${renderGradientPresetsControl()}
                      </div>
                    </div>
                    ${renderSliderRow({
                      id: 'static-background-mesh-seed',
                      label: 'Variation',
                      min: 0,
                      max: 100,
                      // A tenth moves the arrangement enough to see and little
                      // enough to hunt with; whole numbers skipped past layouts.
                      step: 0.1,
                      value: state.staticBackgroundMeshSeed,
                      valueId: 'static-background-mesh-seed-value',
                      valueLabel: formatSeed(state.staticBackgroundMeshSeed)
                    })}
                    ${renderSliderRow({
                      id: 'static-background-mesh-mix',
                      label: 'Blend',
                      min: 0,
                      max: 100,
                      step: 1,
                      value: state.staticBackgroundMeshMix,
                      valueId: 'static-background-mesh-mix-value',
                      valueLabel: `${state.staticBackgroundMeshMix}%`
                    })}
                    ${renderSliderRow({
                      id: 'static-background-mesh-distortion',
                      label: 'Flow',
                      min: 0,
                      max: 100,
                      step: 1,
                      value: state.staticBackgroundMeshDistortion,
                      valueId: 'static-background-mesh-distortion-value',
                      valueLabel: `${state.staticBackgroundMeshDistortion}%`
                    })}
                    ${renderSliderRow({
                      id: 'static-background-mesh-grain',
                      label: 'Grain',
                      min: 0,
                      max: 100,
                      step: 1,
                      value: state.staticBackgroundMeshGrain,
                      valueId: 'static-background-mesh-grain-value',
                      valueLabel: `${state.staticBackgroundMeshGrain}%`
                    })}
                    <p class="hint background-hint">Variation reshuffles the points; Edit Mesh places them by hand. The exported PNG uses the same deterministic frame.</p>
                  ` : ''}
                `
    }
  `;
}

/**
 * BG Animation.
 *
 * The keyframe editor changes shape with the mode because a "state" means
 * something different in each: for Solid it is one colour, so the frames sit
 * side by side on a single line; for the ramps and Mesh a frame is a whole set
 * of stops, so each one gets its own row. The model underneath is the same list
 * of colour lists either way.
 */
function renderBackgroundAnimationControls(): string {
  const mode = state.staticBackgroundMode;
  if (mode === 'none' || mode === 'image' || state.backgroundTransparent) {
    return '';
  }

  const animation = state.staticBackgroundAnimation;
  const isSolid = mode === 'solid';
  const isMesh = mode === 'mesh';

  const swatch = (frame: number, slot: number, color: string) => `
    <input
      class="color-input anim-swatch"
      type="color"
      value="${escapeHtml(color)}"
      data-anim-color="${frame}"
      data-anim-slot="${slot}"
      aria-label="Keyframe ${frame + 1} colour ${slot + 1}"
    />
  `;

  const addButton = (label: string) => `
    <button
      type="button"
      id="bg-animation-add"
      class="anim-add"
      title="${escapeHtml(label)}"
      aria-label="${escapeHtml(label)}"
      ${animation.keyframes.length >= BACKGROUND_ANIMATION_MAX_KEYFRAMES ? 'disabled' : ''}
    >+</button>
  `;

  // Solid steps between single colours, so its frames read as one row.
  const keyframeEditor = isSolid
    ? `<div class="anim-row anim-row-solid">
        ${animation.keyframes.map((frame, index) => swatch(index, 0, frame[0] || STATIC_BACKGROUND_DEFAULT_COLORS[0])).join('')}
        ${addButton('Add a colour')}
      </div>`
    : `<div class="anim-rows">
        ${animation.keyframes
          .map(
            (frame, index) => `
              <div class="anim-row">
                ${frame.map((color, slot) => swatch(index, slot, color)).join('')}
                <button
                  type="button"
                  class="anim-remove"
                  data-anim-remove="${index}"
                  title="Remove this step"
                  aria-label="Remove step ${index + 1}"
                  ${animation.keyframes.length <= 1 ? 'disabled' : ''}
                >×</button>
              </div>
            `
          )
          .join('')}
        ${addButton('Add a step')}
      </div>`;

  return `
    <div class="background-animation ${animation.enabled ? 'is-on' : ''}">
      <label class="admin-toggle">
        <input id="bg-animation-enabled" type="checkbox" ${animation.enabled ? 'checked' : ''} />
        <span>BG Animation</span>
      </label>
      ${
        animation.enabled
          ? `
            <label class="creator-checkbox anim-loop">
              <input id="bg-animation-loop" type="checkbox" ${animation.loop ? 'checked' : ''} />
              <span>Loop</span>
            </label>
            <div class="field">
              <span>Length</span>
              ${renderSegmentedControl(
                BACKGROUND_ANIMATION_DURATIONS.map((seconds) => ({ value: String(seconds), label: `${seconds}s` })),
                String(animation.duration),
                'anim-duration',
                'Animation length',
                'sm'
              )}
            </div>
            <p class="hint background-hint">
              An animated background exports as an MP4 of this length, not a still image.
              Rendering needs a desktop browser — Chrome on Mac or PC.
            </p>
            ${
              isMesh
                ? `
                  <div class="anim-mesh-switches">
                    <label class="creator-checkbox">
                      <input id="bg-animation-mesh-move" type="checkbox" ${animation.meshMove ? 'checked' : ''} />
                      <span>Move</span>
                    </label>
                    <label class="creator-checkbox">
                      <input id="bg-animation-mesh-color" type="checkbox" ${animation.meshColor ? 'checked' : ''} />
                      <span>Color</span>
                    </label>
                  </div>
                  ${renderSliderRow({
                    id: 'bg-animation-mesh-motion-speed',
                    label: 'Motion Speed',
                    min: 0,
                    max: 100,
                    step: 1,
                    value: animation.meshMotionSpeed,
                    valueId: 'bg-animation-mesh-motion-speed-value',
                    valueLabel: `${animation.meshMotionSpeed}%`
                  })}
                  ${renderSliderRow({
                    id: 'bg-animation-speed',
                    label: 'Color Speed',
                    min: 0,
                    max: 100,
                    step: 1,
                    value: animation.speed,
                    valueId: 'bg-animation-speed-value',
                    valueLabel: `${animation.speed}%`
                  })}
                  ${
                    animation.meshMove
                      ? renderSliderRow({
                          id: 'bg-animation-mesh-motion-seed',
                          label: 'Random Seed',
                          min: 0,
                          max: 100,
                          step: 1,
                          value: animation.meshMotionSeed,
                          valueId: 'bg-animation-mesh-motion-seed-value',
                          valueLabel: String(animation.meshMotionSeed)
                        })
                      : ''
                  }
                  <p class="hint background-hint">Each point follows its own seamless orbit. Longer clips use wider orbits; Random Seed changes their directions. Motion and color both close on the selected loop length.</p>
                `
                : renderSliderRow({
                    id: 'bg-animation-speed',
                    label: 'Speed',
                    min: 0,
                    max: 100,
                    step: 1,
                    value: animation.speed,
                    valueId: 'bg-animation-speed-value',
                    valueLabel: `${animation.speed}%`
                  })
            }
            ${isMesh && !animation.meshColor ? '' : keyframeEditor}
          `
          : ''
      }
    </div>
  `;
}

function renderDeviceShadowControls(template: MockupTemplate): string {
  if (template.isVideo || !supportsStaticBackgroundComposer(template)) {
    return '';
  }

  const available = isDeviceShadowAvailable(template);
  const enabled = available && state.deviceShadowEnabled;
  return `
    <div class="background-effect-divider"></div>
    <div class="device-shadow-controls ${available ? '' : 'disabled'}">
      <label class="admin-toggle">
        <input id="device-shadow-enabled" type="checkbox" ${enabled ? 'checked' : ''} ${available ? '' : 'disabled'} />
        <span>Device shadow</span>
      </label>
      ${
        available
          ? enabled
            ? `
                <div class="device-shadow-preset-control">
                  <span>Style</span>
                  ${renderSegmentedControl(
                    DEVICE_SHADOW_PRESET_OPTIONS,
                    state.deviceShadowPreset,
                    'device-shadow-preset',
                    'Device shadow style',
                    'sm',
                    'device-shadow-presets'
                  )}
                </div>
                ${renderSliderRow({
                  id: 'device-shadow-angle',
                  label: 'Direction',
                  min: 0,
                  max: 359,
                  step: 1,
                  value: state.deviceShadowAngle,
                  valueId: 'device-shadow-angle-value',
                  valueLabel: `${state.deviceShadowAngle}°`
                })}
                ${renderSliderRow({
                  id: 'device-shadow-opacity',
                  label: 'Opacity',
                  min: 0,
                  max: 100,
                  step: 1,
                  value: state.deviceShadowOpacity,
                  valueId: 'device-shadow-opacity-value',
                  valueLabel: `${state.deviceShadowOpacity}%`
                })}
                <label class="device-shadow-color-control" for="device-shadow-color">
                  <span>Color</span>
                  <input
                    id="device-shadow-color"
                    class="color-input"
                    type="color"
                    value="${escapeHtml(state.deviceShadowColor)}"
                    aria-label="Device shadow color"
                  />
                </label>
              `
            : '<p class="hint background-hint">Add a subtle shadow behind the isolated device.</p>'
          : '<p class="hint background-hint">Available with Solid, Gradient, User image, or Transparent background.</p>'
      }
    </div>
  `;
}

/**
 * Dynamic Zoom / Out.
 *
 * A camera over the finished frame rather than a per-layer effect, so the whole
 * mockup travels together with what is inside the device. Only static templates
 * get it — a motion template already carries its own movement.
 *
 * Length comes from the user's clip when there is one, because the camera has
 * to land inside footage that already exists; a still has no length of its own,
 * so the author picks one and the export becomes a video of that length.
 */
/** Length the camera is being timed against: the user's clip when there is one,
 *  otherwise the length they picked for the still. */
function getDynamicZoomDuration(template = getSelectedTemplate()): number {
  const videos = getDynamicZoomClips(template);
  return videos.length ? Math.max(...videos.map(video => video.duration)) : getEditedDynamicZoom().duration;
}

function getEditedDynamicZoom(): DynamicZoomState {
  return state.dynamicZoomDraft ?? state.dynamicZoom;
}
function renderDynamicZoomEditor(template: MockupTemplate | null): string {
  return state.dynamicZoomEditorOpen && template ? renderDynamicZoomEditorMarkup() : '';
}

function renderDynamicZoomControls(template: MockupTemplate | null): string {
  if (!template || template.isVideo) {
    return '';
  }

  const zoom = state.dynamicZoom;
  const clips = getDynamicZoomClips(template);
  const clipLength = clips.length ? getDynamicZoomDuration(template) : null;

  return `
    <section class="control-disclosure ${state.dynamicZoomOpen ? 'open' : ''}">
      <button id="dynamic-zoom-toggle" class="control-disclosure-toggle" type="button" aria-expanded="${state.dynamicZoomOpen ? 'true' : 'false'}">
        <span class="control-disclosure-title">Dynamic Zoom/Out</span>
        <span class="control-disclosure-chevron">›</span>
      </button>
      <div class="control-disclosure-body">
        <div class="control-disclosure-inner">
          <div class="control-disclosure-content color-controls compact">
            <label class="admin-toggle">
              <input id="dynamic-zoom-enabled" type="checkbox" ${zoom.enabled ? 'checked' : ''} />
              <span>On</span>
            </label>
            ${
              zoom.enabled
                ? `
                  <div class="field">
                    <span>Background motion</span>
                    ${renderSegmentedControl([
                      { value: 'stick', label: 'Stick' },
                      { value: 'static', label: 'Static' },
                      { value: 'parallax', label: 'Parallax' }
                    ], normalizeBackgroundMotion(zoom.backgroundMotion), 'dynamic-zoom-background', 'Background motion', 'sm')}
                  </div>
                  ${normalizeBackgroundMotion(zoom.backgroundMotion) !== 'stick' && !isStaticBackgroundActive(getStaticBackgroundComposition())
                    ? '<p class="hint">Choose a background colour, gradient or image to move it separately.</p>' : ''}
                  ${
                    clipLength
                      ? `<p class="hint">Timed to your ${clips.length > 1 ? 'longest clip' : 'clip'} — ${clipLength.toFixed(1)}s.</p>`
                      : `
                        <div class="field">
                          <span>Length</span>
                          ${renderSegmentedControl(
                            DYNAMIC_ZOOM_IMAGE_DURATIONS.map((seconds) => ({
                              value: String(seconds),
                              label: `${seconds}s`
                            })),
                            String(zoom.duration),
                            'dynamic-zoom-duration',
                            'Animation length',
                            'sm'
                          )}
                        </div>
                        <p class="hint">A still exports as an MP4 of this length.</p>
                      `
                  }
                  <button id="dynamic-zoom-edit" class="custom-mode-edit" type="button">Edit Animation</button>
                  ${
                    zoom.keys.length > 0
                      ? `<p class="hint">${zoom.keys.length} key${zoom.keys.length > 1 ? 's' : ''} placed · ${
                          zoom.mode === 'transitions' ? 'Transitions' : 'Animation'
                        } · up to ${DYNAMIC_ZOOM_SPEED_SECONDS[zoom.speed]}s moves</p>`
                      : '<p class="hint">No keys yet — open Edit Animation to place them.</p>'
                  }
                `
                : ''
            }
          </div>
        </div>
      </div>
    </section>
  `;
}

function renderBackgroundControls(template: MockupTemplate | null): string {
  if (!template) {
    return '';
  }

  const staticCapabilities = template.isVideo ? null : resolveStaticTemplateCapabilities(template);
  const hasBackgroundTreatment = template.isVideo
    ? Boolean(template.backgroundMask)
    : staticCapabilities?.backgroundTreatment === true;
  const hasSurfaceReflection = template.isVideo
    ? template.reflectionEnabled === true
    : staticCapabilities?.surfaceReflection === true;
  const hasSurfaceReflectionBlur = template.isVideo
    ? template.reflectionEnabled === true
    : staticCapabilities?.surfaceReflectionBlur === true;
  const hasStaticBackgroundComposer = supportsStaticBackgroundComposer(template);
  const staticVideoChromaMode = Boolean(
    !template.isVideo &&
      state.staticExport.renderMode === 'static_video' &&
      state.userVideo &&
      hasBackgroundTreatment
  );

  if (!hasBackgroundTreatment && !hasSurfaceReflection && !hasStaticBackgroundComposer) {
    return '';
  }

  const sectionTitle = staticVideoChromaMode
    ? 'Chroma key background'
    : hasSurfaceReflection
      ? 'Reflection settings'
      : 'Background';

  return `
    <section class="control-disclosure ${state.backgroundControlsOpen ? 'open' : ''} ${state.gradientPresetsOpen ? 'has-open-popover' : ''}">
      <button id="background-controls-toggle" class="control-disclosure-toggle" type="button" aria-expanded="${state.backgroundControlsOpen ? 'true' : 'false'}">
        <span class="control-disclosure-title">${sectionTitle}</span>
        <span class="control-disclosure-chevron">›</span>
      </button>
      <div class="control-disclosure-body">
        <div class="control-disclosure-inner">
          <div class="control-disclosure-content color-controls compact">
            <div class="control-actions-row">
              <button id="background-reset">Default</button>
            </div>
            ${
              hasBackgroundTreatment || hasStaticBackgroundComposer
                ? `
                  ${hasBackgroundTreatment ? `
                    <label class="admin-toggle">
                      <input id="background-transparent" type="checkbox" ${state.backgroundTransparent ? 'checked' : ''} />
                      <span>${staticVideoChromaMode ? 'Chroma key background' : 'Transparent background'}</span>
                    </label>
                  ` : ''}
                  ${
                    staticVideoChromaMode && state.backgroundTransparent
                      ? `
                          <div class="chroma-key-grid" role="radiogroup" aria-label="Chroma key color">
                            <button type="button" class="chroma-key-option ${state.staticVideoChromaPreset === 'green' ? 'active' : ''}" data-static-video-chroma-preset="green" aria-pressed="${state.staticVideoChromaPreset === 'green'}">
                              <span class="chroma-key-swatch" style="background:${STATIC_VIDEO_CHROMA_GREEN}"></span>
                              <span>Green</span>
                            </button>
                            <button type="button" class="chroma-key-option ${state.staticVideoChromaPreset === 'blue' ? 'active' : ''}" data-static-video-chroma-preset="blue" aria-pressed="${state.staticVideoChromaPreset === 'blue'}">
                              <span class="chroma-key-swatch" style="background:${STATIC_VIDEO_CHROMA_BLUE}"></span>
                              <span>Blue</span>
                            </button>
                            <label class="chroma-key-option chroma-key-custom ${state.staticVideoChromaPreset === 'custom' ? 'active' : ''}">
                              <input id="static-video-chroma-custom" type="color" value="${escapeHtml(state.staticVideoChromaCustomColor)}" aria-label="Custom chroma key color" />
                              <span class="chroma-key-swatch" style="background:${escapeHtml(state.staticVideoChromaCustomColor)}"></span>
                              <span>Custom</span>
                            </label>
                          </div>
                          <p class="hint background-hint">The masked background will be replaced with this color in the MP4 export.</p>
                        `
                      : renderStaticBackgroundSourceControls(hasBackgroundTreatment)
                  }
                  ${renderBackgroundAnimationControls()}
                  ${renderDeviceShadowControls(template)}
                `
                : ''
            }
            ${
              hasSurfaceReflection
                ? `
                  <div class="background-effect-divider"></div>
                  <label class="admin-toggle">
                    <input id="reflection-visible" type="checkbox" ${state.reflectionVisible ? 'checked' : ''} />
                    <span>Enable reflection</span>
                  </label>
                  ${renderSliderRow({
                    id: 'reflection-opacity',
                    label: 'Reflection opacity',
                    min: 0,
                    max: 100,
                    step: 1,
                    value: state.reflectionOpacity,
                    valueId: 'reflection-opacity-value',
                    valueLabel: state.reflectionOpacity + '%'
                  })}
                  ${
                    hasSurfaceReflectionBlur
                      ? renderSliderRow({
                          id: 'reflection-blur',
                          label: 'Reflection blur',
                          min: 0.1,
                          max: 100,
                          step: 0.1,
                          value: state.reflectionBlur,
                          valueId: 'reflection-blur-value',
                          valueLabel: formatReflectionBlurValue(state.reflectionBlur)
                        })
                      : ''
                  }

                `
                : ''
            }
          </div>
        </div>
      </div>
    </section>
  `;
}

function renderDynamicIslandControl(template: MockupTemplate | null): string {
  if (!hasDynamicIslandControl(template)) {
    return '';
  }

  return `
    <label class="admin-toggle dynamic-island-row">
      <input id="dynamic-island-toggle" type="checkbox" ${state.dynamicIsland ? 'checked' : ''} />
      <span>Dynamic Island</span>
    </label>
  `;
}

function renderInterfaceMediaGuidance(template: MockupTemplate): string {
  const supportedLabel = template.isVideo
    ? 'MP4, PNG or JPG • drag and drop here'
    : 'PNG, JPG, MP4 or MOV • drag and drop here';
  return `
    <div id="interface-media-guidance" class="file-picker-guidance">
      <p>${supportedLabel}</p>
      ${template.isVideo ? '' : '<p>Images render on any device. Video renders to MP4 on Mac/PC in Google Chrome only.</p>'}
    </div>
  `;
}

function renderInterfaceFilePicker(template: MockupTemplate, screen: 1 | 2 | 3): string {
  const suffix = screen === 1 ? '' : `-${screen}`;
  const hasMedia = screen === 3
    ? Boolean(state.userVideo3 || state.userImage3)
    : screen === 2
      ? Boolean(state.userVideo2 || state.userImage2)
      : Boolean(state.userVideo || state.userImage);
  const accept = template.isVideo
    ? 'video/mp4,image/png,image/jpeg'
    : 'image/png,image/jpeg,video/mp4,video/quicktime,.mov';
  return `
    <div id="interface-dropzone${suffix}" class="file-picker-dropzone" data-screen="${screen}">
      <div class="file-picker-upload-row">
      <input id="interface-input${suffix}" class="hidden-file-input" type="file" accept="${accept}" aria-describedby="interface-media-guidance" />
      <div class="file-picker-actions">
        ${
          screen === 1 && isEmbeddedPlugin
            ? `<button id="figma-import-button" class="file-picker-secondary-button" type="button" ${state.importingFromFigma ? 'disabled' : ''}>
                ${state.importingFromFigma ? 'Importing…' : 'Import from Figma'}
              </button>`
            : ''
        }
        <button id="interface-picker-button${suffix}" class="file-picker-button" type="button">Choose File</button>
        ${
          hasMedia
            ? `<button
                id="interface-clear-button${suffix}"
                class="file-picker-clear-button"
                type="button"
                aria-label="Remove ${getDuoInputLabel(template, screen) || `Screen ${screen}`} media"
                title="Remove uploaded media"
              ><span aria-hidden="true">&times;</span></button>`
            : ''
        }
      </div>
      <div class="file-picker-copy">
        ${getTemplateScreenCount(template) > 1 || template.flexibleDisplay ? `<span class="file-picker-screen-label">${getDuoInputLabel(template, screen) || `Screen ${screen}`}</span>` : ''}
        <span class="file-picker-name">${escapeHtml(getActiveUserMediaName(template, screen) || 'No file selected')}</span>
      </div>
      </div>
      ${hasMedia ? renderPlacementModeControl(template, screen, hasMedia) : ''}
      <div class="file-picker-drop-overlay" aria-hidden="true">
        <strong>${template.isVideo ? 'Drop MP4 or PNG/JPG' : 'Drop PNG/JPG/MP4/MOV'}</strong>
        <span>for ${getDuoInputLabel(template, screen) || `Screen ${screen}`}</span>
      </div>
    </div>
  `;
}

function renderPlacementModeControl(template: MockupTemplate, screen: 1 | 2 | 3, hasMedia: boolean): string {
  const mode = getScreenExportMode(screen);
  const screenLabel = getDuoInputLabel(template, screen) || `Screen ${screen}`;
  return `
    <div class="screen-placement-control" data-placement-screen="${screen}">
      ${renderSegmentedControl(
        STATIC_EXPORT_MODE_OPTIONS.map(option => ({ ...option, disabled: !hasMedia })),
        mode,
        'static-export-mode',
        `Position ${screenLabel}`,
        'sm'
      )}
      ${hasMedia && mode === 'custom'
        ? `<button class="custom-mode-edit" data-custom-mode-edit data-screen="${screen}" type="button">Edit Frame</button>`
        : ''}
    </div>
  `;
}

/** Where a normalised point sits inside the drag zone, as a percentage. */
function meshPointToZonePercent(value: number): number {
  return ((value - MESH_POINT_MIN) / (MESH_POINT_MAX - MESH_POINT_MIN)) * 100;
}

function getEditedMeshPoints(): MeshPoint[] {
  return state.staticBackgroundMeshDraftPoints ?? state.staticBackgroundMeshPoints;
}

/**
 * Mesh point editor.
 *
 * The preview sits in the middle of a larger drag zone rather than filling the
 * dialog, because a colour point is often most useful just off-canvas: anchored
 * outside the frame it washes a corner evenly instead of showing up as a blob.
 * The zone is exactly the range the shader accepts, so anywhere the author can
 * drop a point is somewhere the renderer will honour.
 */
function renderMeshEditor(): string {
  if (!state.staticBackgroundMeshEditorOpen) {
    return '';
  }

  const points = getEditedMeshPoints();
  const colors = state.staticBackgroundColors;
  const previewInset = meshPointToZonePercent(0);
  const previewSize = meshPointToZonePercent(1) - previewInset;

  return `
    <div class="custom-editor-backdrop" id="mesh-editor-backdrop" role="presentation">
      <div class="custom-editor-modal mesh-editor-modal" role="dialog" aria-modal="true" aria-labelledby="mesh-editor-title">
        <div class="custom-editor-header">
          <div>
            <h3 id="mesh-editor-title">Mesh points</h3>
            <p>Drag a point to move its colour. Points may sit outside the frame.</p>
          </div>
          <button id="mesh-editor-close" class="star-button" type="button">×</button>
        </div>
        <div class="custom-editor-body">
          <div class="custom-editor-stage">
            <div class="mesh-editor-zone" id="mesh-editor-zone">
              <div
                class="mesh-editor-preview"
                id="mesh-editor-preview"
                style="left: ${previewInset}%; top: ${previewInset}%; width: ${previewSize}%; height: ${previewSize}%;"
              >${renderEditorObjectOverlay()}</div>
              ${(() => {
                const flow = state.staticBackgroundMeshFlowDraft ?? state.staticBackgroundMeshFlowCenter;
                return `
                  <button
                    class="mesh-editor-flow"
                    type="button"
                    data-mesh-flow="1"
                    aria-label="Flow centre"
                    title="Flow centre"
                    style="left: ${meshPointToZonePercent(flow.x)}%; top: ${meshPointToZonePercent(flow.y)}%;"
                  ><span>✛</span></button>
                `;
              })()}
              ${points
                .map(
                  (point, index) => `
                    <button
                      class="mesh-editor-dot"
                      type="button"
                      data-mesh-dot="${index}"
                      aria-label="Colour point ${index + 1}"
                      style="left: ${meshPointToZonePercent(point.x)}%; top: ${meshPointToZonePercent(
                        point.y
                      )}%; --dot-color: ${escapeHtml(colors[index] || STATIC_BACKGROUND_DEFAULT_COLORS[index])};"
                    ><span>${index + 1}</span></button>
                  `
                )
                .join('')}
            </div>
            <p class="mesh-editor-zone-label">Drag zone — the frame shows what is exported</p>
          </div>
          <div class="custom-editor-controls mesh-editor-controls">
            <span class="static-background-placement-label">Colours</span>
            <div class="mesh-editor-swatches">
              ${COLOR_SLOT_INDEXES.map(
                (slot) => `
                  <label class="mesh-editor-swatch">
                    <span>${slot + 1}</span>
                    <input
                      class="color-input"
                      type="color"
                      value="${escapeHtml(colors[slot] || STATIC_BACKGROUND_DEFAULT_COLORS[slot])}"
                      data-mesh-editor-color="${slot}"
                    />
                  </label>
                `
              ).join('')}
            </div>
          </div>
        </div>
        <div class="custom-editor-actions">
          <button id="mesh-editor-reset" type="button">Reset</button>
          <div class="custom-editor-confirm-actions">
            <button id="mesh-editor-cancel" type="button">Cancel</button>
            <button id="mesh-editor-apply" class="primary" type="button">OK</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

/** The mockup, drawn faintly over the gradient so the author can see what the
 *  background will actually sit behind — the same overlay the image placement
 *  editor uses. */
function renderEditorObjectOverlay(): string {
  if (!staticBackgroundObjectOverlaySrc) {
    return '';
  }
  return `<img
    class="custom-editor-object-overlay"
    src="${escapeHtml(staticBackgroundObjectOverlaySrc)}"
    alt=""
    aria-hidden="true"
    draggable="false"
  />`;
}

function axisPointToZonePercent(value: number): number {
  return ((value - AXIS_POINT_MIN) / (AXIS_POINT_MAX - AXIS_POINT_MIN)) * 100;
}

/**
 * Linear and radial gradient editor.
 *
 * Two handles carry the ends of the ramp — for a radial one, the centre and a
 * point on its rim — and every colour in between rides the segment at a
 * position the author slides. Those middle markers are constrained to the line
 * because an intermediate stop genuinely has one degree of freedom; letting the
 * marker wander off the segment would imply a control that does not exist.
 */
function renderGradientAxisEditor(): string {
  if (!state.staticBackgroundAxisEditorOpen) {
    return '';
  }

  const isRadial = state.staticBackgroundMode === 'radial';
  const axis = getEditedAxis();
  const colors = state.staticBackgroundColors;
  const stops = normalizeStopPositions(axis.stops, colors.length);
  const previewInset = axisPointToZonePercent(0);
  const previewSize = axisPointToZonePercent(1) - previewInset;

  const handle = (point: AxisPoint, index: number, label: string, extraClass: string) => `
    <button
      class="mesh-editor-dot axis-editor-handle ${extraClass}"
      type="button"
      data-axis-handle="${index}"
      aria-label="${escapeHtml(label)}"
      style="left: ${axisPointToZonePercent(point.x)}%; top: ${axisPointToZonePercent(point.y)}%; --dot-color: ${escapeHtml(
        colors[index === 0 ? 0 : colors.length - 1] || STATIC_BACKGROUND_DEFAULT_COLORS[0]
      )};"
    ><span>${index === 0 ? (isRadial ? '◉' : '1') : isRadial ? 'R' : String(colors.length)}</span></button>
  `;

  // Only the switched-on interior colours get a marker: the ends are the
  // handles themselves, and a slot that is off is not on the ramp at all.
  const stopMarkers = COLOR_SLOT_INDEXES.filter(
    (slot) => slot !== 0 && slot !== COLOR_SLOT_INDEXES.length - 1 && isGradientSlotActive(slot)
  )
    .map((slot) => {
      const position = pointOnAxis(axis.start, axis.end, stops[slot]);
      return `
        <button
          class="axis-editor-stop"
          type="button"
          data-axis-stop="${slot}"
          aria-label="Colour ${slot + 1} position"
          style="left: ${axisPointToZonePercent(position.x)}%; top: ${axisPointToZonePercent(
            position.y
          )}%; --dot-color: ${escapeHtml(colors[slot] || STATIC_BACKGROUND_DEFAULT_COLORS[slot])};"
        ><span>${slot + 1}</span></button>
      `;
    })
    .join('');

  return `
    <div class="custom-editor-backdrop" id="axis-editor-backdrop" role="presentation">
      <div class="custom-editor-modal mesh-editor-modal" role="dialog" aria-modal="true" aria-labelledby="axis-editor-title">
        <div class="custom-editor-header">
          <div>
            <h3 id="axis-editor-title">${isRadial ? 'Radial gradient' : 'Linear gradient'}</h3>
            <p>${
              isRadial
                ? 'Drag ◉ to move the centre and R to set the reach. Numbered stops slide along the line.'
                : 'Drag the ends of the ramp. Numbered stops slide between them.'
            }</p>
          </div>
          <button id="axis-editor-close" class="star-button" type="button">×</button>
        </div>
        <div class="custom-editor-body">
          <div class="custom-editor-stage">
            <div class="mesh-editor-zone" id="axis-editor-zone">
              <div
                class="mesh-editor-preview"
                id="axis-editor-preview"
                style="left: ${previewInset}%; top: ${previewInset}%; width: ${previewSize}%; height: ${previewSize}%;"
              >${renderEditorObjectOverlay()}</div>
              <svg class="axis-editor-line" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                <line
                  id="axis-editor-line-segment"
                  x1="${axisPointToZonePercent(axis.start.x)}"
                  y1="${axisPointToZonePercent(axis.start.y)}"
                  x2="${axisPointToZonePercent(axis.end.x)}"
                  y2="${axisPointToZonePercent(axis.end.y)}"
                />
              </svg>
              ${handle(axis.start, 0, isRadial ? 'Centre' : 'Gradient start', 'is-start')}
              ${handle(axis.end, 1, isRadial ? 'Reach' : 'Gradient end', 'is-end')}
              ${stopMarkers}
            </div>
            <p class="mesh-editor-zone-label">Drag zone — the frame shows what is exported</p>
          </div>
          <div class="custom-editor-controls mesh-editor-controls">
            <span class="static-background-placement-label">Colours</span>
            <div class="mesh-editor-swatches">
              ${COLOR_SLOT_INDEXES.map((slot) =>
                renderPaletteSwatch(
                  slot,
                  'data-axis-editor-color',
                  'data-axis-editor-slot',
                  // The ends are what the two handles carry, so they are named
                  // rather than numbered — the number told you the order, not
                  // which end of the ramp you were holding.
                  slot === 0 ? 'start' : slot === COLOR_SLOT_INDEXES.length - 1 ? 'end' : String(slot + 1)
                )
              ).join('')}
            </div>
          </div>
        </div>
        <div class="custom-editor-actions">
          <button id="axis-editor-reset" type="button">Reset</button>
          <div class="custom-editor-confirm-actions">
            <button id="axis-editor-cancel" type="button">Cancel</button>
            <button id="axis-editor-apply" class="primary" type="button">OK</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderCustomPlacementEditor(template: MockupTemplate | null): string {
  const screen = state.staticExport.customEditorScreen;
  const isBackgroundEditor = state.staticExport.customEditorTarget === 'background';
  const editorSource = getActiveCustomPlacementEditorSource();
  if (!state.staticExport.customEditorOpen || !template || !editorSource) {
    return '';
  }

  const screenAspect = isBackgroundEditor ? 1 : getSelectedScreenAspect(template, screen);
  const placement = getActiveCustomDraftPlacement() || getActiveEffectiveCustomPlacement();
  const editorMedia =
    editorSource.kind === 'video'
      ? `<video id="custom-editor-image" class="custom-editor-image" src="${escapeHtml(
          editorSource.src
        )}" muted playsinline loop preload="auto"></video>`
      : `<img id="custom-editor-image" class="custom-editor-image" src="${escapeHtml(
          editorSource.src
        )}" alt="Custom placement preview" />`;
  const editorGhostMedia =
    editorSource.kind === 'image'
      ? `<div
          class="custom-editor-ghost-viewport"
          style="--custom-editor-aspect: ${screenAspect}; aspect-ratio: ${screenAspect};"
          aria-hidden="true"
        >
          <img id="custom-editor-ghost" class="custom-editor-ghost" src="${escapeHtml(
            editorSource.src
          )}" alt="" draggable="false" />
        </div>`
      : editorSource.kind === 'video'
        ? `<div
            class="custom-editor-ghost-viewport"
            style="--custom-editor-aspect: ${screenAspect}; aspect-ratio: ${screenAspect};"
            aria-hidden="true"
          >
            <video
              id="custom-editor-ghost"
              class="custom-editor-ghost"
              src="${escapeHtml(editorSource.src)}"
              muted
              playsinline
              loop
              preload="auto"
            ></video>
          </div>`
      : '';
  const backgroundObjectOverlay =
    isBackgroundEditor && staticBackgroundObjectOverlaySrc
      ? `<img
          class="custom-editor-object-overlay"
          src="${escapeHtml(staticBackgroundObjectOverlaySrc)}"
          alt=""
          aria-hidden="true"
          draggable="false"
        />`
      : '';
  const backgroundFormatMask = isBackgroundEditor
    ? `<div
        class="custom-editor-format-mask ${state.staticExport.format === '1:1' ? 'is-hidden' : ''}"
        style="${getStaticBackgroundEditorFormatMaskStyle(state.staticExport.format)}"
        aria-hidden="true"
      ></div>`
    : '';

  return `
    <div class="custom-editor-backdrop" id="custom-editor-backdrop" role="presentation">
      <div class="custom-editor-modal" role="dialog" aria-modal="true" aria-labelledby="custom-editor-title">
        <div class="custom-editor-header">
          <div>
            <h3 id="custom-editor-title">${isBackgroundEditor
              ? 'Background placement'
              : `Custom placement${getDuoInputLabel(template, screen) ? ` · ${getDuoInputLabel(template, screen)}` : getTemplateScreenCount(template) > 1 ? ` · Screen ${screen}` : ''}`}</h3>
            <p>${isBackgroundEditor
              ? 'Move and scale the image across the full canvas.'
              : 'Move and scale the interface inside the screen area.'}</p>
          </div>
          <button id="custom-editor-close" class="star-button" type="button">×</button>
        </div>
        <div class="custom-editor-body">
          <div class="custom-editor-stage">
            ${editorGhostMedia}
            <div
              id="custom-editor-viewport"
              class="custom-editor-viewport"
              style="--custom-editor-aspect: ${screenAspect}; aspect-ratio: ${screenAspect};"
            >
              ${editorMedia}
              ${backgroundObjectOverlay}
              ${backgroundFormatMask}
              <div class="custom-editor-frame" aria-hidden="true"></div>
              ${renderSnapGuides()}
              <div class="custom-editor-hint">Drag to move • Scroll or pinch to zoom</div>
            </div>
          </div>
          <div class="custom-editor-controls">
            ${renderSliderRow({
              id: 'custom-editor-scale',
              label: 'Scale',
              min: isBackgroundEditor ? STATIC_BACKGROUND_CUSTOM_MIN_SCALE : SCREEN_CUSTOM_MIN_SCALE,
              max: 6,
              step: 0.01,
	              value: placement.scale.toFixed(2),
	              valueId: 'custom-editor-scale-value',
	              valueLabel: placement.scale.toFixed(2) + 'x'
            })}
          </div>
        </div>
        <div class="custom-editor-actions">
          <div class="editor-action-tools"><button id="custom-editor-reset" type="button">Reset</button>${renderSnapsButton('custom-editor-snaps', 'custom')}</div>
          <div class="custom-editor-confirm-actions">
            <button id="custom-editor-cancel" type="button">Cancel</button>
            <button id="custom-editor-apply" class="primary" type="button">OK</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function getTemplateScope(options: { ignoreCategory?: boolean; ignoreSearch?: boolean } = {}): MockupTemplate[] {
  let templates = getTypeScopedCatalog();

  if (state.favoritesOnly) {
    templates = templates.filter((item) => isFavoriteTemplate(item.id));
  } else if (state.selectedCollectionId) {
    templates = templates.filter(
      (item) => (item.collectionId || getDefaultCollectionIdForType(item.templateType)) === state.selectedCollectionId
    );
  } else if (state.selectedPurposeId) {
    const ids = new Set(state.collections.filter(c=>getCollectionPurposeId(c,state.collectionPurposes)===state.selectedPurposeId).map(c=>c.id));
    templates = templates.filter(t=>ids.has(t.collectionId||getDefaultCollectionIdForType(t.templateType)));
  }

  if (!options.ignoreCategory && state.category !== 'All') {
    templates = templates.filter((item) => item.category === state.category);
  }

  const query = options.ignoreSearch ? '' : state.catalogSearch.trim().toLowerCase();
  if (query) {
    templates = templates.filter((item) => matchesTemplateSearchQuery(item, query));
  }

  return templates;
}

function filteredCatalog(): MockupTemplate[] {
  return getTemplateScope();
}

function matchesTemplateNameSearchQuery(template: MockupTemplate, query: string): boolean {
  const tokens = String(query || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return true;
  }

  const name = template.name.toLowerCase();
  return tokens.every((token) => name.includes(token));
}

function filteredCollectionSearchTemplates(): MockupTemplate[] {
  const query = state.catalogSearch.trim();
  if (state.usePaneView !== 'collections' || state.quickCatalogFilter !== 'collections' || !query) {
    return [];
  }

  const allowed = state.selectedPurposeId ? new Set(state.collections.filter(c=>getCollectionPurposeId(c,state.collectionPurposes)===state.selectedPurposeId).map(c=>c.id)) : null;
  return getTypeScopedCatalog().filter((item) => (!allowed || allowed.has(item.collectionId)) && matchesTemplateNameSearchQuery(item, query));
}

function getAvailableCategoryFilters(options: { ignoreSearch?: boolean } = {}): CategoryFilter[] {
  const templates = getTemplateScope({ ignoreCategory: true, ignoreSearch: options.ignoreSearch });
  const domainSubCategories = getSubCategoriesForDomain(state.selectedDomainCategory);
  const categories: CategoryFilter[] = ['All', ...domainSubCategories.filter(
    (cat) => templates.some((template) => template.category === cat)
  )];
  return categories;
}

function filteredCollections(): MockupCollection[] {
  const query = state.catalogSearch.trim().toLowerCase();
  let collections = state.collections.filter(
    (c) => c.published!==false && c.listed!==false && (c.domainCategory ?? 'App') === state.selectedDomainCategory &&
      (c.templateType.startsWith('MOTION') ? 'MOTION' : 'STATIC') === state.selectedContentType &&
      // A pending refresh may still contain collections from the previous source.
      (!['CG', 'AI'].includes(state.selectedSourceFilter) || c.templateType.replace(/^MOTION-/, '') === state.selectedSourceFilter) &&
      (!state.selectedPurposeId || getCollectionPurposeId(c,state.collectionPurposes)===state.selectedPurposeId)
  );
  if (query) {
    collections = collections.filter((item) => item.name.toLowerCase().includes(query) || item.id.toLowerCase().includes(query));
  }
  return collections;
}

function countTemplatesForCollection(collectionId: string): number {
  return getTypeScopedCatalog().filter(
    (item) => (item.collectionId || getDefaultCollectionIdForType(item.templateType)) === collectionId
  ).length;
}

function renderTemplateGridItems(templates: MockupTemplate[], placeholderCount = 0): string {
  return `
    ${templates
      .map(
        (item) => `
          <article class="template-item">
            <button
              class="mockup-card ${item.id === state.selectedId ? 'active' : ''}"
              data-id="${escapeHtml(item.id)}"
              ${
                item.isVideo && item.previewAtlasUrl
                  ? `data-motion-preview-url="${escapeHtml(item.previewAtlasUrl)}" data-motion-atlas-layout="${escapeHtml(
                      item.atlasLayout || 'standard-4'
                    )}"`
                  : ''
              }
            >
              <img src="${escapeHtml(getTemplatePreviewUrl(item))}" alt="${escapeHtml(item.name)}" />
              <span class="mockup-card-title">${escapeHtml(item.name)}</span>
            </button>
            <button
              class="favorite-toggle ${isFavoriteTemplate(item.id) ? 'active' : ''}"
              data-favorite-id="${escapeHtml(item.id)}"
              title="Favorite"
            >
              ${isFavoriteTemplate(item.id) ? '★' : '☆'}
            </button>
          </article>
        `
      )
      .join('')}
    ${Array.from({ length: placeholderCount })
      .map(() => '<div class="template-placeholder" aria-hidden="true"></div>')
      .join('')}
  `;
}

function getAssetCacheKey(template: MockupTemplate, variant: 'preview' | 'export'): string {
  return [
    variant,
    template.id,
    template.base,
    template.editBase || '',
    template.mask,
    template.editMask || '',
    template.mask2 || '',
    template.editMask2 || '',
    template.mask3 || '',
    template.editMask3 || '',
    template.overlay,
    template.editOverlay || '',
    template.backgroundMask || '',
    template.editBackgroundMask || '',
    template.reflectionMask || '',
    template.editReflectionMask || '',
    template.reflectionBlurMask || '',
    template.editReflectionBlurMask || '',
    template.screenBlurMask || '',
    template.editScreenBlurMask || '',
    template.updatedAt || '',
    template.previewUpdatedAt || ''
  ].join('|');
}

function getMotionAssetCacheKey(template: MockupTemplate, variant: 'preview' | 'export'): string {
  return [
    variant,
    template.id,
    template.previewAtlasUrl || '',
    template.atlasUrl || '',
    template.trackingUrl || '',
    template.screenBlurEnabled === false ? 'screen-blur-off' : 'screen-blur-on',
    template.motionDynamicIslandToggle === true ? 'dynamic-island-toggle' : 'fixed-screen-mask',
    template.updatedAt || '',
    template.previewUpdatedAt || ''
  ].join('|');
}

function getInteractiveUserImage(): HTMLImageElement | null {
  return state.userImagePreview || state.userImage;
}

function getInteractiveStaticSource(): CanvasImageSource | null {
  if (state.staticExport.renderMode === 'static_video' && state.userVideo) {
    return state.userVideo;
  }
  return state.userImagePreview || state.userImage;
}

function getInteractiveStaticSource2(template = getSelectedTemplate()): CanvasImageSource | null {
  if (!hasTemplateScreen(template, 2)) return null;
  if (state.staticExport.renderMode === 'static_video' && state.userVideo2) {
    return state.userVideo2;
  }
  return state.userImagePreview2 || state.userImage2;
}

function getInteractiveStaticSource3(template = getSelectedTemplate()): CanvasImageSource | null {
  if (!hasTemplateScreen(template, 3)) return null;
  if (state.staticExport.renderMode === 'static_video' && state.userVideo3) {
    return state.userVideo3;
  }
  return state.userImagePreview3 || state.userImage3;
}

interface StaticVideoStoryboardSource {
  screen: 1 | 2;
  video: HTMLVideoElement;
  sourceUrl: string;
  duration: number;
}

function getStaticVideoStoryboardSource(
  template = getSelectedTemplate()
): StaticVideoStoryboardSource | null {
  if (!template || template.isVideo || state.staticExport.renderMode !== 'static_video') {
    return null;
  }

  if (state.userVideo && state.userVideoSrc) {
    return {
      screen: 1,
      video: state.userVideo,
      sourceUrl: state.userVideoSrc,
      duration: state.uploadedMedia?.duration || state.userVideo.duration
    };
  }

  if (isDualScreenTemplate(template) && state.userVideo2 && state.userVideoSrc2) {
    return {
      screen: 2,
      video: state.userVideo2,
      sourceUrl: state.userVideoSrc2,
      duration: state.uploadedMedia2?.duration || state.userVideo2.duration
    };
  }

  return null;
}

function shouldUseStaticVideoStoryboard(template = getSelectedTemplate()): boolean {
  // Static now uses the live player only. Keep legacy release paths for media
  // changes, but never sample/decode frames for the removed thumbnail strip.
  return false;
}

function syncStaticVideoStoryboardFromMedia(): void {
  if (shouldUseStaticVideoStoryboard()) {
    void refreshStaticVideoStoryboard();
  } else {
    clearStaticVideoStoryboard();
  }
}

function clearStaticVideoStoryboard(options: { resetSelection?: boolean } = {}): void {
  pauseStaticPreview();
  staticVideoStoryboardGeneration += 1;
  staticVideoStoryboardAbortController?.abort();
  staticVideoStoryboardAbortController = null;
  staticVideoStoryboardSeekGeneration += 1;
  staticVideoStoryboardSeekAbortController?.abort();
  staticVideoStoryboardSeekAbortController = null;
  clearStaticVideoStoryboardSampleCache();
  revokeStaticVideoStoryboardFrames(state.staticVideoStoryboard.frames);
  state.staticVideoStoryboard.frames = [];
  state.staticVideoStoryboard.loading = false;
  state.staticVideoStoryboard.error = '';
  if (options.resetSelection !== false) {
    state.staticVideoStoryboard.selectedIndex = 0;
  }
}

function clearStaticVideoStoryboardSampleCache(): void {
  staticVideoStoryboardSampleAbortController?.abort();
  staticVideoStoryboardSampleAbortController = null;
  staticVideoStoryboardSamplePromise = null;
  staticVideoStoryboardSamplePromiseKey = '';
  if (staticVideoStoryboardSampleCache) {
    releaseStaticVideoStoryboardSamples(staticVideoStoryboardSampleCache.samples);
    staticVideoStoryboardSampleCache = null;
  }
}

function getStaticVideoStoryboardSampleKey(
  sourceUrl: string,
  duration: number,
  maxSourceSize: number
): string {
  return [sourceUrl, duration.toFixed(4), maxSourceSize].join('|');
}

function getStaticVideoStoryboardSamples(
  sourceUrl: string,
  duration: number,
  maxSourceSize: number
): Promise<StaticVideoStoryboardSample[]> {
  const key = getStaticVideoStoryboardSampleKey(sourceUrl, duration, maxSourceSize);
  if (staticVideoStoryboardSampleCache?.key === key) {
    return Promise.resolve(staticVideoStoryboardSampleCache.samples);
  }
  if (staticVideoStoryboardSamplePromise && staticVideoStoryboardSamplePromiseKey === key) {
    return staticVideoStoryboardSamplePromise;
  }

  clearStaticVideoStoryboardSampleCache();
  const controller = new AbortController();
  staticVideoStoryboardSampleAbortController = controller;
  staticVideoStoryboardSamplePromiseKey = key;
  const promise = sampleStaticVideoStoryboardFrames({
    sourceUrl,
    duration,
    count: STATIC_VIDEO_STORYBOARD_FRAME_COUNT,
    maxSourceSize,
    signal: controller.signal
  })
    .then((samples) => {
      if (controller.signal.aborted || staticVideoStoryboardSamplePromiseKey !== key) {
        releaseStaticVideoStoryboardSamples(samples);
        throw controller.signal.reason instanceof Error
          ? controller.signal.reason
          : new DOMException('Operation aborted', 'AbortError');
      }
      staticVideoStoryboardSampleCache = { key, samples };
      return samples;
    })
    .finally(() => {
      if (staticVideoStoryboardSampleAbortController === controller) {
        staticVideoStoryboardSampleAbortController = null;
      }
      if (staticVideoStoryboardSamplePromise === promise) {
        staticVideoStoryboardSamplePromise = null;
        staticVideoStoryboardSamplePromiseKey = '';
      }
    });
  staticVideoStoryboardSamplePromise = promise;
  return promise;
}

function revokeStaticVideoStoryboardFrames(frames: StaticVideoStoryboardFrame[]): void {
  for (const frame of frames) {
    if (frame.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(frame.previewUrl);
    }
  }
}

async function refreshStaticVideoStoryboard(options: { preserveSelection?: boolean } = {}): Promise<void> {
  const template = getSelectedTemplate();
  if (!shouldUseStaticVideoStoryboard(template)) return;
  const storyboardSource = getStaticVideoStoryboardSource(template);
  if (!storyboardSource || !template) {
    clearStaticVideoStoryboard();
    return;
  }

  const { screen, sourceUrl, duration } = storyboardSource;
  const selectedIndex = options.preserveSelection
    ? clamp(state.staticVideoStoryboard.selectedIndex, 0, STATIC_VIDEO_STORYBOARD_FRAME_COUNT - 1)
    : 0;
  const previousFrames = state.staticVideoStoryboard.frames;

  staticVideoStoryboardAbortController?.abort();
  staticVideoStoryboardSeekAbortController?.abort();
  state.staticVideoStoryboard.loading = true;
  state.staticVideoStoryboard.error = '';
  state.staticVideoStoryboard.selectedIndex = selectedIndex;

  const controller = new AbortController();
  const generation = ++staticVideoStoryboardGeneration;
  staticVideoStoryboardAbortController = controller;
  const isMobile = isMobilePreviewDevice();
  const maxSourceSize = isMobile
    ? STATIC_VIDEO_STORYBOARD_SOURCE_SIZE_MOBILE
    : STATIC_VIDEO_STORYBOARD_SOURCE_SIZE_DESKTOP;
  const storyboardRenderSize = isMobile
    ? STATIC_VIDEO_STORYBOARD_RENDER_SIZE_MOBILE
    : STATIC_VIDEO_STORYBOARD_RENDER_SIZE_DESKTOP;
  const renderOptions = {
    renderSize: storyboardRenderSize,
    gridX: STATIC_VIDEO_STORYBOARD_GRID_SIZE,
    gridY: STATIC_VIDEO_STORYBOARD_GRID_SIZE,
    diffuse: getDiffuseAdjustments(),
    background: getBackgroundAdjustments(),
    deviceShadow: getDeviceShadowAdjustments(template),
    reflection: getReflectionAdjustments(),
    screenOverlay: getScreenOverlayAdjustments(),
    useAlternateMask: shouldUseAlternateMask(),
    customPlacement: state.staticExport.mode === 'custom' ? getEffectiveCustomPlacement() : null,
    customPlacement2: state.staticExport.mode2 === 'custom' ? getEffectiveCustomPlacement(2) : null,
    warpMode: state.staticExport.mode,
    warpMode2: state.staticExport.mode2
  } as const;
  let nextFrames: StaticVideoStoryboardFrame[] = [];
  let companionSampler: HTMLVideoElement | null = null;
  let committed = false;

  try {
    const companionVideo = isDualScreenTemplate(template) ? (screen === 1 ? state.userVideo2 : state.userVideo) : null;
    const [assets, sampledFrames, staticBackground] = await Promise.all([
      ensurePreviewAssets(template),
      getStaticVideoStoryboardSamples(sourceUrl, duration, maxSourceSize),
      ensureStaticBackgroundComposition(template, storyboardRenderSize),
      companionVideo ? loadVideoFromUrl(companionVideo.currentSrc || companionVideo.src).then(video => {
        companionSampler = video;
        if (controller.signal.aborted) { video.pause(); video.removeAttribute('src'); video.load(); }
      }) : Promise.resolve()
    ]);

    for (const sample of sampledFrames) {
      if (controller.signal.aborted || generation !== staticVideoStoryboardGeneration) {
        return;
      }
      await waitForStaticVideoStoryboardPaint(controller.signal);
      if (companionSampler) {
        await seekUserVideoAtPlaybackTime(companionSampler, sample.time);
      }
      const thumbnailCanvas = renderMaster(
        assets,
        screen === 1 ? sample.source : companionSampler || getInteractiveStaticSource(),
        {
          ...renderOptions,
          staticBackground,
          userImage2: isDualScreenTemplate(template)
            ? screen === 2
              ? sample.source
              : companionSampler || getInteractiveStaticSource2()
            : null
        }
      );
      applyStaticVideoChromaMatte(thumbnailCanvas, template);
      const thumbnailBlob = await canvasToBlob(thumbnailCanvas, 'image/webp', 0.8);
      thumbnailCanvas.width = 1;
      thumbnailCanvas.height = 1;
      nextFrames.push({
        time: sample.time,
        previewUrl: URL.createObjectURL(thumbnailBlob)
      });
    }

    if (
      controller.signal.aborted ||
      generation !== staticVideoStoryboardGeneration ||
      getSelectedTemplate()?.id !== template.id ||
      (screen === 1 ? state.userVideoSrc : state.userVideoSrc2) !== sourceUrl
    ) {
      return;
    }

    state.staticVideoStoryboard.frames = nextFrames;
    state.staticVideoStoryboard.loading = false;
    state.staticVideoStoryboard.error = '';
    state.staticVideoStoryboard.selectedIndex = clamp(selectedIndex, 0, Math.max(0, nextFrames.length - 1));
    committed = true;
    syncStaticVideoStoryboardUi(template);
    revokeStaticVideoStoryboardFrames(previousFrames);
  } catch (error) {
    if (controller.signal.aborted || generation !== staticVideoStoryboardGeneration) {
      return;
    }
    state.staticVideoStoryboard.loading = false;
    state.staticVideoStoryboard.error =
      error instanceof Error ? error.message : 'Unable to build video frame previews.';
    syncStaticVideoStoryboardUi(template);
  } finally {
    if (companionSampler) { companionSampler.pause(); companionSampler.removeAttribute('src'); companionSampler.load(); }
    if (!committed) {
      revokeStaticVideoStoryboardFrames(nextFrames);
      nextFrames = [];
    }
    if (staticVideoStoryboardAbortController === controller) {
      staticVideoStoryboardAbortController = null;
    }
  }
}

async function waitForStaticVideoStoryboardPaint(signal: AbortSignal): Promise<void> {
  if (signal.aborted) {
    throw signal.reason instanceof Error ? signal.reason : new DOMException('Operation aborted', 'AbortError');
  }

  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (error?: Error): void => {
      if (settled) {
        return;
      }
      settled = true;
      signal.removeEventListener('abort', onAbort);
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    };
    const onAbort = (): void =>
      finish(signal.reason instanceof Error ? signal.reason : new DOMException('Operation aborted', 'AbortError'));

    signal.addEventListener('abort', onAbort, { once: true });
    window.requestAnimationFrame(() => finish());
  });
}

function selectStaticVideoStoryboardFrame(index: number): void {
  const frame = state.staticVideoStoryboard.frames[index];
  if (!frame || !isStaticPreviewAvailable()) return;
  state.staticVideoStoryboard.selectedIndex = index;
  syncStaticVideoStoryboardSelection();
  pauseStaticPreview();
  void seekStaticPreviewTo(frame.time);
}

function syncStaticVideoStoryboardSelection(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-static-video-storyboard-index]').forEach((button) => {
    const index = Number(button.dataset.staticVideoStoryboardIndex);
    const active = index === state.staticVideoStoryboard.selectedIndex;
    button.classList.toggle('active', active);
    button.setAttribute('aria-pressed', String(active));
  });
}

function renderStaticVideoStoryboard(template: MockupTemplate): string {
  if (!shouldUseStaticVideoStoryboard(template)) {
    return '';
  }

  const storyboard = state.staticVideoStoryboard;
  if (storyboard.loading && storyboard.frames.length === 0) {
    return `
      <div
        id="static-video-storyboard"
        class="preview-static-video-storyboard is-loading"
        data-preview-control-surface
        aria-label="Preparing video frame previews"
      >
        ${Array.from({ length: STATIC_VIDEO_STORYBOARD_FRAME_COUNT })
          .map(() => '<span class="preview-static-video-storyboard-skeleton" aria-hidden="true"></span>')
          .join('')}
      </div>
    `;
  }

  if (storyboard.error || storyboard.frames.length === 0) {
    return storyboard.error
      ? `
        <div
          id="static-video-storyboard"
          class="preview-static-video-storyboard is-error"
          data-preview-control-surface
          role="status"
        >
          Frame previews unavailable
        </div>
      `
      : '';
  }

  return `
    <div
      id="static-video-storyboard"
      class="preview-static-video-storyboard"
      data-preview-control-surface
      role="group"
      aria-label="Choose a video frame"
    >
      ${storyboard.frames
        .map(
          (frame, index) => `
            <button
              class="preview-static-video-storyboard-frame ${index === storyboard.selectedIndex ? 'active' : ''}"
              type="button"
              data-static-video-storyboard-index="${index}"
              aria-label="Show video frame ${index + 1} at ${formatStaticVideoStoryboardTime(frame.time)}"
              aria-pressed="${index === storyboard.selectedIndex ? 'true' : 'false'}"
              title="${formatStaticVideoStoryboardTime(frame.time)}"
            >
              <img src="${escapeHtml(frame.previewUrl)}" alt="" draggable="false" />
            </button>
          `
        )
        .join('')}
    </div>
  `;
}

function syncStaticVideoStoryboardUi(template = getSelectedTemplate()): void {
  const existing = document.getElementById('static-video-storyboard');
  const previewDropzone = document.getElementById('preview-dropzone');
  const shouldShow = Boolean(template && shouldUseStaticVideoStoryboard(template));
  previewDropzone?.classList.toggle('has-static-video-storyboard', shouldShow);

  if (!template || !shouldShow) {
    existing?.remove();
    return;
  }

  const markup = renderStaticVideoStoryboard(template);
  if (!markup) {
    existing?.remove();
    return;
  }

  if (existing) {
    existing.outerHTML = markup;
  } else {
    document.getElementById('static-preview-thumbnails')?.insertAdjacentHTML('beforeend', markup);
  }

  const storyboard = document.getElementById('static-video-storyboard');
  if (storyboard) {
    bindStaticVideoStoryboardEvents(storyboard);
  }
}

function formatStaticVideoStoryboardTime(seconds: number): string {
  const safeSeconds = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = Math.floor(safeSeconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function getInteractiveMotionSource(): CanvasImageSource | null {
  return state.userVideo || state.userImagePreview || state.userImage;
}

function getInteractiveMotionSource2(template = getSelectedTemplate()): CanvasImageSource | null {
  if (!hasTemplateScreen(template, 2)) return null;
  return state.userVideo2 || state.userImagePreview2 || state.userImage2;
}

function getInteractiveMotionSource3(template = getSelectedTemplate()): CanvasImageSource | null {
  if (!hasTemplateScreen(template, 3)) return null;
  return state.userVideo3 || state.userImagePreview3 || state.userImage3;
}

function getMotionExportSource(): CanvasImageSource | null {
  return state.userVideo || state.userImage;
}

function getMotionExportSource2(): CanvasImageSource | null {
  return state.userVideo2 || state.userImage2;
}

function getMotionExportSource3(): CanvasImageSource | null {
  return state.userVideo3 || state.userImage3;
}

function getEffectiveMotionPlaybackDurationForSources(
  assets: LoadedMotionAssets,
  primary: CanvasImageSource | null,
  secondary: CanvasImageSource | null,
  tertiary: CanvasImageSource | null
): number {
  return Math.max(
    getEffectiveMotionPlaybackDuration(assets.tracking, primary, assets.template.motionLoop === true),
    hasTemplateScreen(assets.template, 2) && secondary
      ? getEffectiveMotionPlaybackDuration(assets.tracking, secondary, assets.template.motionLoop === true)
      : 0,
    hasTemplateScreen(assets.template, 3) && tertiary
      ? getEffectiveMotionPlaybackDuration(assets.tracking, tertiary, assets.template.motionLoop === true)
      : 0
  );
}

function seekUserVideoAtPlaybackTime(source: CanvasImageSource | null, time: number): Promise<void> {
  if (!(source instanceof HTMLVideoElement)) {
    return Promise.resolve();
  }
  const maxTime = Number.isFinite(source.duration) && source.duration > 0
    ? Math.max(0, source.duration - 1 / 120)
    : Math.max(0, time);
  return seekVideo(source, Math.min(Math.max(0, time), maxTime));
}

/**
 * Lets go of everything loaded for templates other than `keepId`.
 *
 * The asset caches were never trimmed, so every template opened or exported in
 * a session kept its decoded artwork — a static template's 4K set is several
 * hundred megabytes, a motion template holds live video decoders. A phone ran
 * out of memory after a few templates in a row. Only the template on screen is
 * kept; going back to an earlier one reloads it from the HTTP cache.
 *
 * Skipped while an export runs: the export is still reading its template's
 * assets, and the next switch catches up.
 */
function releaseOtherTemplateResources(keepId: string): void {
  if (state.exporting || activeVideoExportTask) {
    return;
  }
  evictOtherTemplates(previewAssetCache, keepId);
  evictOtherTemplates(exportAssetCache, keepId);
  evictOtherTemplates(previewMotionAssetCache, keepId, releaseMotionAssetVideos);
  evictOtherTemplates(exportMotionAssetCache, keepId, releaseMotionAssetVideos);
  if (dynamicZoomPreviewMaster && dynamicZoomPreviewMaster.templateId !== keepId) {
    dynamicZoomPreviewMaster = null;
  }
  releaseDeviceShadowCache();
}

function evictOtherTemplates<T>(
  cache: Map<string, Promise<T>>,
  keepId: string,
  release?: (value: T) => void
): void {
  for (const [key, pending] of [...cache]) {
    // Both key builders put the template id second.
    if (key.split('|')[1] === keepId) continue;
    cache.delete(key);
    if (release) void pending.then(release, () => undefined);
  }
}

/** A detached <video> keeps its decoder until its source is dropped. */
function releaseMotionAssetVideos(assets: LoadedMotionAssets): void {
  for (const video of [assets.atlasVideo, assets.colorMatteVideo, assets.backgroundMatteVideo, assets.neutralAtlasVideo]) {
    if (!video) continue;
    video.pause();
    video.removeAttribute('src');
    video.load();
  }
}

async function ensurePreviewAssets(template: MockupTemplate): Promise<LoadedMockupAssets> {
  const key = getAssetCacheKey(template, 'preview');
  const cached = previewAssetCache.get(key);
  if (cached) {
    return cached;
  }

  const loading = loadMockupAssets(template, 'preview');
  previewAssetCache.set(key, loading);

  try {
    return await loading;
  } catch (error) {
    previewAssetCache.delete(key);
    throw error;
  }
}

async function ensureExportAssets(template: MockupTemplate): Promise<LoadedMockupAssets> {
  const key = getAssetCacheKey(template, 'export');
  const cached = exportAssetCache.get(key);
  if (cached) {
    return cached;
  }

  const loading = loadMockupAssets(template, 'export');
  exportAssetCache.set(key, loading);

  try {
    return await loading;
  } catch (error) {
    exportAssetCache.delete(key);
    throw error;
  }
}

async function ensurePreviewMotionAssets(template: MockupTemplate): Promise<LoadedMotionAssets> {
  const key = getMotionAssetCacheKey(template, 'preview');
  const cached = previewMotionAssetCache.get(key);
  if (cached) {
    return cached;
  }

  const loading = loadMotionAssets(template, 'preview');
  previewMotionAssetCache.set(key, loading);

  try {
    return await loading;
  } catch (error) {
    previewMotionAssetCache.delete(key);
    throw error;
  }
}

async function ensureExportMotionAssets(template: MockupTemplate): Promise<LoadedMotionAssets> {
  await billingRequest('/me/motion-access', 'POST');
  const key = getMotionAssetCacheKey(template, 'export');
  const cached = exportMotionAssetCache.get(key);
  if (cached) {
    return cached;
  }

  const loading = loadMotionAssets(template, 'export');
  exportMotionAssetCache.set(key, loading);

  try {
    return await loading;
  } catch (error) {
    exportMotionAssetCache.delete(key);
    throw error;
  }
}

async function handleInterfaceUpload(file: File, screen: 1 | 2 | 3 = 1): Promise<void> {
  const selected = getSelectedTemplate();
  if (!hasTemplateScreen(selected, screen)) {
    return;
  }
  const expectsMotionMedia = Boolean(selected?.isVideo);
  const lowerName = file.name.trim().toLowerCase();
  const isVideoFile = file.type.startsWith('video/') || lowerName.endsWith('.mp4');
  const isImageFile =
    file.type.startsWith('image/') || lowerName.endsWith('.png') || lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg');
  const uploadedMediaType = getUploadedMediaType(file);

  if (file.size <= 0) {
    setNotice('Unsupported file: file is empty', 'error');
    return;
  }
  // TODO: apply the shared product upload-size limit here once it is defined.

  if (expectsMotionMedia && !isVideoFile && !isImageFile) {
    setNotice('Unsupported file: use MP4, PNG or JPG for MOTION templates', 'error');
    return;
  }

  if (!expectsMotionMedia && uploadedMediaType === 'unknown') {
    setNotice('Unsupported file: use PNG, JPG, MP4 or MOV', 'error');
    return;
  }

  state.rendering = true;
  render();

  try {
    if (expectsMotionMedia && isVideoFile) {
      if (screen === 3) {
        await applyLoadedThirdUserVideoFromFile(file, false);
        state.uploadedMedia3 = null;
      } else if (screen === 2) {
        await applyLoadedSecondaryUserVideoFromFile(file, false);
        state.uploadedMedia2 = null;
      } else {
        await applyLoadedUserVideoFromFile(file);
        state.uploadedMedia = null;
      }
      state.staticExport.renderMode = 'static_image';
      if (selected) {
        await ensurePreviewMotionAssets(selected);
      }
      setNotice('Motion source video loaded', 'success');
    } else if (expectsMotionMedia && isImageFile) {
      if (screen === 3) {
        await applyLoadedThirdUserImageFromFile(file);
        state.uploadedMedia3 = null;
      } else if (screen === 2) {
        await applyLoadedSecondaryUserImageFromFile(file);
        state.uploadedMedia2 = null;
      } else {
        await applyLoadedUserImageFromFile(file);
        state.uploadedMedia = null;
      }
      state.staticExport.renderMode = 'static_image';
      if (selected) {
        await ensurePreviewMotionAssets(selected);
      }
      setNotice('Motion source image loaded', 'success');
    } else if (!expectsMotionMedia && uploadedMediaType === 'video') {
      if (screen === 3) {
        await applyLoadedThirdUserVideoFromFile(file, true);
      } else if (screen === 2) {
        await applyLoadedSecondaryUserVideoFromFile(file, true);
      } else {
        await applyLoadedStaticUserVideoFromFile(file);
      }
      if (selected) {
        await ensurePreviewAssets(selected);
      }
      setNotice(
        isMobileVideoExportRuntime()
          ? 'Video detected. MP4 render is available on Mac/PC only.'
          : 'Static video source loaded',
        isMobileVideoExportRuntime() ? 'error' : 'success'
      );
    } else {
      if (screen === 3) {
        await applyLoadedThirdUserImageFromFile(file);
        state.uploadedMedia3 = {
          file,
          type: 'image',
          width: state.userImage3?.naturalWidth || state.userImage3?.width || undefined,
          height: state.userImage3?.naturalHeight || state.userImage3?.height || undefined
        };
      } else if (screen === 2) {
        await applyLoadedSecondaryUserImageFromFile(file);
        state.uploadedMedia2 = {
          file,
          type: 'image',
          width: state.userImage2?.naturalWidth || state.userImage2?.width || undefined,
          height: state.userImage2?.naturalHeight || state.userImage2?.height || undefined
        };
      } else {
        await applyLoadedUserImageFromFile(file);
        state.uploadedMedia = {
          file,
          type: 'image',
          width: state.userImage?.naturalWidth || state.userImage?.width || undefined,
          height: state.userImage?.naturalHeight || state.userImage?.height || undefined
        };
      }
      syncStaticRenderModeFromMedia();
      if (selected) {
        await ensurePreviewAssets(selected);
      }
      setNotice('Interface image loaded', 'success');
    }
  } catch (error) {
    setNotice(error instanceof Error ? error.message : 'Failed to load media', 'error');
  } finally {
    state.rendering = false;
    render();
  }
}

function syncStaticRenderModeFromMedia(): void {
  const selected = getSelectedTemplate();
  state.staticExport.renderMode = state.userVideo ||
    (hasTemplateScreen(selected, 2) && state.userVideo2) ||
    (hasTemplateScreen(selected, 3) && state.userVideo3)
    ? 'static_video'
    : 'static_image';
}

async function applyLoadedUserImageFromFile(file: File): Promise<void> {
  const nextSrc = URL.createObjectURL(file);
  let loadedImage: HTMLImageElement;
  try {
    loadedImage = await loadImageFromUrl(nextSrc);
  } catch (error) {
    URL.revokeObjectURL(nextSrc);
    throw error;
  }

  await applyUserImageState(loadedImage, nextSrc, file.name);
}

async function applyLoadedSecondaryUserImageFromFile(file: File): Promise<void> {
  const nextSrc = URL.createObjectURL(file);
  let loadedImage: HTMLImageElement;
  try {
    loadedImage = await loadImageFromUrl(nextSrc);
  } catch (error) {
    URL.revokeObjectURL(nextSrc);
    throw error;
  }

  const previewCopy = await createPreviewImageCopy(loadedImage, PREVIEW_RENDER_SIZE);
  revokeUserImageSrc2(nextSrc, previewCopy.src);
  revokeUserVideoSrc2();
  state.userVideo2 = null;
  state.userVideoSrc2 = '';
  state.userVideoName2 = '';
  state.userImage2 = loadedImage;
  state.userImagePreview2 = previewCopy.image;
  state.userImageSrc2 = nextSrc;
  state.userImagePreviewSrc2 = previewCopy.src;
  state.userImageName2 = file.name;
  state.staticExport.customPlacement2 = null;
  state.staticExport.customDraftPlacement2 = null;
  if (state.staticExport.mode2 === 'custom') {
    state.staticExport.customModeFallback2 = 'fit';
    state.staticExport.mode2 = 'fit';
  }
  syncStaticRenderModeFromMedia();
  syncStaticVideoStoryboardFromMedia();
}

async function applyLoadedThirdUserImageFromFile(file: File): Promise<void> {
  const nextSrc = URL.createObjectURL(file);
  let loadedImage: HTMLImageElement;
  try {
    loadedImage = await loadImageFromUrl(nextSrc);
  } catch (error) {
    URL.revokeObjectURL(nextSrc);
    throw error;
  }

  const previewCopy = await createPreviewImageCopy(loadedImage, PREVIEW_RENDER_SIZE);
  revokeUserImageSrc3(nextSrc, previewCopy.src);
  revokeUserVideoSrc3();
  state.userVideo3 = null;
  state.userVideoSrc3 = '';
  state.userVideoName3 = '';
  state.userImage3 = loadedImage;
  state.userImagePreview3 = previewCopy.image;
  state.userImageSrc3 = nextSrc;
  state.userImagePreviewSrc3 = previewCopy.src;
  state.userImageName3 = file.name;
  state.staticExport.customPlacement3 = null;
  state.staticExport.customDraftPlacement3 = null;
  if (state.staticExport.mode3 === 'custom') {
    state.staticExport.customModeFallback3 = 'fit';
    state.staticExport.mode3 = 'fit';
  }
  syncStaticRenderModeFromMedia();
  syncStaticVideoStoryboardFromMedia();
}

async function applyLoadedUserImageFromFigma(bytes: ArrayBuffer | Uint8Array | number[], name: string): Promise<void> {
  const imageBytes = Uint8Array.from(toUint8Array(bytes));
  const blob = new Blob([imageBytes], { type: 'image/png' });
  const nextSrc = URL.createObjectURL(blob);
  let loadedImage: HTMLImageElement;
  try {
    loadedImage = await loadImageFromBlob(blob);
  } catch (error) {
    URL.revokeObjectURL(nextSrc);
    throw error;
  }

  await applyUserImageState(loadedImage, nextSrc, name);
}

async function createMotionSourceFile(): Promise<File> {
  if (state.userVideo && state.userVideoSrc) {
    const response = await fetch(state.userVideoSrc);
    const blob = await response.blob();
    const fileName = state.userVideoName || 'motion-source.mp4';
    const fileType = blob.type || 'video/mp4';
    return new File([blob], fileName, { type: fileType });
  }

  if (state.userImage && state.userImageSrc) {
    const response = await fetch(state.userImageSrc);
    const blob = await response.blob();
    const lowerName = (state.userImageName || 'motion-source.png').toLowerCase();
    const fallbackType = lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg') ? 'image/jpeg' : 'image/png';
    return new File([blob], state.userImageName || 'motion-source.png', {
      type: blob.type || fallbackType
    });
  }

  throw new Error(isEmbeddedPlugin ? 'Load MP4, PNG, JPG or import from Figma first.' : 'Load MP4, PNG or JPG first.');
}

function buildMotionBrowserExportUrl(sessionId: string): string {
  // APP_URL is set at build time via VITE_APP_URL env var.
  // It is needed when the code runs inside a Figma plugin iframe where
  // window.location.href is a Figma-internal URL, not the public web app URL.
  const base = APP_URL || window.location.href;
  const url = new URL(base);
  url.searchParams.delete('shell_v');
  url.searchParams.set('motionExportSession', sessionId);
  url.searchParams.set('motionAutoExport', '1');
  return url.toString();
}

async function hydrateMotionExportSession(sessionId: string, options: { autoExport: boolean }): Promise<void> {
  const session = await loadMotionExportSession(sessionId);
  const template = state.catalog.find((item) => item.id === session.templateId && item.isVideo);
  if (!template) {
    throw new Error('Motion export session template is not available in this library.');
  }

  state.templateType = template.templateType;
  state.selectedContentType = 'MOTION';
  state.selectedSourceFilter = 'ALL';
  await refreshCollections({ quiet: true });
  state.selectedCollectionId = template.collectionId || getDefaultCollectionIdForType(template.templateType);
  state.selectedId = template.id;
  state.usePaneView = 'detail';
  resetTemplateControls({ preserveOutputSettings: true });

  if (session.mediaKind === 'video') {
    const video = await loadVideoFromUrl(session.mediaUrl);
    await applyUserVideoState(video, session.mediaUrl, session.mediaName);
  } else {
    const image = await loadImageFromUrl(session.mediaUrl);
    await applyUserImageState(image, session.mediaUrl, session.mediaName);
  }

  applyMotionExportSessionSettings(session.settings);

  await ensurePreviewMotionAssets(template);
  render();

  if (options.autoExport) {
    if (!isMotionVideoExportSupported()) {
      setNotice(
        'Browser export session loaded, but video export is unavailable in this browser.',
        'error'
      );
      return;
    }
    await handleDownload();
  } else {
    setNotice('Motion export session loaded in browser.', 'success');
  }
}

function applyMotionExportSessionSettings(settings: MotionExportSessionSettings): void {
  state.motionExport.resizeMode = isMotionResizeMode(settings.resizeMode) ? settings.resizeMode : '1:1';
  state.staticExport.mode = settings.warpMode === 'custom' ? 'custom' : 'fit';
  state.staticExport.customModeFallback = 'fit';
  state.staticExport.customPlacement =
    settings.warpMode === 'custom'
      ? cloneCustomPlacement(settings.customPlacement || null) || createDefaultCustomPlacement()
      : null;
  state.staticExport.customDraftPlacement = null;
  state.staticExport.customEditorOpen = false;

  state.diffuseHue = settings.diffuse.hue;
  state.diffuseSaturation = settings.diffuse.saturation;
  state.diffuseBrightness = settings.diffuse.brightness;
  state.diffuseContrast = settings.diffuse.contrast;
  state.diffuseMonochrome = settings.diffuse.monochrome;

  state.backgroundTransparent = settings.background.transparent;
  state.backgroundMode = settings.background.mode;
  state.backgroundShadowColor = settings.background.shadowColor;
  state.backgroundMidColor = settings.background.midColor;
  state.backgroundHighlightColor = settings.background.highlightColor;

  state.reflectionVisible = settings.reflection.enabled;
  state.reflectionOpacity = settings.reflection.opacity;
  state.reflectionBlur = settings.reflection.blur;
  state.dynamicIsland = getSelectedTemplate()?.motionDynamicIslandToggle === true
    ? !settings.useAlternateMask
    : settings.useAlternateMask;
}

async function applyUserImageState(image: HTMLImageElement, src: string, name: string): Promise<void> {
  const previewCopy = await createPreviewImageCopy(image, PREVIEW_RENDER_SIZE);
  clearStaticVideoStoryboard();
  revokeUserImageSrc(src, previewCopy.src);
  const preserveMotionPlayback = Boolean(getSelectedTemplate()?.isVideo && state.motionPlaying);
  if (!preserveMotionPlayback) {
    pauseMotionPlayback();
  }
  revokeUserVideoSrc();
  state.userVideo = null;
  state.userVideoSrc = '';
  state.userVideoName = '';
  state.userImage = image;
  state.userImagePreview = previewCopy.image;
  state.userImageSrc = src;
  state.userImagePreviewSrc = previewCopy.src;
  state.userImageName = name;
  state.uploadedMedia = null;
  state.staticExport.renderMode = 'static_image';
  resetHomeCompositePreviews();
  if (!preserveMotionPlayback) {
    state.motionFrameIndex = 0;
  }
  state.staticExport.customPlacement = null;
  state.staticExport.customDraftPlacement = null;
  if (state.staticExport.mode === 'custom') {
    state.staticExport.customModeFallback = 'fit';
    state.staticExport.mode = 'fit';
  }
  syncStaticRenderModeFromMedia();
  syncStaticVideoStoryboardFromMedia();
}

async function applyLoadedUserVideoFromFile(file: File): Promise<void> {
  let loadedVideo: HTMLVideoElement;
  try {
    loadedVideo = await loadVideoFromFile(file);
  } catch (error) {
    throw error;
  }

  await applyUserVideoState(loadedVideo, loadedVideo.currentSrc || loadedVideo.src, file.name);
}

async function applyLoadedStaticUserVideoFromFile(file: File): Promise<void> {
  let loadedVideo: HTMLVideoElement;
  try {
    loadedVideo = await loadVideoFromFile(file);
  } catch (error) {
    throw error;
  }

  const duration = loadedVideo.duration;
  if (!Number.isFinite(duration) || duration <= 0) {
    revokeManagedMediaUrl(loadedVideo);
    throw new Error('Unable to read video duration.');
  }

  const width = loadedVideo.videoWidth || 0;
  const height = loadedVideo.videoHeight || 0;
  if (width <= 0 || height <= 0) {
    revokeManagedMediaUrl(loadedVideo);
    throw new Error('Unable to read video dimensions.');
  }

  await applyUserVideoState(loadedVideo, loadedVideo.currentSrc || loadedVideo.src, file.name);
  state.uploadedMedia = {
    file,
    type: 'video',
    duration,
    width,
    height
  };
  state.staticExport.renderMode = 'static_video';
  state.staticExport.resolution = STATIC_VIDEO_EXPORT_RESOLUTION;
  void refreshStaticVideoStoryboard();
}

async function applyLoadedSecondaryUserVideoFromFile(file: File, staticVideo: boolean): Promise<void> {
  const loadedVideo = await loadVideoFromFile(file);
  const duration = loadedVideo.duration;
  const width = loadedVideo.videoWidth || 0;
  const height = loadedVideo.videoHeight || 0;
  if (!Number.isFinite(duration) || duration <= 0 || width <= 0 || height <= 0) {
    revokeManagedMediaUrl(loadedVideo);
    throw new Error(!Number.isFinite(duration) || duration <= 0
      ? 'Unable to read video duration.'
      : 'Unable to read video dimensions.');
  }

  const src = loadedVideo.currentSrc || loadedVideo.src;
  revokeUserVideoSrc2(src);
  revokeUserImageSrc2();
  state.userImage2 = null;
  state.userImagePreview2 = null;
  state.userImageSrc2 = '';
  state.userImagePreviewSrc2 = '';
  state.userImageName2 = '';
  state.userVideo2 = loadedVideo;
  state.userVideoSrc2 = src;
  state.userVideoName2 = file.name;
  state.staticExport.customPlacement2 = null;
  state.staticExport.customDraftPlacement2 = null;
  if (state.staticExport.mode2 === 'custom') {
    state.staticExport.customModeFallback2 = 'fit';
    state.staticExport.mode2 = 'fit';
  }
  state.uploadedMedia2 = staticVideo ? { file, type: 'video', duration, width, height } : null;
  if (staticVideo) {
    state.staticExport.resolution = STATIC_VIDEO_EXPORT_RESOLUTION;
  }
  syncStaticRenderModeFromMedia();
  if (staticVideo) {
    syncStaticVideoStoryboardFromMedia();
  }
}

async function applyLoadedThirdUserVideoFromFile(file: File, staticVideo: boolean): Promise<void> {
  const loadedVideo = await loadVideoFromFile(file);
  const duration = loadedVideo.duration;
  const width = loadedVideo.videoWidth || 0;
  const height = loadedVideo.videoHeight || 0;
  if (!Number.isFinite(duration) || duration <= 0 || width <= 0 || height <= 0) {
    revokeManagedMediaUrl(loadedVideo);
    throw new Error(!Number.isFinite(duration) || duration <= 0
      ? 'Unable to read video duration.'
      : 'Unable to read video dimensions.');
  }

  const src = loadedVideo.currentSrc || loadedVideo.src;
  revokeUserVideoSrc3(src);
  revokeUserImageSrc3();
  state.userImage3 = null;
  state.userImagePreview3 = null;
  state.userImageSrc3 = '';
  state.userImagePreviewSrc3 = '';
  state.userImageName3 = '';
  state.userVideo3 = loadedVideo;
  state.userVideoSrc3 = src;
  state.userVideoName3 = file.name;
  state.staticExport.customPlacement3 = null;
  state.staticExport.customDraftPlacement3 = null;
  if (state.staticExport.mode3 === 'custom') {
    state.staticExport.customModeFallback3 = 'fit';
    state.staticExport.mode3 = 'fit';
  }
  state.uploadedMedia3 = staticVideo ? { file, type: 'video', duration, width, height } : null;
  if (staticVideo) {
    state.staticExport.resolution = STATIC_VIDEO_EXPORT_RESOLUTION;
  }
  syncStaticRenderModeFromMedia();
  if (staticVideo) {
    syncStaticVideoStoryboardFromMedia();
  }
}

async function applyUserVideoState(video: HTMLVideoElement, src: string, name: string): Promise<void> {
  clearStaticVideoStoryboard();
  const preserveMotionPlayback = Boolean(getSelectedTemplate()?.isVideo && state.motionPlaying);
  if (!preserveMotionPlayback) {
    pauseMotionPlayback();
  }
  revokeUserVideoSrc(src);
  revokeUserImageSrc();
  state.userImage = null;
  state.userImagePreview = null;
  state.userImageSrc = '';
  state.userImagePreviewSrc = '';
  state.userImageName = '';
  resetHomeCompositePreviews();
  state.userVideo = video;
  state.userVideoSrc = src;
  state.userVideoName = name;
  if (!preserveMotionPlayback) {
    state.motionFrameIndex = 0;
  }
  state.staticExport.customPlacement = null;
  state.staticExport.customDraftPlacement = null;
  if (state.staticExport.mode === 'custom') {
    state.staticExport.customModeFallback = 'fit';
    state.staticExport.mode = 'fit';
  }
  syncStaticRenderModeFromMedia();
}

function clearUserMedia(screen: 1 | 2 | 3 = 1): void {
  if (screen === 3) {
    revokeUserImageSrc3();
    revokeUserVideoSrc3();
    state.userImage3 = null;
    state.userImagePreview3 = null;
    state.userImageSrc3 = '';
    state.userImagePreviewSrc3 = '';
    state.userImageName3 = '';
    state.userVideo3 = null;
    state.userVideoSrc3 = '';
    state.userVideoName3 = '';
    state.uploadedMedia3 = null;
    state.staticExport.customPlacement3 = null;
    state.staticExport.customDraftPlacement3 = null;
    if (state.staticExport.mode3 === 'custom') {
      state.staticExport.customModeFallback3 = 'fit';
      state.staticExport.mode3 = 'fit';
    }
    syncStaticRenderModeFromMedia();
    syncStaticVideoStoryboardFromMedia();
    state.detailPreviewReadyKey = '';
    setNotice('Screen 3 media removed', 'success');
    render();
    return;
  }
  if (screen === 2) {
    revokeUserImageSrc2();
    revokeUserVideoSrc2();
    state.userImage2 = null;
    state.userImagePreview2 = null;
    state.userImageSrc2 = '';
    state.userImagePreviewSrc2 = '';
    state.userImageName2 = '';
    state.userVideo2 = null;
    state.userVideoSrc2 = '';
    state.userVideoName2 = '';
    state.uploadedMedia2 = null;
    state.staticExport.customPlacement2 = null;
    state.staticExport.customDraftPlacement2 = null;
    if (state.staticExport.mode2 === 'custom') {
      state.staticExport.customModeFallback2 = 'fit';
      state.staticExport.mode2 = 'fit';
    }
    syncStaticRenderModeFromMedia();
    syncStaticVideoStoryboardFromMedia();
    state.detailPreviewReadyKey = '';
    setNotice('Screen 2 media removed', 'success');
    render();
    return;
  }
  clearStaticVideoStoryboard();
  pauseMotionPlayback();
  revokeUserImageSrc();
  revokeUserVideoSrc();

  state.userImage = null;
  state.userImagePreview = null;
  state.userImageSrc = '';
  state.userImagePreviewSrc = '';
  state.userImageName = '';
  state.userVideo = null;
  state.userVideoSrc = '';
  state.userVideoName = '';
  state.uploadedMedia = null;
  state.staticExport.renderMode = 'static_image';
  state.staticExport.customPlacement = null;
  state.staticExport.customDraftPlacement = null;
  state.staticExport.customEditorOpen = false;
  if (state.staticExport.mode === 'custom') {
    state.staticExport.customModeFallback = 'fit';
    state.staticExport.mode = 'fit';
  }

  syncStaticRenderModeFromMedia();
  syncStaticVideoStoryboardFromMedia();

  resetHomeCompositePreviews();
  clearHomeCompositePreviewLayers();
  state.detailPreviewReadyKey = '';
  setNotice('Uploaded media removed', 'success');
  render();
}

function pauseMotionPlayback(options: { preserveFrame?: boolean } = {}): void {
  motionPlaybackToken += 1;
  if (motionPlaybackRequestId !== null) {
    window.cancelAnimationFrame(motionPlaybackRequestId);
    motionPlaybackRequestId = null;
  }
  motionPlaybackStartAt = 0;
  motionPlaybackStartTime = 0;
  motionPlaybackLastRenderedFrameIndex = -1;
  motionPlaybackLastUserFrameIndex = -1;
  motionPlaybackRenderInvalidated = true;

  state.motionPlaying = false;

  if (!options.preserveFrame) {
    state.motionFrameIndex = 0;
    motionPlaybackCurrentTime = 0;
    motionPlaybackDuration = 0;
    motionTimelineResumeAfterSeek = false;
    motionTimelineSeekGeneration += 1;
    motionTimelineSeekPromise = null;
  }
}

function formatMotionTimelineTime(value: number): string {
  const totalSeconds = Math.max(0, Math.floor(Number.isFinite(value) ? value : 0));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function getMotionTimelineProgressValue(): number {
  if (!Number.isFinite(motionPlaybackDuration) || motionPlaybackDuration <= 0) {
    return 0;
  }
  return Math.round(clamp(motionPlaybackCurrentTime / motionPlaybackDuration, 0, 1) * 1000);
}

function syncMotionTimelineControls(
  assets?: LoadedMotionAssets,
  playbackTime = motionPlaybackCurrentTime
): void {
  const scrubber = document.getElementById('preview-motion-scrubber') as HTMLInputElement | null;
  const currentTimeNode = document.getElementById('preview-motion-current-time');
  const durationNode = document.getElementById('preview-motion-duration');

  if (assets) {
    motionPlaybackDuration = Math.max(
      1 / Math.max(1, assets.tracking.fps),
      getEffectiveMotionPlaybackDurationForSources(
        assets,
        getInteractiveMotionSource(),
        getInteractiveMotionSource2(),
        getInteractiveMotionSource3()
      )
    );
  }

  const duration =
    Number.isFinite(motionPlaybackDuration) && motionPlaybackDuration > 0 ? motionPlaybackDuration : 0;
  motionPlaybackCurrentTime = duration > 0 ? clamp(playbackTime, 0, duration) : 0;
  const progress = duration > 0 ? clamp(motionPlaybackCurrentTime / duration, 0, 1) : 0;

  if (scrubber) {
    scrubber.disabled = duration <= 0;
    scrubber.value = String(Math.round(progress * 1000));
    scrubber.style.setProperty('--motion-timeline-progress', `${progress * 100}%`);
    scrubber.setAttribute(
      'aria-valuetext',
      `${formatMotionTimelineTime(motionPlaybackCurrentTime)} of ${formatMotionTimelineTime(duration)}`
    );
  }
  if (currentTimeNode) {
    currentTimeNode.textContent = formatMotionTimelineTime(motionPlaybackCurrentTime);
  }
  if (durationNode) {
    durationNode.textContent = formatMotionTimelineTime(duration);
  }
}

function syncMotionPlaybackControls(): void {
  const rowButton = document.getElementById('motion-playback-toggle');
  if (rowButton instanceof HTMLButtonElement) {
    rowButton.textContent = state.motionPlaying ? 'Pause' : 'Play';
    rowButton.disabled = false;
  }

  const previewButton = document.getElementById('preview-motion-playback-toggle');
  if (previewButton instanceof HTMLButtonElement) {
    previewButton.classList.toggle('is-playing', state.motionPlaying);
    previewButton.setAttribute('aria-label', state.motionPlaying ? 'Pause preview' : 'Play preview');
    previewButton.setAttribute('title', state.motionPlaying ? 'Pause preview' : 'Play preview');
  }
  syncMotionTimelineControls();
}

async function startMotionPlayback(): Promise<void> {
  const selected = getSelectedTemplate();
  if (!selected?.isVideo || state.motionPlaying) {
    return;
  }

  try {
    const token = ++motionPlaybackToken;
    const previewAssets = await ensurePreviewMotionAssets(selected);
    const currentSelected = getSelectedTemplate();
    if (token !== motionPlaybackToken || !currentSelected?.isVideo || currentSelected.id !== selected.id) {
      return;
    }

    const startFrame = Math.max(0, Math.min(state.motionFrameIndex, previewAssets.tracking.frameCount - 1));
    motionPlaybackDuration = Math.max(
      1 / Math.max(1, previewAssets.tracking.fps),
      getEffectiveMotionPlaybackDurationForSources(
        previewAssets,
        getInteractiveMotionSource(),
        getInteractiveMotionSource2(),
        getInteractiveMotionSource3()
      )
    );
    const frameTime = getMotionFrameTime(previewAssets.tracking, startFrame);
    const hasCurrentTimelineTime =
      Number.isFinite(motionPlaybackCurrentTime) &&
      motionPlaybackCurrentTime >= 0 &&
      motionPlaybackCurrentTime <= motionPlaybackDuration;
    motionPlaybackStartTime = hasCurrentTimelineTime ? motionPlaybackCurrentTime : frameTime;
    motionPlaybackStartAt = performance.now();
    motionPlaybackLastRenderedFrameIndex = -1;
    motionPlaybackLastUserFrameIndex = -1;
    motionPlaybackRenderInvalidated = true;

    state.motionPlaying = true;
    syncMotionPlaybackControls();

    scheduleMotionPlaybackTick(token, previewAssets, selected.id);
  } catch (error) {
    setNotice(error instanceof Error ? error.message : 'Motion preview failed', 'error');
    render();
  }
}

async function toggleMotionPlayback(): Promise<void> {
  const selected = getSelectedTemplate();
  if (!selected?.isVideo) {
    return;
  }

  if (state.motionPlaying) {
    pauseMotionPlayback({ preserveFrame: true });
    syncMotionPlaybackControls();
    return;
  }

  await startMotionPlayback();
}

function scheduleMotionPlaybackTick(
  token: number,
  previewAssets: LoadedMotionAssets,
  templateId: string
): void {
  motionPlaybackRequestId = window.requestAnimationFrame(() => {
    void tickMotionPlayback(token, previewAssets, templateId);
  });
}

async function tickMotionPlayback(
  token: number,
  previewAssets: LoadedMotionAssets,
  templateId: string
): Promise<void> {
  const motionSource = getInteractiveMotionSource();
  if (token !== motionPlaybackToken || !state.motionPlaying) {
    return;
  }

  const selected = getSelectedTemplate();
  if (!selected?.isVideo || selected.id !== templateId) {
    pauseMotionPlayback({ preserveFrame: true });
    return;
  }
  const motionSource2 = getInteractiveMotionSource2(selected);
  const motionSource3 = getInteractiveMotionSource3(selected);

  const canvas = document.getElementById('preview-canvas') as HTMLCanvasElement | null;
  if (!canvas) {
    pauseMotionPlayback({ preserveFrame: true });
    return;
  }

  syncPreviewCanvasResolution(canvas);
  const elapsedSeconds = Math.max(0, (performance.now() - motionPlaybackStartAt) / 1000);
  const playbackDuration = Math.max(
    1 / Math.max(1, previewAssets.tracking.fps),
    getEffectiveMotionPlaybackDurationForSources(
      previewAssets,
      motionSource,
      motionSource2,
      motionSource3
    )
  );
  const playbackTime = (motionPlaybackStartTime + elapsedSeconds) % playbackDuration;
  motionPlaybackDuration = playbackDuration;
  motionPlaybackCurrentTime = playbackTime;
  const trackingTime = getMotionSequencePlaybackTime(
    previewAssets.tracking,
    playbackTime,
    previewAssets.template.motionLoop === true
  );
  const frameIndex = getMotionFrameIndex(previewAssets.tracking, trackingTime);
  state.motionFrameIndex = frameIndex;

  // Source video is 30 FPS while requestAnimationFrame commonly runs at 60 or
  // 120 Hz. Re-compositing the same decoded frame wastes most of the preview
  // budget, especially when matte textures are active. Control changes mark
  // the current frame dirty so their result still appears immediately.
  const userFrameIndex = motionSource instanceof HTMLVideoElement ||
    motionSource2 instanceof HTMLVideoElement ||
    motionSource3 instanceof HTMLVideoElement
    ? Math.floor(playbackTime * 30)
    : -1;
  if (frameIndex === motionPlaybackLastRenderedFrameIndex && userFrameIndex === motionPlaybackLastUserFrameIndex && !motionPlaybackRenderInvalidated) {
    syncMotionTimelineControls(previewAssets, playbackTime);
    scheduleMotionPlaybackTick(token, previewAssets, templateId);
    return;
  }
  motionPlaybackRenderInvalidated = false;

  try {
    const atlasTime = getMotionAtlasFrameTime(previewAssets.tracking, frameIndex);
    const colorMatteAdjustments = getMotionColorMatteAdjustments();
    await Promise.all([
      seekVideo(getActiveAtlasVideo(previewAssets, colorMatteAdjustments), atlasTime),
      isMotionColorMatteActive(previewAssets.template)
        ? seekMotionColorMatte(previewAssets, atlasTime, colorMatteAdjustments)
        : Promise.resolve(),
      seekUserVideoAtPlaybackTime(motionSource, playbackTime),
      seekUserVideoAtPlaybackTime(motionSource2, playbackTime),
      seekUserVideoAtPlaybackTime(motionSource3, playbackTime)
    ]);
    if (token !== motionPlaybackToken || !state.motionPlaying) {
      return;
    }

    const motionPreviewRenderSize = getMotionPreviewRenderSize(selected, canvas.width, true);
    const motionPreviewGridSize = getMotionPreviewGridSize(true);
    const motionWarpMode = getCurrentStaticExportMode();
    const squareMotionCanvas = renderMotionFrame(previewAssets, motionSource, frameIndex, {
      userSource2: motionSource2,
      userSource3: motionSource3,
      renderSize: motionPreviewRenderSize,
      gridX: motionPreviewGridSize,
      gridY: motionPreviewGridSize,
      diffuse: getDiffuseAdjustments(),
      background: getBackgroundAdjustments(),
      reflection: getReflectionAdjustments(),
      screenOverlay: getScreenOverlayAdjustments(),
      colorMatte: getMotionColorMatteAdjustments(),
      useAlternateMask: shouldUseAlternateMask(),
      customPlacement: motionWarpMode === 'custom' ? getEffectiveCustomPlacement() : null,
      customPlacement2: state.staticExport.mode2 === 'custom' ? getEffectiveCustomPlacement(2) : null,
      customPlacement3: state.staticExport.mode3 === 'custom' ? getEffectiveCustomPlacement(3) : null,
      warpMode: motionWarpMode,
      warpMode2: state.staticExport.mode2,
      warpMode3: state.staticExport.mode3,
      realtime: true
    });
    const previewCanvas = createMotionPreviewViewportCanvas(
      squareMotionCanvas,
      selected,
      canvas,
      motionPreviewRenderSize
    );

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    paintPreviewCanvas(ctx, canvas, selected, previewCanvas);
    motionPlaybackLastRenderedFrameIndex = frameIndex;
    motionPlaybackLastUserFrameIndex = userFrameIndex;
    updateMotionDiagnosticsPanel(previewAssets, frameIndex);
    syncMotionTimelineControls(previewAssets, playbackTime);
    markDetailPreviewReady(getDetailPreviewKey(selected));
  } catch (error) {
    pauseMotionPlayback({ preserveFrame: true });
    setNotice(error instanceof Error ? error.message : 'Motion preview failed', 'error');
    render();
    return;
  }

  scheduleMotionPlaybackTick(token, previewAssets, templateId);
}

async function seekMotionTimeline(progress: number): Promise<void> {
  const selected = getSelectedTemplate();
  if (!selected?.isVideo) {
    return;
  }

  const generation = ++motionTimelineSeekGeneration;
  const selectedId = selected.id;
  const assets = await ensurePreviewMotionAssets(selected);
  const currentSelected = getSelectedTemplate();
  if (
    generation !== motionTimelineSeekGeneration ||
    !currentSelected?.isVideo ||
    currentSelected.id !== selectedId
  ) {
    return;
  }

  motionPlaybackDuration = Math.max(
    1 / Math.max(1, assets.tracking.fps),
    getEffectiveMotionPlaybackDurationForSources(
      assets,
      getInteractiveMotionSource(),
      getInteractiveMotionSource2(),
      getInteractiveMotionSource3()
    )
  );
  motionPlaybackCurrentTime = clamp(progress, 0, 1) * motionPlaybackDuration;
  const trackingTime = getMotionSequencePlaybackTime(
    assets.tracking,
    motionPlaybackCurrentTime,
    assets.template.motionLoop === true
  );
  state.motionFrameIndex = getMotionFrameIndex(assets.tracking, trackingTime);
  syncMotionTimelineControls(assets, motionPlaybackCurrentTime);
  schedulePreviewRender();
}

function toUint8Array(input: ArrayBuffer | Uint8Array | number[]): Uint8Array {
  if (input instanceof Uint8Array) {
    return input;
  }
  if (input instanceof ArrayBuffer) {
    return new Uint8Array(input);
  }
  return new Uint8Array(input);
}

function isSliderDragActive(): boolean {
  return sliderDragActive;
}

function markSliderDragging(): void {
  sliderDragActive = true;
  if (sliderDragResetTimer !== null) {
    window.clearTimeout(sliderDragResetTimer);
  }
  sliderDragResetTimer = window.setTimeout(() => {
    sliderDragActive = false;
    sliderDragResetTimer = null;
    // Final full-quality render after the user stops dragging.
    schedulePreviewRender();
  }, 220);
}

function bindSliderDragTracking(): void {
  // Single delegated capture listener — flips the drag flag whenever any
  // <input type=range> fires an  event. Resets 220ms after the
  // last event, then triggers a final full-quality preview re-render.
  document.addEventListener(
    'input',
    (event) => {
      const target = event.target as HTMLElement | null;
      if (target instanceof HTMLInputElement && target.type === 'range') {
        markSliderDragging();
      }
    },
    { capture: true, passive: true }
  );
}

function schedulePreviewRender(cameraOnly = false): void {
  if (!cameraOnly) dynamicZoomPreviewMaster = null;
  const cached = cameraOnly ? dynamicZoomPreviewMaster : null;
  const selected = getSelectedTemplate();
  if (cached && selected?.id === cached.templateId && !state.dynamicZoomEditorOpen) {
    const canvas = document.getElementById('preview-canvas') as HTMLCanvasElement | null;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      syncPreviewCanvasResolution(canvas);
      paintPreviewCanvas(ctx, canvas, selected, cached.image, state.staticExport.format === '1:1z' ? ZOOM_FACTOR_1_1Z : 1);
      return;
    }
  }
  previewRenderPending = true;
  if (previewRenderScheduled) {
    return;
  }

  previewRenderScheduled = true;
  window.requestAnimationFrame(() => {
    previewRenderScheduled = false;
    void flushPreviewRenderQueue();
  });
}

async function flushPreviewRenderQueue(): Promise<void> {
  if (previewRenderInFlight) {
    return;
  }

  previewRenderInFlight = true;
  try {
    while (previewRenderPending) {
      previewRenderPending = false;
      await drawPreview();
    }
  } finally {
    previewRenderInFlight = false;
    if (previewRenderPending) {
      schedulePreviewRender();
    }
  }
}

async function drawPreview(): Promise<void> {
  // The modal owns the same media and has its own coalesced renderer. Rendering
  // the obscured main preview would compete for decoders and Canvas resources.
  if (state.dynamicZoomEditorOpen) return;
  const canvas = document.getElementById('preview-canvas') as HTMLCanvasElement | null;
  if (!canvas) {
    return;
  }
  const previewDropzone = document.getElementById('preview-dropzone') as HTMLDivElement | null;
  const generation = ++previewDrawGeneration;
  cancelScheduledPreviewUpgrade();

  syncPreviewCanvasResolution(canvas);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }

  const selected = getSelectedTemplate();
  if (!selected) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    return;
  }
  const detailPreviewKey = previewDropzone?.dataset.previewKey || getDetailPreviewKey(selected);

  if (selected.isVideo) {
    try {
      const motionAssets = await ensurePreviewMotionAssets(selected);
      if (generation !== previewDrawGeneration) {
        return;
      }

      const interactiveMotionSource = getInteractiveMotionSource();
      const interactiveMotionSource2 = hasTemplateScreen(selected, 2)
        ? getInteractiveMotionSource2()
        : null;
      const interactiveMotionSource3 = hasTemplateScreen(selected, 3)
        ? getInteractiveMotionSource3()
        : null;
      const timelineDuration = Math.max(
        1 / Math.max(1, motionAssets.tracking.fps),
        getEffectiveMotionPlaybackDurationForSources(
          motionAssets,
          interactiveMotionSource,
          interactiveMotionSource2,
          interactiveMotionSource3
        )
      );
      motionPlaybackDuration = timelineDuration;
      motionPlaybackCurrentTime = clamp(motionPlaybackCurrentTime, 0, timelineDuration);
      const trackingTime = getMotionSequencePlaybackTime(
        motionAssets.tracking,
        motionPlaybackCurrentTime,
        motionAssets.template.motionLoop === true
      );
      const frameIndex = getMotionFrameIndex(motionAssets.tracking, trackingTime);
      const atlasTime = getMotionAtlasFrameTime(motionAssets.tracking, frameIndex);
      const colorMatteAdjustments = getMotionColorMatteAdjustments();
      await Promise.all([
        seekVideo(getActiveAtlasVideo(motionAssets, colorMatteAdjustments), atlasTime),
        isMotionColorMatteActive(motionAssets.template)
          ? seekMotionColorMatte(motionAssets, atlasTime, colorMatteAdjustments)
          : Promise.resolve(),
        seekUserVideoAtPlaybackTime(interactiveMotionSource, motionPlaybackCurrentTime),
        seekUserVideoAtPlaybackTime(interactiveMotionSource2, motionPlaybackCurrentTime),
        seekUserVideoAtPlaybackTime(interactiveMotionSource3, motionPlaybackCurrentTime)
      ]);
      if (generation !== previewDrawGeneration) {
        return;
      }
      // Keep the previous frame visible while seek/decode is in progress. The
      // visible canvas is replaced only after the next frame is fully composed,
      // so slow mobile seeks cannot expose a cleared viewport between frames.
      const useInteractivePreviewQuality = state.motionPlaying || isSliderDragActive();
      const motionPreviewRenderSize = getMotionPreviewRenderSize(
        selected,
        canvas.width,
        useInteractivePreviewQuality
      );
      const motionPreviewGridSize = getMotionPreviewGridSize(useInteractivePreviewQuality);
      const motionWarpMode = getCurrentStaticExportMode();
      const squareMotionCanvas = renderMotionFrame(motionAssets, interactiveMotionSource, frameIndex, {
        userSource2: interactiveMotionSource2,
        userSource3: interactiveMotionSource3,
        renderSize: motionPreviewRenderSize,
        gridX: motionPreviewGridSize,
        gridY: motionPreviewGridSize,
        diffuse: getDiffuseAdjustments(),
        background: getBackgroundAdjustments(),
        reflection: getReflectionAdjustments(),
        screenOverlay: getScreenOverlayAdjustments(),
        colorMatte: getMotionColorMatteAdjustments(),
        useAlternateMask: shouldUseAlternateMask(),
        customPlacement: motionWarpMode === 'custom' ? getEffectiveCustomPlacement() : null,
        customPlacement2: state.staticExport.mode2 === 'custom' ? getEffectiveCustomPlacement(2) : null,
        customPlacement3: state.staticExport.mode3 === 'custom' ? getEffectiveCustomPlacement(3) : null,
        warpMode: motionWarpMode,
        warpMode2: state.staticExport.mode2,
        warpMode3: state.staticExport.mode3,
        realtime: useInteractivePreviewQuality
      });
      const motionCanvas = createMotionPreviewViewportCanvas(
        squareMotionCanvas,
        selected,
        canvas,
        motionPreviewRenderSize
      );

      state.motionFrameIndex = frameIndex;
      paintPreviewCanvas(ctx, canvas, selected, motionCanvas);
      updateMotionDiagnosticsPanel(motionAssets, frameIndex);
      syncMotionTimelineControls(motionAssets, motionPlaybackCurrentTime);
      markDetailPreviewReady(detailPreviewKey);
      return;
    } catch (error) {
      markDetailPreviewReady(detailPreviewKey);
      setNotice(error instanceof Error ? error.message : 'Motion render failed', 'error');
      return;
    }
  }

  const interactiveStaticSource = getInteractiveStaticSource();
  const interactiveStaticSource2 = hasTemplateScreen(selected, 2) ? getInteractiveStaticSource2() : null;
  const interactiveStaticSource3 = hasTemplateScreen(selected, 3) ? getInteractiveStaticSource3() : null;
  const staticBackgroundActive = !state.backgroundTransparent && supportsStaticBackgroundComposer(selected) &&
    isStaticBackgroundActive(getStaticBackgroundComposition());
  const deviceShadowActive = isDeviceShadowActive(selected);
  if (!interactiveStaticSource && !interactiveStaticSource2 && !interactiveStaticSource3 && !staticBackgroundActive && !deviceShadowActive) {
    try {
      const previewUrl = getTemplateDetailPreviewUrl(selected);
      const previewImage = await loadImageFromUrl(previewUrl);
      if (generation !== previewDrawGeneration) {
        return;
      }
      paintPreviewFallback(ctx, canvas, selected, previewImage, state.staticExport.format === '1:1z' ? ZOOM_FACTOR_1_1Z : 1);
      return;
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Preview load failed', 'error');
      return;
    }
  }

  const previewRenderSize = getPreviewRenderSize(selected, canvas.width);
  const hasFootage = getStaticPreviewClips().length > 0;
  const quickPreviewRenderSize = hasFootage ? previewRenderSize : Math.min(previewRenderSize, selected.editSize || PREVIEW_RENDER_SIZE);
  const customPlacement = state.staticExport.mode === 'custom' ? getEffectiveCustomPlacement() : null;

  try {
    const previewStarted = performance.now();
    const [previewAssets, staticBackground] = await Promise.all([
      ensurePreviewAssets(selected),
      ensureStaticBackgroundComposition(selected, quickPreviewRenderSize)
    ]);
    if (generation !== previewDrawGeneration) {
      return;
    }
    const quickMaster = renderMaster(previewAssets, interactiveStaticSource, {
      userImage2: interactiveStaticSource2,
      userImage3: interactiveStaticSource3,
      renderSize: quickPreviewRenderSize,
      gridX: PREVIEW_GRID_SIZE,
      gridY: PREVIEW_GRID_SIZE,
      diffuse: getDiffuseAdjustments(),
      background: getBackgroundAdjustments(),
      staticBackground,
      separateCameraBackground: state.dynamicZoom.enabled && normalizeBackgroundMotion(state.dynamicZoom.backgroundMotion) !== 'stick',
      deviceShadow: getDeviceShadowAdjustments(selected),
      reflection: getReflectionAdjustments(),
      screenOverlay: getScreenOverlayAdjustments(),
      useAlternateMask: shouldUseAlternateMask(),
      customPlacement,
      customPlacement2: state.staticExport.mode2 === 'custom' ? getEffectiveCustomPlacement(2) : null,
      customPlacement3: state.staticExport.mode3 === 'custom' ? getEffectiveCustomPlacement(3) : null,
      warpMode: state.staticExport.mode,
      warpMode2: state.staticExport.mode2,
      warpMode3: state.staticExport.mode3
    });
    paintPreviewCanvas(ctx, canvas, selected, quickMaster, state.staticExport.format === '1:1z' ? ZOOM_FACTOR_1_1Z : 1);
    if (hasFootage && (staticPreviewPlaying || isSliderDragActive())) staticFootagePreviewQuality.record(performance.now() - previewStarted, isMobilePreviewDevice() ? 768 : 1280);
    schedulePreviewUpgrade(selected.id, generation);
    markDetailPreviewReady(detailPreviewKey);
  } catch (error) {
    markDetailPreviewReady(detailPreviewKey);
    setNotice(error instanceof Error ? error.message : 'Render failed', 'error');
  }
}

function cancelScheduledPreviewUpgrade(): void {
  if (previewUpgradeTimer !== null) {
    window.clearTimeout(previewUpgradeTimer);
    previewUpgradeTimer = null;
  }
  previewUpgradeToken += 1;
}

function schedulePreviewUpgrade(selectedId: string, generation: number): void {
  if (isMobilePreviewDevice() || staticPreviewPlaying || isSliderDragActive()) {
    cancelScheduledPreviewUpgrade();
    return;
  }

  const token = ++previewUpgradeToken;
  if (previewUpgradeTimer !== null) {
    window.clearTimeout(previewUpgradeTimer);
  }

  previewUpgradeTimer = window.setTimeout(() => {
    previewUpgradeTimer = null;
    void upgradePreviewToOriginal(selectedId, generation, token);
  }, getStaticPreviewClips().length > 0 ? 180 : SHARP_PREVIEW_IDLE_MS);
}

async function upgradePreviewToOriginal(selectedId: string, generation: number, token: number): Promise<void> {
  if (token !== previewUpgradeToken || generation !== previewDrawGeneration) {
    return;
  }

  const selected = getSelectedTemplate();
  if (!selected || selected.id !== selectedId) {
    return;
  }

  const canvas = document.getElementById('preview-canvas') as HTMLCanvasElement | null;
  if (!canvas) {
    return;
  }

  const ctx = canvas.getContext('2d');
  const interactiveStaticSource = getInteractiveStaticSource();
  const interactiveStaticSource2 = hasTemplateScreen(selected, 2) ? getInteractiveStaticSource2() : null;
  const interactiveStaticSource3 = hasTemplateScreen(selected, 3) ? getInteractiveStaticSource3() : null;
  const staticBackgroundActive = !state.backgroundTransparent && supportsStaticBackgroundComposer(selected) &&
    isStaticBackgroundActive(getStaticBackgroundComposition());
  const deviceShadowActive = isDeviceShadowActive(selected);
  if (!ctx || (!interactiveStaticSource && !interactiveStaticSource2 && !interactiveStaticSource3 && !staticBackgroundActive && !deviceShadowActive)) {
    return;
  }

  try {
    const renderSize = getPreviewRenderSize(selected, canvas.width);
    const [assets, staticBackground] = await Promise.all([
      ensureExportAssets(selected),
      ensureStaticBackgroundComposition(selected, renderSize)
    ]);
    if (token !== previewUpgradeToken || generation !== previewDrawGeneration) {
      return;
    }

    const master = renderMaster(assets, interactiveStaticSource, {
      userImage2: interactiveStaticSource2,
      userImage3: interactiveStaticSource3,
      renderSize,
      gridX: PREVIEW_GRID_SIZE,
      gridY: PREVIEW_GRID_SIZE,
      diffuse: getDiffuseAdjustments(),
      background: getBackgroundAdjustments(),
      staticBackground,
      separateCameraBackground: state.dynamicZoom.enabled && normalizeBackgroundMotion(state.dynamicZoom.backgroundMotion) !== 'stick',
      deviceShadow: getDeviceShadowAdjustments(selected),
      reflection: getReflectionAdjustments(),
      screenOverlay: getScreenOverlayAdjustments(),
      useAlternateMask: shouldUseAlternateMask(),
      customPlacement: state.staticExport.mode === 'custom' ? getEffectiveCustomPlacement() : null,
      customPlacement2: state.staticExport.mode2 === 'custom' ? getEffectiveCustomPlacement(2) : null,
      customPlacement3: state.staticExport.mode3 === 'custom' ? getEffectiveCustomPlacement(3) : null,
      warpMode: state.staticExport.mode,
      warpMode2: state.staticExport.mode2,
      warpMode3: state.staticExport.mode3
    });
    paintPreviewCanvas(ctx, canvas, selected, master, state.staticExport.format === '1:1z' ? ZOOM_FACTOR_1_1Z : 1);
  } catch {
    // Leave the fast preview in place if the original assets fail to load.
  }
}

function getPreviewRenderSize(selected: MockupTemplate, canvasPixelSize: number): number {
  const editSize = selected.editSize || PREVIEW_RENDER_SIZE;
  if (!selected.isVideo && getStaticPreviewClips().length > 0) {
    const mobile = isMobilePreviewDevice();
    if (staticPreviewPlaying || isSliderDragActive()) return Math.min(staticFootagePreviewQuality.size, mobile ? 768 : 1280);
    const aspect = getMotionPreviewAspectRatio(selected);
    const longEdge = Math.max(canvasPixelSize, canvasPixelSize / aspect);
    const zoom = state.dynamicZoom.enabled ? getViewportAt(state.dynamicZoom, dynamicZoomTime, getDynamicZoomDuration(selected)).zoom : 1;
    return getPausedFootageSize(Math.max(longEdge, staticFootagePreviewQuality.size), zoom * (state.staticExport.format === '1:1z' ? 1.5 : 1), mobile ? 1024 : 1536);
  }
  // Mobile slider-drag fast path: render preview at a smaller size while the
  // user is actively scrubbing a slider. Reduces draw time so input events
  // don't queue up behind heavy reflection-blur work and the thumb stays smooth.
  if (isSliderDragActive()) {
    return Math.min(editSize, isMobilePreviewDevice() ? 384 : 640);
  }
  if (canvasPixelSize <= editSize) {
    return editSize;
  }

  return Math.max(editSize, Math.min(selected.outputSize, canvasPixelSize));
}

function getMotionPreviewRenderSize(
  selected: MockupTemplate,
  canvasPixelSize: number,
  isPlayback: boolean
): number {
  const baseSize = getPreviewRenderSize(selected, canvasPixelSize);
  // Already-narrow path: getPreviewRenderSize will return 384 during drag on
  // mobile. Cap it once more here so motion playback stays responsive too.
  if (isSliderDragActive() && isMobilePreviewDevice()) {
    return Math.min(baseSize, 320);
  }
  if (!isPlayback) {
    return baseSize;
  }

  return Math.min(
    baseSize,
    isMobilePreviewDevice() ? MOTION_PLAYBACK_RENDER_MAX_MOBILE : MOTION_PLAYBACK_RENDER_MAX_DESKTOP
  );
}

function getMotionPreviewGridSize(isPlayback: boolean): number {
  const isMobile = isMobilePreviewDevice();
  if (isPlayback) {
    return isMobile ? MOTION_PLAYBACK_GRID_MOBILE : MOTION_PLAYBACK_GRID_DESKTOP;
  }
  return isMobile ? MOTION_PREVIEW_GRID_MOBILE : MOTION_PREVIEW_GRID_DESKTOP;
}

function isMobilePreviewDevice(): boolean {
  const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const narrowViewport = Math.max(window.innerWidth || 0, document.documentElement.clientWidth || 0) <= 900;
  return coarse || narrowViewport;
}

function syncPreviewCanvasResolution(canvas: HTMLCanvasElement): void {
  const displayWidth = computeStablePreviewDisplayWidth(canvas);
  const aspectRatio = getMotionPreviewAspectRatio(getSelectedTemplate());
  const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
  let pixelWidth = Math.max(320, Math.min(1280, Math.round(displayWidth * dpr)));
  let pixelHeight = Math.max(180, Math.round(pixelWidth / aspectRatio));

  if (pixelHeight > 1280) {
    pixelHeight = 1280;
    pixelWidth = Math.max(320, Math.round(pixelHeight * aspectRatio));
  }

  if (canvas.width === pixelWidth && canvas.height === pixelHeight) {
    return;
  }

  canvas.width = pixelWidth;
  canvas.height = pixelHeight;
}

function computeStablePreviewDisplayWidth(canvas: HTMLCanvasElement): number {
  const liveWidth = Math.max(
    1,
    Math.round(canvas.clientWidth || canvas.getBoundingClientRect().width || 320)
  );
  if (!isMobilePreviewDevice()) {
    return liveWidth;
  }
  // On mobile the .preview-dropzone shrinks via the --mobile-preview-width CSS
  // variable while scrolled. The parent .detail-preview-column keeps its
  // natural width, so we size the bitmap to that — browser then only ever
  // downscales the bitmap (visual quality stays sharp), and after expand the
  // bitmap is exactly the right resolution for the full-size display.
  const column = canvas.closest('.detail-preview-column') as HTMLElement | null;
  if (!column) {
    return liveWidth;
  }
  const style = window.getComputedStyle(column);
  const padding =
    Number.parseFloat(style.paddingLeft || '0') + Number.parseFloat(style.paddingRight || '0');
  const expanded = Math.max(0, column.clientWidth - padding);
  return Math.max(liveWidth, expanded);
}

function createMotionPreviewViewportCanvas(
  source: CanvasImageSource,
  template: MockupTemplate,
  targetCanvas: HTMLCanvasElement,
  squareRenderSize: number
): HTMLCanvasElement {
  const resizeMode = getActiveMotionResizeMode(template);
  return createMotionViewportCanvas(source, resizeMode, {
    squareRenderSize,
    targetWidth: targetCanvas.width,
    targetHeight: targetCanvas.height
  });
}

function paintPreviewCanvas(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  template: MockupTemplate,
  image: CanvasImageSource,
  zoomFactor = 1
): void {
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  drawPreviewBackdrop(ctx, canvas.width, canvas.height, template);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  // Camera motion reads cached foreground/background layers after compositing.
  if (!template.isVideo && state.dynamicZoom.enabled && !isBackgroundAnimationActive() && getDynamicZoomClips().length === 0 && image instanceof HTMLCanvasElement) {
    // A camera move over a still only changes the crop. Keep one composed
    // master; ordinary control changes invalidate it in schedulePreviewRender.
    dynamicZoomPreviewMaster = { templateId: template.id, image };
  }
  if (!template.isVideo && state.dynamicZoom.enabled && image instanceof HTMLCanvasElement) {
    drawDynamicZoomFrame(ctx, image, getViewportAt(state.dynamicZoom, dynamicZoomTime, getDynamicZoomDuration(template)),
      getStaticFraming(template), normalizeBackgroundMotion(state.dynamicZoom.backgroundMotion));
    return;
  }
  const camera = getDynamicZoomSourceRect(image);
  if (camera) {
    ctx.drawImage(image, camera.x, camera.y, camera.width, camera.height, 0, 0, canvas.width, canvas.height);
    return;
  }
  if (!template.isVideo && 'width' in image && 'height' in image) {
    const crop = getStaticFrameRect(Number(image.width), Number(image.height), getStaticFraming(template));
    ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
    return;
  }
  if (zoomFactor !== 1 && 'width' in image && 'height' in image) {
    const imgW = (image as { width: number }).width;
    const imgH = (image as { height: number }).height;
    const srcW = imgW / zoomFactor;
    const srcH = imgH / zoomFactor;
    const srcX = (imgW - srcW) / 2;
    const srcY = (imgH - srcH) / 2;
    ctx.drawImage(image, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);
  } else {
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  }
}

/** The window the camera is looking through right now, or null when Dynamic
 *  Zoom is off or has nothing placed. */
function getDynamicZoomSourceRect(
  image: CanvasImageSource
): { x: number; y: number; width: number; height: number } | null {
  const zoom = state.dynamicZoom;
  const template = getSelectedTemplate();
  if (!template || template.isVideo || !zoom.enabled || zoom.keys.length === 0) {
    return null;
  }
  if (!('width' in image) || !('height' in image)) {
    return null;
  }
  const width = Number((image as { width: number }).width);
  const height = Number((image as { height: number }).height);
  if (!(width > 0) || !(height > 0)) {
    return null;
  }
  return getFramedCameraRect(getViewportAt(zoom, dynamicZoomTime, getDynamicZoomDuration(template)), width, height, getStaticFraming(template));
}

function getStaticFraming(template: MockupTemplate): StaticFraming {
  return { format: state.staticExport.format, safeRect: template.safeRect, referenceSize: template.outputSize };
}

function paintPreviewFallback(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  template: MockupTemplate,
  image: CanvasImageSource,
  zoomFactor = 1
): void {
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  drawPreviewBackdrop(ctx, canvas.width, canvas.height, template);
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  const destRect = { dx: 0, dy: 0, dw: canvas.width, dh: canvas.height };
  if (!template.isVideo) {
    const source = image as CanvasImageSource & { width?: number; height?: number; naturalWidth?: number; naturalHeight?: number };
    const width = source.naturalWidth || source.width || 1;
    const height = source.naturalHeight || source.height || 1;
    const crop = getStaticFrameRect(width, height, getStaticFraming(template));
    drawImageWithDiffuseAdjustments(ctx, image, getDiffuseAdjustments(), { sx: crop.x, sy: crop.y, sw: crop.width, sh: crop.height }, destRect);
    ctx.restore();
    return;
  }
  if (zoomFactor !== 1 && 'width' in image && 'height' in image) {
    const imgW = (image as { width: number }).width;
    const imgH = (image as { height: number }).height;
    const srcW = imgW / zoomFactor;
    const srcH = imgH / zoomFactor;
    const srcX = (imgW - srcW) / 2;
    const srcY = (imgH - srcH) / 2;
    drawImageWithDiffuseAdjustments(
      ctx,
      image,
      getDiffuseAdjustments(),
      { sx: srcX, sy: srcY, sw: srcW, sh: srcH },
      destRect
    );
  } else {
    drawImageWithDiffuseAdjustments(ctx, image, getDiffuseAdjustments(), null, destRect);
  }
  ctx.restore();
}

function updateMotionDiagnosticsPanel(assets: LoadedMotionAssets, frameIndex: number): void {
  const frameNode = document.getElementById('motion-debug-frame');
  const fpsNode = document.getElementById('motion-debug-fps');
  const boundsNode = document.getElementById('motion-debug-bounds');
  const statusNode = document.getElementById('motion-debug-status');
  const warningNode = document.getElementById('motion-debug-warning');

  if (!frameNode || !fpsNode || !boundsNode || !statusNode || !warningNode) {
    return;
  }

  const frameTotal = Math.max(1, assets.tracking.frameCount);
  const currentFrame = Math.max(0, Math.min(frameIndex, frameTotal - 1));
  frameNode.textContent = `${currentFrame + 1} / ${frameTotal}`;
  fpsNode.textContent = formatMotionFps(assets.tracking.fps);
  boundsNode.textContent = formatMotionBounds(assets.tracking);

  const warnings: string[] = [];
  if (assets.tracking.staticQuad) {
    warnings.push('Tracking is static across all frames.');
  }
  if (isMotionTrackingCompletelyOutsideFrame(assets.tracking)) {
    warnings.push('Quad stays entirely outside the 1920×1920 frame.');
  }

  statusNode.textContent = warnings.length === 0 ? 'Tracking loaded' : 'Tracking loaded with warnings';
  warningNode.textContent = warnings.join(' ');
  warningNode.classList.toggle('is-hidden', warnings.length === 0);
}

function formatMotionFps(fps: number): string {
  return `${Number.isInteger(fps) ? fps : fps.toFixed(2)} FPS`;
}

function formatMotionBounds(assets: LoadedMotionAssets['tracking']): string {
  const { minX, maxX, minY, maxY } = assets.bounds;
  return `x ${formatCoord(minX)}..${formatCoord(maxX)} · y ${formatCoord(minY)}..${formatCoord(maxY)}`;
}

function formatCoord(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function isMotionTrackingCompletelyOutsideFrame(tracking: LoadedMotionAssets['tracking']): boolean {
  return (
    tracking.bounds.maxX < 0 ||
    tracking.bounds.maxY < 0 ||
    tracking.bounds.minX > tracking.outputWidth ||
    tracking.bounds.minY > tracking.outputHeight
  );
}

function assertStaticTemplate(template: MockupTemplate): void {
  if (template.isVideo) {
    throw new Error('Static export cannot be used with MOTION template.');
  }
}

function assertMotionTemplate(template: MockupTemplate): void {
  if (!template.isVideo) {
    throw new Error('Motion export cannot be used with STATIC template.');
  }
}

function getStaticExportRenderSize(): number {
  const exportFormat = state.staticExport.format;
  const exportSize = getStaticExportSize(exportFormat, state.staticExport.resolution);
  const baseRenderSize = Math.max(exportSize.width, exportSize.height);

  if (exportFormat === '1:1z') {
    return Math.round(baseRenderSize * ZOOM_FACTOR_1_1Z);
  }

  return baseRenderSize;
}

function getStaticExportWarpGridSize(renderSize: number): number {
  if (renderSize >= 3200) {
    return 96;
  }
  if (renderSize >= 1920) {
    return 72;
  }
  return 48;
}

function scaleSafeRectToRenderSize(safeRect: SafeRect, templateOutputSize: number, renderSize: number): SafeRect {
  const sourceSize = Math.max(1, templateOutputSize || renderSize);
  if (sourceSize === renderSize) {
    return safeRect;
  }

  const scale = renderSize / sourceSize;
  return {
    x: safeRect.x * scale,
    y: safeRect.y * scale,
    w: safeRect.w * scale,
    h: safeRect.h * scale
  };
}

async function buildStaticExportCanvas(): Promise<HTMLCanvasElement> {
  const selected = getSelectedTemplate();
  if (!selected) {
    throw new Error('No mockup selected');
  }
  assertStaticTemplate(selected);
  if (
    !state.userImage &&
    !(hasTemplateScreen(selected, 2) && state.userImage2) &&
    !(hasTemplateScreen(selected, 3) && state.userImage3)
  ) {
    throw new Error('Select a mockup and upload an interface image first.');
  }

  const activeExportMode = getCurrentStaticExportMode();
  state.staticExport.mode = activeExportMode;

  const renderSize = getStaticExportRenderSize();
  const [assets, staticBackground] = await Promise.all([
    ensureExportAssets(selected),
    ensureStaticBackgroundComposition(selected, renderSize)
  ]);
  const preserveAlpha = selected.transparency || (Boolean(selected.backgroundMask) && state.backgroundTransparent);
  const isZoom = state.staticExport.format === '1:1z';
  const exportWarpGridSize = getStaticExportWarpGridSize(renderSize);
  const exportSafeRect = scaleSafeRectToRenderSize(selected.safeRect, selected.outputSize, renderSize);

  const pngCache = selected.flexibleDisplay?.mode === 'both' ? new StaticRenderCache(0) : undefined;
  const compose = (): HTMLCanvasElement => {
      const master = renderMaster(assets, state.userImage, {
        userImage2: hasTemplateScreen(selected, 2) ? state.userImage2 : null,
        userImage3: hasTemplateScreen(selected, 3) ? state.userImage3 : null,
        renderSize,
        gridX: exportWarpGridSize,
        gridY: exportWarpGridSize,
        diffuse: getDiffuseAdjustments(),
        background: getBackgroundAdjustments(),
        staticBackground,
        deviceShadow: getDeviceShadowAdjustments(selected),
        reflection: getReflectionAdjustments(),
        screenOverlay: getScreenOverlayAdjustments(),
        useAlternateMask: shouldUseAlternateMask(),
        customPlacement: activeExportMode === 'custom' ? getEffectiveCustomPlacement() : null,
        customPlacement2: state.staticExport.mode2 === 'custom' ? getEffectiveCustomPlacement(2) : null,
        customPlacement3: state.staticExport.mode3 === 'custom' ? getEffectiveCustomPlacement(3) : null,
        warpMode: activeExportMode,
        warpMode2: state.staticExport.mode2,
        warpMode3: state.staticExport.mode3,
        forExport: true,
        staticCache: pngCache
      });

      const finalExportMode: StaticExportMode = isZoom ? 'zoom' : state.staticExport.format === '1:1' ? 'fit' : 'crop';

      return renderStaticExport(master, state.staticExport.format, state.staticExport.resolution, finalExportMode, exportSafeRect, {
        preserveAlpha
      });
  };
  try {
    return pngCache ? withCanvasMemoryScope(compose) : compose();
  } finally {
    pngCache?.dispose();
  }
}

function isStaticVideoRenderActive(template: MockupTemplate | null | undefined = getSelectedTemplate()): boolean {
  return Boolean(
    !template?.isVideo &&
    state.staticExport.renderMode === 'static_video' &&
    (state.userVideo ||
      (hasTemplateScreen(template, 2) && state.userVideo2) ||
      (hasTemplateScreen(template, 3) && state.userVideo3))
  );
}

function isStaticBackgroundAnimationVideoActive(
  template: MockupTemplate | null | undefined = getSelectedTemplate()
): boolean {
  return Boolean(
    template &&
      !template.isVideo &&
      supportsStaticBackgroundComposer(template) &&
      isBackgroundAnimationActive()
  );
}

/** A still with a camera on it is a video, the same way an animated background
 *  makes it one: there is motion to record. */
function isDynamicZoomVideoActive(
  template: MockupTemplate | null | undefined = getSelectedTemplate()
): boolean {
  return Boolean(template && !template.isVideo && state.dynamicZoom.enabled && state.dynamicZoom.keys.length > 0);
}

function isStaticVideoExportActive(
  template: MockupTemplate | null | undefined = getSelectedTemplate()
): boolean {
  return (
    isStaticVideoRenderActive(template) ||
    isStaticBackgroundAnimationVideoActive(template) ||
    isDynamicZoomVideoActive(template)
  );
}

async function buildStaticVideoExport(
  signal?: AbortSignal,
  outputFileHandle?: FileSystemFileHandle | null
) {
  const selected = getSelectedTemplate();
  if (!selected) {
    throw new Error('No mockup selected');
  }
  assertStaticTemplate(selected);
  const primarySource = state.userVideo || state.userImage;
  const secondarySource = state.userVideo2 || state.userImage2;
  const tertiarySource = state.userVideo3 || state.userImage3;
  if (
    !primarySource &&
    !(hasTemplateScreen(selected, 2) && secondarySource) &&
    !(hasTemplateScreen(selected, 3) && tertiarySource)
  ) {
    throw new Error('Upload media for at least one screen first.');
  }

  if (!isStaticVideoExportSupported()) {
    throw new Error('MP4 rendering is unavailable in this browser.');
  }

  const activeExportMode = getCurrentStaticExportMode();
  state.staticExport.mode = activeExportMode;

  const assets = await waitForRenderOperation(
    ensureExportAssets(selected),
    {
      signal,
      label: 'Static template asset loading'
    }
  );
  touchActiveVideoExport('static-assets-ready');
  const preserveAlpha = selected.transparency || (Boolean(selected.backgroundMask) && state.backgroundTransparent);
  const isZoom = state.staticExport.format === '1:1z';
  const renderSize = getStaticExportRenderSize();
  const dynamicCameraActive = isDynamicZoomVideoActive(selected);
  const dynamicCameraDuration = getDynamicZoomDuration(selected);
  // The background is composed at the same size as everything else in the
  // frame; sized to the export instead, it would be the one soft layer under a
  // sharp device the moment the camera pushed in.
  const backgroundRenderSize = dynamicCameraActive
    ? getCameraMasterSize(renderSize, getMaxZoom(state.dynamicZoom), selected.outputSize)
    : renderSize;
  const animatedStaticBackground = createStaticBackgroundAnimationTimeline();
  const staticBackground = animatedStaticBackground
    ? undefined
    : await waitForRenderOperation(
        ensureStaticBackgroundComposition(selected, backgroundRenderSize),
        {
          signal,
          label: 'Static background preparation'
        }
      );
  const exportSafeRect = scaleSafeRectToRenderSize(selected.safeRect, selected.outputSize, renderSize);
  const finalExportMode: StaticExportMode = isZoom ? 'zoom' : state.staticExport.format === '1:1' ? 'fit' : 'crop';

  return renderStaticVideoExport(assets, primarySource, {
    includeSourceAudio: state.staticUseOriginalAudio,
    userSource2: hasTemplateScreen(selected, 2) ? secondarySource : null,
    userSource3: hasTemplateScreen(selected, 3) ? tertiarySource : null,
    usePrimarySourceAudioOnly: isTripleScreenTemplate(selected),
    format: state.staticExport.format,
    resolution: state.staticExport.resolution,
    exportMode: finalExportMode,
    safeRect: exportSafeRect,
    renderSize,
    preserveAlpha,
    chromaKeyColor: getStaticVideoChromaKeyColor(selected) || undefined,
    diffuse: getDiffuseAdjustments(),
    background: getBackgroundAdjustments(),
    staticBackground,
    animatedStaticBackground,
    sourceCeiling: selected.outputSize,
    dynamicCamera: dynamicCameraActive
      ? {
          durationSeconds: dynamicCameraDuration,
          backgroundMotion: normalizeBackgroundMotion(state.dynamicZoom.backgroundMotion),
          viewportAtTime: (time: number) => getViewportAt(state.dynamicZoom, time, dynamicCameraDuration),
          maxZoom: getMaxZoom(state.dynamicZoom)
        }
      : undefined,
    deviceShadow: getDeviceShadowAdjustments(selected),
    reflection: getReflectionAdjustments(),
    screenOverlay: getScreenOverlayAdjustments(),
    useAlternateMask: shouldUseAlternateMask(),
    customPlacement: activeExportMode === 'custom' ? getEffectiveCustomPlacement() : null,
    customPlacement2: state.staticExport.mode2 === 'custom' ? getEffectiveCustomPlacement(2) : null,
    customPlacement3: state.staticExport.mode3 === 'custom' ? getEffectiveCustomPlacement(3) : null,
    warpMode: activeExportMode,
    warpMode2: state.staticExport.mode2,
    warpMode3: state.staticExport.mode3,
    outputFileHandle,
    userSourceUrl: state.userVideoSrc || undefined,
    userSourceUrl2: hasTemplateScreen(selected, 2) ? state.userVideoSrc2 || undefined : undefined,
    userSourceUrl3: hasTemplateScreen(selected, 3) ? state.userVideoSrc3 || undefined : undefined,
    signal,
    onStageChange: (stage) => {
      touchActiveVideoExport(`static-stage:${stage}`);
      setExportOverlayStage(stage);
    },
    onProgress: (completed, total) => {
      state.motionExportProgress = { completed, total };
      touchActiveVideoExport(`static-frame:${completed}/${total}`);
      updateExportOverlay(completed, total);
    }
  });
}

const EXPORT_OVERLAY_ID = 'motion-export-overlay';
const EXPORT_CANCEL_BUTTON_ID = 'cancel-video-export';

interface ActiveVideoExportTask {
  controller: AbortController;
  watchdog: RenderStallWatchdog;
  abortReason: Error | null;
  progressToken: string;
}

let exportFrameTimeEstimator: ExportFrameTimeEstimator | null = null;

function startVideoExportTask(): ActiveVideoExportTask {
  const controller = new AbortController();
  let task: ActiveVideoExportTask;
  const watchdog = new RenderStallWatchdog(() => {
    abortVideoExport(
      task,
      new Error('Rendering stopped responding. Please try the export again.')
    );
  });
  task = {
    controller,
    watchdog,
    abortReason: null,
    progressToken: 'starting'
  };
  activeVideoExportTask = task;
  return task;
}

function touchActiveVideoExport(progressToken: string): void {
  const task = activeVideoExportTask;
  if (!task || task.controller.signal.aborted || task.progressToken === progressToken) {
    return;
  }
  task.progressToken = progressToken;
  task.watchdog.touch();
}

function abortVideoExport(
  task: ActiveVideoExportTask | null = activeVideoExportTask,
  reason = new Error('Video export canceled. You can try the export again.')
): void {
  if (!task || task.controller.signal.aborted) {
    return;
  }
  task.abortReason = reason;
  task.watchdog.stop();
  task.controller.abort(reason);
  setExportOverlayCanceling(reason.message);
}

function finishVideoExportTask(task: ActiveVideoExportTask | null): void {
  if (!task) {
    return;
  }
  task.watchdog.stop();
  if (activeVideoExportTask === task) {
    activeVideoExportTask = null;
  }
}

function showExportOverlay(
  labelText = 'Rendering video…',
  variant: 'progress' | 'busy' = 'progress'
): void {
  if (document.getElementById(EXPORT_OVERLAY_ID)) {
    return;
  }

  const overlay = document.createElement('div');
  overlay.id = EXPORT_OVERLAY_ID;
  overlay.className = variant === 'busy' ? 'export-overlay-busy-mode' : 'export-overlay-progress-mode';
  exportFrameTimeEstimator = variant === 'progress' ? new ExportFrameTimeEstimator() : null;
  overlay.innerHTML =
    variant === 'busy'
      ? `
    <div class="export-overlay-busy" aria-hidden="true">
      <span></span>
      <span></span>
      <span></span>
      <span></span>
    </div>
    <p class="export-overlay-label">${escapeHtml(labelText)}</p>
    <p class="export-overlay-frames">Preparing your PNG…</p>
  `
      : `
    <button
      id="${EXPORT_CANCEL_BUTTON_ID}"
      class="export-overlay-cancel"
      type="button"
      aria-label="Cancel video rendering"
      title="Cancel video rendering"
    >&times;</button>
    <div class="export-overlay-ring-wrap">
      <svg class="export-overlay-ring" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
        <circle class="export-overlay-ring-track" cx="40" cy="40" r="34" />
        <circle class="export-overlay-ring-fill" cx="40" cy="40" r="34" />
      </svg>
      <span class="export-overlay-percent">0%</span>
    </div>
    <p class="export-overlay-label">${escapeHtml(labelText)}</p>
    <div class="export-overlay-meta">
      <p class="export-overlay-frames"></p>
      <p class="export-overlay-eta">Estimating time...</p>
    </div>
  `;
  overlay.querySelector(`#${EXPORT_CANCEL_BUTTON_ID}`)?.addEventListener('click', () => {
    abortVideoExport();
  });
  document.body.appendChild(overlay);
}

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(finish, 250);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(finish);
    });
  });
}

function hideExportOverlay(): void {
  document.getElementById(EXPORT_OVERLAY_ID)?.remove();
  exportFrameTimeEstimator = null;
}

function setExportOverlayCanceling(message: string): void {
  const overlay = document.getElementById(EXPORT_OVERLAY_ID);
  if (!overlay) {
    return;
  }
  const cancelButton = overlay.querySelector<HTMLButtonElement>(`#${EXPORT_CANCEL_BUTTON_ID}`);
  if (cancelButton) {
    cancelButton.disabled = true;
    cancelButton.setAttribute('aria-label', 'Canceling video rendering');
  }
  const label = overlay.querySelector<HTMLElement>('.export-overlay-label');
  if (label) {
    label.textContent = message.startsWith('Rendering stopped')
      ? 'Rendering stopped responding'
      : 'Canceling video render…';
  }
  const framesLabel = overlay.querySelector<HTMLElement>('.export-overlay-frames');
  if (framesLabel) {
    framesLabel.textContent = 'Releasing video resources…';
  }
  hideExportOverlayEstimate(overlay);
}

function updateExportOverlay(completed: number, total: number): void {
  const overlay = document.getElementById(EXPORT_OVERLAY_ID);
  if (!overlay) {
    return;
  }

  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const circumference = 2 * Math.PI * 34; // r=34
  const dashOffset = circumference * (1 - pct / 100);

  const fill = overlay.querySelector<SVGCircleElement>('.export-overlay-ring-fill');
  if (fill) {
    fill.style.strokeDashoffset = String(dashOffset);
  }

  const pctLabel = overlay.querySelector<HTMLElement>('.export-overlay-percent');
  if (pctLabel) {
    pctLabel.textContent = `${pct}%`;
  }

  const framesLabel = overlay.querySelector<HTMLElement>('.export-overlay-frames');
  if (framesLabel) {
    framesLabel.textContent = `Frame ${completed} / ${total}`;
  }

  const etaLabel = overlay.querySelector<HTMLElement>('.export-overlay-eta');
  if (etaLabel) {
    etaLabel.hidden = false;
    etaLabel.textContent = formatEstimatedTimeRemaining(
      exportFrameTimeEstimator?.update(completed, total) ?? null
    );
  }
}

function hideExportOverlayEstimate(overlay: HTMLElement): void {
  const etaLabel = overlay.querySelector<HTMLElement>('.export-overlay-eta');
  if (etaLabel) {
    etaLabel.hidden = true;
    etaLabel.textContent = '';
  }
}

function setExportOverlayStage(stage: 'rendering' | 'packaging'): void {
  const overlay = document.getElementById(EXPORT_OVERLAY_ID);
  if (!overlay) {
    return;
  }

  const label = overlay.querySelector<HTMLElement>('.export-overlay-label');
  const framesLabel = overlay.querySelector<HTMLElement>('.export-overlay-frames');
  const pctLabel = overlay.querySelector<HTMLElement>('.export-overlay-percent');
  const fill = overlay.querySelector<SVGCircleElement>('.export-overlay-ring-fill');

  if (stage === 'packaging') {
    const circumference = 2 * Math.PI * 34;
    if (fill) {
      fill.style.strokeDashoffset = '0';
    }
    if (pctLabel) {
      pctLabel.textContent = '100%';
    }
    if (label) {
      label.textContent = 'Packaging MP4…';
    }
    if (framesLabel) {
      framesLabel.textContent = 'Finalizing file…';
    }
    hideExportOverlayEstimate(overlay);
    return;
  }

  if (label) {
    label.textContent = 'Rendering video…';
  }
}

function setStaticExportOverlayPackaging(): void {
  const overlay = document.getElementById(EXPORT_OVERLAY_ID);
  if (!overlay) {
    return;
  }
  if (overlay.classList.contains('export-overlay-busy-mode')) {
    const label = overlay.querySelector<HTMLElement>('.export-overlay-label');
    const framesLabel = overlay.querySelector<HTMLElement>('.export-overlay-frames');
    if (label) {
      label.textContent = 'Preparing PNG…';
    }
    if (framesLabel) {
      framesLabel.textContent = 'Finalizing file…';
    }
    return;
  }

  const label = overlay.querySelector<HTMLElement>('.export-overlay-label');
  const framesLabel = overlay.querySelector<HTMLElement>('.export-overlay-frames');
  const pctLabel = overlay.querySelector<HTMLElement>('.export-overlay-percent');
  const fill = overlay.querySelector<SVGCircleElement>('.export-overlay-ring-fill');

  if (fill) {
    fill.style.strokeDashoffset = '0';
  }
  if (pctLabel) {
    pctLabel.textContent = '100%';
  }
  if (label) {
    label.textContent = 'Packaging PNG…';
  }
  if (framesLabel) {
    framesLabel.textContent = 'Finalizing file…';
  }
}

const SAFARI_VIDEO_EXPORT_BLOCKER_ID = 'safari-video-export-blocker';
const MOBILE_VIDEO_EXPORT_BLOCKER_ID = 'mobile-video-export-blocker';

function isSafariBrowserRuntime(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const vendor = navigator.vendor || '';
  const isAppleSafari = /Safari/i.test(userAgent) && /Apple/i.test(vendor);
  const isChromiumOrFirefoxShell = /Chrome|CriOS|Chromium|Edg|OPR|FxiOS|Android/i.test(userAgent);
  return isAppleSafari && !isChromiumOrFirefoxShell;
}

function isMobileVideoExportRuntime(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const isMobileUserAgent = /Mobi|Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(userAgent);
  const isIPadDesktopUserAgent = /Macintosh/i.test(userAgent) && /Apple/i.test(navigator.vendor || '') && maxTouchPoints > 1;
  const isCoarseSmallViewport =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches &&
    window.matchMedia('(max-width: 1024px)').matches;

  return isMobileUserAgent || isIPadDesktopUserAgent || isCoarseSmallViewport || /iPhone|iPad|iPod/i.test(platform);
}

function showSafariVideoExportBlocker(): void {
  if (document.getElementById(SAFARI_VIDEO_EXPORT_BLOCKER_ID)) {
    return;
  }

  const overlay = document.createElement('div');
  overlay.id = SAFARI_VIDEO_EXPORT_BLOCKER_ID;
  overlay.innerHTML = `
    <div class="safari-video-export-modal" role="dialog" aria-modal="true" aria-labelledby="safari-video-export-title">
      <div class="safari-video-export-icon" aria-hidden="true">MP4</div>
      <div class="safari-video-export-copy">
        <h3 id="safari-video-export-title">Video rendering is unavailable in Safari</h3>
        <p>Please use <strong>Chrome</strong> to render video.</p>
      </div>
      <div class="safari-video-export-actions">
        <button id="safari-video-export-copy-link" class="safari-video-export-copy-link" type="button">Copy template link</button>
        <button id="safari-video-export-close" class="safari-video-export-close" type="button">OK</button>
      </div>
    </div>
  `;

  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKeyDown);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      close();
    }
  };

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      close();
    }
  });
  overlay.querySelector('#safari-video-export-copy-link')?.addEventListener('click', async () => {
    const button = overlay.querySelector<HTMLButtonElement>('#safari-video-export-copy-link');
    const originalText = button?.textContent || 'Copy template link';
    try {
      await navigator.clipboard.writeText(getCurrentTemplateUrl());
      if (button) {
        button.textContent = 'Copied';
      }
    } catch {
      if (button) {
        button.textContent = 'Copy failed';
      }
    }
    window.setTimeout(() => {
      if (button) {
        button.textContent = originalText;
      }
    }, 1400);
  });
  overlay.querySelector('#safari-video-export-close')?.addEventListener('click', close);
  document.addEventListener('keydown', onKeyDown);
  document.body.appendChild(overlay);
}

function showMobileVideoExportBlocker(): void {
  if (document.getElementById(MOBILE_VIDEO_EXPORT_BLOCKER_ID)) {
    return;
  }

  const overlay = document.createElement('div');
  overlay.id = MOBILE_VIDEO_EXPORT_BLOCKER_ID;
  overlay.innerHTML = `
    <div class="safari-video-export-modal" role="dialog" aria-modal="true" aria-labelledby="mobile-video-export-title">
      <div class="safari-video-export-icon" aria-hidden="true">MP4</div>
      <div class="safari-video-export-copy">
        <h3 id="mobile-video-export-title">Video rendering is available only on desktop</h3>
        <p>Please use the desktop version to render video.</p>
      </div>
      <button id="mobile-video-export-close" class="safari-video-export-close" type="button">OK</button>
    </div>
  `;

  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKeyDown);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      close();
    }
  };

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      close();
    }
  });
  overlay.querySelector('#mobile-video-export-close')?.addEventListener('click', close);
  document.addEventListener('keydown', onKeyDown);
  document.body.appendChild(overlay);
}

async function buildMotionExport(
  signal?: AbortSignal,
  outputFileHandle?: FileSystemFileHandle | null
) {
  const selected = getSelectedTemplate();
  const motionSource = getMotionExportSource();
  const motionSource2 = getMotionExportSource2();
  const motionSource3 = getMotionExportSource3();
  if (!selected) {
    throw new Error('No mockup selected');
  }
  assertMotionTemplate(selected);
  if (
    !motionSource &&
    !(hasTemplateScreen(selected, 2) && motionSource2) &&
    !(hasTemplateScreen(selected, 3) && motionSource3)
  ) {
    throw new Error('Select a MOTION template and upload media for at least one screen.');
  }

  if (!isMotionVideoExportSupported()) {
    throw new Error(
      'Для экспорта анимации откройте сервис во внешнем браузере или обновите систему на свежую версию.'
    );
  }

  pauseMotionPlayback({ preserveFrame: true });
  const exportAssets = await waitForRenderOperation(
    ensureExportMotionAssets(selected),
    {
      signal,
      label: 'Motion template asset loading'
    }
  );
  const colorMatte = structuredClone(getMotionColorMatteAdjustments());
  await waitForRenderOperation(
    ensureMotionExportColorAssets(exportAssets, colorMatte),
    {
      signal,
      label: 'Motion color asset loading'
    }
  );
  touchActiveVideoExport('motion-assets-ready');
  const exportSize = getMotionExportOutputSize();
  const motionWarpMode = getCurrentStaticExportMode();
  return renderMotionExport(exportAssets, motionSource, {
    userSource2: hasTemplateScreen(selected, 2) ? motionSource2 : null,
    userSource3: hasTemplateScreen(selected, 3) ? motionSource3 : null,
    diffuse: getDiffuseAdjustments(),
    background: getBackgroundAdjustments(),
    reflection: getReflectionAdjustments(),
    screenOverlay: getScreenOverlayAdjustments(),
    colorMatte,
    useAlternateMask: shouldUseAlternateMask(),
    customPlacement: motionWarpMode === 'custom' ? getEffectiveCustomPlacement() : null,
    customPlacement2: state.staticExport.mode2 === 'custom' ? getEffectiveCustomPlacement(2) : null,
    customPlacement3: state.staticExport.mode3 === 'custom' ? getEffectiveCustomPlacement(3) : null,
    warpMode: motionWarpMode,
    warpMode2: state.staticExport.mode2,
    warpMode3: state.staticExport.mode3,
    resizeMode: state.motionExport.resizeMode,
    renderSize: exportSize.renderSize,
    userSourceUrl: state.userVideoSrc || undefined,
    userSourceUrl2: hasTemplateScreen(selected, 2) ? state.userVideoSrc2 || undefined : undefined,
    userSourceUrl3: hasTemplateScreen(selected, 3) ? state.userVideoSrc3 || undefined : undefined,
    outputFileHandle,
    signal,
    onStageChange: (stage) => {
      touchActiveVideoExport(`motion-stage:${stage}`);
      setExportOverlayStage(stage);
    },
    onProgress: (completed, total) => {
      state.motionExportProgress = { completed, total };
      touchActiveVideoExport(`motion-frame:${completed}/${total}`);
      updateExportOverlay(completed, total);
    }
  });
}

async function handleDownload(): Promise<void> {
  if (state.exporting) {
    return;
  }
  const selected = getSelectedTemplate();
  if (!selected) {
    setNotice('No mockup selected', 'error');
    return;
  }
  const staticVideoExportActive = isStaticVideoExportActive(selected);
  const animatedBackgroundVideoActive = isStaticBackgroundAnimationVideoActive(selected);

  if (selected.isVideo && isMobileVideoExportRuntime()) {
    showMobileVideoExportBlocker();
    return;
  }

  if (staticVideoExportActive && isMobileVideoExportRuntime()) {
    setNotice('MP4 rendering is available on Mac/PC only.', 'error');
    return;
  }

  if (staticVideoExportActive && isSafariBrowserRuntime()) {
    showSafariVideoExportBlocker();
    return;
  }

  if (animatedBackgroundVideoActive && !isStaticVideoWebCodecsSupported()) {
    setNotice('Animated background MP4 rendering requires Google Chrome on Mac/PC.', 'error');
    return;
  }

  if (staticVideoExportActive && !isStaticVideoExportSupported()) {
    setNotice('MP4 rendering is unavailable in this browser.', 'error');
    return;
  }

  if (staticVideoExportActive && state.staticExport.resolution !== STATIC_VIDEO_EXPORT_RESOLUTION) {
    state.staticExport.resolution = STATIC_VIDEO_EXPORT_RESOLUTION;
    setNotice('Static MP4 renders use 2K for browser compatibility.', 'info');
    render();
    return;
  }

  if (
    shouldRequireAuthForDownload(selected, staticVideoExportActive) &&
    requireStandaloneAuth('export', {
      type: 'export',
      mode: 'download'
    })
  ) {
    return;
  }

  if (selected.isVideo) {
    if (isSafariBrowserRuntime()) {
      showSafariVideoExportBlocker();
      return;
    }
  }

  if (selected.isVideo && !(await ensureMotionExportAccess())) return;
  const isVideoExport = selected.isVideo || staticVideoExportActive;
  const videoFileName = selected.isVideo
    ? `${selected.id}-motion-${getMotionExportFileSuffix()}.mp4`
    : staticVideoExportActive
      ? `${selected.id}-static-${state.staticExport.format}-${state.staticExport.resolution}.mp4`
      : null;
  let outputFileHandle: FileSystemFileHandle | null = null;
  if (videoFileName) {
    try {
      outputFileHandle = await requestVideoExportFileHandle(videoFileName);
    } catch (error) {
      if (isVideoExportFilePickerCancellation(error)) {
        setNotice('Video export canceled.', 'info');
        return;
      }
      throw error;
    }
  }

  const exportTask = isVideoExport ? startVideoExportTask() : null;
  let savedVideoConfirmation: SavedVideoConfirmation | null = null;
  state.exporting = true;
  state.motionExportProgress = null;
  showExportOverlay(
    selected.isVideo || staticVideoExportActive ? 'Rendering video…' : 'Preparing PNG…',
    selected.isVideo || staticVideoExportActive ? 'progress' : 'busy'
  );

  if (selected.isVideo || staticVideoExportActive) {
    render();
  } else {
    await waitForNextPaint();
  }

  let pngCanvas: HTMLCanvasElement | null = null;
  try {
    if (selected.isVideo) {
      const artifact = await buildMotionExport(
        exportTask?.controller.signal,
        outputFileHandle
      );
      downloadVideoExportArtifact(
        artifact,
        videoFileName!
      );
      recordSelectedExportEvent(selected, 'mp4', {
        format: state.motionExport.resizeMode,
        resolution: getMotionExportLabel(selected)
      });
      if (artifact.storageKind === 'direct-file' && outputFileHandle) {
        savedVideoConfirmation = {
          fileHandle: outputFileHandle,
          fileName: outputFileHandle.name || videoFileName!
        };
      }
      return;
    }

    if (staticVideoExportActive) {
      const artifact = await buildStaticVideoExport(
        exportTask?.controller.signal,
        outputFileHandle
      );
      downloadVideoExportArtifact(
        artifact,
        videoFileName!
      );
      recordSelectedExportEvent(selected, 'mp4', {
        format: state.staticExport.format,
        resolution: `${state.staticExport.resolution.toUpperCase()} MP4`
      });
      if (artifact.storageKind === 'direct-file' && outputFileHandle) {
        savedVideoConfirmation = {
          fileHandle: outputFileHandle,
          fileName: outputFileHandle.name || videoFileName!
        };
      }
      return;
    }

    pngCanvas = await buildStaticExportCanvas();
    setStaticExportOverlayPackaging();
    await downloadCanvasPng(pngCanvas, `${selected.id}-${state.staticExport.format}-${state.staticExport.resolution}.png`);
    recordSelectedExportEvent(selected, 'png', {
      format: state.staticExport.format,
      resolution: state.staticExport.resolution
    });
    setNotice('PNG exported to your computer', 'success', { render: false });
  } catch (error) {
    const resolvedError = exportTask?.abortReason
      ?? (exportTask?.controller.signal.aborted
        ? getRenderAbortError(exportTask.controller.signal)
        : error instanceof Error
          ? error
          : new Error('Export failed'));
    setNotice(resolvedError.message, 'error', { render: false });
  } finally {
    if (pngCanvas) {
      pngCanvas.width = 1;
      pngCanvas.height = 1;
    }
    finishVideoExportTask(exportTask);
    state.exporting = false;
    state.motionExportProgress = null;
    hideExportOverlay();
    if (selected.isVideo || staticVideoExportActive) {
      render();
    }
    if (savedVideoConfirmation) {
      showVideoSavedModal(savedVideoConfirmation);
    }
  }
}

async function handleOpenMotionExportInBrowser(): Promise<void> {
  if (
    shouldRequireAuthForVideoRender() &&
    requireStandaloneAuth('browser-export', {
      type: 'export',
      mode: 'browser'
    })
  ) {
    return;
  }

  const selected = getSelectedTemplate();
  if (!selected?.isVideo) {
    throw new Error('Select a MOTION template first.');
  }

  if (!(await ensureMotionExportAccess())) return;
  const sourceFile = await createMotionSourceFile();
  const mediaKind = sourceFile.type.startsWith('video/') ? 'video' : 'image';
  const session = await createMotionExportSession(
    {
      templateId: selected.id,
      mediaName: sourceFile.name || getActiveUserMediaName(selected) || 'motion-source',
      mediaType: sourceFile.type,
      mediaKind,
      settings: buildMotionExportSessionSettings()
    },
    sourceFile
  );
  recordSelectedExportEvent(selected, 'browser_session', {
    format: state.motionExport.resizeMode,
    resolution: getMotionExportLabel(selected)
  });

  const browserUrl = buildMotionBrowserExportUrl(session.id);
  if (isEmbeddedPlugin) {
    parent.postMessage(
      {
        pluginMessage: {
          type: 'OPEN_EXTERNAL_URL',
          payload: {
            url: browserUrl
          }
        }
      },
      '*'
    );
    setNotice('Opening browser export session…', 'info');
    return;
  }

  window.open(browserUrl, '_blank', 'noopener,noreferrer');
}

function recordSelectedExportEvent(
  template: MockupTemplate,
  exportType: ExportEventType,
  options: { format?: string; resolution?: string } = {}
): void {
  if (!isStandaloneWebApp || !state.authUser) {
    return;
  }

  void recordExportEvent({
    templateId: template.id,
    exportType,
    format: options.format,
    resolution: options.resolution
  })
    .then(() => {
      state.accountOverview = null;
      if (state.accountMenuOpen) {
        void refreshAccountOverview({ renderWhileLoading: true });
      } else {
        void refreshAccountOverview();
      }
    })
    .catch((error) => {
      console.warn('[metrics] export event was not recorded', error);
    });
}

async function handleInsertToFigma(): Promise<void> {
  state.inserting = true;
  render();

  try {
    const selected = getSelectedTemplate();
    if (!selected) {
      throw new Error('No mockup selected');
    }

    if (selected.isVideo) {
      throw new Error('Motion MP4 export is available only to computer in this build.');
    }

    const canvas = await buildStaticExportCanvas();
    const bytes = await canvasToPngBytes(canvas);

    parent.postMessage(
      {
        pluginMessage: {
          type: 'INSERT_PNG',
          payload: {
            bytes,
            width: canvas.width,
            height: canvas.height,
            name: `${selected.name} (${state.staticExport.format})`
          }
        }
      },
      '*'
    );
  } catch (error) {
    state.inserting = false;
    setNotice(error instanceof Error ? error.message : 'Insert failed', 'error');
    render();
  }
}

function renderThemeToggle(): string {
  if (!isStandaloneWebApp) {
    return '';
  }

  const title = isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode';
  const sunClass = isDarkTheme ? 'theme-icon is-visible' : 'theme-icon';
  const moonClass = isDarkTheme ? 'theme-icon' : 'theme-icon is-visible';
  return [
    `<button id="theme-toggle" class="icon-button theme-toggle-button" type="button" title="${escapeHtml(title)}" aria-label="${escapeHtml(title)}">`,
    `<svg class="${sunClass}" data-theme-icon="sun" viewBox="0 0 24 24" aria-hidden="true" focusable="false">`,
    '<circle cx="12" cy="12" r="4.5" />',
    '<path d="M12 2.5v2" />',
    '<path d="M12 19.5v2" />',
    '<path d="m4.6 4.6 1.4 1.4" />',
    '<path d="m18 18 1.4 1.4" />',
    '<path d="M2.5 12h2" />',
    '<path d="M19.5 12h2" />',
    '<path d="m4.6 19.4 1.4-1.4" />',
    '<path d="m18 6 1.4-1.4" />',
    '</svg>',
    `<svg class="${moonClass}" data-theme-icon="moon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">`,
    '<path d="M20.4 14.5A8 8 0 1 1 9.5 3.6a6.2 6.2 0 0 0 10.9 10.9Z" />',
    '</svg>',
    '</button>'
  ].join('');
}
function renderAccountControl(): string {
  if (!isStandaloneWebApp) {
    return '';
  }

  if (state.authUser) {
    const displayName = state.authUser.displayName || state.authUser.email;

    return `
      <div class="account-control signed-in ${state.accountMenuOpen ? 'open' : ''}">
        <button
          id="account-menu-button"
          class="account-identity account-menu-button"
          type="button"
          title="${escapeHtml(state.authUser.email)}"
          aria-haspopup="menu"
          aria-expanded="${state.accountMenuOpen ? 'true' : 'false'}"
        >
          <span class="account-name">${escapeHtml(displayName)}</span>
          <span class="account-access-chip ${state.accountAccess?.source === 'vip' ? 'is-vip' : ''}">${getAccountAccessLabel()}</span>
        </button>
        ${state.accountMenuOpen ? renderAccountDropdown() : ''}
      </div>
    `;
  }

  const loadingLabel = state.authReady ? 'Login' : '...';
  return `
    <button id="login-button" class="auth-login-button" type="button" title="Login">
      ${renderLoginIcon()}
      <span>${loadingLabel}</span>
    </button>
  `;
}

function renderAccountDropdown(): string {
  const items: Array<{ view: AccountMenuView; label: string; badge?: string }> = [
    { view: 'favorites', label: 'Favorites', badge: String(state.favoriteTemplateIds.length) },
    { view: 'exports', label: 'Last exports' },
    { view: 'stats', label: 'Statistics' },
    { view: 'subscription', label: getAccountSubscriptionMenuLabel() }
  ];

  return `
    <div class="account-dropdown" role="menu" aria-label="Account menu">
      ${renderAccountIdentity()}
      <div class="account-dropdown-nav">
        ${items
          .map(
            (item) => `
              <button
                type="button"
                class="account-menu-tab ${state.accountMenuView === item.view ? 'active' : ''}"
                data-account-view="${item.view}"
              >
                <span>${escapeHtml(item.label)}</span>
                ${item.badge ? `<strong>${escapeHtml(item.badge)}</strong>` : ''}
              </button>
            `
          )
          .join('')}
      </div>
      <div class="account-dropdown-body">
        ${renderAccountDropdownBody()}
      </div>
      <div class="account-dropdown-footer">
        <button id="account-open-page-button" type="button">Open page</button>
        <button id="logout-button" type="button">Logout</button>
      </div>
    </div>
  `;
}

function renderAccountDropdownBody(): string {
  if (state.accountOverviewError) {
    return `<p class="account-menu-message error">${escapeHtml(state.accountOverviewError)}</p>`;
  }

  if (state.accountMenuView === 'favorites') {
    return renderAccountFavoritesPanel();
  }
  if (state.accountMenuView === 'exports') {
    return renderAccountExportsPanel();
  }
  if (state.accountMenuView === 'stats') {
    return renderAccountStatsPanel();
  }
  return renderAccountSubscriptionPanel();
}

function renderAccountPage(): string {
  const items: Array<{ view: AccountMenuView; label: string; note: string }> = [
    { view: 'favorites', label: 'Favorites', note: `${state.favoriteTemplateIds.length} saved` },
    { view: 'exports', label: 'Last exports', note: `${state.accountOverview?.recentExports.length || 0} latest` },
    { view: 'stats', label: 'Statistics', note: `${state.accountOverview?.stats.totalExports || 0} exports` },
    { view: 'subscription', label: getAccountSubscriptionMenuLabel(), note: '$10/month · Unlimited' }
  ];

  return `
    <section class="account-page">
      <div class="account-page-header">
        <div>
          <span class="account-page-kicker">Profile</span>
          <h2>${escapeHtml(getAccountPageTitle(state.accountMenuView))}</h2>
          <p>${escapeHtml(getAccountPageSubtitle(state.accountMenuView))}</p>
        </div>
        <button id="account-page-back" class="account-page-back" type="button">Back to mockups</button>
      </div>
      ${renderAccountIdentity()}
      ${state.accountAccess?.source === 'vip' ? '<div class="vip-unlimited-banner">VIP UNLIMITED<small>Lifetime access</small></div>' : ''}
      <nav class="account-page-tabs" aria-label="Profile sections">
        ${items
          .map(
            (item) => `
              <button
                type="button"
                class="${state.accountMenuView === item.view ? 'active' : ''}"
                data-account-page-view="${item.view}"
              >
                <span>${escapeHtml(item.label)}</span>
                <small>${escapeHtml(item.note)}</small>
              </button>
            `
          )
          .join('')}
      </nav>
      <div class="account-page-card">
        ${state.accountOverviewLoading ? '<p class="account-menu-message">Loading account data...</p>' : ''}
        ${renderAccountDropdownBody()}
      </div>
    </section>
  `;
}

function getAccountPageTitle(view: AccountMenuView): string {
  if (view === 'exports') return 'Last exports';
  if (view === 'stats') return 'Statistics';
  if (view === 'subscription') return 'Subscription';
  return 'Favorites';
}

function getAccountPageSubtitle(view: AccountMenuView): string {
  if (view === 'exports') return 'Recent templates used for rendering and downloads.';
  if (view === 'stats') return 'Downloads and template usage.';
  if (view === 'subscription') return 'Unlimited Motion templates and exports for $10/month.';
  return 'All saved templates in one place, with Static and Motion filters.';
}

function renderAccountFavoritesPanel(): string {
  const allFavorites = getAccountFavoriteTemplates();
  const visibleFavorites = filterAccountFavoriteTemplates(allFavorites, state.accountFavoriteFilter);
  const filters: AccountFavoriteFilter[] = ['ALL', 'STATIC', 'MOTION'];

  return `
    <section class="account-panel">
      <div class="account-panel-head">
        <h3>Favorites</h3>
        <span>${visibleFavorites.length} shown</span>
      </div>
      <div class="account-filter-pills" role="group" aria-label="Favorite filters">
        ${filters
          .map(
            (filter) => `
              <button
                type="button"
                class="${state.accountFavoriteFilter === filter ? 'active' : ''}"
                data-account-favorite-filter="${filter}"
              >${filter === 'ALL' ? 'All' : filter === 'STATIC' ? 'Static' : 'Motion'}</button>
            `
          )
          .join('')}
      </div>
      <div class="account-list">
        ${
          visibleFavorites.length > 0
            ? visibleFavorites.map(renderAccountFavoriteItem).join('')
            : '<p class="account-menu-message">No favorites yet.</p>'
        }
      </div>
    </section>
  `;
}

function renderAccountFavoriteItem(template: MockupTemplate): string {
  const contentType = isMotionTemplateType(template.templateType) ? 'Motion' : 'Static';
  return `
    <button class="account-template-item" type="button" data-account-template-id="${escapeHtml(template.id)}">
      <img src="${escapeHtml(getTemplatePreviewUrl(template))}" alt="" loading="lazy" />
      <span>
        <strong>${escapeHtml(template.name)}</strong>
        <small>${contentType} · ${escapeHtml(template.category || 'Template')}</small>
      </span>
    </button>
  `;
}

function renderAccountExportsPanel(): string {
  const exports = state.accountOverview?.recentExports || [];
  return `
    <section class="account-panel">
      <div class="account-panel-head">
        <h3>Last exports</h3>
        <span>${exports.length} latest</span>
      </div>
      <div class="account-list">
        ${
          exports.length > 0
            ? exports
                .map(
                  (item) => `
                    <button class="account-export-item" type="button" data-account-template-id="${escapeHtml(item.templateId)}">
                      <span>
                        <strong>${escapeHtml(item.templateName || item.templateId)}</strong>
                        <small>${escapeHtml(formatAccountExportType(item.exportType))} · ${escapeHtml(item.format || item.contentType)} · ${escapeHtml(formatAccountDate(item.createdAt))}</small>
                      </span>
                    </button>
                  `
                )
                .join('')
            : `<p class="account-menu-message">${state.accountOverviewLoading ? 'Loading exports...' : 'No exports yet.'}</p>`
        }
      </div>
    </section>
  `;
}

function renderAccountStatsPanel(): string {
  const stats = state.accountOverview?.stats;
  const rows = [
    { label: 'Total exports', value: stats?.totalExports ?? 0 },
    { label: 'PNG downloads', value: stats?.pngDownloads ?? 0 },
    { label: 'Video renders', value: stats?.mp4Downloads ?? 0 },
    { label: 'Browser sessions', value: stats?.browserSessions ?? 0 },
    { label: 'Templates used', value: stats?.uniqueTemplates ?? 0 }
  ];

  return `
    <section class="account-panel">
      <div class="account-panel-head">
        <h3>Statistics</h3>
        <span>${state.accountOverviewLoading ? 'Updating' : 'Account'}</span>
      </div>
      <div class="account-stats-grid">
        ${rows
          .map(
            (row) => `
              <div class="account-stat-card">
                <span>${escapeHtml(row.label)}</span>
                <strong>${escapeHtml(String(row.value))}</strong>
              </div>
            `
          )
          .join('')}
      </div>
    </section>
  `;
}

function getAccountPublicId(): string {
  return state.authUser ? state.accountPublicId || state.accountOverview?.publicId || '' : '';
}

function renderAccountIdentity(): string {
  if (!state.authUser) return '';
  const publicId = getAccountPublicId();
  return `<div class="account-public-id"><span>Your User ID</span><code>${escapeHtml(publicId || 'Loading…')}</code>
    <button type="button" data-copy-user-id ${publicId ? '' : 'disabled'}>Copy ID</button></div>`;
}

function renderAccountSubscriptionPanel(): string {
  const access = state.accountAccess;
  const subscription = access?.subscription;
  const vip = access?.source === 'vip';
  const title = vip ? 'VIP UNLIMITED' : access?.unlimited ? 'UNLIMITED' : 'Motion Unlimited';
  return `<section class="account-panel account-subscription-panel">
    <div class="account-panel-head"><h3>Subscription</h3><span>${vip ? 'Lifetime access' : access?.unlimited ? 'Active' : '$10/month'}</span></div>
    <div class="account-subscription-summary ${vip ? 'is-vip' : ''}">
      <strong>${title}</strong>
      <span>${vip ? 'Lifetime access · Unlimited Motion templates and exports' : access?.unlimited ? 'Unlimited Motion templates and exports' : '$10/month · Unlimited Motion templates and exports'}</span>
      ${vip && access?.vipSince ? `<small>VIP since ${formatAccountDate(access.vipSince)}</small>` : ''}
      ${subscription?.currentPeriodEnd && !vip ? `<small>${subscription.cancelAtPeriodEnd ? 'Access until' : 'Current period ends'} ${formatAccountDate(subscription.currentPeriodEnd)}</small>` : ''}
      ${subscription?.status === 'past_due' ? '<small>Your payment is overdue. Update your payment method to restore access.</small>' : ''}
    </div>
    ${!vip && !access?.unlimited && !access?.canManageSubscription ? `<button data-subscribe class="public-primary-button" type="button" ${billingConfig?.configured ? '' : 'disabled'}>Subscribe · $10/month</button>` : ''}
    ${access?.canManageSubscription ? '<button data-manage-subscription class="public-secondary-button" type="button">Manage subscription</button>' : ''}
    ${vip && access?.canManageSubscription ? '<p>You also have a Paddle subscription. Manage it to stop future charges.</p>' : ''}
    ${!vip && !access?.unlimited && !billingConfig?.configured ? '<p>Payments are not available yet.</p>' : ''}
    <p class="billing-status" role="status">${escapeHtml(billingStatus)}</p>
  </section>`;
}

function formatAccountExportType(value: string): string {
  if (value === 'png') return 'PNG';
  if (value === 'mp4') return 'MP4';
  if (value === 'browser_session') return 'Browser render';
  return value;
}

function formatAccountDate(value: string): string {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No date';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function renderAuthPrompt(): string {
  if (!state.authPrompt) {
    return '';
  }

  const configMessage = state.authConfigured ? '' : buildMissingAuthConfigMessage();
  const message = state.authMessage || (configMessage ? { type: 'error' as const, message: configMessage } : null);

  return `
    <div class="auth-modal-backdrop" role="presentation">
      <section class="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
        <button id="auth-modal-close" class="auth-modal-close" type="button" aria-label="Close">×</button>
        <div class="auth-modal-icon" aria-hidden="true">${renderLoginIcon()}</div>
        <h2 id="auth-modal-title">${escapeHtml(getAuthPromptTitle(state.authPrompt.reason))}</h2>
        <p>${escapeHtml(getAuthPromptCopy(state.authPrompt.reason))}</p>
        <button id="auth-google-button" class="auth-provider-button" type="button" ${state.authBusy || !state.authConfigured ? 'disabled' : ''}>
          <span aria-hidden="true">G</span>
          <strong>${state.authBusy ? 'Opening...' : 'Continue with Google'}</strong>
        </button>
        <form id="auth-email-form" class="auth-email-form">
          <label for="auth-email-input">Email</label>
          <div class="auth-email-row">
            <input
              id="auth-email-input"
              type="email"
              value="${escapeHtml(state.authEmail)}"
              placeholder="name@example.com"
              autocomplete="email"
              ${state.authBusy || !state.authConfigured ? 'disabled' : ''}
            />
            <button type="submit" ${state.authBusy || !state.authConfigured ? 'disabled' : ''}>Send link</button>
          </div>
        </form>
        ${message ? `<p class="auth-message ${message.type}">${escapeHtml(message.message)}</p>` : ''}
      </section>
    </div>
  `;
}

function getAuthPromptTitle(reason: AuthPromptReason): string {
  if (reason === 'favorite') {
    return 'Login to save favorite';
  }
  if (reason === 'favorites') {
    return 'Login to open favorites';
  }
  if (reason === 'gradient-preset') {
    return 'Login to save palettes';
  }
  if (reason === 'export' || reason === 'browser-export') {
    return 'Login to export';
  }
  return 'Login';
}

function getAuthPromptCopy(reason: AuthPromptReason): string {
  if (reason === 'favorite') {
    return 'Favorites are stored in your account, so they stay available on every device.';
  }
  if (reason === 'favorites') {
    return 'Your saved templates are tied to your account.';
  }
  if (reason === 'gradient-preset') {
    return 'Gradient presets are stored in your account and stay available on every device.';
  }
  if (reason === 'export' || reason === 'browser-export') {
    return 'Image and video export is available after login.';
  }
  return 'Use Google or email to continue.';
}

function getAccountInitial(user: AuthUser): string {
  const source = (user.displayName || user.email || 'U').trim();
  return source.slice(0, 1).toUpperCase();
}

function renderLoginIcon(): string {
  return `
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path d="M8.5 3.5H5.8A1.8 1.8 0 0 0 4 5.3v9.4a1.8 1.8 0 0 0 1.8 1.8h2.7" />
      <path d="M10 6.5 13.5 10 10 13.5" />
      <path d="M13.2 10H7.5" />
    </svg>
  `;
}

function renderLogoutIcon(): string {
  return `
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path d="M11.5 3.5h2.7A1.8 1.8 0 0 1 16 5.3v9.4a1.8 1.8 0 0 1-1.8 1.8h-2.7" />
      <path d="M10 6.5 6.5 10 10 13.5" />
      <path d="M6.8 10h5.7" />
    </svg>
  `;
}

function renderHomeScreen(): string {
  const carouselTemplates = getHomeCarouselTemplates();
  const carouselItems =
    carouselTemplates.length > 0
      ? [...carouselTemplates, ...carouselTemplates]
          .map((template, index) => renderHomeTemplateCard(template, index))
          .join('')
      : renderHomeCarouselPlaceholders();

  return `
    <main class="home-screen ${state.heroDragActive ? 'is-dragging' : ''}">
      ${renderHomeTopbar()}
      ${renderHomeObject3DLeft()}
      ${renderHomeObject3DRight()}
      <section class="home-hero" aria-label="MotionPitch Mockups">
        <h1 class="home-title">
          Mockups for <em>every day</em><br />
          for every need
        </h1>
        <p class="home-subtitle">
          Free, fast &amp; professional. A handcrafted collection by a motion designer with 10 years of experience.
        </p>
        <div class="home-divider" aria-hidden="true"></div>
        <button id="home-browse" class="home-primary-button" type="button">Browse Collection <span aria-hidden="true">→</span></button>
        <p class="home-meta">Free static mockups · Motion Unlimited for $10/month</p>
        <div id="home-carousel" class="home-carousel-wrap">
          <div class="home-carousel-fade" aria-hidden="true"></div>
          <div class="home-carousel-track">${carouselItems}</div>
          <div class="home-carousel-frame" aria-hidden="true"></div>
        </div>
        <div id="home-upload-dropzone" class="home-upload-row">
          <label class="home-upload-button">
            ${renderUploadIcon()}
            <span>${escapeHtml(state.heroFileName || 'Upload your screenshot')}</span>
            <input id="home-file-input" type="file" accept="image/png,image/jpeg,image/webp" />
          </label>
          <div class="home-drop-overlay" aria-hidden="true">
            <strong>Drop image here</strong>
            <span>and try it free</span>
          </div>
        </div>
        <p class="home-upload-hint">${state.heroFileName ? 'Open a collection to choose the final mockup.' : 'Drop your screenshot and try it free'}</p>
        ${renderHomeStatus()}
      </section>
      ${renderHomeCopyright()}
    </main>
    ${renderAuthPrompt()}
  `;
}

function renderHomeTopbar(): string {
  return `
    <header class="home-topbar" aria-label="MotionPitch public navigation">
      <nav class="home-topbar-links" aria-label="Footer links">
        <a href="/pricing">Pricing</a>
        <a href="/terms">Terms</a>
        <a href="/privacy">Privacy</a>
        <a href="/refund-policy">Refund Policy</a>
        <a href="/contact">Contact</a>
      </nav>
      <div class="home-topbar-actions">
        ${renderAccountControl()}
      </div>
    </header>
  `;
}

function renderHomeCopyright(): string {
  return `
    <footer class="home-copyright">
      <span>&copy; 2026 MotionPitch. Digital mockup templates and SaaS features.</span>
      <nav class="home-copyright-links" aria-label="Footer links">
        <a href="/pricing">Pricing</a>
        <a href="/terms">Terms</a>
        <a href="/privacy">Privacy</a>
        <a href="/refund-policy">Refund Policy</a>
        <a href="/contact">Contact</a>
      </nav>
    </footer>
  `;
}

function renderHomeStatus(): string {
  return `<p id="home-status" class="home-status ${state.notice ? state.notice.type : 'is-hidden'}" role="status">${state.notice ? escapeHtml(state.notice.message) : ''}</p>`;
}

function getPublicPageRoute(): PublicPageRoute | null {
  if (isEmbeddedPlugin) {
    return null;
  }

  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/pricing') return 'pricing';
  if (path === '/terms') return 'terms';
  if (path === '/privacy') return 'privacy';
  if (path === '/refund') return 'refund';
  if (path === '/refund-policy') return 'refund-policy';
  if (path === '/payment/success') return 'payment-success';
  if (path === '/payment/cancel') return 'payment-cancel';
  if (path === '/billing') return 'billing';
  if (path === '/contact') return 'contact';
  return null;
}

function renderPublicRouteIfNeeded(): boolean {
  const route = getPublicPageRoute();
  if (!route) {
    return false;
  }

  cleanupAccountMenuOutsideClick();
  cleanupGradientPresetsOutsideClick();
  cleanupMobileStickyPreviewBehavior();
  clearPendingMotionCardHoverPreview();
  disposeActiveMotionCardHoverPreview();
  pauseMotionPlayback({ preserveFrame: true });
  appRoot.innerHTML = renderPublicPage(route);
  bindPublicPageEvents();
  window.requestAnimationFrame(syncScrollChrome);
  return true;
}

function renderPublicPage(route: PublicPageRoute): string {
  const content = (() => {
    if (route === 'pricing') return renderPricingPage();
    if (route === 'terms') return renderTermsPage();
    if (route === 'privacy') return renderPrivacyPage();
    if (route === 'refund' || route === 'refund-policy') return renderRefundPage();
    if (route === 'payment-success') return renderPaymentSuccessPage();
    if (route === 'payment-cancel') return renderPaymentCancelPage();
    if (route === 'billing') return renderBillingPage();
    return renderContactPage();
  })();

  return `
    <main class="public-page">
      ${renderPublicHeader()}
      ${content}
      ${renderPublicFooter(route)}
    </main>
  `;
}

function renderPublicHeader(): string {
  return `
    <header class="public-header">
      <a class="public-brand" href="/" aria-label="MotionPitch Mockups">
        <img class="topbar-logo" src="${escapeHtml(motionPitchLogo)}" alt="MotionPitch" />
        <span>MotionPitch Mockups</span>
      </a>
      <nav class="public-nav" aria-label="Public pages">
        <a href="/pricing">Pricing</a>
        <a href="/terms">Terms</a>
        <a href="/privacy">Privacy</a>
        ${renderThemeToggle()}
      </nav>
    </header>
  `;
}

function renderPublicFooter(context: PublicPageRoute | 'home'): string {
  const compact = context === 'home' ? ' home-footer' : '';
  return `
    <footer class="public-footer${compact}">
      <nav aria-label="Footer links">
        <a href="/pricing">Pricing</a>
        <a href="/terms">Terms</a>
        <a href="/privacy">Privacy</a>
        <a href="/refund-policy">Refund Policy</a>
        <a href="/contact">Contact</a>
      </nav>
      <span>&copy; 2026 MotionPitch. Digital mockup templates and SaaS features.</span>
    </footer>
  `;
}

function renderPricingPage(): string {
  return `<section class="public-hero-card"><p class="public-kicker">Pricing</p>
    <h1>Unlimited Motion. One simple subscription.</h1>
    <p>Create and export as many Motion mockups as you need.</p></section>
    <section class="pricing-grid" aria-label="MotionPitch plans">
      <article class="pricing-card"><span class="pricing-label">Static</span><h2>Free</h2>
        <p>Explore the library and create static mockups.</p><a class="public-secondary-button" href="/">Open templates</a></article>
      <article class="pricing-card featured"><span class="pricing-label">Motion Unlimited</span><h2>$10<span>/month</span></h2>
        <p>Full access to Motion templates. Unlimited exports.</p>
        <ul><li>All Motion templates</li><li>Unlimited Motion renders and downloads</li><li>New Motion templates included</li><li>Cancel any time; access continues to the end of your paid period</li></ul>
        ${state.accountAccess?.unlimited ? `<p class="${state.accountAccess.source === 'vip' ? 'vip-unlimited-banner' : ''}">${getAccountAccessLabel()}</p>` : `<button data-subscribe class="public-primary-button" type="button" ${billingConfig?.configured ? '' : 'disabled'}>Subscribe · $10/month</button>`}
        ${state.accountAccess?.canManageSubscription ? '<button data-manage-subscription class="public-secondary-button" type="button">Manage subscription</button>' : ''}
        <p class="pricing-help">${billingConfig?.configured ? 'Secure checkout by Paddle. Renews monthly. Applicable taxes are shown at checkout.' : 'Payments are not available yet.'}</p>
        <p class="billing-status" role="status">${escapeHtml(billingStatus)}</p></article>
    </section>`;
}

function renderTermsPage(): string {
  return `
    <article class="legal-page">
      <p class="public-kicker">Terms of Service</p>
      <h1>Terms of Service</h1>
      <p class="legal-updated">Last updated: ${LEGAL_LAST_UPDATED}</p>
      <p>MotionPitch provides digital mockup templates, design assets, and SaaS features.</p>
      <h2>Use of MotionPitch</h2>
      <p>
        Users can use exported mockups for portfolios, client presentations, websites, social
        media, and marketing materials. Users are responsible for the content they upload or
        insert into mockups.
      </p>
      <h2>Ownership</h2>
      <p>
        MotionPitch owns its templates, mockups, visuals, website design, and related assets.
        You may not redistribute, resell, clone, or package MotionPitch templates as a competing
        product without written permission.
      </p>
      <h2>Payments and subscriptions</h2>
      <p>
        Payments are processed by Paddle. All products are digital. No physical goods are shipped.
        Subscriptions can be cancelled according to Paddle customer billing options.
      </p>
      <h2>Contact</h2>
      <p>For support or legal questions, contact us at <a href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a>.</p>
    </article>
  `;
}

function renderPrivacyPage(): string {
  return `
    <article class="legal-page">
      <p class="public-kicker">Privacy Policy</p>
      <h1>Privacy Policy</h1>
      <p class="legal-updated">Last updated: ${PRIVACY_LAST_UPDATED}</p>
      <p>
        MotionPitch may collect basic account information such as email address, subscription
        status, and usage data.
      </p>
      <h2>Uploaded content</h2>
      <p>
        User uploaded designs are used only to provide mockup generation and export features.
      </p>
      <h2>Payments</h2>
      <p>
        MotionPitch does not store full payment card details. Payments are processed by Paddle.
      </p>
      <h2>Analytics and third-party services</h2>
      <p>
        MotionPitch may use Google Tag Manager, Google Analytics, and Yandex Metrica to understand
        page usage, navigation, and general interactions so we can improve the product. Yandex
        Session Replay and click maps may record interactions with the interface. We do not
        intentionally add uploaded design files, payment card details, or account email addresses
        to analytics events. Other third-party services may include Paddle, hosting, and storage
        providers.
      </p>
      <h2>Contact</h2>
      <p>For privacy questions, contact us at <a href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a>.</p>
    </article>
  `;
}

function renderRefundPage(): string {
  return `
    <article class="legal-page">
      <p class="public-kicker">Refund Policy</p>
      <h1>Refund Policy</h1>
      <p class="legal-updated">Last updated: ${LEGAL_LAST_UPDATED}</p>
      <p>MotionPitch sells digital products and SaaS features.</p>
      <h2>Refund review</h2>
      <p>
        Refunds are reviewed case by case. Refund requests may be accepted within 14 days if
        the user was charged by mistake, cannot access the product due to unresolved technical
        issues, or the product was not as described.
      </p>
      <h2>When refunds may be declined</h2>
      <p>
        Refunds may be declined if premium assets or export features were substantially used,
        downloaded, exported, or if the request appears abusive or fraudulent.
      </p>
      <h2>Payment processor</h2>
      <p>Payments and refunds are processed by Paddle.</p>
      <h2>Contact</h2>
      <p>To request help, contact us at <a href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a>.</p>
    </article>
  `;
}

function renderPaymentSuccessPage(): string {
  const active = state.accountAccess?.unlimited;
  return `<section class="public-message-card"><p class="public-kicker">Subscription</p>
    <h1>${active ? 'Your unlimited access is active' : 'Confirming your subscription'}</h1>
    <p>${active ? 'You can now export unlimited Motion mockups.' : 'Access is activated after Paddle confirms your payment. This can take a moment.'}</p>
    <button data-refresh-billing class="public-secondary-button" type="button">Check access</button>
    <p class="billing-status" role="status">${escapeHtml(billingStatus)}</p>
    <a class="public-primary-button" href="/?subscription=1">Open profile</a></section>`;
}

function renderPaymentCancelPage(): string {
  return `
    <section class="public-message-card">
      <p class="public-kicker">Payment</p>
      <h1>Payment canceled</h1>
      <p>Your payment was not completed. You can return to the pricing page and try again.</p>
      <a class="public-secondary-button" href="/pricing">Back to pricing</a>
    </section>
  `;
}

function renderBillingPage(): string {
  return `<section class="public-message-card"><p class="public-kicker">Billing</p><h1>Your subscription</h1>
    ${state.authUser ? renderAccountSubscriptionPanel() : '<p>Sign in to manage your subscription.</p><a class="public-primary-button" href="/?subscription=1">Sign in</a>'}
    <p>For billing support: <a href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a></p></section>`;
}

function renderContactPage(): string {
  return `
    <section class="public-message-card">
      <p class="public-kicker">Contact</p>
      <h1>Contact MotionPitch</h1>
      <p>For support, billing, privacy, or legal questions, contact us at:</p>
      <p><a class="public-email-link" href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a></p>
    </section>
  `;
}

function bindPublicPageEvents(): void {
  document.getElementById('theme-toggle')?.addEventListener('click', () => {
    applyThemePreference(!isDarkTheme, true);
  });

  bindBillingEvents();
}

let paddleScriptPromise: Promise<void> | null = null;
let paddleInitialized = false;

function showBillingStatus(message: string): void {
  billingStatus = message;
  document.querySelectorAll('.billing-status').forEach((node) => { node.textContent = message; });
  setNotice(message, 'info');
}

async function refreshPublicBilling(): Promise<void> {
  try {
    billingConfig = await loadBillingConfig();
    const user = await getCurrentAuthUser();
    if (state.authUser?.id !== user?.id) {
      state.accountOverview = null; state.accountAccess = null; state.accountPublicId = null;
    }
    state.authUser = user;
    if (user) {
      const summary = await billingRequest<{ access: AccessSummary; publicId: string }>('/me/access');
      state.accountAccess = summary.access;
      state.accountPublicId = summary.publicId;
    } else { state.accountAccess = null; state.accountPublicId = null; }
    billingStatus = '';
  } catch (error) { billingStatus = error instanceof Error ? error.message : 'Unable to check subscription.'; }
  render();
}

function bindBillingEvents(): void {
  document.querySelectorAll('[data-subscribe]').forEach((button) => button.addEventListener('click', () => { void openPaddleCheckout(); }));
  document.querySelectorAll('[data-manage-subscription]').forEach((button) => button.addEventListener('click', () => { void openBillingPortal(); }));
  document.querySelectorAll('[data-refresh-billing]').forEach((button) => button.addEventListener('click', () => { void refreshPublicBilling(); }));
  document.querySelectorAll('[data-copy-user-id]').forEach((button) => button.addEventListener('click', () => {
    const publicId = getAccountPublicId();
    if (publicId) void navigator.clipboard.writeText(publicId).then(() => { button.textContent = 'Copied'; }).catch(() => { button.textContent = 'Select and copy ID'; });
  }));
}

async function openBillingPortal(): Promise<void> {
  if (billingBusy) return;
  billingBusy = true;
  try {
    const { url } = await billingRequest<{ url: string }>('/me/billing/portal', 'POST');
    window.location.assign(url);
  } catch (error) { showBillingStatus(error instanceof Error ? error.message : 'Unable to open billing.'); }
  finally { billingBusy = false; }
}

async function initializePaddleCheckout(): Promise<void> {
  billingConfig = await loadBillingConfig();
  if (!billingConfig.configured) throw new Error('Payments are not available yet.');
  await loadPaddleCheckout();
  if (!window.Paddle) throw new Error('Paddle checkout is not available.');
  if (!paddleInitialized) {
    if (billingConfig.environment === 'sandbox') window.Paddle.Environment?.set('sandbox');
    window.Paddle.Initialize({ token: billingConfig.clientToken, eventCallback: (event) => {
      if (event.name === 'checkout.completed') void refreshPublicBilling();
    } });
    paddleInitialized = true;
  }
}

async function openPaddleCheckout(): Promise<void> {
  if (billingBusy) return;
  billingBusy = true;
  try {
    const user = await getCurrentAuthUser();
    if (!user) {
      if (getPublicPageRoute()) window.location.assign('/?subscription=1');
      else openAuthPrompt('account');
      return;
    }
    await initializePaddleCheckout();
    const { transactionId } = await billingRequest<{ transactionId: string }>('/me/billing/checkout', 'POST');
    window.Paddle.Checkout.open({ transactionId, settings: { successUrl: PADDLE_SUCCESS_URL } });
  } catch (error) { showBillingStatus(error instanceof Error ? error.message : 'Payment checkout failed.'); }
  finally { billingBusy = false; }
}

async function ensureMotionExportAccess(): Promise<boolean> {
  if (motionAccessChecking) return false;
  if (requireStandaloneAuth('export', { type: 'export', mode: 'download' })) return false;
  motionAccessChecking = true;
  try {
    await billingRequest('/me/motion-access', 'POST');
    return true;
  } catch (error) {
    if (error instanceof BillingRequestError && error.status === 402) {
      openAccountPage('subscription');
      showBillingStatus('Subscribe for $10/month to export unlimited Motion mockups.');
    } else showBillingStatus(error instanceof Error ? error.message : 'Unable to verify access.');
    return false;
  } finally { motionAccessChecking = false; }
}

function loadPaddleCheckout(): Promise<void> {
  if (window.Paddle) {
    return Promise.resolve();
  }

  if (!paddleScriptPromise) {
    paddleScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PADDLE_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => { paddleScriptPromise = null; script.remove(); reject(new Error('Failed to load Paddle checkout')); };
      document.head.appendChild(script);
    });
  }

  return paddleScriptPromise;
}

function getHomeCarouselTemplateCount(): number {
  return 12;
}

function isHomeCarouselStaticTemplate(item: MockupTemplate): boolean {
  return (
    item.published !== false &&
    !item.isVideo &&
    !isMotionTemplateType(item.templateType) &&
    Boolean(getTemplatePreviewUrl(item))
  );
}

function compareHomeFallbackTemplates(a: MockupTemplate, b: MockupTemplate): number {
  const orderDelta = (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
  if (orderDelta !== 0) {
    return orderDelta;
  }

  return a.name.localeCompare(b.name);
}

function compareHomeCarouselTemplates(a: MockupTemplate, b: MockupTemplate): number {
  const carouselOrderDelta =
    (a.homeCarouselOrder ?? Number.MAX_SAFE_INTEGER) - (b.homeCarouselOrder ?? Number.MAX_SAFE_INTEGER);
  if (carouselOrderDelta !== 0) {
    return carouselOrderDelta;
  }

  return compareHomeFallbackTemplates(a, b);
}

function getHomeCarouselTemplates(): MockupTemplate[] {
  const configured = state.catalog
    .filter((item) => item.homeCarousel === true && isHomeCarouselStaticTemplate(item))
    .sort(compareHomeCarouselTemplates);
  const candidates =
    configured.length > 0
      ? configured
      : state.catalog.filter(isHomeCarouselStaticTemplate).sort(compareHomeFallbackTemplates);

  if (candidates.length === 0) {
    return [];
  }

  const targetCount = getHomeCarouselTemplateCount();
  return Array.from({ length: targetCount }, (_, index) => candidates[index % candidates.length]);
}

function renderHomeTemplateCard(template: MockupTemplate, index: number): string {
  const previewUrl = getTemplatePreviewUrl(template);
  const eager = index < 6 ? 'eager' : 'lazy';
  const phaseIndex = index % getHomeCarouselTemplateCount();
  const userPreviewLayer = `<img
    class="home-card-user-preview"
    data-home-composite-id="${escapeHtml(template.id)}"
    alt=""
    aria-hidden="true"
    loading="${eager}"
  />`;

  return `
    <button
      class="home-phone-card"
      type="button"
      data-home-template-id="${escapeHtml(template.id)}"
      aria-label="Open ${escapeHtml(template.name)}"
      style="--home-card-phase: ${phaseIndex};"
    >
      <img class="home-card-base-preview" src="${escapeHtml(previewUrl)}" alt="${escapeHtml(template.name)}" loading="${eager}" />
      ${userPreviewLayer}
      <span class="home-card-title">${escapeHtml(template.name)}</span>
    </button>
  `;
}

function renderHomeCarouselPlaceholders(): string {
  return Array.from({ length: getHomeCarouselTemplateCount() * 2 }, () => '<div class="home-phone-card is-placeholder"></div>').join('');
}

function updateHomeUploadUi(): void {
  const uploadLabel = document.querySelector<HTMLElement>('#home-upload-dropzone .home-upload-button span');
  if (uploadLabel) {
    uploadLabel.textContent = state.heroFileName || 'Upload your screenshot';
  }

  const uploadHint = document.querySelector<HTMLElement>('.home-upload-hint');
  if (uploadHint) {
    uploadHint.textContent = state.heroFileName
      ? 'Open a collection to choose the final mockup.'
      : 'Drop your screenshot and try it free';
  }
}

function renderHomeNoticeSlot(): void {
  const status = document.getElementById('home-status');
  if (!status) {
    return;
  }

  if (!state.notice) {
    status.className = 'home-status is-hidden';
    status.textContent = '';
    return;
  }

  status.className = `home-status ${state.notice.type}`;
  status.textContent = state.notice.message;
}

function setHomeNotice(message: string, type: 'info' | 'error' | 'success'): void {
  state.notice = { message, type };
  renderHomeNoticeSlot();

  if (noticeTimer) {
    window.clearTimeout(noticeTimer);
  }

  noticeTimer = window.setTimeout(() => {
    state.notice = null;
    renderHomeNoticeSlot();
  }, 3500);
}

function clearHomeCompositePreviewLayers(): void {
  document.querySelectorAll<HTMLImageElement>('.home-card-user-preview').forEach((image) => {
    image.classList.remove('is-ready');
    image.removeAttribute('src');
  });
}

function resetHomeCompositePreviews(): void {
  homeCompositePreviewToken += 1;
  homeCompositePreviewCache.clear();
}

function getHomeCompositePreviewCacheKey(template: MockupTemplate): string {
  return [
    template.id,
    template.previewUpdatedAt || template.updatedAt || '',
    state.userImagePreviewSrc || state.userImageSrc || state.userImageName || '',
    shouldUseAlternateMask(template) ? 'mask2' : 'mask1'
  ].join('|');
}

async function renderHomeCompositePreviewUrl(template: MockupTemplate): Promise<string> {
  const userImage = getInteractiveUserImage();
  if (!userImage) {
    return '';
  }

  const key = getHomeCompositePreviewCacheKey(template);
  const cached = homeCompositePreviewCache.get(key);
  if (cached) {
    return cached;
  }

  const loading = (async () => {
    const assets = await ensurePreviewAssets(template);
    const previewCanvas = renderMaster(assets, userImage, {
      renderSize: 640,
      gridX: 18,
      gridY: 18,
      diffuse: getDiffuseAdjustments(),
      background: getBackgroundAdjustments(),
      reflection: getReflectionAdjustments(),
      screenOverlay: getScreenOverlayAdjustments(),
      useAlternateMask: shouldUseAlternateMask(template),
      warpMode: 'fit',
      realtime: true
    });
    return previewCanvas.toDataURL('image/jpeg', 0.86);
  })();

  homeCompositePreviewCache.set(key, loading);
  try {
    return await loading;
  } catch (error) {
    homeCompositePreviewCache.delete(key);
    throw error;
  }
}

function hydrateHomeCarouselCompositePreviews(): void {
  if (!state.userImage) {
    homeCompositePreviewToken += 1;
    return;
  }

  const token = homeCompositePreviewToken + 1;
  homeCompositePreviewToken = token;
  const images = Array.from(document.querySelectorAll<HTMLImageElement>('.home-card-user-preview[data-home-composite-id]'));
  const templateIds = Array.from(new Set(images.map((image) => image.dataset.homeCompositeId || '').filter(Boolean)));

  void (async () => {
    for (const templateId of templateIds) {
      if (token !== homeCompositePreviewToken || state.usePaneView !== 'home') {
        return;
      }

      const template = state.catalog.find((item) => item.id === templateId);
      if (!template) {
        continue;
      }

      try {
        const previewUrl = await renderHomeCompositePreviewUrl(template);
        if (token !== homeCompositePreviewToken || state.usePaneView !== 'home') {
          return;
        }

        document.querySelectorAll<HTMLImageElement>('.home-card-user-preview[data-home-composite-id]').forEach((image) => {
          if (image.dataset.homeCompositeId !== templateId) {
            return;
          }
          image.src = previewUrl;
          image.classList.add('is-ready');
        });
      } catch {
        // Keep the original preview if a template's edit assets are unavailable.
      }
    }
  })();
}

function renderHomeObject3DLeft(): string {
  return `
    <div class="home-object home-object-left" aria-hidden="true">
      <div class="home-orbit-large"></div>
      <div class="home-sphere-small"></div>
      <svg class="home-cube-left" width="90" height="90" viewBox="0 0 90 90" fill="none">
        <rect x="22" y="22" width="48" height="48" rx="3" stroke="#0f5bff" stroke-width="1.2" transform="rotate(12 46 46)" />
        <rect x="12" y="12" width="48" height="48" rx="3" stroke="#0f5bff" stroke-width=".5" transform="rotate(20 36 36)" />
      </svg>
      <svg class="home-dash-left" width="2" height="500" viewBox="0 0 2 500">
        <line x1="1" y1="0" x2="1" y2="500" stroke="#0f5bff" stroke-width="1" stroke-dasharray="4 12" />
      </svg>
    </div>
  `;
}

function renderHomeObject3DRight(): string {
  return `
    <div class="home-object home-object-right" aria-hidden="true">
      <div class="home-ring-right"></div>
      <div class="home-sphere-right"></div>
      <div class="home-dot home-dot-1"></div>
      <div class="home-dot home-dot-2"></div>
      <div class="home-dot home-dot-3"></div>
      <svg class="home-cube-right" width="90" height="90" viewBox="0 0 90 90" fill="none">
        <rect x="22" y="22" width="48" height="48" rx="3" stroke="#0f5bff" stroke-width="1.2" transform="rotate(-22 46 46)" />
        <rect x="12" y="12" width="48" height="48" rx="3" stroke="#0f5bff" stroke-width=".5" transform="rotate(-10 36 36)" />
      </svg>
      <svg class="home-dash-right" width="2" height="500" viewBox="0 0 2 500">
        <line x1="1" y1="0" x2="1" y2="500" stroke="#0f5bff" stroke-width="1" stroke-dasharray="4 12" />
      </svg>
    </div>
  `;
}

function renderUploadIcon(): string {
  return `
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  `;
}

function enterCatalogFromHome(): void {
  state.selectedPurposeId = '';
  discardNewTemplatesMode();
  state.usePaneView = 'collections';
  state.quickCatalogFilter = 'collections';
  state.favoritesOnly = false;
  state.selectedCollectionId = '';
  state.catalogSearch = '';
  state.category = 'All';
  pushNavHash();
  render();
}

function enterHomeScreen(): void {
  pauseMotionPlayback({ preserveFrame: true });
  discardNewTemplatesMode();
  state.usePaneView = 'home';
  state.favoritesOnly = false;
  pushNavHash();
  render();
}

function openHeroTemplateFromHome(templateId = ''): void {
  const templates = getHomeCarouselTemplates();
  const template = templateId
    ? state.catalog.find((item) => item.id === templateId)
    : templates[0];
  if (!template) {
    enterCatalogFromHome();
    return;
  }

  focusTemplateForHomeExport(template);
  openTemplateDetail(template.id);
}

function isSupportedHomeImageFile(file: File): boolean {
  const lowerName = file.name.trim().toLowerCase();
  return (
    file.type.startsWith('image/') ||
    lowerName.endsWith('.png') ||
    lowerName.endsWith('.jpg') ||
    lowerName.endsWith('.jpeg') ||
    lowerName.endsWith('.webp')
  );
}

async function handleHomeImageUpload(file: File): Promise<void> {
  state.heroDragActive = false;

  if (!isSupportedHomeImageFile(file)) {
    setHomeNotice('Unsupported file: use PNG, JPG or WebP', 'error');
    return;
  }

  state.heroFileName = file.name;
  updateHomeUploadUi();
  clearHomeCompositePreviewLayers();
  setHomeNotice('Loading screenshot...', 'info');

  try {
    await applyLoadedUserImageFromFile(file);
    updateHomeUploadUi();
    hydrateHomeCarouselCompositePreviews();
    setHomeNotice('Screenshot loaded. Choose a mockup to continue.', 'success');
  } catch (error) {
    state.heroFileName = '';
    updateHomeUploadUi();
    setHomeNotice(error instanceof Error ? error.message : 'Failed to load screenshot', 'error');
  }
}

function getHomeExportTemplate(): MockupTemplate | null {
  return filteredCatalog().find((item) => !item.isVideo) || state.catalog.find((item) => !item.isVideo) || null;
}

function focusTemplateForHomeExport(template: MockupTemplate): void {
  state.selectedContentType = isMotionTemplateType(template.templateType) ? 'MOTION' : 'STATIC';
  state.selectedSourceFilter = isMotionTemplateType(template.templateType) ? 'ALL' : template.templateType === 'AI' ? 'AI' : 'ALL';
  state.templateType = getEffectiveTemplateType();
  state.selectedDomainCategory = normalizeDomainCategory(template.domainCategory ?? DEFAULT_DOMAIN_CATEGORY);
  state.selectedCollectionId = template.collectionId || getDefaultCollectionIdForType(template.templateType);
  state.quickCatalogFilter = 'collections';
  state.favoritesOnly = false;
  state.category = 'All';
}

async function handleHomeExportDemo(): Promise<void> {
  if (!state.heroFileName || state.heroExporting) {
    return;
  }

  if (!state.userImage) {
    setNotice('Upload a screenshot first', 'error');
    return;
  }

  if (
    shouldRequireAuthForImageRender() &&
    requireStandaloneAuth('export', {
      type: 'export',
      mode: 'download'
    })
  ) {
    return;
  }

  const template = getHomeExportTemplate();
  if (!template) {
    setNotice('No static mockups available for export', 'error');
    enterCatalogFromHome();
    return;
  }

  state.heroExporting = true;
  render();

  try {
    focusTemplateForHomeExport(template);
    openTemplateDetail(template.id);
    await handleDownload();
  } finally {
    state.heroExporting = false;
  }
}

function bindHomeEvents(): void {
  hydrateHomeCarouselCompositePreviews();
  document.getElementById('home-browse')?.addEventListener('click', enterCatalogFromHome);
  document.querySelectorAll<HTMLElement>('[data-home-template-id]').forEach((item) => {
    item.addEventListener('click', () => {
      openHeroTemplateFromHome(item.dataset.homeTemplateId || '');
    });
  });

  const fileInput = document.getElementById('home-file-input') as HTMLInputElement | null;
  fileInput?.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) {
      void handleHomeImageUpload(file);
    }
  });

  const dropzone = document.getElementById('home-upload-dropzone');
  dropzone?.addEventListener('dragover', (event) => {
    event.preventDefault();
    dropzone.classList.add('is-dragging');
  });
  dropzone?.addEventListener('dragleave', (event) => {
    const nextTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    if (nextTarget && dropzone.contains(nextTarget)) {
      return;
    }
    dropzone.classList.remove('is-dragging');
  });
  dropzone?.addEventListener('drop', (event) => {
    event.preventDefault();
    dropzone.classList.remove('is-dragging');
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      void handleHomeImageUpload(file);
    } else {
      state.heroDragActive = false;
    }
  });
}

/**
 * What makes this a different screen rather than the same one redrawn.
 *
 * render() replaces the whole document body, which resets the scroll position.
 * That is correct when the user has navigated somewhere new, and wrong when
 * they have only touched a control: adjusting a gradient threw the panel back
 * to the top of the page, away from the control being adjusted. Scroll is
 * therefore restored whenever the screen identity is unchanged.
 */
function getRenderScreenKey(): string {
  return [
    state.usePaneView,
    state.selectedId,
    state.selectedCollectionId,
    state.selectedPurposeId,
    state.selectedContentType,
    state.selectedDomainCategory,
    state.category,
    state.quickCatalogFilter,
    state.favoritesOnly ? 'fav' : '',
    state.newTemplatesMode ? 'new' : '',
    state.catalogSearch
  ].join('|');
}

/** Called straight after the markup is swapped in, before the frame is painted,
 *  so the jump never becomes visible. */
/**
 * Carries the drawn preview across a re-render.
 *
 * render() writes a fresh <canvas> into the markup, so every full redraw threw
 * away the painted frame and left the preview blank until the next async render
 * finished — the flash the panel was showing on almost every control. Moving
 * the old node into the new markup keeps the last good frame on screen while
 * the new one is computed. Nothing binds listeners to the canvas itself (they
 * live on .preview-dropzone), so the node travels safely.
 */
function preserveDrawnPreview(): void {
  const drawn = document.getElementById('preview-canvas') as HTMLCanvasElement | null;
  preservedPreviewCanvas = drawn && drawn.width > 1 && drawn.height > 1 ? drawn : null;
}

function reuseDrawnPreview(): void {
  const kept = preservedPreviewCanvas;
  preservedPreviewCanvas = null;
  if (!kept) {
    return;
  }
  const fresh = document.getElementById('preview-canvas') as HTMLCanvasElement | null;
  if (fresh && fresh !== kept) {
    fresh.replaceWith(kept);
  }
}

function restoreRenderScroll(): void {
  const appTarget = pendingRenderScrollTop;
  const editorTarget = pendingEditorScrollTop;
  if (appTarget === null && editorTarget === null) {
    return;
  }
  const token = renderScrollToken;
  pendingRenderScrollTop = null;
  pendingEditorScrollTop = null;

  const restore = (): void => {
    if (token !== renderScrollToken) {
      return;
    }
    if (appTarget !== null && appRoot.scrollTop !== appTarget) {
      appRoot.scrollTop = appTarget;
    }
    if (editorTarget !== null) {
      const editor = appRoot.querySelector<HTMLElement>('.detail-editor-column');
      if (editor && editor.scrollTop !== editorTarget) {
        editor.scrollTop = editorTarget;
      }
    }
  };

  restore();
  // Anything that changes height after this frame — an image settling, a
  // disclosure measuring itself — would otherwise clamp the value away. The
  // token keeps a late frame from dragging a *new* screen back to where the
  // previous one was scrolled: navigating away must still start at the top.
  window.requestAnimationFrame(restore);
}

function deferCatalogContentSwitch(): boolean {
  const pane = document.getElementById('use-pane');
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const changed = Boolean(pane?.dataset.catalogContentType && pane.dataset.catalogContentType !== state.selectedContentType);
  if (!pane || !changed || state.usePaneView === 'home' || reducedMotion || typeof pane.animate !== 'function') {
    if (catalogPaneExitAnimation) {
      const animation = catalogPaneExitAnimation;
      const opacity = pane ? Number(getComputedStyle(pane).opacity) : 1;
      catalogPaneExitAnimation = null;
      animation.cancel();
      if (!reducedMotion && state.usePaneView !== 'home') {
        catalogPaneRevealFromOpacity = opacity;
        catalogPaneRevealStartedAt = performance.now();
      }
    }
    if (pane) pane.inert = false;
    catalogPaneSwapReady = false;
    if (reducedMotion || state.usePaneView === 'home') catalogPaneRevealStartedAt = null;
    return false;
  }
  if (catalogPaneSwapReady) {
    catalogPaneSwapReady = false;
    return false;
  }
  if (catalogPaneExitAnimation) return true;

  pane.inert = true;
  const animation = pane.animate([
    { opacity: getComputedStyle(pane).opacity }, { opacity: 0 }
  ], { duration: 140, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' });
  catalogPaneExitAnimation = animation;
  const finish = (): void => {
    if (catalogPaneExitAnimation !== animation) return;
    catalogPaneExitAnimation = null;
    catalogPaneSwapReady = true;
    catalogPaneRevealFromOpacity = 0;
    catalogPaneRevealStartedAt = performance.now();
    pane.inert = false;
    render();
    animation.cancel();
  };
  void animation.finished.then(finish, finish);
  return true;
}

function syncCatalogPaneReveal(): void {
  if (catalogPaneRevealStartedAt === null) return;
  const elapsed = performance.now() - catalogPaneRevealStartedAt;
  const pane = document.getElementById('use-pane');
  if (elapsed >= 280 || !pane || typeof pane.animate !== 'function' ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    catalogPaneRevealStartedAt = null;
    return;
  }
  // A quiet catalogue/auth refresh must resume the reveal, not start it over.
  const animation = pane.animate([{ opacity: catalogPaneRevealFromOpacity }, { opacity: 1 }], {
    duration: 280, easing: 'cubic-bezier(0.22, 0.8, 0.25, 1)'
  });
  animation.currentTime = elapsed;
}

type SourceIndicator = Exclude<SourceFilter, 'ALL'>;

function resolveTemplateSourceIndicator(templateType: string | null | undefined): SourceIndicator | null {
  if (templateType === 'AI' || templateType === 'MOTION-AI') return 'AI';
  if (templateType === 'CG' || templateType === 'MOTION-CG') return 'CG';
  return null;
}

function renderSourceFilterToggle(indicatorSource: SourceIndicator | null = null): string {
  if (indicatorSource) {
    return `<div class="source-filter-toggle is-source-indicator" role="status" aria-label="Collection source: ${indicatorSource}">
      <span class="segmented-thumb" aria-hidden="true"></span>
      <button type="button" class="is-source-empty" disabled tabindex="-1" aria-hidden="true">All</button>
      <button type="button" class="${indicatorSource === 'CG' ? 'is-current-source' : 'is-other-source'}" disabled tabindex="-1" ${indicatorSource === 'CG' ? 'aria-current="true"' : ''}>CG</button>
      <button type="button" class="${indicatorSource === 'AI' ? 'is-current-source' : 'is-other-source'}" disabled tabindex="-1" ${indicatorSource === 'AI' ? 'aria-current="true"' : ''}>AI</button>
    </div>`;
  }
  return `<div class="source-filter-toggle" data-segmented="source-filter" role="group" aria-label="Source filter">
    <span class="segmented-thumb" aria-hidden="true"></span>
    ${(['ALL', 'CG', 'AI'] as const).map(value =>
      `<button type="button" class="${state.selectedSourceFilter === value ? 'active' : ''}" data-source-filter="${value}" aria-pressed="${state.selectedSourceFilter === value}">${value === 'ALL' ? 'All' : value}</button>`
    ).join('')}
  </div>`;
}

function render(): void {
  // A background preview/auth refresh must not replace a pending save/discard
  // question. Apply the latest render after the decision instead.
  if (appRoot.querySelector('.editor-unsaved-backdrop')) { editorRenderDeferred = true; return; }
  if (renderPublicRouteIfNeeded()) return;
  if (deferCatalogContentSwitch()) return;
  captureHeaderThumbs();
  editorRenderDeferred = false;
  editorDismissGuards.forEach(guard => guard.dispose());
  editorDismissGuards = [];
  const previousScreenKey = lastRenderScreenKey;
  const previousScrollTop = appRoot.scrollTop;
  const previousEditorScrollTop =
    appRoot.querySelector<HTMLElement>('.detail-editor-column')?.scrollTop || 0;
  lastRenderScreenKey = getRenderScreenKey();
  renderScrollToken += 1;
  preserveDrawnPreview();
  const sameScreen = previousScreenKey === lastRenderScreenKey;
  pendingRenderScrollTop = sameScreen && previousScrollTop > 0 ? previousScrollTop : null;
  pendingEditorScrollTop = sameScreen && previousEditorScrollTop > 0 ? previousEditorScrollTop : null;
  if (!sameScreen) {
    // A new screen starts at the top, whatever the old one was showing.
    appRoot.scrollTop = 0;
  }

  cleanupAccountMenuOutsideClick();
  cleanupGradientPresetsOutsideClick();
  cleanupMobileStickyPreviewBehavior();
  cleanupNewTemplatesLoadObserver();
  clearPendingMotionCardHoverPreview();
  disposeActiveMotionCardHoverPreview();
  const selected = getSelectedTemplate();
  const selectedCollection = getSelectedCollection();
  const selectedDetailCollection = selected ? getTemplateCollection(selected) : null;
  const visibleTemplates = filteredCatalog();
  if (!hasDynamicIslandControl(selected)) {
    state.dynamicIsland = false;
  }

  if (state.usePaneView === 'home') {
    appRoot.innerHTML = renderHomeScreen();
    reuseDrawnPreview();
    restoreRenderScroll();
    bindHomeEvents();
    bindUseModeEvents();
    syncSegmentedThumbs();
    restoreQueuedInputFocus();
    window.requestAnimationFrame(syncScrollChrome);
    return;
  }

  const isCollectionTemplateView = state.usePaneView === 'templates' && Boolean(state.selectedCollectionId) && !state.favoritesOnly;
  const scopedSourceIndicator = resolveTemplateSourceIndicator(
    state.usePaneView === 'detail'
      ? selected?.templateType
      : isCollectionTemplateView
        ? selectedCollection?.templateType
        : null
  );
  const sourceFilterInteractive =
    (state.usePaneView === 'collections' || (state.usePaneView === 'templates' && !isCollectionTemplateView));
  const availableCategoryFilters = getAvailableCategoryFilters({ ignoreSearch: isCollectionTemplateView });
  const showInlineCategoryTabs = state.quickCatalogFilter === 'collections' && state.usePaneView === 'templates' && !isCollectionTemplateView;
  const visibleCollections = filteredCollections();
  const hasCollectionsSearchQuery =
    state.usePaneView === 'collections' &&
    state.quickCatalogFilter === 'collections' &&
    Boolean(state.catalogSearch.trim());
  const collectionSearchTemplates = filteredCollectionSearchTemplates();
  const renderedTemplates = state.newTemplatesMode
    ? visibleTemplates.slice(0, state.newTemplatesVisibleCount)
    : visibleTemplates;
  const hasMoreNewTemplates = state.newTemplatesMode && renderedTemplates.length < visibleTemplates.length;
  const templateGridPlaceholderCount =
    state.usePaneView === 'templates' ? (3 - (renderedTemplates.length % 3 || 3)) % 3 : 0;
  const favoriteCount = getFavoriteCountForCurrentType();
  const templatesTitle = state.favoritesOnly
    ? 'Favorites'
    : state.newTemplatesMode
      ? 'New Templates'
      : state.selectedCollectionId
        ? selectedCollection?.name || 'Collection'
        : state.category === 'All'
          ? 'All Templates'
          : `Category: ${state.category}`;
  const toolbarNavLabel = state.usePaneView === 'collections' ? 'Refresh' : 'Back';
  const toolbarNavClass = state.usePaneView === 'collections' ? '' : 'primary';
  const detailPreviewKey = selected ? getDetailPreviewKey(selected) : '';
  const showDetailPreviewLoading = Boolean(selected) && state.detailPreviewReadyKey !== detailPreviewKey;
  const detailNavigation = selected ? getTemplateNavigation(visibleTemplates, selected.id) : { previousId: null, nextId: null };
  const selectedStaticVideoExportActive = isStaticVideoExportActive(selected);
  const exportRequiresLogin =
    Boolean(selected) &&
    isStandaloneWebApp &&
    !state.authUser &&
    shouldRequireAuthForDownload(selected, selectedStaticVideoExportActive);
  const quickCatalogFilterOptions: QuickCatalogFilter[] = isCollectionTemplateView
    ? availableCategoryFilters
    : ['collections', 'All', ...getSubCategoriesForDomain(state.selectedDomainCategory)];
  const quickCatalogSelectValue: QuickCatalogFilter =
    isCollectionTemplateView && state.quickCatalogFilter === 'collections'
      ? state.category
      : state.quickCatalogFilter;

  appRoot.innerHTML = `
    <div class="app-shell web-app-shell">
      <header class="topbar">
        <div class="topbar-row">
          <div id="topbar-home-link" class="topbar-brand" role="button" tabindex="0" title="Home">
            <img class="topbar-logo" src="${escapeHtml(motionPitchLogo)}" alt="MotionPitch" />
            <h1>MotionPitch Mockups</h1>
          </div>
          <div class="topbar-controls">
            <div class="topbar-status-slot">
              ${
                state.notice
                  ? `<p class="topbar-status ${state.notice.type}" title="${escapeHtml(state.notice.message)}">${escapeHtml(state.notice.message)}</p>`
                  : '<span class="topbar-status-placeholder" aria-hidden="true"></span>'
              }
            </div>
            ${renderThemeToggle()}
            ${renderAccountControl()}
          </div>
        </div>
        <div class="topbar-divider" aria-hidden="true"></div>
        <div class="topbar-row-secondary">
          <div class="content-type-toggle" data-segmented="content-type" role="tablist" aria-label="Content Type">
            <span class="segmented-thumb" aria-hidden="true"></span>
            ${CONTENT_TYPES.map(
              (ct) =>
                `<button type="button" class="${state.selectedContentType === ct ? 'active' : ''}" data-content-type="${ct}" aria-pressed="${state.selectedContentType === ct}">${ct === 'STATIC' ? 'Static' : 'Motion'}</button>`
            ).join('')}
          </div>
          <div class="domain-category-tabs${SHOW_DOMAIN_CATEGORY_TABS ? '' : ' is-hidden'}" role="tablist" aria-label="Domain Category"${
            SHOW_DOMAIN_CATEGORY_TABS ? '' : ' aria-hidden="true"'
          }>
            ${DOMAIN_CATEGORIES.map(
              (dc) =>
                `<button type="button" class="domain-tab ${state.selectedDomainCategory === dc ? 'active' : ''}" data-domain-category="${dc}">${dc}</button>`
            ).join('')}
          </div>
          ${
            isStandaloneWebApp && sourceFilterInteractive
              ? renderSourceFilterToggle()
              : isStandaloneWebApp && scopedSourceIndicator
                ? renderSourceFilterToggle(scopedSourceIndicator)
                : '<span class="source-filter-placeholder" aria-hidden="true"></span>'
          }
        </div>
      </header>

      ${state.error ? `<p class="error">${escapeHtml(state.error)}</p>` : ''}

      <section id="use-pane" data-catalog-content-type="${state.selectedContentType}" class="pane ${state.usePaneView === 'templates' ? 'pane-templates' : ''} ${state.usePaneView === 'account' ? 'pane-account' : ''} ${state.usePaneView === 'detail' ? `pane-detail ${selected?.isVideo ? 'pane-detail-motion' : 'pane-detail-static'}` : ''}">
        ${
          state.usePaneView === 'account'
            ? ''
            : state.usePaneView === 'detail' && selected
            ? `
              <div class="catalog-header detail-toolbar-row">
                <div class="catalog-toolbar detail-toolbar">
                  ${isStandaloneWebApp ? '' : `<button id="catalog-nav-button" class="toolbar-nav-button ${toolbarNavClass}">${toolbarNavLabel}</button>`}
                  ${renderDetailBreadcrumb(selected, selectedDetailCollection)}
                  <button
                    id="detail-favorite"
                    class="detail-toolbar-favorite ${isFavoriteTemplate(selected.id) ? 'active' : ''}"
                    type="button"
                    aria-label="${isFavoriteTemplate(selected.id) ? 'Remove from favorites' : 'Add to favorites'}"
                    title="${isFavoriteTemplate(selected.id) ? 'In Favorites' : 'Add to Favorites'}"
                  >★</button>
                </div>
              </div>
            `
            : `
              <div class="catalog-header ${state.usePaneView === 'templates' ? 'catalog-header-breadcrumb' : ''}">
                <div class="catalog-toolbar catalog-toolbar-nav">
                  ${
                    state.usePaneView === 'templates'
                      ? renderTemplatesBreadcrumb(selectedCollection)
                      : !isStandaloneWebApp && state.usePaneView === 'collections' && state.selectedContentType === 'MOTION'
                        ? `<button id="catalog-nav-button" class="toolbar-nav-button ${toolbarNavClass}">${toolbarNavLabel}</button>`
                        : state.usePaneView === 'collections'
                          ? renderCollectionsLocationLabel()
                          : ''
                  }
                </div>
                <div class="catalog-toolbar catalog-toolbar-controls">
                  ${
                    !isStandaloneWebApp && state.usePaneView === 'collections' && state.selectedContentType === 'STATIC'
                      ? `<select id="source-filter-select" class="source-filter-select" title="Source filter">
                           <option value="ALL" ${state.selectedSourceFilter === 'ALL' ? 'selected' : ''}>All</option>
                           <option value="CG" ${state.selectedSourceFilter === 'CG' ? 'selected' : ''}>CG</option>
                           <option value="AI" ${state.selectedSourceFilter === 'AI' ? 'selected' : ''}>AI</option>
                         </select>`
                      : ''
                  }
                  ${
                    isStandaloneWebApp
                      ? ''
                      : `<button id="open-favorites" class="star-button ${state.favoritesOnly ? 'active' : ''}" title="Favorites">
                          ★ ${state.favoritesLoading ? '...' : favoriteCount}
                        </button>`
                  }
                  ${
                    isStandaloneWebApp
                      ? `<button
                          id="new-templates-toggle"
                          class="new-templates-toggle ${state.newTemplatesMode ? 'active' : ''}"
                          type="button"
                          aria-pressed="${state.newTemplatesMode ? 'true' : 'false'}"
                          title="Show newest templates"
                        >NEW</button>`
                      : ''
                  }
                  <select id="quick-catalog-filter">
                    ${quickCatalogFilterOptions
                      .map((option) => {
                        const label = option === 'collections' ? 'Collections' : option;
                        return `<option value="${escapeHtml(option)}" ${quickCatalogSelectValue === option ? 'selected' : ''}>${escapeHtml(label)}</option>`;
                      })
                      .join('')}
                  </select>
                  <input
                    id="catalog-search"
                    type="text"
                    value="${escapeHtml(state.catalogSearch)}"
                    placeholder="${
                      state.usePaneView === 'collections' && state.quickCatalogFilter === 'collections'
                        ? 'Search collections'
                        : 'Search templates'
                    }"
                  />
                </div>
                <span>${state.loadingCatalog || state.loadingCollections ? 'Loading...' : ''}</span>
              </div>
            `
        }

	        ${
	          state.usePaneView === 'account'
	            ? renderAccountPage()
	            : state.usePaneView === 'collections'
	            ? `
                  ${renderCollectionHierarchy({ purposes: state.collectionPurposes, collections: visibleCollections,
                    loading: state.loadingCollections, error: state.collectionsError,
                    contentType: state.selectedContentType, selectedPurposeId: state.selectedPurposeId,
                    query: state.catalogSearch.trim(), countTemplates: countTemplatesForCollection,
                    allCollections: state.collections.filter(c => (c.domainCategory ?? 'App') === state.selectedDomainCategory),
                    matchingTemplateCollectionIds: collectionSearchTemplates.map(t => t.collectionId)
                  })}
	              ${
	                !state.loadingCollections && !state.collectionsError && hasCollectionsSearchQuery && collectionSearchTemplates.length > 0
	                  ? `
	                    <section class="selected-panel templates-panel collection-search-template-results">
	                      <h3>Templates</h3>
	                      <div class="template-grid">
	                        ${renderTemplateGridItems(collectionSearchTemplates)}
	                      </div>
	                    </section>
	                  `
	                  : ''
	              }
	            `
	            : state.usePaneView === 'templates'
	              ? `
                <section class="selected-panel templates-panel">
                  ${isCollectionTemplateView ? '' : `<h3>${escapeHtml(templatesTitle)}</h3>`}

                  ${
                    showInlineCategoryTabs
                      ? `
                        <div class="category-tabs">
                          ${availableCategoryFilters
                            .map(
                              (category) =>
                                `<button class="chip ${state.category === category ? 'active' : ''}" data-category="${category}">${category}</button>`
                            )
                            .join('')}
                        </div>
                      `
                      : ''
                  }

	                  <div class="template-grid">
	                    ${renderTemplateGridItems(renderedTemplates, templateGridPlaceholderCount)}
	                  </div>
                  ${
                    hasMoreNewTemplates
                      ? `<div id="new-templates-load-sentinel" class="new-templates-load-sentinel" aria-hidden="true"></div>`
                      : ''
                  }
                  ${renderedTemplates.length === 0 ? '<p class="hint">No templates found.</p>' : ''}
                </section>
              `
              : selected
                ? `
                  <section class="selected-panel detail-panel">
                    <div class="detail-preview-column">
                    <div
                      id="preview-dropzone"
                      class="preview-dropzone ${showDetailPreviewLoading ? 'preview-loading' : ''} ${selected.isVideo ? 'has-motion-controls' : ''} ${getMotionPreviewAspectRatio(selected) < 1 ? 'is-vertical-motion-preview' : ''} ${isStaticPreviewAvailable(selected) ? 'has-static-controls' : ''}"
                      data-preview-key="${escapeHtml(detailPreviewKey)}"
                      ${getMotionResizeInlineStyle(selected)}
                    >
                      ${
                        state.detailTransitionOverlayUrl
                          ? `<img
                              id="detail-preview-transition-overlay"
                              class="detail-preview-transition-overlay"
                              src="${escapeHtml(state.detailTransitionOverlayUrl)}"
                              alt=""
                              aria-hidden="true"
                            />`
                          : ''
                      }
                      ${
                        selected.isVideo ||
                        hasActiveUserMedia(selected) ||
                        state.staticExport.format !== '1:1' ||
                        (!state.backgroundTransparent &&
                          supportsStaticBackgroundComposer(selected) &&
                          isStaticBackgroundActive(getStaticBackgroundComposition()))
                          ? '<canvas id="preview-canvas" width="320" height="320"></canvas>'
                          : `<img
                              id="preview-image"
                              class="preview-image"
                              src="${escapeHtml(getTemplateDetailPreviewUrl(selected))}"
                              alt="${escapeHtml(selected.name)}"
                            />`
                      }
                      <div
                        class="preview-format-mask is-hidden"
                        aria-hidden="true"
                        style="${getStaticPreviewFormatMaskStyle(state.staticExport.format)}"
                      ></div>
                      <button
                        class="preview-nav-button preview-nav-prev"
                        type="button"
                        data-preview-nav="prev"
                        aria-label="Previous template"
                        ${detailNavigation.previousId ? '' : 'disabled'}
                      >
                        <span aria-hidden="true">‹</span>
                      </button>
                      <button
                        class="preview-nav-button preview-nav-next"
                        type="button"
                        data-preview-nav="next"
                        aria-label="Next template"
                        ${detailNavigation.nextId ? '' : 'disabled'}
                      >
                        <span aria-hidden="true">›</span>
                      </button>
                      <div class="preview-loading-indicator" aria-hidden="true">
                        <span class="preview-loading-ring"></span>
                      </div>
                      ${renderStaticPreviewPlayer(selected)}
                      ${
                        selected.isVideo
                          ? `
                            <div class="preview-motion-controls" data-preview-control-surface>
                              <button
                                id="preview-motion-playback-toggle"
                                class="preview-motion-playback-toggle ${state.motionPlaying ? 'is-playing' : ''}"
                                type="button"
                                aria-label="${state.motionPlaying ? 'Pause preview' : 'Play preview'}"
                                title="${state.motionPlaying ? 'Pause preview' : 'Play preview'}"
                              >
                                <span class="preview-motion-icon preview-motion-icon-play" aria-hidden="true">
                                  <svg viewBox="0 0 24 24" focusable="false">
                                    <path d="M8.4 6.2v11.6L17.6 12 8.4 6.2Z"></path>
                                  </svg>
                                </span>
                                <span class="preview-motion-icon preview-motion-icon-pause" aria-hidden="true">
                                  <svg viewBox="0 0 24 24" focusable="false">
                                    <rect x="7.2" y="6.2" width="3.4" height="11.6" rx="1"></rect>
                                    <rect x="13.4" y="6.2" width="3.4" height="11.6" rx="1"></rect>
                                  </svg>
                                </span>
                              </button>
                              <div class="preview-motion-timeline">
                                <span id="preview-motion-current-time" class="preview-motion-time">${formatMotionTimelineTime(
                                  motionPlaybackCurrentTime
                                )}</span>
                                <input
                                  id="preview-motion-scrubber"
                                  type="range"
                                  min="0"
                                  max="1000"
                                  step="1"
                                  value="${getMotionTimelineProgressValue()}"
                                  aria-label="Motion preview timeline"
                                  aria-valuetext="${formatMotionTimelineTime(
                                    motionPlaybackCurrentTime
                                  )} of ${formatMotionTimelineTime(motionPlaybackDuration)}"
                                  style="--motion-timeline-progress: ${getMotionTimelineProgressValue() / 10}%;"
                                  ${motionPlaybackDuration > 0 ? '' : 'disabled'}
                                />
                                <span id="preview-motion-duration" class="preview-motion-time">${formatMotionTimelineTime(
                                  motionPlaybackDuration
                                )}</span>
                              </div>
                            </div>
                          `
                          : ''
                      }
                      <div class="preview-drop-overlay" aria-hidden="true">
                        <strong>${selected.isVideo ? 'Drop MP4 or PNG/JPG' : 'Drop PNG/JPG/MP4/MOV'}</strong>
                        <span>onto the preview</span>
                      </div>
                    </div>
                    </div>
                    <aside class="detail-editor-column" aria-label="Template editor">
                    ${renderMotionLoopInfo(selected)}
                    ${renderInterfaceMediaGuidance(selected)}
                    <div class="detail-editor-file-row ${getTemplateScreenCount(selected) > 1 ? 'is-multi-screen' : ''}">
                      ${renderInterfaceFilePicker(selected, 1)}
                      ${hasTemplateScreen(selected, 2) ? renderInterfaceFilePicker(selected, 2) : ''}
                      ${hasTemplateScreen(selected, 3) ? renderInterfaceFilePicker(selected, 3) : ''}
                    </div>

                    ${!selected.isVideo ? `<label class="source-audio-control" data-static-audio-control hidden><input type="checkbox" data-static-use-audio ${state.staticUseOriginalAudio ? 'checked' : ''} />Use original audio</label>` : ''}
                    ${renderDynamicIslandControl(selected)}

                    <section class="control-disclosure ${state.diffuseControlsOpen ? 'open' : ''}">
                      <button id="diffuse-controls-toggle" class="control-disclosure-toggle" type="button" aria-expanded="${state.diffuseControlsOpen ? 'true' : 'false'}">
                        <span class="control-disclosure-title">Color Control (Diffuse)</span>
                        <span class="control-disclosure-chevron">›</span>
                      </button>
                      <div class="control-disclosure-body">
                        <div class="control-disclosure-inner">
                          <div class="control-disclosure-content color-controls compact">
                            <div class="control-actions-row">
                              <button id="diffuse-reset">Default</button>
                            </div>
                            ${
                              shouldShowScreenReflectionControl(selected)
                                ? renderSliderRow({
                                    id: 'screen-reflection-opacity',
                                    label: 'Reflection',
                                    min: 0,
                                    max: 100,
                                    step: 1,
                                    value: state.screenOverlayOpacity,
                                    valueId: 'screen-reflection-opacity-value',
                                    valueLabel: state.screenOverlayOpacity + '%'
                                  })
                                : ''
                            }
                            ${
                              shouldShowScreenBlurControl(selected)
                                ? renderSliderRow({
                                    id: 'screen-blur',
                                    label: 'Screen blur',
                                    min: 0.1,
                                    max: 100,
                                    step: 0.1,
                                    value: state.screenBlur,
                                    valueId: 'screen-blur-value',
                                    valueLabel: formatScreenBlurValue(state.screenBlur)
                                  })
                                : ''
                            }
                            ${renderSliderRow({
                              id: 'diffuse-hue',
                              label: 'Hue',
                              min: -180,
                              max: 180,
                              step: 1,
                              value: state.diffuseHue,
                              valueId: 'diffuse-hue-value',
                              valueLabel: state.diffuseHue + 'deg'
                            })}
                            ${renderSliderRow({
                              id: 'diffuse-saturation',
                              label: 'Saturation',
                              min: 0,
                              max: 200,
                              step: 1,
                              value: state.diffuseSaturation,
                              valueId: 'diffuse-saturation-value',
                              valueLabel: state.diffuseSaturation + '%'
                            })}
                            ${renderSliderRow({
                              id: 'diffuse-brightness',
                              label: 'Brightness',
                              min: 0,
                              max: 200,
                              step: 1,
                              value: state.diffuseBrightness,
                              valueId: 'diffuse-brightness-value',
                              valueLabel: state.diffuseBrightness + '%'
                            })}
                            ${renderSliderRow({
                              id: 'diffuse-contrast',
                              label: 'Contrast',
                              min: 0,
                              max: 200,
                              step: 1,
                              value: state.diffuseContrast,
                              valueId: 'diffuse-contrast-value',
                              valueLabel: state.diffuseContrast + '%'
                            })}
                            <label class="admin-toggle">
                              <input id="diffuse-monochrome" type="checkbox" ${state.diffuseMonochrome ? 'checked' : ''} />
                              <span id="diffuse-mode-label">${state.diffuseMonochrome ? 'Monochrome' : 'Color'}</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </section>

                    ${renderMotionColorMatteControls(selected)}

                    ${renderBackgroundControls(selected)}
                    ${renderDynamicZoomControls(selected)}

                    ${
	                      selected.isVideo
	                        ? `
	                          <div class="export-controls motion-export-controls">
	                            <div class="export-control export-control-wide">
	                              <span>Format</span>
	                              ${renderSegmentedControl(
	                                MOTION_RESIZE_OPTIONS,
	                                state.motionExport.resizeMode,
	                                'motion-resize-mode',
	                                'Motion format',
	                                'sm'
	                              )}
	                            </div>
		                          </div>

	                          <div class="motion-export-meta">
	                            <span>Video export · ${state.motionExport.resizeMode} · ${state.staticExport.mode === 'custom' ? 'custom' : 'fit'}</span>
	                            <strong>${getMotionExportLabel(selected)}</strong>
                            <small>${
                              isMobileVideoExportRuntime()
                                ? 'Desktop only for MP4 rendering'
                                : isSafariBrowserRuntime()
                                ? 'Use Chrome for MP4 rendering'
                                : isMotionVideoExportSupported()
                                ? 'WebCodecs export available'
                                : isEmbeddedPlugin
                                  ? 'Use Export in Browser from Figma'
                                  : 'WebCodecs unavailable in this environment'
                            }</small>
                          </div>
                        `
                        : `
                          <div class="export-controls">
                            <div class="export-control">
                              <span>Quality</span>
                              ${renderSegmentedControl(
                                EXPORT_RESOLUTION_OPTIONS,
                                state.staticExport.resolution,
                                'static-export-resolution',
                                'Export quality',
                                'sm'
                              )}
                            </div>
                            <div class="export-control export-control-wide">
                              <span>Format</span>
                              ${renderSegmentedControl(
                                STATIC_EXPORT_FORMAT_OPTIONS,
                                state.staticExport.format,
                                'static-export-format',
                                'Export format',
                                'sm'
                              )}
                            </div>
                          </div>
                        `
                    }

                    <div class="button-row">
                      <button
                        id="download-export"
                        class="primary"
                        ${
                          !hasRequiredUserMedia(selected) ||
                          state.exporting ||
                          (!selected.isVideo &&
                            selectedStaticVideoExportActive &&
                            (isMobileVideoExportRuntime() ||
                              (!isSafariBrowserRuntime() &&
                                (isStaticBackgroundAnimationVideoActive(selected)
                                  ? !isStaticVideoWebCodecsSupported()
                                  : !isStaticVideoExportSupported())))) ||
                          (selected.isVideo &&
                            !isMobileVideoExportRuntime() &&
                            !isSafariBrowserRuntime() &&
                            !isMotionVideoExportSupported())
                            ? 'disabled'
                            : ''
                        }
                      >
                        ${
                          state.exporting
                            ? 'Export...'
                            : exportRequiresLogin
                              ? 'Login to Export'
                              : selected.isVideo
                                ? 'Export MP4'
                                : selectedStaticVideoExportActive
                                  ? 'Export MP4'
                                  : isStandaloneWebApp
                                  ? 'Download PNG'
                                  : 'Export to Computer'
                        }
                      </button>
                      ${
                        selected.isVideo && isEmbeddedPlugin
                          ? `<button id="open-browser-export" ${!hasActiveUserMedia(selected) || state.openingBrowserExport ? 'disabled' : ''}>
                              ${state.openingBrowserExport ? 'Opening…' : 'Export in Browser'}
                            </button>`
                          : ''
                      }
                      ${
                        selected.isVideo || isStandaloneWebApp
                          ? ''
                          : `<button id="insert-export" ${!state.userImage || state.inserting ? 'disabled' : ''}>
                              ${state.inserting ? 'Insert...' : 'Insert into Project'}
                            </button>`
                      }
                    </div>
                    </aside>
                  </section>
                `
                : '<p class="hint">Select a mockup from the catalog.</p>'
        }
      </section>
      ${renderCustomPlacementEditor(selected)}
      ${renderMeshEditor()}
      ${renderGradientAxisEditor()}
      ${renderDynamicZoomEditor(selected)}
      ${renderAuthPrompt()}
    </div>
    <div id="overlay-scrollbar" class="overlay-scrollbar hidden" aria-hidden="true">
      <div id="overlay-scrollbar-thumb" class="overlay-scrollbar-thumb"></div>
    </div>
  `;

  reuseDrawnPreview();
  restoreRenderScroll();
  bindUseModeEvents();
  syncBackgroundAnimationLoop();
  syncDynamicZoomLoop();
  bindScrollChromeThumb();
  bindMobileStickyPreviewBehavior();
  syncSegmentedThumbs();

  syncCatalogPaneReveal();

  const hasActiveStaticBackground = Boolean(
    selected &&
    !state.backgroundTransparent &&
    supportsStaticBackgroundComposer(selected) &&
    isStaticBackgroundActive(getStaticBackgroundComposition())
  );
  if (
    state.usePaneView === 'detail' &&
    (Boolean(selected?.isVideo) || hasActiveUserMedia(selected) || state.staticExport.format !== '1:1' || hasActiveStaticBackground)
  ) {
    schedulePreviewRender();
  }

  if (state.usePaneView === 'detail' && selected?.isVideo && pendingMotionDetailAutoplayTemplateId === selected.id) {
    pendingMotionDetailAutoplayTemplateId = '';
    window.requestAnimationFrame(() => {
      const currentSelected = getSelectedTemplate();
      if (state.usePaneView !== 'detail' || !currentSelected?.isVideo || currentSelected.id !== selected.id || state.motionPlaying) {
        return;
      }
      void startMotionPlayback();
    });
  }

  restoreQueuedInputFocus();
  window.requestAnimationFrame(syncScrollChrome);
}

function bindPreviewControlSurfaceEvents(surface: HTMLElement | null): void {
  if (!surface) {
    return;
  }

  ['pointerdown', 'pointerup', 'pointercancel', 'click', 'touchstart', 'touchend'].forEach((eventName) => {
    surface.addEventListener(eventName, (event) => {
      event.stopPropagation();
    });
  });
}

function bindStaticVideoStoryboardEvents(root: ParentNode = document): void {
  const surface =
    root instanceof HTMLElement && root.matches('#static-video-storyboard')
      ? root
      : root.querySelector<HTMLElement>('#static-video-storyboard');
  bindPreviewControlSurfaceEvents(surface);

  const supportsStoryboardHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  root.querySelectorAll<HTMLButtonElement>('[data-static-video-storyboard-index]').forEach((button) => {
    const selectFrame = (): void => {
      const index = Number(button.dataset.staticVideoStoryboardIndex);
      if (Number.isInteger(index)) {
        selectStaticVideoStoryboardFrame(index);
      }
    };

    if (supportsStoryboardHover) {
      button.addEventListener('mouseenter', selectFrame);
    }
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      selectFrame();
    });
  });
}

/**
 * The sliding pill behind the active option of a segmented control.
 *
 * Choosing an option re-renders the whole pane, so the control the viewer
 * clicked is gone by the time the new one appears and a plain CSS transition has
 * nothing to animate from. The geometry is therefore captured on the way down —
 * a capture-phase listener runs before the button's own handler calls render() —
 * and replayed on the freshly built pill: it is placed where the old one stood,
 * then released, so the movement reads as one continuous slide.
 *
 * Offsets are measured inside the control rather than against the page, so the
 * animation is unaffected by scrolling between the two frames.
 */
function getSegmentedControlKey(control: HTMLElement): string {
  const name = control.dataset.segmented || '';
  const siblings = Array.from(document.querySelectorAll<HTMLElement>(`[data-segmented="${name}"]`));
  // Dual-screen panes carry two controls with the same name, so position
  // disambiguates them.
  return `${name}#${siblings.indexOf(control)}`;
}

function captureSegmentedThumb(control: HTMLElement): void {
  const active = control.querySelector<HTMLElement>('.segmented-option.active');
  segmentedThumbSnapshot = active
    ? { key: getSegmentedControlKey(control), left: active.offsetLeft, width: active.offsetWidth }
    : null;
}

function captureHeaderThumbs(): void {
  headerThumbSnapshots.clear();
  // Preserve the visible position, including an interrupted slide or a quiet
  // catalogue refresh that rebuilds the header while it is still moving.
  document.querySelectorAll<HTMLElement>('.content-type-toggle, .source-filter-toggle').forEach(control => {
    const bounds = control.querySelector<HTMLElement>('.segmented-thumb')?.getBoundingClientRect();
    if (!bounds || bounds.width <= 0) return;
    const key = getSegmentedControlKey(control);
    headerThumbSnapshots.set(key, { key, left: bounds.left - control.getBoundingClientRect().left, width: bounds.width });
  });
}

function syncHeaderSelection(selector: string, attribute: string, value: string): void {
  const control = document.querySelector<HTMLElement>(selector);
  if (!control) return;
  control.querySelectorAll<HTMLButtonElement>('button').forEach((button) => {
    const active = button.dataset[attribute] === value;
    button.classList.toggle('active', active);
    button.setAttribute('aria-pressed', String(active));
  });
  const active = control.querySelector<HTMLElement>('button.active');
  const thumb = control.querySelector<HTMLElement>('.segmented-thumb');
  if (active && thumb) {
    thumb.style.width = `${active.offsetWidth}px`;
    thumb.style.transform = `translateX(${active.offsetLeft}px)`;
  }
}

function syncContentTypeSelection(contentType: ContentType): void {
  syncHeaderSelection('.content-type-toggle', 'contentType', contentType);
}

function syncSegmentedThumbs(animate = true): void {
  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const editorSnapshot = segmentedThumbSnapshot;
  const headerSnapshots = headerThumbSnapshots;
  segmentedThumbSnapshot = null;
  headerThumbSnapshots = new Map();

  document.querySelectorAll<HTMLElement>('.segmented-control, .content-type-toggle, .source-filter-toggle').forEach((control) => {
    const thumb = control.querySelector<HTMLElement>('.segmented-thumb');
    if (!thumb) {
      return;
    }

    // Re-registered rather than accumulated: every render builds a new control.
    const isHeaderToggle = control.classList.contains('content-type-toggle') || control.classList.contains('source-filter-toggle');
    const snapshot = isHeaderToggle ? headerSnapshots.get(getSegmentedControlKey(control)) || null : editorSnapshot;
    if (!isHeaderToggle) {
      control.addEventListener('click', () => captureSegmentedThumb(control), { capture: true });
    }

    const active = control.querySelector<HTMLElement>('.segmented-option.active, [data-content-type].active, [data-source-filter].active');
    if (!active || active.offsetWidth <= 0) {
      // Nothing selected, or the pane is not laid out yet. Showing a pill of
      // zero width in the corner would be worse than showing none.
      thumb.style.opacity = '0';
      return;
    }

    const left = active.offsetLeft;
    const width = active.offsetWidth;
    const moved =
      animate &&
      !prefersReducedMotion &&
      snapshot !== null &&
      snapshot.key === getSegmentedControlKey(control) &&
      snapshot.width > 0 &&
      (snapshot.left !== left || snapshot.width !== width);

    thumb.style.transition = 'none';
    thumb.style.opacity = '';
    if (moved) {
      thumb.style.width = `${snapshot!.width}px`;
      thumb.style.transform = `translateX(${snapshot!.left}px)`;
    } else {
      thumb.style.width = `${width}px`;
      thumb.style.transform = `translateX(${left}px)`;
    }

    // Forces the start position to be committed before the transition is
    // restored, otherwise both writes collapse into one and nothing animates.
    void thumb.offsetWidth;
    thumb.style.transition = '';

    if (moved) {
      thumb.style.width = `${width}px`;
      thumb.style.transform = `translateX(${left}px)`;
    }
  });
}

function bindUseModeEvents(): void {
  bindBillingEvents();
  const boundSelected = getSelectedTemplate();
  const topbarHomeLink = document.getElementById('topbar-home-link');
  const contentTypeButtons = document.querySelectorAll<HTMLButtonElement>('[data-content-type]');
  const domainCategoryButtons = document.querySelectorAll<HTMLButtonElement>('[data-domain-category]');
  const sourceFilterSelect = document.getElementById('source-filter-select') as HTMLSelectElement | null;
  const sourceFilterButtons = document.querySelectorAll<HTMLButtonElement>('[data-source-filter]');
  const toolbarNavButton = document.getElementById('catalog-nav-button');
  const newTemplatesToggle = document.getElementById('new-templates-toggle');
  const quickCatalogFilterSelect = document.getElementById('quick-catalog-filter') as HTMLSelectElement | null;
  const catalogSearchInput = document.getElementById('catalog-search') as HTMLInputElement | null;
  const openFavoritesButton = document.getElementById('open-favorites');
  const detailFavoriteButton = document.getElementById('detail-favorite');
  const breadcrumbLinks = document.querySelectorAll<HTMLElement>('[data-breadcrumb-target]');
  const loginButton = document.getElementById('login-button');
  const logoutButton = document.getElementById('logout-button');
  const accountMenuButton = document.getElementById('account-menu-button');
  const accountOpenPageButton = document.getElementById('account-open-page-button');
  const accountPageBackButton = document.getElementById('account-page-back');
  const accountMenuTabs = document.querySelectorAll<HTMLButtonElement>('[data-account-view]');
  const accountPageTabs = document.querySelectorAll<HTMLButtonElement>('[data-account-page-view]');
  const accountFavoriteFilterButtons = document.querySelectorAll<HTMLButtonElement>('[data-account-favorite-filter]');
  const accountTemplateButtons = document.querySelectorAll<HTMLButtonElement>('[data-account-template-id]');
  const themeToggleButton = document.getElementById('theme-toggle');
  const authModalCloseButton = document.getElementById('auth-modal-close');
  const authGoogleButton = document.getElementById('auth-google-button');
  const authEmailForm = document.getElementById('auth-email-form') as HTMLFormElement | null;
  const authEmailInput = document.getElementById('auth-email-input') as HTMLInputElement | null;
  const customModeEditButtons = document.querySelectorAll<HTMLButtonElement>('[data-custom-mode-edit]');
  const diffuseControlsToggle = document.getElementById('diffuse-controls-toggle');
  const backgroundControlsToggle = document.getElementById('background-controls-toggle');
  const motionColorMatteToggle = document.getElementById('motion-color-matte-toggle');
  const motionColorMatteResetButton = document.getElementById('motion-color-matte-reset');
  const motionColorMatteEnabledInput = document.getElementById(
    'motion-color-matte-enabled'
  ) as HTMLInputElement | null;
  const motionColorMatteColorInputs = Array.from(
    document.querySelectorAll<HTMLInputElement>('[data-motion-color-matte-color]')
  );
  const previewDropzone = document.getElementById('preview-dropzone') as HTMLDivElement | null;
  const previewImage = document.getElementById('preview-image') as HTMLImageElement | null;
  const interfaceDropzone = document.getElementById('interface-dropzone') as HTMLDivElement | null;
  const interfaceDropzone2 = document.getElementById('interface-dropzone-2') as HTMLDivElement | null;
  const interfaceDropzone3 = document.getElementById('interface-dropzone-3') as HTMLDivElement | null;
  const motionPlaybackButton = document.getElementById('motion-playback-toggle');
  const previewMotionPlaybackButton = document.getElementById('preview-motion-playback-toggle');
  const previewMotionControlSurface = document.querySelector<HTMLElement>('.preview-motion-controls');
  const previewMotionScrubber = document.getElementById('preview-motion-scrubber') as HTMLInputElement | null;
  const dynamicIslandToggle = document.getElementById('dynamic-island-toggle') as HTMLInputElement | null;
  const diffuseResetButton = document.getElementById('diffuse-reset');
  const backgroundResetButton = document.getElementById('background-reset');
  const backgroundTransparentInput = document.getElementById('background-transparent') as HTMLInputElement | null;
  const staticVideoChromaPresetButtons = document.querySelectorAll<HTMLButtonElement>(
    '[data-static-video-chroma-preset]'
  );
  const staticVideoChromaCustomInput = document.getElementById(
    'static-video-chroma-custom'
  ) as HTMLInputElement | null;
  const staticBackgroundColorInputs = Array.from(
    document.querySelectorAll<HTMLInputElement>('[data-static-background-color]')
  );
  const staticBackgroundAngleInput = document.getElementById('static-background-angle') as HTMLInputElement | null;
  const staticBackgroundMeshSeedInput = document.getElementById('static-background-mesh-seed') as HTMLInputElement | null;
  const staticBackgroundMeshMixInput = document.getElementById('static-background-mesh-mix') as HTMLInputElement | null;
  const staticBackgroundMeshDistortionInput = document.getElementById('static-background-mesh-distortion') as HTMLInputElement | null;
  const staticBackgroundMeshGrainInput = document.getElementById('static-background-mesh-grain') as HTMLInputElement | null;
  const staticBackgroundMeshEditButton = document.getElementById('static-background-mesh-edit');
  const staticBackgroundAxisEditButton = document.getElementById('static-background-axis-edit');
  const gradientPaletteUndoButton = document.getElementById('gradient-palette-undo');
  const gradientPaletteRandomButton = document.getElementById('gradient-palette-random');
  const gradientPresetsTrigger = document.getElementById('gradient-presets-trigger');
  const gradientPresetsContainer = gradientPresetsTrigger?.closest<HTMLElement>('.gradient-presets') || null;
  const gradientPresetAddButton = document.getElementById('gradient-preset-add');
  const staticBackgroundImageInput = document.getElementById('static-background-image-input') as HTMLInputElement | null;
  const staticBackgroundImageButton = document.getElementById('static-background-image-button');
  const staticBackgroundImageClearButton = document.getElementById('static-background-image-clear');
  const staticBackgroundImageDropzone = document.getElementById('static-background-image-dropzone') as HTMLDivElement | null;
  const staticBackgroundImageFitInput = document.getElementById('static-background-image-fit') as HTMLSelectElement | null;
  const staticBackgroundImageModeButtons = document.querySelectorAll<HTMLButtonElement>(
    '[data-static-background-image-mode]'
  );
  const staticBackgroundImageEditButton = document.getElementById('static-background-image-edit');
  const deviceShadowEnabledInput = document.getElementById('device-shadow-enabled') as HTMLInputElement | null;
  const deviceShadowPresetButtons = document.querySelectorAll<HTMLButtonElement>('[data-device-shadow-preset]');
  const deviceShadowAngleInput = document.getElementById('device-shadow-angle') as HTMLInputElement | null;
  const deviceShadowOpacityInput = document.getElementById('device-shadow-opacity') as HTMLInputElement | null;
  const deviceShadowColorInput = document.getElementById('device-shadow-color') as HTMLInputElement | null;
  const backgroundShadowInput = document.getElementById('background-shadow-color') as HTMLInputElement | null;
  const backgroundMidInput = document.getElementById('background-mid-color') as HTMLInputElement | null;
  const backgroundHighlightInput = document.getElementById('background-highlight-color') as HTMLInputElement | null;
  const reflectionVisibleInput = document.getElementById('reflection-visible') as HTMLInputElement | null;
  const reflectionOpacityInput = document.getElementById('reflection-opacity') as HTMLInputElement | null;
  const reflectionBlurInput = document.getElementById('reflection-blur') as HTMLInputElement | null;
  const screenReflectionOpacityInput = document.getElementById('screen-reflection-opacity') as HTMLInputElement | null;
  const screenBlurInput = document.getElementById('screen-blur') as HTMLInputElement | null;
  const hueInput = document.getElementById('diffuse-hue') as HTMLInputElement | null;
  const saturationInput = document.getElementById('diffuse-saturation') as HTMLInputElement | null;
  const brightnessInput = document.getElementById('diffuse-brightness') as HTMLInputElement | null;
  const contrastInput = document.getElementById('diffuse-contrast') as HTMLInputElement | null;
  const monochromeInput = document.getElementById('diffuse-monochrome') as HTMLInputElement | null;
  const hueValue = document.getElementById('diffuse-hue-value');
  const saturationValue = document.getElementById('diffuse-saturation-value');
  const brightnessValue = document.getElementById('diffuse-brightness-value');
  const contrastValue = document.getElementById('diffuse-contrast-value');
  const monochromeLabel = document.getElementById('diffuse-mode-label');
  const reflectionOpacityValue = document.getElementById('reflection-opacity-value');
  const reflectionBlurValue = document.getElementById('reflection-blur-value');
  const screenReflectionOpacityValue = document.getElementById('screen-reflection-opacity-value');
  const screenBlurValue = document.getElementById('screen-blur-value');

  [
    hueInput,
    saturationInput,
    brightnessInput,
    contrastInput,
    reflectionOpacityInput,
    reflectionBlurInput,
    screenReflectionOpacityInput,
    screenBlurInput,
    staticBackgroundAngleInput,
    staticBackgroundMeshSeedInput,
    staticBackgroundMeshMixInput,
    staticBackgroundMeshDistortionInput
  ].forEach(syncSliderProgress);

  bindNewTemplatesPagination();

  contentTypeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const nextContentType = button.dataset.contentType as ContentType;
      if (nextContentType !== 'STATIC' && nextContentType !== 'MOTION') return;
      syncContentTypeSelection(nextContentType);
      if (nextContentType !== state.selectedContentType) {
        applyContentTypeFilter(nextContentType);
      } else {
        pauseMotionPlayback({ preserveFrame: true });
        discardNewTemplatesMode();
        state.selectedPurposeId = '';
        state.selectedCollectionId = '';
        state.quickCatalogFilter = 'collections';
        state.favoritesOnly = false;
        state.catalogSearch = '';
        state.category = 'All';
        state.usePaneView = 'collections';
        pushNavHash();
      }
      void refreshCollections({ quiet: true }).then(() => render());
    });
  });

  topbarHomeLink?.addEventListener('click', enterHomeScreen);
  topbarHomeLink?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      enterHomeScreen();
    }
  });

  loginButton?.addEventListener('click', () => {
    openAuthPrompt('account');
  });

  accountMenuButton?.addEventListener('click', () => {
    if (state.accountMenuOpen) {
      closeAccountMenu();
      return;
    }
    openAccountMenu();
  });

  accountMenuTabs.forEach((button) => {
    button.addEventListener('click', () => {
      const view = button.dataset.accountView as AccountMenuView;
      if (!view) return;
      state.accountMenuView = view;
      state.accountMenuOpen = true;
      render();
      if (!state.accountOverviewLoading) {
        void refreshAccountOverview({ renderWhileLoading: true });
      }
    });
  });

  accountPageTabs.forEach((button) => {
    button.addEventListener('click', () => {
      const view = button.dataset.accountPageView as AccountMenuView;
      if (!view) return;
      state.accountMenuView = view;
      state.accountMenuOpen = false;
      render();
      if (!state.accountOverviewLoading) {
        void refreshAccountOverview({ renderWhileLoading: true });
      }
    });
  });

  accountFavoriteFilterButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const filter = button.dataset.accountFavoriteFilter as AccountFavoriteFilter;
      if (filter !== 'ALL' && filter !== 'STATIC' && filter !== 'MOTION') return;
      state.accountFavoriteFilter = filter;
      state.accountMenuOpen = !button.closest('.account-page');
      state.accountMenuView = 'favorites';
      render();
    });
  });

  accountTemplateButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const templateId = button.dataset.accountTemplateId || '';
      if (!templateId) return;
      if (!state.catalog.some((template) => template.id === templateId)) {
        setNotice('Template is no longer available', 'error');
        return;
      }
      state.accountMenuOpen = false;
      openTemplateDetail(templateId);
    });
  });

  accountOpenPageButton?.addEventListener('click', () => {
    openAccountPage(state.accountMenuView);
  });

  accountPageBackButton?.addEventListener('click', () => {
    state.usePaneView = 'templates';
    state.quickCatalogFilter = 'All';
    state.selectedCollectionId = '';
    state.favoritesOnly = false;
    state.category = 'All';
    state.catalogSearch = '';
    state.accountMenuOpen = false;
    pushNavHash();
    render();
  });

  bindAccountMenuOutsideClick();

  logoutButton?.addEventListener('click', () => {
    void handleSignOut();
  });

  themeToggleButton?.addEventListener('click', () => {
    applyThemePreference(!isDarkTheme, true);
  });

  authModalCloseButton?.addEventListener('click', () => {
    closeAuthPrompt();
  });

  authGoogleButton?.addEventListener('click', () => {
    void handleGoogleSignIn();
  });

  authEmailInput?.addEventListener('input', () => {
    queueInputFocusRestore(authEmailInput);
    state.authEmail = authEmailInput.value;
  });

  authEmailForm?.addEventListener('submit', (event) => {
    event.preventDefault();
    void handleEmailSignIn(authEmailInput?.value || state.authEmail);
  });

  domainCategoryButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const dc = button.dataset.domainCategory as DomainCategory;
      if (!DOMAIN_CATEGORIES.includes(dc)) return;
      if (dc === state.selectedDomainCategory) return;
      applyDomainCategoryFilter(dc);
      render();
    });
  });

  sourceFilterSelect?.addEventListener('change', () => {
    const value = sourceFilterSelect.value as SourceFilter;
    const keepCollectionScope = state.usePaneView === 'templates' && Boolean(state.selectedCollectionId) && !state.favoritesOnly;
    applySourceFilterChange(value);
    if (keepCollectionScope) {
      render();
      return;
    }
    void refreshCollections({ quiet: true }).then(() => render());
  });

  sourceFilterButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const value = button.dataset.sourceFilter as SourceFilter;
      if (!['ALL', 'CG', 'AI'].includes(value) || value === state.selectedSourceFilter) return;
      syncHeaderSelection('.source-filter-toggle', 'sourceFilter', value);
      const keepCollectionScope = state.usePaneView === 'templates' && Boolean(state.selectedCollectionId) && !state.favoritesOnly;
      applySourceFilterChange(value);
      if (keepCollectionScope) {
        render();
        return;
      }
      void refreshCollections({ quiet: true }).then(() => render());
    });
  });

  if (previewDropzone && previewImage && !boundSelected?.isVideo && !hasActiveUserMedia(boundSelected)) {
    const key = previewDropzone.dataset.previewKey || '';
    const markReady = () => {
      if (key) {
        markDetailPreviewReady(key);
      }
    };

    if (previewImage.complete && previewImage.naturalWidth > 0) {
      markReady();
    } else {
      previewImage.addEventListener('load', markReady, { once: true });
      previewImage.addEventListener('error', markReady, { once: true });
    }
  }

  toolbarNavButton?.addEventListener('click', () => {
    if (state.usePaneView === 'collections') {
      void Promise.all([refreshCatalog('', { quiet: true }), refreshCollections({ quiet: true })]).then(() => render());
      return;
    }

    if (state.usePaneView === 'detail') {
      pauseMotionPlayback({ preserveFrame: true });
      state.usePaneView = 'templates';
      pushNavHash();
      render();
      return;
    }

    state.usePaneView = 'collections';
    state.quickCatalogFilter = 'collections';
    state.favoritesOnly = false;
    state.category = 'All';
    pushNavHash();
    render();
  });

  breadcrumbLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const target = link.dataset.breadcrumbTarget;
      pauseMotionPlayback({ preserveFrame: true });
      discardNewTemplatesMode();
      state.favoritesOnly = false;
      state.category = 'All';
      state.catalogSearch = '';

      if (target === 'collection') {
        state.quickCatalogFilter = 'collections';
        const selectedTemplate = getSelectedTemplate();
        const collectionId =
          link.dataset.collectionId ||
          selectedTemplate?.collectionId ||
          (selectedTemplate ? getDefaultCollectionIdForType(selectedTemplate.templateType) : '');
        if (collectionId) {
          state.selectedCollectionId = collectionId;
          state.selectedPurposeId = getCollectionPurposeId(getSelectedCollection(),state.collectionPurposes);
        }
        state.usePaneView = 'templates';
        pushNavHash();
        render();
        return;
      }

      if (target === 'collections') {
        state.selectedPurposeId = '';
        state.selectedCollectionId = '';
        state.quickCatalogFilter = 'collections';
        state.usePaneView = 'collections';
        pushNavHash();
        render();
        return;
      }

      if (target === 'purpose') {
        state.selectedPurposeId = link.dataset.purposeId || '';
        state.selectedCollectionId = '';
        state.quickCatalogFilter = 'collections';
        state.usePaneView = 'collections';
        pushNavHash(); render(); return;
      }
      state.selectedCollectionId = '';
      state.quickCatalogFilter = 'All';
      state.usePaneView = 'templates';
      pushNavHash();
      render();
    });
  });

  quickCatalogFilterSelect?.addEventListener('change', () => {
    const value = quickCatalogFilterSelect.value as QuickCatalogFilter;
    discardNewTemplatesMode();
    const keepCollectionScope = state.usePaneView === 'templates' && Boolean(state.selectedCollectionId) && !state.favoritesOnly;
    state.quickCatalogFilter = value;
    state.catalogSearch = '';

    if (value === 'collections') {
      state.usePaneView = 'collections';
      state.favoritesOnly = false;
      state.category = 'All';
      state.selectedCollectionId = '';
      pushNavHash();
      render();
      return;
    }

    state.favoritesOnly = false;
    if (!keepCollectionScope) {
      state.selectedCollectionId = '';
    }
    state.usePaneView = 'templates';
    state.category = value;
    pushNavHash();
    render();
  });

  newTemplatesToggle?.addEventListener('click', () => {
    if (state.newTemplatesMode) {
      deactivateNewTemplatesMode();
    } else {
      activateNewTemplatesMode();
    }
    pushNavHash();
    render();
  });

  catalogSearchInput?.addEventListener('input', () => {
    queueInputFocusRestore(catalogSearchInput);
    state.catalogSearch = catalogSearchInput.value;
    if (state.newTemplatesMode) {
      state.newTemplatesVisibleCount = NEW_TEMPLATES_BATCH_SIZE;
    }
    render();
  });

  openFavoritesButton?.addEventListener('click', () => {
    if (
      requireStandaloneAuth('favorites', {
        type: 'favorites'
      })
    ) {
      return;
    }

    if (state.favoritesOnly && state.usePaneView === 'templates') {
      state.favoritesOnly = false;
      state.usePaneView = 'templates';
      state.quickCatalogFilter = 'All';
      state.selectedCollectionId = '';
      state.category = 'All';
      state.catalogSearch = '';
      pushNavHash();
      render();
      return;
    }

    state.favoritesOnly = true;
    state.quickCatalogFilter = 'collections';
    state.usePaneView = 'templates';
    state.selectedCollectionId = '';
    state.category = 'All';
    render();
  });

  detailFavoriteButton?.addEventListener('click', () => {
    const selected = getSelectedTemplate();
    if (!selected) {
      return;
    }
    void toggleFavoriteTemplate(selected.id);
  });

  // Toggle the class on the live node instead of re-rendering. render() rebuilds
  // the section with .open already applied, so the body would mount at its final
  // size and the transition would have no starting state to run from — that is
  // why this never animated. The body markup is always present either way, so
  // nothing here needs a rebuild.
  const setDisclosureOpen = (toggle: HTMLElement, open: boolean): void => {
    toggle.closest('.control-disclosure')?.classList.toggle('open', open);
    toggle.setAttribute('aria-expanded', String(open));
  };

  diffuseControlsToggle?.addEventListener('click', () => {
    state.diffuseControlsOpen = !state.diffuseControlsOpen;
    setDisclosureOpen(diffuseControlsToggle, state.diffuseControlsOpen);
  });

  backgroundControlsToggle?.addEventListener('click', () => {
    state.backgroundControlsOpen = !state.backgroundControlsOpen;
    if (!state.backgroundControlsOpen && state.gradientPresetsOpen) {
      state.gradientPresetsOpen = false;
      const disclosure = backgroundControlsToggle.closest('.control-disclosure');
      disclosure?.classList.remove('has-open-popover');
      disclosure?.querySelector('.gradient-presets')?.classList.remove('is-open');
      disclosure?.querySelector('#gradient-presets-trigger')?.setAttribute('aria-expanded', 'false');
    }
    setDisclosureOpen(backgroundControlsToggle, state.backgroundControlsOpen);
  });

  motionColorMatteToggle?.addEventListener('click', () => {
    state.motionColorMatteOpen = !state.motionColorMatteOpen;
    setDisclosureOpen(motionColorMatteToggle, state.motionColorMatteOpen);
  });

  const motionColorMatteBackgroundInput = document.getElementById(
    'motion-color-matte-background'
  ) as HTMLInputElement | null;

  const syncMotionColorMatteControls = (): void => {
    const enabled = state.motionColorMatte.enabled;
    // The background stands on its own: it needs neither the colour channels nor
    // the neutral atlas, so it is not gated by the master switch.
    const backgroundOn = state.motionColorMatte.background.enabled;
    if (motionColorMatteEnabledInput) {
      motionColorMatteEnabledInput.checked = enabled;
      // Set here rather than left to the markup: the toggles update live nodes
      // instead of re-rendering, so the group would otherwise keep whatever
      // state it was built with.
      motionColorMatteEnabledInput.closest('.matte-group')?.classList.toggle('enabled', enabled);
    }
    if (motionColorMatteBackgroundInput) {
      motionColorMatteBackgroundInput.checked = backgroundOn;
      motionColorMatteBackgroundInput
        .closest('.matte-background')
        ?.classList.toggle('enabled', backgroundOn);
    }
    document.querySelectorAll<HTMLInputElement>('[data-motion-color-matte-background]').forEach((input) => {
      input.disabled = !backgroundOn;
      input.value =
        input.dataset.motionColorMatteBackground === 'light'
          ? state.motionColorMatte.background.light
          : state.motionColorMatte.background.dark;
    });
    document.querySelectorAll<HTMLInputElement>('[data-motion-color-matte-color]').forEach((input) => {
      const channel = input.dataset.motionColorMatteColor;
      if (!isMotionColorMatteChannel(channel)) {
        return;
      }
      input.disabled = !enabled;
      input.value = state.motionColorMatte.colors[channel] || '#ffffff';
      input.closest('.matte-color-row')?.classList.toggle('enabled', enabled);
    });
  };

  motionColorMatteResetButton?.addEventListener('click', () => {
    resetMotionColorMatteAdjustments();
    // Updated on the live nodes rather than through render(): rebuilding the
    // pane would replace the preview canvas and send playback back to frame one.
    syncMotionColorMatteControls();
    refreshMotionPreviewAfterControlChange();
  });

  motionColorMatteEnabledInput?.addEventListener('change', () => {
    state.motionColorMatte.enabled = motionColorMatteEnabledInput.checked;
    syncMotionColorMatteControls();

    // Playback consumes the updated state on its next frame. A separate preview
    // draw here would seek the same videos concurrently and intermittently stall
    // the viewport. While paused, keep the immediate still-frame refresh.
    refreshMotionPreviewAfterControlChange();
    if (state.motionColorMatte.enabled) {
      const selectedTemplate = getSelectedTemplate();
      if (selectedTemplate?.isVideo) {
        void ensurePreviewMotionAssets(selectedTemplate)
          .then((assets) =>
            Promise.all([
              ensureNeutralAtlas(assets, () => getCurrentMotionAtlasTime(assets)),
              ensureMotionPreviewColorAssets(assets, getMotionColorMatteAdjustments())
            ])
          )
          .then(() => refreshMotionPreviewAfterControlChange())
          .catch(() => undefined);
      }
    }
  });

  motionColorMatteBackgroundInput?.addEventListener('change', () => {
    state.motionColorMatte.background.enabled = motionColorMatteBackgroundInput.checked;
    syncMotionColorMatteControls();
    refreshMotionPreviewAfterControlChange();
    if (state.motionColorMatte.background.enabled) {
      const selectedTemplate = getSelectedTemplate();
      if (selectedTemplate?.isVideo) {
        void ensurePreviewMotionAssets(selectedTemplate)
          .then((assets) => ensureMotionPreviewColorAssets(assets, getMotionColorMatteAdjustments()))
          .then(() => refreshMotionPreviewAfterControlChange())
          .catch(() => undefined);
      }
    }
  });

  document.querySelectorAll<HTMLInputElement>('[data-motion-color-matte-background]').forEach((input) => {
    input.addEventListener('input', () => {
      if (input.dataset.motionColorMatteBackground === 'light') {
        state.motionColorMatte.background.light = input.value;
      } else {
        state.motionColorMatte.background.dark = input.value;
      }
      refreshMotionPreviewAfterControlChange();
    });
  });

  motionColorMatteColorInputs.forEach((input) => {
    input.addEventListener('input', () => {
      const channel = input.dataset.motionColorMatteColor;
      if (!isMotionColorMatteChannel(channel)) {
        return;
      }
      state.motionColorMatte.colors[channel] = input.value;
      refreshMotionPreviewAfterControlChange();
    });
  });

  diffuseResetButton?.addEventListener('click', () => {
    const selectedTemplate = getSelectedTemplate();
    resetDiffuseAdjustments();
    if (shouldShowScreenReflectionControl(selectedTemplate)) {
      state.screenOverlayOpacity = 50;
    }
    render();
  });

  backgroundResetButton?.addEventListener('click', () => {
    resetBackgroundAdjustments();
    refreshStaticVideoChromaPreview(true);
    render();
  });

  backgroundTransparentInput?.addEventListener('change', () => {
    state.backgroundTransparent = backgroundTransparentInput.checked;
    if (!isDeviceShadowAvailable()) {
      state.deviceShadowEnabled = false;
    }
    invalidateStaticBackgroundSource();
    refreshStaticVideoChromaPreview(true);
    window.setTimeout(render, 240);
  });

  staticVideoChromaPresetButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const preset = button.dataset.staticVideoChromaPreset;
      if (preset !== 'green' && preset !== 'blue') {
        return;
      }
      state.staticVideoChromaPreset = preset;
      refreshStaticVideoChromaPreview(true);
      render();
    });
  });

  staticVideoChromaCustomInput?.addEventListener('input', () => {
    state.staticVideoChromaPreset = 'custom';
    state.staticVideoChromaCustomColor = staticVideoChromaCustomInput.value;
    const option = staticVideoChromaCustomInput.closest<HTMLElement>('.chroma-key-option');
    const swatch = option?.querySelector<HTMLElement>('.chroma-key-swatch');
    swatch?.style.setProperty('background', state.staticVideoChromaCustomColor);
    staticVideoChromaPresetButtons.forEach((button) => button.classList.remove('active'));
    option?.classList.add('active');
    refreshStaticVideoChromaPreview(false);
  });

  staticVideoChromaCustomInput?.addEventListener('change', () => {
    state.staticVideoChromaPreset = 'custom';
    state.staticVideoChromaCustomColor = staticVideoChromaCustomInput.value;
    refreshStaticVideoChromaPreview(true);
    render();
  });

  document.getElementById('dynamic-zoom-edit')?.addEventListener('click', () => openDynamicZoomEditor());
  const dynamicZoomToggle = document.getElementById('dynamic-zoom-toggle');
  dynamicZoomToggle?.addEventListener('click', () => {
    state.dynamicZoomOpen = !state.dynamicZoomOpen;
    setDisclosureOpen(dynamicZoomToggle, state.dynamicZoomOpen);
  });
  document.getElementById('dynamic-zoom-enabled')?.addEventListener('change', (event) => {
    state.dynamicZoom.enabled = (event.target as HTMLInputElement).checked;
    dynamicZoomTime = 0;
    render();
    schedulePreviewRender();
  });
  document.querySelectorAll<HTMLButtonElement>('[data-dynamic-zoom-duration]').forEach((button) => {
    button.addEventListener('click', () => {
      state.dynamicZoom.duration = Number(button.dataset.dynamicZoomDuration);
      dynamicZoomTime = 0;
      render();
    });
  });
  document.querySelectorAll<HTMLButtonElement>('[data-dynamic-zoom-background]').forEach((button) => {
    button.addEventListener('click', () => {
      state.dynamicZoom.backgroundMotion = normalizeBackgroundMotion(button.dataset.dynamicZoomBackground);
      render();
      schedulePreviewRender();
    });
  });

  const animation = state.staticBackgroundAnimation;
  document.getElementById('bg-animation-enabled')?.addEventListener('change', (event) => {
    animation.enabled = (event.target as HTMLInputElement).checked;
    if (animation.enabled) {
      ensureAnimationKeyframes();
      backgroundAnimationTime = 0;
      state.staticExport.resolution = STATIC_VIDEO_EXPORT_RESOLUTION;
    }
    invalidateStaticBackgroundSource();
    render();
    schedulePreviewRender();
  });
  document.getElementById('bg-animation-loop')?.addEventListener('change', (event) => {
    animation.loop = (event.target as HTMLInputElement).checked;
    if (animation.loop && backgroundAnimationTime >= animation.duration) {
      backgroundAnimationTime = 0;
    }
    invalidateStaticBackgroundSource();
    schedulePreviewRender();
    syncBackgroundAnimationLoop();
  });
  document.querySelectorAll<HTMLButtonElement>('[data-anim-duration]').forEach((button) => {
    button.addEventListener('click', () => {
      animation.duration = Number(button.dataset.animDuration);
      backgroundAnimationTime = 0;
      render();
    });
  });
  document.getElementById('bg-animation-mesh-move')?.addEventListener('change', (event) => {
    animation.meshMove = (event.target as HTMLInputElement).checked;
    invalidateStaticBackgroundSource();
    schedulePreviewRender();
  });
  document.getElementById('bg-animation-mesh-color')?.addEventListener('change', (event) => {
    animation.meshColor = (event.target as HTMLInputElement).checked;
    if (animation.meshColor) ensureAnimationKeyframes();
    invalidateStaticBackgroundSource();
    render();
    schedulePreviewRender();
  });
  document.getElementById('bg-animation-add')?.addEventListener('click', () => {
    animation.keyframes = appendKeyframe(animation.keyframes, getAnimationBaseColours());
    render();
    schedulePreviewRender();
  });
  document.querySelectorAll<HTMLButtonElement>('[data-anim-remove]').forEach((button) => {
    button.addEventListener('click', () => {
      animation.keyframes = removeKeyframe(animation.keyframes, Number(button.dataset.animRemove));
      render();
      schedulePreviewRender();
    });
  });
  document.querySelectorAll<HTMLInputElement>('[data-anim-color]').forEach((input) => {
    input.addEventListener('input', () => {
      const frame = Number(input.dataset.animColor);
      const slot = Number(input.dataset.animSlot);
      const frames = animation.keyframes.map((row) => [...row]);
      if (!frames[frame]) return;
      frames[frame][slot] = input.value;
      animation.keyframes = frames;
      invalidateStaticBackgroundSource();
      schedulePreviewRender();
    });
  });

  const sourceTrigger = document.getElementById('static-background-source-trigger');
  const sourceSelect = sourceTrigger?.closest('.source-select') as HTMLElement | null;
  if (sourceTrigger && sourceSelect) {
    // Opening a list is not a change of state worth redrawing the page for.
    // render() rebuilds the entire document, so routing this through it made
    // the whole screen blink for what is a show/hide of one panel.
    const setOpen = (open: boolean) => {
      state.staticBackgroundSourceOpen = open;
      sourceSelect.classList.toggle('is-open', open);
      sourceTrigger.setAttribute('aria-expanded', String(open));
    };

    sourceTrigger.addEventListener('click', (event) => {
      event.stopPropagation();
      setOpen(!state.staticBackgroundSourceOpen);
    });

    sourceSelect.addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Escape' && state.staticBackgroundSourceOpen) {
        setOpen(false);
        sourceTrigger.focus();
      }
    });

    // Clicking away closes it, as a dropdown should. Bound per render alongside
    // the element it serves, so it dies with the markup it belongs to.
    document.addEventListener('click', (event) => {
      if (!state.staticBackgroundSourceOpen) return;
      if (!sourceSelect.isConnected) return;
      if (!sourceSelect.contains(event.target as Node)) setOpen(false);
    });
  }

  document.querySelectorAll<HTMLButtonElement>('[data-static-background-source]').forEach((button) => {
    button.addEventListener('click', () => {
      const value = button.dataset.staticBackgroundSource || '';
      if (!value) return;
      // Picking closes the list, the way a dropdown does.
      state.staticBackgroundSourceOpen = false;
      state.gradientPresetsOpen = false;
      state.backgroundTransparent = false;
      if (value === 'original' || value === 'tint' || value === 'tritone') {
        state.backgroundMode = value;
        state.staticBackgroundMode = 'none';
      } else if (isStaticBackgroundMode(value)) {
        state.backgroundMode = 'original';
        state.staticBackgroundMode = value;
      }
      if (state.staticBackgroundAnimation.enabled) {
        ensureAnimationKeyframes();
      }
      if (!isDeviceShadowAvailable()) {
        state.deviceShadowEnabled = false;
      }
      invalidateStaticBackgroundSource();
      render();
    });
  });

  deviceShadowEnabledInput?.addEventListener('change', () => {
    state.deviceShadowEnabled = deviceShadowEnabledInput.checked && isDeviceShadowAvailable();
    render();
  });

  deviceShadowPresetButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const preset = button.dataset.deviceShadowPreset;
      if (preset !== 'soft' && preset !== 'close' && preset !== 'floating' && preset !== 'long') {
        return;
      }
      state.deviceShadowPreset = preset;
      render();
    });
  });

  deviceShadowAngleInput?.addEventListener('input', () => {
    state.deviceShadowAngle = Number(deviceShadowAngleInput.value);
    syncSliderProgress(deviceShadowAngleInput);
    const valueNode = document.getElementById('device-shadow-angle-value');
    if (valueNode) valueNode.textContent = `${state.deviceShadowAngle}°`;
    schedulePreviewRender();
  });

  deviceShadowOpacityInput?.addEventListener('input', () => {
    state.deviceShadowOpacity = Number(deviceShadowOpacityInput.value);
    syncSliderProgress(deviceShadowOpacityInput);
    const valueNode = document.getElementById('device-shadow-opacity-value');
    if (valueNode) valueNode.textContent = `${state.deviceShadowOpacity}%`;
    schedulePreviewRender();
  });

  deviceShadowColorInput?.addEventListener('input', () => {
    state.deviceShadowColor = deviceShadowColorInput.value;
    schedulePreviewRender();
  });

  document.querySelectorAll<HTMLInputElement>('[data-static-background-slot]').forEach((input) => {
    input.addEventListener('change', () => {
      toggleGradientSlot(Number(input.dataset.staticBackgroundSlot), input.checked);
      if (state.staticBackgroundAnimation.enabled) {
        ensureAnimationKeyframes();
      }
      render();
      schedulePreviewRender();
    });
  });

  staticBackgroundColorInputs.forEach((input) => {
    bindGradientColorHistory(input);
    input.addEventListener('input', () => {
      const index = Number(input.dataset.staticBackgroundColor);
      if (!Number.isInteger(index) || index < 0 || index >= STATIC_BACKGROUND_DEFAULT_COLORS.length) {
        return;
      }
      const colors = [...state.staticBackgroundColors];
      colors[index] = input.value;
      state.staticBackgroundColors = colors;
      invalidateStaticBackgroundSource();
      schedulePreviewRender();
    });
  });

  const bindStaticBackgroundSlider = (
    input: HTMLInputElement | null,
    valueId: string,
    suffix: string,
    update: (value: number) => void
  ): void => {
    input?.addEventListener('input', () => {
      const value = Number(input.value);
      update(value);
      syncSliderProgress(input);
      const valueNode = document.getElementById(valueId);
      if (valueNode) valueNode.textContent = `${value}${suffix}`;
      invalidateStaticBackgroundSource();
      schedulePreviewRender();
    });
  };

  bindStaticBackgroundSlider(
    document.getElementById('bg-animation-speed') as HTMLInputElement | null,
    'bg-animation-speed-value',
    '%',
    (value) => {
      state.staticBackgroundAnimation.speed = value;
    }
  );
  bindStaticBackgroundSlider(
    document.getElementById('bg-animation-mesh-motion-speed') as HTMLInputElement | null,
    'bg-animation-mesh-motion-speed-value',
    '%',
    (value) => {
      state.staticBackgroundAnimation.meshMotionSpeed = value;
    }
  );
  bindStaticBackgroundSlider(
    document.getElementById('bg-animation-mesh-motion-seed') as HTMLInputElement | null,
    'bg-animation-mesh-motion-seed-value',
    '',
    (value) => {
      state.staticBackgroundAnimation.meshMotionSeed = value;
    }
  );
  bindStaticBackgroundSlider(staticBackgroundAngleInput, 'static-background-angle-value', '°', (value) => {
    state.staticBackgroundAngle = value;
    // Re-aims the ramp about its own midpoint, so the slider and the handles
    // describe the same gradient instead of drifting apart.
    const rotated = rotateAxis(state.staticBackgroundAxisStart, state.staticBackgroundAxisEnd, value);
    state.staticBackgroundAxisStart = rotated.start;
    state.staticBackgroundAxisEnd = rotated.end;
  });
  bindStaticBackgroundSlider(staticBackgroundMeshSeedInput, 'static-background-mesh-seed-value', '', (value) => {
    state.staticBackgroundMeshSeed = value;
    // Variation is a shuffle now: it deals a fresh arrangement, which the mesh
    // editor then lets the author move. Dragging a point and nudging Variation
    // afterwards is meant to start over, not to fight the drag.
    state.staticBackgroundMeshPoints = seededMeshPoints(value, MESH_POINT_LIMIT);
  });
  bindStaticBackgroundSlider(staticBackgroundMeshMixInput, 'static-background-mesh-mix-value', '%', (value) => {
    state.staticBackgroundMeshMix = value;
  });
  bindStaticBackgroundSlider(
    staticBackgroundMeshDistortionInput,
    'static-background-mesh-distortion-value',
    '%',
    (value) => {
      state.staticBackgroundMeshDistortion = value;
    }
  );
  bindStaticBackgroundSlider(staticBackgroundMeshGrainInput, 'static-background-mesh-grain-value', '%', (value) => {
    state.staticBackgroundMeshGrain = value;
  });

  staticBackgroundMeshEditButton?.addEventListener('click', () => openMeshEditor());
  staticBackgroundAxisEditButton?.addEventListener('click', () => openGradientAxisEditor());
  gradientPaletteUndoButton?.addEventListener('click', undoGradientPalette);
  gradientPaletteRandomButton?.addEventListener('click', randomizeGradientPalette);
  gradientPresetsTrigger?.addEventListener('click', (event) => {
    event.stopPropagation();
    if (
      requireStandaloneAuth('gradient-preset', {
        type: 'gradient-presets'
      })
    ) {
      return;
    }
    state.gradientPresetsOpen = !state.gradientPresetsOpen;
    render();
  });
  gradientPresetAddButton?.addEventListener('click', () => {
    void addCurrentGradientPreset();
  });
  document.querySelectorAll<HTMLButtonElement>('[data-gradient-preset-apply]').forEach((button) => {
    button.addEventListener('click', () => {
      const preset = state.gradientPresets.find((item) => item.id === button.dataset.gradientPresetApply);
      if (preset) {
        applyGradientPreset(preset);
      }
    });
  });
  document.querySelectorAll<HTMLButtonElement>('[data-gradient-preset-delete]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      const presetId = button.dataset.gradientPresetDelete || '';
      if (presetId) {
        void removeGradientPreset(presetId);
      }
    });
  });
  gradientPresetsContainer?.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !state.gradientPresetsOpen) {
      return;
    }
    event.preventDefault();
    state.gradientPresetsOpen = false;
    render();
  });
  if (gradientPresetsContainer) {
    bindGradientPresetsOutsideClick(gradientPresetsContainer);
  }

  staticBackgroundImageButton?.addEventListener('click', () => staticBackgroundImageInput?.click());
  staticBackgroundImageDropzone?.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('#static-background-image-button') || target?.closest('#static-background-image-clear')) {
      return;
    }
    staticBackgroundImageInput?.click();
  });
  staticBackgroundImageInput?.addEventListener('change', () => {
    const file = staticBackgroundImageInput.files?.[0];
    if (file) void loadStaticBackgroundImage(file);
    staticBackgroundImageInput.value = '';
  });
  staticBackgroundImageClearButton?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    revokeStaticBackgroundImagePreviewSrc();
    state.staticBackgroundImage = null;
    state.staticBackgroundImagePreviewSrc = '';
    state.staticBackgroundImageName = '';
    state.staticBackgroundImageRevision += 1;
    state.staticBackgroundImagePlacementMode = 'fit';
    state.staticBackgroundImagePlacement = null;
    state.staticBackgroundImageDraftPlacement = null;
    resetStaticBackgroundObjectOverlay();
    invalidateStaticBackgroundSource();
    render();
  });
  staticBackgroundImageModeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const nextMode = button.dataset.staticBackgroundImageMode;
      if (nextMode !== 'fit' && nextMode !== 'custom') {
        return;
      }
      if (nextMode === 'custom') {
        openBackgroundPlacementEditor();
        return;
      }
      if (state.staticBackgroundImagePlacementMode === 'fit') {
        return;
      }
      state.staticBackgroundImagePlacementMode = 'fit';
      invalidateStaticBackgroundSource();
      render();
    });
  });
  staticBackgroundImageEditButton?.addEventListener('click', () => {
    openBackgroundPlacementEditor();
  });
  staticBackgroundImageFitInput?.addEventListener('change', () => {
    state.staticBackgroundImageFit = staticBackgroundImageFitInput.value === 'contain' ? 'contain' : 'cover';
    invalidateStaticBackgroundSource();
    schedulePreviewRender();
  });
  staticBackgroundImageDropzone?.addEventListener('dragover', (event) => {
    event.preventDefault();
    staticBackgroundImageDropzone.classList.add('drag-active');
  });
  staticBackgroundImageDropzone?.addEventListener('dragleave', () => {
    staticBackgroundImageDropzone.classList.remove('drag-active');
  });
  staticBackgroundImageDropzone?.addEventListener('drop', (event) => {
    event.preventDefault();
    staticBackgroundImageDropzone.classList.remove('drag-active');
    const file = event.dataTransfer?.files?.[0];
    if (file) void loadStaticBackgroundImage(file);
  });

  document.querySelectorAll<HTMLButtonElement>('[data-category]').forEach((button) => {
    button.addEventListener('click', () => {
      state.category = button.dataset.category as CategoryFilter;
      if (!state.selectedCollectionId && !state.favoritesOnly) {
        state.quickCatalogFilter = state.category;
      }
      render();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-open-collection-id]').forEach((button) => {
    button.addEventListener('click', () => {
      const collectionId = button.dataset.openCollectionId;
      if (!collectionId) {
        return;
      }
      state.selectedCollectionId = collectionId;
      state.selectedPurposeId = getCollectionPurposeId(getSelectedCollection(), state.collectionPurposes);
      state.quickCatalogFilter = 'collections';
      state.favoritesOnly = false;
      state.usePaneView = 'templates';
      pushNavHash();
      state.category = 'All';
      render();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-open-purpose-id]').forEach(button=>{
    button.addEventListener('click',()=>{
      const id=button.dataset.openPurposeId;
      if(!state.collectionPurposes.some(p=>p.id===id&&p.contentTypes.includes(state.selectedContentType)))return;
      discardNewTemplatesMode();state.selectedPurposeId=id!;state.selectedCollectionId='';
      state.quickCatalogFilter='collections';state.usePaneView='collections';state.favoritesOnly=false;state.category='All';
      pushNavHash();render();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-id]').forEach((button) => {
    button.addEventListener('click', () => {
      const id = button.dataset.id;
      if (!id) {
        return;
      }

      openTemplateDetail(id);
    });
  });

  if (supportsMotionCardHoverPreview()) {
    document.querySelectorAll<HTMLElement>('.template-item').forEach((item) => {
      const button = item.querySelector<HTMLButtonElement>('[data-motion-preview-url]');
      if (!button) {
        return;
      }
      item.addEventListener('pointerenter', () => {
        clearPendingMotionCardHoverPreview();
        pendingMotionCardHoverPreviewCard = button;
        pendingMotionCardHoverPreviewTarget = item;
        pendingMotionCardHoverPreviewTimer = window.setTimeout(() => {
          pendingMotionCardHoverPreviewTimer = null;
          if (
            pendingMotionCardHoverPreviewCard !== button ||
            pendingMotionCardHoverPreviewTarget !== item ||
            !item.matches(':hover')
          ) {
            return;
          }
          pendingMotionCardHoverPreviewCard = null;
          pendingMotionCardHoverPreviewTarget = null;
          startMotionCardHoverPreview(button, item);
        }, MOTION_CARD_HOVER_PREVIEW_DELAY_MS);
      });
      item.addEventListener('pointerleave', () => {
        if (pendingMotionCardHoverPreviewCard === button || pendingMotionCardHoverPreviewTarget === item) {
          clearPendingMotionCardHoverPreview();
        }
        if (activeMotionCardHoverPreview?.card === button) {
          disposeActiveMotionCardHoverPreview();
        }
      });
      item.addEventListener('pointercancel', () => {
        if (pendingMotionCardHoverPreviewCard === button || pendingMotionCardHoverPreviewTarget === item) {
          clearPendingMotionCardHoverPreview();
        }
        if (activeMotionCardHoverPreview?.card === button) {
          disposeActiveMotionCardHoverPreview();
        }
      });
    });
  }

  document.querySelectorAll<HTMLButtonElement>('[data-preview-nav]').forEach((button) => {
    const navigatePreview = (event: Event): void => {
      event.preventDefault();
      event.stopPropagation();
      if (button.disabled) {
        return;
      }

      const direction = button.dataset.previewNav;
      const selectedTemplate = getSelectedTemplate();
      if (!selectedTemplate) {
        return;
      }

      const navigation = getTemplateNavigation(filteredCatalog(), selectedTemplate.id);
      const nextId = direction === 'prev' ? navigation.previousId : navigation.nextId;
      if (!nextId) {
        return;
      }

      openTemplateDetail(nextId, { preserveCustomPlacement: true });
    };

    button.addEventListener('pointerup', (event) => {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') {
        return;
      }
      previewNavTouchHandledUntil = performance.now() + 500;
      navigatePreview(event);
    });

    button.addEventListener('touchend', (event) => {
      if (performance.now() < previewNavTouchHandledUntil) {
        return;
      }
      previewNavTouchHandledUntil = performance.now() + 500;
      navigatePreview(event);
    });

    button.addEventListener('click', (event) => {
      if (performance.now() < previewNavTouchHandledUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      navigatePreview(event);
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-favorite-id]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const id = button.dataset.favoriteId;
      if (!id) {
        return;
      }
      void toggleFavoriteTemplate(id);
    });
  });

  hueInput?.addEventListener('input', () => {
    state.diffuseHue = Number(hueInput.value);
    syncSliderProgress(hueInput);
    if (hueValue) {
      hueValue.textContent = `${state.diffuseHue}deg`;
    }
    schedulePreviewRender();
  });

  saturationInput?.addEventListener('input', () => {
    state.diffuseSaturation = Number(saturationInput.value);
    syncSliderProgress(saturationInput);
    if (saturationValue) {
      saturationValue.textContent = `${state.diffuseSaturation}%`;
    }
    schedulePreviewRender();
  });

  brightnessInput?.addEventListener('input', () => {
    state.diffuseBrightness = Number(brightnessInput.value);
    syncSliderProgress(brightnessInput);
    if (brightnessValue) {
      brightnessValue.textContent = `${state.diffuseBrightness}%`;
    }
    schedulePreviewRender();
  });

  contrastInput?.addEventListener('input', () => {
    state.diffuseContrast = Number(contrastInput.value);
    syncSliderProgress(contrastInput);
    if (contrastValue) {
      contrastValue.textContent = `${state.diffuseContrast}%`;
    }
    schedulePreviewRender();
  });

  monochromeInput?.addEventListener('change', () => {
    state.diffuseMonochrome = monochromeInput.checked;
    if (monochromeLabel) {
      monochromeLabel.textContent = state.diffuseMonochrome ? 'Monochrome' : 'Color';
    }
    schedulePreviewRender();
  });

  backgroundShadowInput?.addEventListener('input', () => {
    state.backgroundShadowColor = backgroundShadowInput.value;
    schedulePreviewRender();
  });

  backgroundMidInput?.addEventListener('input', () => {
    state.backgroundMidColor = backgroundMidInput.value;
    schedulePreviewRender();
  });

  backgroundHighlightInput?.addEventListener('input', () => {
    state.backgroundHighlightColor = backgroundHighlightInput.value;
    schedulePreviewRender();
  });

  reflectionVisibleInput?.addEventListener('change', () => {
    state.reflectionVisible = reflectionVisibleInput.checked;
    schedulePreviewRender();
  });

  reflectionOpacityInput?.addEventListener('input', () => {
    const value = Number(reflectionOpacityInput.value);
    state.reflectionOpacity = value;
    syncSliderProgress(reflectionOpacityInput);
    if (reflectionOpacityValue) {
      reflectionOpacityValue.textContent = `${value}%`;
    }
    schedulePreviewRender();
  });

  screenReflectionOpacityInput?.addEventListener('input', () => {
    const value = Number(screenReflectionOpacityInput.value);
    state.screenOverlayOpacity = value;
    syncSliderProgress(screenReflectionOpacityInput);
    if (screenReflectionOpacityValue) {
      screenReflectionOpacityValue.textContent = `${value}%`;
    }
    schedulePreviewRender();
  });

  screenBlurInput?.addEventListener('input', () => {
    state.screenBlur = Number(screenBlurInput.value);
    syncSliderProgress(screenBlurInput);
    if (screenBlurValue) {
      screenBlurValue.textContent = formatScreenBlurValue(state.screenBlur);
    }
    schedulePreviewRender();
  });

  reflectionBlurInput?.addEventListener('input', () => {
    state.reflectionBlur = Number(reflectionBlurInput.value);
    syncSliderProgress(reflectionBlurInput);
    if (reflectionBlurValue) {
      reflectionBlurValue.textContent = formatReflectionBlurValue(state.reflectionBlur);
    }
    schedulePreviewRender();
  });

  dynamicIslandToggle?.addEventListener('change', () => {
    state.dynamicIsland = dynamicIslandToggle.checked;
    schedulePreviewRender();
  });

  const interfaceInput = document.getElementById('interface-input') as HTMLInputElement | null;
  const interfacePickerButton = document.getElementById('interface-picker-button');
  const interfaceClearButton = document.getElementById('interface-clear-button');
  const interfaceInput2 = document.getElementById('interface-input-2') as HTMLInputElement | null;
  const interfacePickerButton2 = document.getElementById('interface-picker-button-2');
  const interfaceClearButton2 = document.getElementById('interface-clear-button-2');
  const interfaceInput3 = document.getElementById('interface-input-3') as HTMLInputElement | null;
  const interfacePickerButton3 = document.getElementById('interface-picker-button-3');
  const interfaceClearButton3 = document.getElementById('interface-clear-button-3');
  const figmaImportButton = document.getElementById('figma-import-button');
  interfacePickerButton?.addEventListener('click', () => {
    interfaceInput?.click();
  });
  interfacePickerButton2?.addEventListener('click', () => {
    interfaceInput2?.click();
  });
  interfacePickerButton3?.addEventListener('click', () => {
    interfaceInput3?.click();
  });
  interfaceDropzone?.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (
      target?.closest('#interface-picker-button') ||
      target?.closest('#interface-clear-button') ||
      target?.closest('#figma-import-button') ||
      target?.closest('.screen-placement-control')
    ) {
      return;
    }
    interfaceInput?.click();
  });
  interfaceClearButton?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    clearUserMedia();
  });
  interfaceDropzone2?.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('#interface-picker-button-2') || target?.closest('#interface-clear-button-2') || target?.closest('.screen-placement-control')) {
      return;
    }
    interfaceInput2?.click();
  });
  interfaceClearButton2?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    clearUserMedia(2);
  });
  interfaceDropzone3?.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('#interface-picker-button-3') || target?.closest('#interface-clear-button-3') || target?.closest('.screen-placement-control')) {
      return;
    }
    interfaceInput3?.click();
  });
  interfaceClearButton3?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    clearUserMedia(3);
  });
  figmaImportButton?.addEventListener('click', () => {
    state.importingFromFigma = true;
    render();
    parent.postMessage(
      {
        pluginMessage: {
          type: 'IMPORT_FROM_FIGMA'
        }
      },
      '*'
    );
  });
  interfaceInput?.addEventListener('change', () => {
    const file = interfaceInput.files?.[0];
    if (!file) {
      return;
    }
    void handleInterfaceUpload(file);
  });
  interfaceInput2?.addEventListener('change', () => {
    const file = interfaceInput2.files?.[0];
    if (file) {
      void handleInterfaceUpload(file, 2);
    }
  });
  interfaceInput3?.addEventListener('change', () => {
    const file = interfaceInput3.files?.[0];
    if (file) {
      void handleInterfaceUpload(file, 3);
    }
  });

  motionPlaybackButton?.addEventListener('click', () => {
    void toggleMotionPlayback();
  });
  previewMotionPlaybackButton?.addEventListener('click', () => {
    void toggleMotionPlayback();
  });

  bindPreviewControlSurfaceEvents(previewMotionControlSurface);
  bindStaticPreviewPlayer();
  document.querySelectorAll<HTMLInputElement>('[data-static-use-audio]').forEach(input => {
    input.addEventListener('change', () => setStaticUseOriginalAudio(input.checked));
  });

  if (previewMotionScrubber) {
    const pauseForTimelineSeek = (): void => {
      if (!state.motionPlaying) {
        return;
      }
      motionTimelineResumeAfterSeek = true;
      pauseMotionPlayback({ preserveFrame: true });
      syncMotionPlaybackControls();
    };

    const queueTimelineSeek = (): void => {
      const progress = clamp(Number(previewMotionScrubber.value) / 1000, 0, 1);
      pauseForTimelineSeek();
      motionTimelineSeekPromise = seekMotionTimeline(progress).catch((error) => {
        setNotice(error instanceof Error ? error.message : 'Unable to seek motion preview', 'error');
        render();
      });
    };

    const finishTimelineSeek = (): void => {
      if (!motionTimelineResumeAfterSeek) {
        return;
      }
      const pendingSeek = motionTimelineSeekPromise;
      motionTimelineResumeAfterSeek = false;
      void (async () => {
        await pendingSeek;
        const currentSelected = getSelectedTemplate();
        if (currentSelected?.isVideo && state.usePaneView === 'detail' && !state.motionPlaying) {
          await startMotionPlayback();
        }
      })();
    };

    previewMotionScrubber.addEventListener('pointerdown', pauseForTimelineSeek);
    previewMotionScrubber.addEventListener('input', queueTimelineSeek);
    previewMotionScrubber.addEventListener('change', finishTimelineSeek);
    previewMotionScrubber.addEventListener('pointerup', finishTimelineSeek);
    previewMotionScrubber.addEventListener('pointercancel', finishTimelineSeek);
  }

  document.querySelectorAll<HTMLButtonElement>('[data-motion-resize-mode]').forEach((button) => {
    button.addEventListener('click', () => {
      const nextMode = button.dataset.motionResizeMode;
      if (!isMotionResizeMode(nextMode)) {
        return;
      }
      if (state.motionExport.resizeMode === nextMode) {
        return;
      }
      clearDetailTransitionCleanupTimer();
      state.detailTransitionOverlayUrl = captureCurrentDetailPreviewOverlay();
      state.motionExport.resizeMode = nextMode;
      render();
    });
  });

  bindInterfaceDropzone(previewDropzone, 1);
  bindInterfaceDropzone(interfaceDropzone, 1);
  bindInterfaceDropzone(interfaceDropzone2, 2);
  bindInterfaceDropzone(interfaceDropzone3, 3);

  document.querySelectorAll<HTMLButtonElement>('[data-static-export-resolution]').forEach((button) => {
    button.addEventListener('click', () => {
      const nextResolution = button.dataset.staticExportResolution;
      if (!isExportResolution(nextResolution) || state.staticExport.resolution === nextResolution) {
        return;
      }
      if (isStaticVideoExportActive() && nextResolution !== STATIC_VIDEO_EXPORT_RESOLUTION) {
        state.staticExport.resolution = STATIC_VIDEO_EXPORT_RESOLUTION;
        setNotice('Static MP4 renders use 2K for browser compatibility.', 'info');
        render();
        return;
      }
      state.staticExport.resolution = nextResolution;
      render();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-static-export-format]').forEach((button) => {
    button.addEventListener('click', () => {
      const nextFormat = button.dataset.staticExportFormat;
      if (!isStaticExportFormat(nextFormat) || state.staticExport.format === nextFormat) {
        return;
      }
      state.staticExport.format = nextFormat;
      render();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-static-export-mode]').forEach((button) => {
    button.addEventListener('click', () => {
      const nextMode = button.dataset.staticExportMode;
      const rawScreen = Number(button.closest<HTMLElement>('[data-placement-screen]')?.dataset.placementScreen);
      const screen: 1 | 2 | 3 = rawScreen === 3 ? 3 : rawScreen === 2 ? 2 : 1;
      if (!isStaticInteractiveExportMode(nextMode)) {
        return;
      }
      if (nextMode === 'custom') {
        openCustomPlacementEditor(
          getScreenExportMode(screen) === 'custom'
            ? getScreenCustomModeFallback(screen)
            : normalizeInteractiveStaticExportMode(getScreenExportMode(screen)),
          screen
        );
        return;
      }

      if (getScreenExportMode(screen) === 'fit') {
        return;
      }
      setScreenExportMode('fit', screen);
      render();
    });
  });

  customModeEditButtons.forEach((button) => button.addEventListener('click', () => {
    const rawScreen = Number(button.dataset.screen);
    const screen: 1 | 2 | 3 = rawScreen === 3 ? 3 : rawScreen === 2 ? 2 : 1;
    openCustomPlacementEditor(getScreenCustomModeFallback(screen), screen);
  }));

  const downloadButton = document.getElementById('download-export');
  downloadButton?.addEventListener('click', () => {
    void handleDownload();
  });

  const openBrowserExportButton = document.getElementById('open-browser-export');
  openBrowserExportButton?.addEventListener('click', () => {
    state.openingBrowserExport = true;
    render();
    void handleOpenMotionExportInBrowser()
      .catch((error) => {
        setNotice(error instanceof Error ? error.message : 'Failed to open browser export', 'error');
      })
      .finally(() => {
        state.openingBrowserExport = false;
        render();
      });
  });

  const insertButton = document.getElementById('insert-export');
  insertButton?.addEventListener('click', () => {
    void handleInsertToFigma();
  });

  bindCustomPlacementEditor();
  bindMeshEditor();
  bindGradientAxisEditor();
  bindDynamicZoomEditor();
}

function ensureScrollChromeBindings(): void {
  if (scrollChromeBound) {
    return;
  }

  scrollChromeBound = true;

  appRoot.addEventListener(
    'scroll',
    () => {
      scheduleScrollChromeSync();
      revealScrollChrome();
    },
    { passive: true }
  );

  window.addEventListener('resize', () => {
    if (scrollChromeFrameId !== null) {
      window.cancelAnimationFrame(scrollChromeFrameId);
      scrollChromeFrameId = null;
    }
    syncScrollChrome();
    // Segment widths follow the pane, so the pill has to be re-measured. Without
    // an animation: nothing was chosen, the layout simply moved under it.
    syncSegmentedThumbs(false);
  });

  window.addEventListener('pointermove', handleScrollThumbDrag);
  window.addEventListener('pointerup', stopScrollThumbDrag);
}

function scheduleScrollChromeSync(): void {
  if (!isMobilePreviewDevice()) {
    syncScrollChrome();
    return;
  }

  if (scrollChromeFrameId !== null) {
    return;
  }

  scrollChromeFrameId = window.requestAnimationFrame(() => {
    scrollChromeFrameId = null;
    syncScrollChrome();
  });
}

function bindScrollChromeThumb(): void {
  const thumb = document.getElementById('overlay-scrollbar-thumb') as HTMLDivElement | null;
  if (!thumb) {
    return;
  }

  thumb.onpointerdown = (event: PointerEvent) => {
    const scrollbar = document.getElementById('overlay-scrollbar') as HTMLDivElement | null;
    if (!scrollbar || scrollbar.classList.contains('hidden')) {
      return;
    }

    event.preventDefault();
    scrollThumbDrag = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startScrollTop: appRoot.scrollTop
    };

    document.body.classList.add('scrollbar-dragging');
    thumb.setPointerCapture?.(event.pointerId);
    revealScrollChrome();
  };
}

function handleScrollThumbDrag(event: PointerEvent): void {
  if (!scrollThumbDrag || event.pointerId !== scrollThumbDrag.pointerId) {
    return;
  }

  const scrollbar = document.getElementById('overlay-scrollbar') as HTMLDivElement | null;
  const thumb = document.getElementById('overlay-scrollbar-thumb') as HTMLDivElement | null;
  if (!scrollbar || !thumb) {
    return;
  }

  const trackHeight = scrollbar.clientHeight;
  const thumbHeight = thumb.offsetHeight;
  const maxThumbOffset = Math.max(1, trackHeight - thumbHeight);
  const maxScroll = Math.max(1, appRoot.scrollHeight - appRoot.clientHeight);
  const deltaY = event.clientY - scrollThumbDrag.startY;
  const nextScrollTop = scrollThumbDrag.startScrollTop + (deltaY / maxThumbOffset) * maxScroll;

  appRoot.scrollTop = nextScrollTop;
  syncScrollChrome();
}

function stopScrollThumbDrag(event?: PointerEvent): void {
  if (!scrollThumbDrag) {
    return;
  }

  if (event && event.pointerId !== scrollThumbDrag.pointerId) {
    return;
  }

  scrollThumbDrag = null;
  document.body.classList.remove('scrollbar-dragging');
}

function revealScrollChrome(): void {
  const scrollbar = document.getElementById('overlay-scrollbar') as HTMLDivElement | null;
  if (!scrollbar || scrollbar.classList.contains('hidden')) {
    return;
  }

  scrollbar.classList.add('visible');

  if (scrollChromeHideTimer) {
    window.clearTimeout(scrollChromeHideTimer);
  }

  scrollChromeHideTimer = window.setTimeout(() => {
    scrollbar.classList.remove('visible');
  }, 700);
}

function syncScrollChrome(): void {
  const scrollbar = document.getElementById('overlay-scrollbar') as HTMLDivElement | null;
  const thumb = document.getElementById('overlay-scrollbar-thumb') as HTMLDivElement | null;
  if (!scrollbar || !thumb) {
    return;
  }

  const viewportHeight = appRoot.clientHeight;
  const contentHeight = appRoot.scrollHeight;
  if (contentHeight <= viewportHeight + 1) {
    scrollbar.classList.add('hidden');
    scrollbar.classList.remove('visible');
    thumb.style.height = '0px';
    thumb.style.transform = 'translateY(0px)';
    return;
  }

  scrollbar.classList.remove('hidden');

  const trackHeight = Math.max(1, scrollbar.clientHeight);
  const thumbHeight = Math.max(28, Math.min(trackHeight, (viewportHeight / contentHeight) * trackHeight));
  const maxScroll = Math.max(1, contentHeight - viewportHeight);
  const progress = appRoot.scrollTop / maxScroll;
  const maxThumbOffset = Math.max(0, trackHeight - thumbHeight);
  const thumbOffset = progress * maxThumbOffset;
  const thumbHeightValue = `${thumbHeight}px`;

  if (thumb.style.height !== thumbHeightValue) {
    thumb.style.height = thumbHeightValue;
  }
  thumb.style.transform = `translateY(${thumbOffset}px)`;
}

function getCurrentStaticExportMode(screen: 1 | 2 | 3 = 1): StaticInteractiveExportMode {
  return getScreenExportMode(screen) === 'custom' ? 'custom' : 'fit';
}

function bindGradientAxisEditor(): void {
  if (!state.staticBackgroundAxisEditorOpen) {
    return;
  }

  const backdrop = document.getElementById('axis-editor-backdrop') as HTMLDivElement | null;
  const zone = document.getElementById('axis-editor-zone') as HTMLDivElement | null;
  if (!backdrop || !zone) {
    return;
  }
  const dismiss = bindEditorDismissGuard(backdrop, () => !axisEditorInitial || !editorValuesEqual(axisEditorInitial, {
    axis: state.staticBackgroundAxisDraft, colors: state.staticBackgroundColors, slots: state.staticBackgroundGradientSlots
  }), closeGradientAxisEditor);

  refreshGradientAxisPreview();

  const line = document.getElementById('axis-editor-line-segment');
  const colorCount = state.staticBackgroundColors.length;

  const zonePoint = (event: PointerEvent): AxisPoint | null => {
    const rect = zone.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return null;
    }
    const span = AXIS_POINT_MAX - AXIS_POINT_MIN;
    return {
      x: AXIS_POINT_MIN + ((event.clientX - rect.left) / rect.width) * span,
      y: AXIS_POINT_MIN + ((event.clientY - rect.top) / rect.height) * span
    };
  };

  const redrawOverlay = (): void => {
    const axis = getEditedAxis();
    const stops = normalizeStopPositions(axis.stops, colorCount);
    line?.setAttribute('x1', String(axisPointToZonePercent(axis.start.x)));
    line?.setAttribute('y1', String(axisPointToZonePercent(axis.start.y)));
    line?.setAttribute('x2', String(axisPointToZonePercent(axis.end.x)));
    line?.setAttribute('y2', String(axisPointToZonePercent(axis.end.y)));
    zone.querySelectorAll<HTMLButtonElement>('[data-axis-handle]').forEach((node) => {
      const point = node.dataset.axisHandle === '0' ? axis.start : axis.end;
      node.style.left = `${axisPointToZonePercent(point.x)}%`;
      node.style.top = `${axisPointToZonePercent(point.y)}%`;
    });
    zone.querySelectorAll<HTMLButtonElement>('[data-axis-stop]').forEach((node) => {
      const index = Number(node.dataset.axisStop);
      const point = pointOnAxis(axis.start, axis.end, stops[index]);
      node.style.left = `${axisPointToZonePercent(point.x)}%`;
      node.style.top = `${axisPointToZonePercent(point.y)}%`;
    });
  };

  const startDrag = (node: HTMLButtonElement, onMove: (point: AxisPoint) => void): void => {
    node.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      node.setPointerCapture(event.pointerId);
      node.classList.add('is-dragging');

      const move = (moveEvent: PointerEvent) => {
        const point = zonePoint(moveEvent);
        if (!point) return;
        onMove(point);
        redrawOverlay();
        invalidateStaticBackgroundSource();
        refreshGradientAxisPreview();
      };
      const up = (upEvent: PointerEvent) => {
        node.releasePointerCapture(upEvent.pointerId);
        node.classList.remove('is-dragging');
        node.removeEventListener('pointermove', move);
        node.removeEventListener('pointerup', up);
        node.removeEventListener('pointercancel', up);
        schedulePreviewRender();
      };
      node.addEventListener('pointermove', move);
      node.addEventListener('pointerup', up);
      node.addEventListener('pointercancel', up);
    });
  };

  zone.querySelectorAll<HTMLButtonElement>('[data-axis-handle]').forEach((node) => {
    startDrag(node, (point) => {
      const draft = state.staticBackgroundAxisDraft;
      if (!draft) return;
      if (node.dataset.axisHandle === '0') {
        draft.start = clampAxisPoint(point);
      } else {
        draft.end = clampAxisPoint(point);
      }
    });
  });

  zone.querySelectorAll<HTMLButtonElement>('[data-axis-stop]').forEach((node) => {
    startDrag(node, (point) => {
      const draft = state.staticBackgroundAxisDraft;
      const index = Number(node.dataset.axisStop);
      if (!draft || !Number.isInteger(index)) return;
      const stops = normalizeStopPositions(draft.stops, colorCount);
      // Neighbours are the switched-on slots either side, not the adjacent
      // array entries: a slot that is off is not on the ramp to bump into.
      const active = getActiveGradientSlots();
      const seat = active.indexOf(index);
      const ramp = active.map((slot) => stops[slot]);
      stops[index] = clampStopBetweenNeighbours(
        ramp,
        seat,
        projectOntoAxis(point, draft.start, draft.end)
      );
      draft.stops = stops;
    });
  });

  backdrop.querySelectorAll<HTMLInputElement>('[data-axis-editor-color]').forEach((input) => {
    bindGradientColorHistory(input);
    input.addEventListener('input', () => {
      const slot = Number(input.dataset.axisEditorColor);
      if (!Number.isInteger(slot) || slot < 0 || slot >= STATIC_BACKGROUND_DEFAULT_COLORS.length) {
        return;
      }
      const colors = [...state.staticBackgroundColors];
      colors[slot] = input.value;
      state.staticBackgroundColors = colors;
      const marker = zone.querySelector<HTMLElement>(`[data-axis-stop="${slot}"]`);
      if (marker) marker.style.setProperty('--dot-color', input.value);
      invalidateStaticBackgroundSource();
      refreshGradientAxisPreview();
      schedulePreviewRender();
    });
  });

  backdrop.querySelectorAll<HTMLInputElement>('[data-axis-editor-slot]').forEach((input) => {
    input.addEventListener('change', () => {
      toggleGradientSlot(Number(input.dataset.axisEditorSlot), input.checked);
      render();
      schedulePreviewRender();
    });
  });

  document.getElementById('axis-editor-reset')?.addEventListener('click', () => {
    const isRadial = state.staticBackgroundMode === 'radial';
    const axis = isRadial
      ? defaultRadialAxis(STATIC_BACKGROUND_DEFAULT_FOCUS.x, STATIC_BACKGROUND_DEFAULT_FOCUS.y)
      : axisFromAngle(STATIC_BACKGROUND_DEFAULT_ANGLE);
    state.staticBackgroundAxisDraft = {
      start: axis.start,
      end: axis.end,
      stops: defaultStopPositions(colorCount)
    };
    invalidateStaticBackgroundSource();
    render();
    schedulePreviewRender();
  });
  document.getElementById('axis-editor-close')?.addEventListener('click', () => dismiss.requestClose());
  document.getElementById('axis-editor-cancel')?.addEventListener('click', () => closeGradientAxisEditor(false));
  document.getElementById('axis-editor-apply')?.addEventListener('click', () => closeGradientAxisEditor(true));
}

/**
 * Preview playback for the camera.
 *
 * Runs only outside the editor: inside it the playhead is the author's, and a
 * clock fighting the scrubber would make the keys impossible to place. Capped
 * like the background animation, for the same reason — a frame is a full
 * composite, not just a crop.
 */
const DYNAMIC_ZOOM_PREVIEW_FPS = 30;

function getStaticPreviewClips(): HTMLVideoElement[] {
  const selected = getSelectedTemplate();
  if (!selected || selected.isVideo) return [];
  return Array.from(new Set([
    getInteractiveStaticSource(),
    hasTemplateScreen(selected, 2) ? getInteractiveStaticSource2() : null,
    hasTemplateScreen(selected, 3) ? getInteractiveStaticSource3() : null
  ].filter(
    (source): source is HTMLVideoElement => source instanceof HTMLVideoElement && Number.isFinite(source.duration) && source.duration > 0
  )));
}

function getStaticPreviewAudioClips(): HTMLVideoElement[] {
  const selected = getSelectedTemplate();
  if (isTripleScreenTemplate(selected)) {
    return state.userVideo ? [state.userVideo] : [];
  }
  return getStaticPreviewClips();
}

function getStaticPreviewDuration(): number {
  const clips = getStaticPreviewClips();
  if (clips.length) return Math.max(...clips.map(clip => clip.duration));
  return Math.max(
    isBackgroundAnimationActive() ? state.staticBackgroundAnimation.duration : 0,
    state.dynamicZoom.enabled && state.dynamicZoom.keys.length ? state.dynamicZoom.duration : 0
  );
}

function isStaticPreviewAvailable(template = getSelectedTemplate()): boolean {
  return Boolean(template && !template.isVideo && isStaticVideoExportActive(template) && getStaticPreviewDuration() > 0);
}

function setStaticPreviewFrameTime(time: number): void {
  dynamicZoomTime = time;
  if (isBackgroundAnimationActive()) {
    backgroundAnimationTime = time;
    invalidateStaticBackgroundSource();
  }
}

function ensureStaticPreviewFootage(): FootagePlayback | null {
  const selected = getSelectedTemplate();
  if (!selected || selected.isVideo) return null;
  const clips = getStaticPreviewClips();
  if (!staticPreviewFootage?.matches(clips) || staticPreviewOwner !== selected.id) {
    pauseStaticPreview();
    staticPreviewFootage?.dispose();
    staticPreviewTime = 0;
    staticPreviewOwner = selected.id;
    staticFootagePreviewQuality.reset();
    const footage = new FootagePlayback(clips, time => {
      if (staticPreviewFootage !== footage || staticPreviewPlaying || state.dynamicZoomEditorOpen) return;
      setStaticPreviewFrameTime(time);
      schedulePreviewRender();
    }, getStaticPreviewAudioClips());
    staticPreviewFootage = footage;
    void footage.audio.ready.then(() => { if (staticPreviewFootage === footage) syncStaticAudioControls(); });
  }
  staticPreviewFootage.audio.setEnabled(state.staticUseOriginalAudio);
  return staticPreviewFootage;
}

function pauseStaticPreview(): void {
  staticPreviewVersion++;
  staticPreviewSeekVersion++;
  staticPreviewPlaying = false;
  if (dynamicZoomRaf !== null) window.cancelAnimationFrame(dynamicZoomRaf);
  dynamicZoomRaf = null;
  staticPreviewFootage?.pause();
  syncStaticPreviewControls();
}

async function playStaticPreview(): Promise<void> {
  if (!isStaticPreviewAvailable() || state.exporting || state.dynamicZoomEditorOpen) return;
  const footage = ensureStaticPreviewFootage();
  if (!footage) return;
  pauseStaticPreview();
  const token = staticPreviewVersion;
  const duration = getStaticPreviewDuration();
  if (staticPreviewTime >= duration - 1 / 30) staticPreviewTime = 0;
  try {
    await footage.play(staticPreviewTime);
    if (token !== staticPreviewVersion || footage !== staticPreviewFootage) return;
    staticPreviewPlaying = true;
    const startedAt = performance.now();
    const startedTime = staticPreviewTime;
    let previousTime = staticPreviewTime;
    let previousPaint = -Infinity;
    const tick = (now: number) => {
      dynamicZoomRaf = null;
      if (token !== staticPreviewVersion || !staticPreviewPlaying) return;
      if (state.usePaneView !== 'detail' || state.exporting || state.dynamicZoomEditorOpen || !isStaticPreviewAvailable() || !document.getElementById('static-preview-player')) { pauseStaticPreview(); return; }
      const time = (startedTime + Math.max(0, now - startedAt) / 1000) % getStaticPreviewDuration();
      if (time < previousTime) void footage.play(time).catch(() => pauseStaticPreview());
      previousTime = time;
      staticPreviewTime = time;
      setStaticPreviewFrameTime(time);
      syncStaticPreviewControls();
      if (now - previousPaint >= 1000 / DYNAMIC_ZOOM_PREVIEW_FPS) {
        previousPaint = now;
        schedulePreviewRender(true);
      }
      dynamicZoomRaf = window.requestAnimationFrame(tick);
    };
    syncStaticPreviewControls();
    dynamicZoomRaf = window.requestAnimationFrame(tick);
  } catch (error) {
    if (token !== staticPreviewVersion) return;
    pauseStaticPreview();
    setNotice(error instanceof Error ? error.message : 'Unable to play the uploaded video.', 'error');
  }
}

function seekStaticPreviewTo(time: number): Promise<void> {
  const footage = ensureStaticPreviewFootage();
  if (!footage) return Promise.resolve();
  staticPreviewTime = clamp(time, 0, getStaticPreviewDuration());
  syncStaticPreviewControls();
  const version = ++staticPreviewSeekVersion;
  const operation = footage.seek(staticPreviewTime).then(() => {
    if (version !== staticPreviewSeekVersion || footage !== staticPreviewFootage) return;
    setStaticPreviewFrameTime(staticPreviewTime);
    schedulePreviewRender();
  }).catch(error => {
    if (version === staticPreviewSeekVersion && error?.name !== 'AbortError') setNotice('Unable to seek this video frame.', 'error');
  });
  staticPreviewSeekPromise = operation;
  return operation;
}

function syncStaticPreviewControls(): void {
  const root = document.getElementById('static-preview-player');
  if (!root) return;
  const duration = getStaticPreviewDuration();
  const time = clamp(staticPreviewTime, 0, duration);
  const button = document.getElementById('static-preview-play');
  if (button) {
    button.classList.toggle('is-playing', staticPreviewPlaying);
    button.setAttribute('aria-label', `${staticPreviewPlaying ? 'Pause' : 'Play'} static preview`);
    button.title = `${staticPreviewPlaying ? 'Pause' : 'Play'} static preview`;
    button.setAttribute('aria-pressed', String(staticPreviewPlaying));
  }
  const scrubber = document.getElementById('static-preview-scrubber') as HTMLInputElement | null;
  if (scrubber) {
    scrubber.value = String(Math.round(duration > 0 ? time / duration * 1000 : 0));
    scrubber.disabled = duration <= 0;
    scrubber.setAttribute('aria-valuetext', `${formatPreviewTime(time)} of ${formatPreviewTime(duration)}`);
  }
  const current = document.getElementById('static-preview-current-time');
  const total = document.getElementById('static-preview-duration');
  if (current) current.textContent = formatPreviewTime(time);
  if (total) total.textContent = formatPreviewTime(duration);
}

function setStaticUseOriginalAudio(enabled: boolean): void {
  state.staticUseOriginalAudio = enabled;
  staticPreviewFootage?.audio.setEnabled(enabled);
  syncStaticAudioControls();
}

function syncStaticAudioControls(): void {
  const available = staticPreviewFootage?.audio.hasAudio === true && !getSelectedTemplate()?.isVideo;
  document.querySelectorAll<HTMLElement>('[data-static-audio-control]').forEach(label => {
    label.hidden = !available;
    const input = label.querySelector<HTMLInputElement>('input');
    if (input) input.checked = state.staticUseOriginalAudio;
  });
}

function renderStaticPreviewPlayer(template: MockupTemplate): string {
  return isStaticPreviewAvailable(template)
    ? renderStaticPreviewControls(getStaticPreviewDuration(), staticPreviewTime, staticPreviewPlaying)
    : '';
}

function bindStaticPreviewPlayer(): void {
  const root = document.getElementById('static-preview-player');
  if (!root) return;
  if (!staticPreviewVisibilityBound) {
    staticPreviewVisibilityBound = true;
    document.addEventListener('visibilitychange', () => { if (document.hidden) pauseStaticPreview(); });
  }
  bindPreviewControlSurfaceEvents(root);
  document.getElementById('static-preview-play')?.addEventListener('click', () => {
    if (staticPreviewPlaying) { pauseStaticPreview(); schedulePreviewRender(); }
    else void playStaticPreview();
  });
  const scrubber = document.getElementById('static-preview-scrubber') as HTMLInputElement | null;
  const begin = () => {
    if (staticPreviewPlaying) { staticPreviewResumeAfterSeek = true; pauseStaticPreview(); }
  };
  const seek = () => {
    const progress = Number(scrubber?.value || 0) / 1000;
    begin();
    if (scrubber) void seekStaticPreviewTo(progress * getStaticPreviewDuration());
  };
  const finish = () => {
    const resume = staticPreviewResumeAfterSeek;
    const version = staticPreviewVersion;
    staticPreviewResumeAfterSeek = false;
    void (staticPreviewSeekPromise ?? Promise.resolve()).then(() => {
      if (version !== staticPreviewVersion) return;
      if (resume) void playStaticPreview(); else schedulePreviewRender();
    });
  };
  scrubber?.addEventListener('pointerdown', begin);
  scrubber?.addEventListener('input', seek);
  scrubber?.addEventListener('change', finish);
  scrubber?.addEventListener('pointerup', finish);
  scrubber?.addEventListener('pointercancel', () => { staticPreviewResumeAfterSeek = false; finish(); });
}

function isDynamicZoomPlaybackActive(): boolean {
  return staticPreviewPlaying && isStaticPreviewAvailable() && !state.dynamicZoomEditorOpen;
}

function stopDynamicZoomLoop(): void {
  pauseStaticPreview();
}

function syncDynamicZoomLoop(): void {
  if (state.usePaneView !== 'detail' || !isStaticPreviewAvailable()) {
    pauseStaticPreview();
    staticPreviewFootage?.dispose();
    staticPreviewFootage = null;
    staticPreviewOwner = '';
    return;
  }
  ensureStaticPreviewFootage();
  if (state.exporting || state.dynamicZoomEditorOpen || state.staticExport.customEditorOpen || state.staticBackgroundMeshEditorOpen || state.staticBackgroundAxisEditorOpen) pauseStaticPreview();
  syncStaticPreviewControls();
  syncStaticAudioControls();
}

function openDynamicZoomEditor(): void {
  stopDynamicZoomLoop();
  previewDrawGeneration += 1;
  cancelScheduledPreviewUpgrade();
  const zoom = state.dynamicZoom;
  dynamicZoomEditorInitial = {
    zoom: { ...zoom, keys: zoom.keys.map(key => ({ ...key })) }, format: state.staticExport.format, audio: state.staticUseOriginalAudio
  };
  state.dynamicZoomDraft = { ...zoom, keys: zoom.keys.map((key) => ({ ...key })) };
  state.dynamicZoomSelectedKey = null;
  state.dynamicZoomEditorOpen = true;
  dynamicZoomTime = 0;
  render();
}

function closeDynamicZoomEditor(commit: boolean): void {
  dynamicZoomEditor?.dispose();
  dynamicZoomEditor = null;
  const draft = state.dynamicZoomDraft;
  if (commit && draft) {
    state.dynamicZoom = { ...draft, keys: draft.keys.map((key) => ({ ...key })) };
  }
  if (!commit && dynamicZoomEditorInitial) {
    state.staticExport.format = dynamicZoomEditorInitial.format;
    setStaticUseOriginalAudio(dynamicZoomEditorInitial.audio);
  }
  dynamicZoomEditorInitial = null;
  state.dynamicZoomDraft = null;
  state.dynamicZoomEditorOpen = false;
  state.dynamicZoomSelectedKey = null;
  state.dynamicZoomPlaying = false;
  dynamicZoomTime = 0;
  staticPreviewTime = 0;
  render();
  void seekStaticPreviewTo(0);
}

function getDynamicZoomClips(selected = getSelectedTemplate()): HTMLVideoElement[] {
  if (!selected || selected.isVideo) return [];
  return Array.from(new Set([
    getInteractiveStaticSource(),
    getInteractiveStaticSource2(selected),
    getInteractiveStaticSource3(selected)
  ].filter(
    (source): source is HTMLVideoElement => source instanceof HTMLVideoElement && Number.isFinite(source.duration) && source.duration > 0
  )));
}

function bindDynamicZoomEditor(): void {
  dynamicZoomEditor?.dispose();
  dynamicZoomEditor = null;
  const root = document.getElementById('dz-editor-backdrop');
  const template = getSelectedTemplate();
  if (!state.dynamicZoomEditorOpen || !root || !template || !state.dynamicZoomDraft) return;
  const clips = getDynamicZoomClips();
  const stillFrames = new Map<number, HTMLCanvasElement>();
  dynamicZoomEditor = new DynamicZoomEditor(root, {
    state: state.dynamicZoomDraft,
    duration: getDynamicZoomDuration(),
    time: dynamicZoomTime,
    selectedKeyId: state.dynamicZoomSelectedKey,
    clips,
    audioClips: getStaticPreviewAudioClips(),
    includeSourceAudio: state.staticUseOriginalAudio,
    onAudioChange: setStaticUseOriginalAudio,
    framing: getStaticFraming(template),
    onFormatChange: format => {
      if (state.staticExport.format === format) return;
      state.staticExport.format = format;
      // Format belongs to the view. The draft, playhead and key IDs survive.
      render();
    },
    onTimeChange: time => { dynamicZoomTime = time; },
    onSelectionChange: id => { state.dynamicZoomSelectedKey = id; },
    onPlaybackChange: playing => { state.dynamicZoomPlaying = playing; },
    onClose: closeDynamicZoomEditor,
    hasChanges: () => !dynamicZoomEditorInitial || !editorValuesEqual(dynamicZoomEditorInitial, {
      zoom: state.dynamicZoomDraft, format: state.staticExport.format, audio: state.staticUseOriginalAudio
    }),
    onKeepEditing: resumeDeferredEditorRender,
    renderFrame: async request => {
      const reusable = clips.length === 0 && !isBackgroundAnimationActive();
      const cached = reusable ? stillFrames.get(request.size) : null;
      if (cached) return cached;
      const assets = await ensurePreviewAssets(template);
      if (request.signal.aborted) return null;
      const userSource = getInteractiveStaticSource();
      const userSource2 = getInteractiveStaticSource2(template);
      const userSource3 = getInteractiveStaticSource3(template);
      if (isBackgroundAnimationActive()) {
        backgroundAnimationTime = request.time;
        invalidateStaticBackgroundSource();
      }
      const [staticBackground] = await Promise.all([
        ensureStaticBackgroundComposition(template, request.size),
        ...(!request.playing ? [userSource, userSource2, userSource3].map(source =>
          source instanceof HTMLVideoElement
            ? seekVideo(source, request.time, { signal: request.signal })
            : Promise.resolve()
        ) : [])
      ]);
      if (request.signal.aborted) return null;
      const frame = renderMaster(assets, userSource, {
        renderSize: request.size,
        gridX: PREVIEW_GRID_SIZE,
        gridY: PREVIEW_GRID_SIZE,
        diffuse: getDiffuseAdjustments(),
        background: getBackgroundAdjustments(),
        staticBackground,
        separateCameraBackground: normalizeBackgroundMotion(getEditedDynamicZoom().backgroundMotion) !== 'stick',
        deviceShadow: getDeviceShadowAdjustments(template),
        reflection: getReflectionAdjustments(),
        screenOverlay: getScreenOverlayAdjustments(),
        useAlternateMask: shouldUseAlternateMask(),
        customPlacement: state.staticExport.mode === 'custom' ? getEffectiveCustomPlacement() : null,
        userImage2: userSource2,
        userImage3: userSource3,
        customPlacement2: state.staticExport.mode2 === 'custom' ? getEffectiveCustomPlacement(2) : null,
        customPlacement3: state.staticExport.mode3 === 'custom' ? getEffectiveCustomPlacement(3) : null,
        warpMode: state.staticExport.mode,
        warpMode2: state.staticExport.mode2,
        warpMode3: state.staticExport.mode3,
        realtime: request.interactive
      });
      if (reusable) {
        stillFrames.set(request.size, frame);
        if (stillFrames.size > 3) stillFrames.delete(stillFrames.keys().next().value!);
      }
      return frame;
    }
  });
}

function bindMeshEditor(): void {
  if (!state.staticBackgroundMeshEditorOpen) {
    return;
  }

  const backdrop = document.getElementById('mesh-editor-backdrop') as HTMLDivElement | null;
  const zone = document.getElementById('mesh-editor-zone') as HTMLDivElement | null;
  if (!backdrop || !zone) {
    return;
  }
  const dismiss = bindEditorDismissGuard(backdrop, () => !meshEditorInitial || !editorValuesEqual(meshEditorInitial, {
    points: state.staticBackgroundMeshDraftPoints, flow: state.staticBackgroundMeshFlowDraft, colors: state.staticBackgroundColors
  }), closeMeshEditor);

  refreshMeshEditorPreview();

  const commitDraft = (index: number, point: MeshPoint): void => {
    const draft = (state.staticBackgroundMeshDraftPoints ?? state.staticBackgroundMeshPoints).map((existing) => ({
      ...existing
    }));
    draft[index] = clampMeshPoint(point);
    state.staticBackgroundMeshDraftPoints = draft;
  };

  zone.querySelectorAll<HTMLButtonElement>('[data-mesh-dot]').forEach((dot) => {
    dot.addEventListener('pointerdown', (event) => {
      const index = Number(dot.dataset.meshDot);
      if (!Number.isInteger(index)) {
        return;
      }
      event.preventDefault();
      dot.setPointerCapture(event.pointerId);
      dot.classList.add('is-dragging');

      const move = (moveEvent: PointerEvent) => {
        const rect = zone.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
          return;
        }
        const span = MESH_POINT_MAX - MESH_POINT_MIN;
        const next = clampMeshPoint({
          x: MESH_POINT_MIN + ((moveEvent.clientX - rect.left) / rect.width) * span,
          y: MESH_POINT_MIN + ((moveEvent.clientY - rect.top) / rect.height) * span
        });
        // The dot follows the finger immediately; the gradient catches up when
        // the renderer is free, so the drag never waits on a WebGL frame.
        dot.style.left = `${meshPointToZonePercent(next.x)}%`;
        dot.style.top = `${meshPointToZonePercent(next.y)}%`;
        commitDraft(index, next);
        invalidateStaticBackgroundSource();
        refreshMeshEditorPreview();
      };

      const up = (upEvent: PointerEvent) => {
        dot.releasePointerCapture(upEvent.pointerId);
        dot.classList.remove('is-dragging');
        dot.removeEventListener('pointermove', move);
        dot.removeEventListener('pointerup', up);
        dot.removeEventListener('pointercancel', up);
        schedulePreviewRender();
      };

      dot.addEventListener('pointermove', move);
      dot.addEventListener('pointerup', up);
      dot.addEventListener('pointercancel', up);
    });
  });

  const flowPin = zone.querySelector<HTMLButtonElement>('[data-mesh-flow]');
  flowPin?.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    flowPin.setPointerCapture(event.pointerId);
    flowPin.classList.add('is-dragging');

    const move = (moveEvent: PointerEvent) => {
      const rect = zone.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const span = MESH_POINT_MAX - MESH_POINT_MIN;
      const next = clampMeshPoint({
        x: MESH_POINT_MIN + ((moveEvent.clientX - rect.left) / rect.width) * span,
        y: MESH_POINT_MIN + ((moveEvent.clientY - rect.top) / rect.height) * span
      });
      flowPin.style.left = `${meshPointToZonePercent(next.x)}%`;
      flowPin.style.top = `${meshPointToZonePercent(next.y)}%`;
      state.staticBackgroundMeshFlowDraft = next;
      invalidateStaticBackgroundSource();
      refreshMeshEditorPreview();
    };
    const up = (upEvent: PointerEvent) => {
      flowPin.releasePointerCapture(upEvent.pointerId);
      flowPin.classList.remove('is-dragging');
      flowPin.removeEventListener('pointermove', move);
      flowPin.removeEventListener('pointerup', up);
      flowPin.removeEventListener('pointercancel', up);
      schedulePreviewRender();
    };
    flowPin.addEventListener('pointermove', move);
    flowPin.addEventListener('pointerup', up);
    flowPin.addEventListener('pointercancel', up);
  });

  backdrop.querySelectorAll<HTMLInputElement>('[data-mesh-editor-color]').forEach((input) => {
    bindGradientColorHistory(input);
    input.addEventListener('input', () => {
      const slot = Number(input.dataset.meshEditorColor);
      if (!Number.isInteger(slot) || slot < 0 || slot >= STATIC_BACKGROUND_DEFAULT_COLORS.length) {
        return;
      }
      const colors = [...state.staticBackgroundColors];
      colors[slot] = input.value;
      state.staticBackgroundColors = colors;
      const dot = zone.querySelector<HTMLButtonElement>(`[data-mesh-dot="${slot}"]`);
      if (dot) dot.style.setProperty('--dot-color', input.value);
      invalidateStaticBackgroundSource();
      refreshMeshEditorPreview();
      schedulePreviewRender();
    });
  });

  document.getElementById('mesh-editor-reset')?.addEventListener('click', () => {
    state.staticBackgroundMeshDraftPoints = seededMeshPoints(state.staticBackgroundMeshSeed, MESH_POINT_LIMIT);
    state.staticBackgroundMeshFlowDraft = { x: 0.5, y: 0.5 };
    invalidateStaticBackgroundSource();
    render();
    schedulePreviewRender();
  });
  document.getElementById('mesh-editor-close')?.addEventListener('click', () => dismiss.requestClose());
  document.getElementById('mesh-editor-cancel')?.addEventListener('click', () => closeMeshEditor(false));
  document.getElementById('mesh-editor-apply')?.addEventListener('click', () => closeMeshEditor(true));
}

function startCustomPlacementVideo(video: HTMLVideoElement, initialTime: number): void {
  video.muted = true;
  video.playsInline = true;
  video.loop = true;
  const start = (): void => {
    if (!video.isConnected) return;
    const time = Number.isFinite(initialTime) ? Math.max(0, initialTime) : 0;
    const maxTime = Number.isFinite(video.duration) && video.duration > 0
      ? Math.max(0, video.duration - 1 / 120)
      : time;
    try {
      video.currentTime = Math.min(time, maxTime);
    } catch {
      // A transient browser seek failure must not break placement controls.
    }
    void video.play().catch(() => {});
  };
  if (video.readyState >= 1) {
    start();
  } else {
    video.addEventListener('loadedmetadata', start, { once: true });
  }
}

function bindCustomPlacementEditor(): void {
  const editorSource = getActiveCustomPlacementEditorSource();
  if (!state.staticExport.customEditorOpen || !editorSource) {
    return;
  }

  const backdrop = document.getElementById('custom-editor-backdrop') as HTMLDivElement | null;
  const viewport = document.getElementById('custom-editor-viewport') as HTMLDivElement | null;
  const media = document.getElementById('custom-editor-image') as HTMLElement | null;
  const ghostMedia = document.getElementById('custom-editor-ghost') as
    | HTMLImageElement
    | HTMLVideoElement
    | null;
  const scaleInput = document.getElementById('custom-editor-scale') as HTMLInputElement | null;
  const scaleValue = document.getElementById('custom-editor-scale-value') as HTMLSpanElement | null;
  const closeButton = document.getElementById('custom-editor-close');
  const cancelButton = document.getElementById('custom-editor-cancel');
  const applyButton = document.getElementById('custom-editor-apply');
  const resetButton = document.getElementById('custom-editor-reset');
  const snapsButton = document.getElementById('custom-editor-snaps');

  if (!backdrop || !viewport || !media || !scaleInput || !scaleValue) {
    return;
  }

  const isBackgroundEditor = state.staticExport.customEditorTarget === 'background';
  const minimumScale = isBackgroundEditor ? STATIC_BACKGROUND_CUSTOM_MIN_SCALE : SCREEN_CUSTOM_MIN_SCALE;
  const snap = new EditorSnapSession();
  const guides = viewport.querySelector<HTMLElement>('[data-editor-snap-guides]');
  let guideTimer: number | null = null;
  let scaleMethod: 'wheel' | 'slider' | 'pinch' | null = null;
  let rawWheelScale: number | null = null;
  let keyboardScale = false;
  const resetSnapping = (): void => {
    if (guideTimer !== null) window.clearTimeout(guideTimer);
    guideTimer = null;
    scaleMethod = null;
    rawWheelScale = null;
    snap.reset();
    paintSnapGuides(guides, null);
  };
  const normalizePlacement = (placement: CustomPlacement): CustomPlacement =>
    normalizeCustomPlacement(
      placement,
      editorSource,
      viewport.clientWidth,
      viewport.clientHeight,
      {
        minScale: minimumScale,
        freePosition: true
      }
    );
  const dismiss = bindEditorDismissGuard(backdrop, () => !customEditorInitial || !editorValuesEqual(
    normalizePlacement(customEditorInitial!.placement),
    normalizePlacement(getActiveCustomDraftPlacement() || getActiveEffectiveCustomPlacement())
  ), closeCustomPlacementEditor);

  if (media instanceof HTMLVideoElement) {
    const initialTime = editorSource.currentTime ?? 0;
    startCustomPlacementVideo(media, initialTime);

    if (ghostMedia instanceof HTMLVideoElement) {
      startCustomPlacementVideo(ghostMedia, initialTime);

      const syncGhostPlayback = (): void => {
        if (Math.abs(ghostMedia.currentTime - media.currentTime) > 0.08) {
          try {
            ghostMedia.currentTime = media.currentTime;
          } catch {
            // Keep the editor responsive if a browser rejects a transient seek.
          }
        }
      };
      media.addEventListener('timeupdate', syncGhostPlayback);
      media.addEventListener('seeking', syncGhostPlayback);
    }
  }

  let layoutRetryCount = 0;
  const syncViewport = (): void => {
    const viewportWidth = viewport.clientWidth;
    const viewportHeight = viewport.clientHeight;
    if (viewportWidth <= 0 || viewportHeight <= 0) {
      if (layoutRetryCount < 12 && document.body.contains(viewport)) {
        layoutRetryCount += 1;
        window.requestAnimationFrame(syncViewport);
      }
      return;
    }
    layoutRetryCount = 0;

    if (!getActiveCustomDraftPlacement()) {
      setActiveCustomDraftPlacement(getActiveEffectiveCustomPlacement());
    }

    const normalized = normalizePlacement(
      getActiveCustomDraftPlacement() || createDefaultCustomPlacement()
    );
    setActiveCustomDraftPlacement(normalized);

    const sourceWidth = Math.max(1, editorSource.width);
    const sourceHeight = Math.max(1, editorSource.height);
    const coverScale = Math.max(viewportWidth / sourceWidth, viewportHeight / sourceHeight);
    const drawWidth = sourceWidth * coverScale * normalized.scale;
    const drawHeight = sourceHeight * coverScale * normalized.scale;
    const centerX = viewportWidth / 2 + normalized.offsetX * viewportWidth;
    const centerY = viewportHeight / 2 + normalized.offsetY * viewportHeight;

    media.style.width = `${drawWidth}px`;
    media.style.height = `${drawHeight}px`;
    media.style.left = `${centerX - drawWidth / 2}px`;
    media.style.top = `${centerY - drawHeight / 2}px`;
    if (ghostMedia) {
      ghostMedia.style.width = `${drawWidth}px`;
      ghostMedia.style.height = `${drawHeight}px`;
      ghostMedia.style.transform = `translate3d(${centerX - drawWidth / 2}px, ${
        centerY - drawHeight / 2
      }px, 0)`;
    }
    scaleInput.value = normalized.scale.toFixed(2);
    scaleValue.textContent = `${normalized.scale.toFixed(2)}x`;
  };

  const beginScaleInteraction = (method: 'wheel' | 'slider' | 'pinch'): void => {
    if (scaleMethod === method) return;
    resetSnapping();
    const placement = getActiveCustomDraftPlacement() || getActiveEffectiveCustomPlacement();
    snap.beginScale(placement.scale);
    scaleMethod = method;
    rawWheelScale = placement.scale;
  };

  const applySnappedPlacement = (placement: CustomPlacement, movement: 'move' | 'scale' | 'pinch', pulse = false): void => {
    const bounds = viewport.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return;
    const normalized = normalizePlacement(placement);
    if (!getEditorSnapsEnabled('custom')) {
      setActiveCustomDraftPlacement(normalized);
      syncViewport();
      paintSnapGuides(guides, null);
      return;
    }
    let result = movement === 'move'
      ? snapPlacementMove(snap, normalized, editorSource, bounds)
      : snap.scale(normalized, editorSource, bounds, minimumScale, 6);
    if (movement === 'pinch') result = snapPlacementMove(snap, result.placement, editorSource, bounds);
    setActiveCustomDraftPlacement(result.placement);
    syncViewport();
    paintSnapGuides(guides, result.guides, true);
    if (guideTimer !== null) window.clearTimeout(guideTimer);
    guideTimer = pulse ? window.setTimeout(resetSnapping, 500) : null;
  };

  const applyScale = (nextScale: number, method: 'wheel' | 'slider' = 'slider'): void => {
    beginScaleInteraction(method);
    const draft = getActiveCustomDraftPlacement() || getActiveEffectiveCustomPlacement();
    applySnappedPlacement({
      ...draft,
      scale: nextScale
    }, 'scale', true);
  };

  let dragStartX = 0;
  let dragStartY = 0;
  let dragOrigin: CustomPlacement | null = null;
  const activePointers = new Map<number, { x: number; y: number }>();
  let pinchOrigin: {
    distance: number;
    midpointX: number;
    midpointY: number;
    mediaCenterX: number;
    mediaCenterY: number;
    placement: CustomPlacement;
  } | null = null;

  const beginSinglePointerDrag = (pointer: { x: number; y: number }): void => {
    resetSnapping();
    if (getEditorSnapsEnabled('custom')) paintSnapGuides(guides, null, true);
    dragStartX = pointer.x;
    dragStartY = pointer.y;
    dragOrigin = cloneCustomPlacement(
      getActiveCustomDraftPlacement() || getActiveEffectiveCustomPlacement()
    );
  };

  const beginPinch = (): void => {
    const [first, second] = Array.from(activePointers.values());
    if (!first || !second) {
      pinchOrigin = null;
      return;
    }

    const viewportRect = viewport.getBoundingClientRect();
    if (viewportRect.width <= 0 || viewportRect.height <= 0) {
      pinchOrigin = null;
      return;
    }

    const distance = Math.hypot(second.x - first.x, second.y - first.y);
    if (!Number.isFinite(distance) || distance < 1) {
      pinchOrigin = null;
      return;
    }

    const placement = normalizePlacement(
      getActiveCustomDraftPlacement() || getActiveEffectiveCustomPlacement()
    );
    beginScaleInteraction('pinch');
    const midpointX = (first.x + second.x) / 2;
    const midpointY = (first.y + second.y) / 2;

    setActiveCustomDraftPlacement(placement);
    dragOrigin = null;
    pinchOrigin = {
      distance,
      midpointX,
      midpointY,
      mediaCenterX:
        viewportRect.left + viewportRect.width / 2 + placement.offsetX * viewportRect.width,
      mediaCenterY:
        viewportRect.top + viewportRect.height / 2 + placement.offsetY * viewportRect.height,
      placement
    };
  };

  const handlePointerMove = (event: PointerEvent): void => {
    if (!activePointers.has(event.pointerId)) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }
    activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (activePointers.size >= 2) {
      if (!pinchOrigin) {
        beginPinch();
      }

      const [first, second] = Array.from(activePointers.values());
      if (!pinchOrigin || !first || !second) {
        return;
      }

      const viewportRect = viewport.getBoundingClientRect();
      if (viewportRect.width <= 0 || viewportRect.height <= 0) {
        return;
      }

      const distance = Math.hypot(second.x - first.x, second.y - first.y);
      if (!Number.isFinite(distance) || distance < 1) {
        return;
      }

      const midpointX = (first.x + second.x) / 2;
      const midpointY = (first.y + second.y) / 2;
      const nextScale = clamp(
        pinchOrigin.placement.scale * (distance / pinchOrigin.distance),
        minimumScale,
        6
      );
      const scaleRatio = nextScale / Math.max(1e-6, pinchOrigin.placement.scale);
      const mediaCenterX =
        midpointX - (pinchOrigin.midpointX - pinchOrigin.mediaCenterX) * scaleRatio;
      const mediaCenterY =
        midpointY - (pinchOrigin.midpointY - pinchOrigin.mediaCenterY) * scaleRatio;

      applySnappedPlacement({
        scale: nextScale,
        offsetX:
          (mediaCenterX - (viewportRect.left + viewportRect.width / 2)) / viewportRect.width,
        offsetY:
          (mediaCenterY - (viewportRect.top + viewportRect.height / 2)) / viewportRect.height
      }, 'pinch');
      return;
    }

    if (!dragOrigin) {
      return;
    }

    const bounds = viewport.getBoundingClientRect();
    const viewportWidth = bounds.width;
    const viewportHeight = bounds.height;
    if (viewportWidth <= 0 || viewportHeight <= 0) {
      return;
    }
    const dx = (event.clientX - dragStartX) / viewportWidth;
    const dy = (event.clientY - dragStartY) / viewportHeight;
    applySnappedPlacement({
      ...dragOrigin,
      offsetX: dragOrigin.offsetX + dx,
      offsetY: dragOrigin.offsetY + dy
    }, 'move');
  };

  const stopDragging = (event: PointerEvent): void => {
    if (!activePointers.has(event.pointerId)) {
      return;
    }

    activePointers.delete(event.pointerId);
    pinchOrigin = null;

    const remainingPointer = activePointers.values().next().value as
      | { x: number; y: number }
      | undefined;
    if (remainingPointer) {
      beginSinglePointerDrag(remainingPointer);
      return;
    }

    dragOrigin = null;
    resetSnapping();
    viewport.classList.remove('dragging');
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', stopDragging);
    window.removeEventListener('pointercancel', stopDragging);
  };

  viewport.addEventListener('pointerdown', (event) => {
    if (event.button > 0) return;
    event.preventDefault();
    if (activePointers.has(event.pointerId) || activePointers.size >= 2) {
      return;
    }

    activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (activePointers.size === 1) {
      beginSinglePointerDrag({ x: event.clientX, y: event.clientY });
    } else {
      beginPinch();
    }

    viewport.classList.add('dragging');
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDragging);
    window.addEventListener('pointercancel', stopDragging);
  });

  viewport.addEventListener(
    'wheel',
    (event) => {
      event.preventDefault();
      const delta = event.deltaY < 0 ? 0.08 : -0.08;
      beginScaleInteraction('wheel');
      rawWheelScale = clamp(rawWheelScale! + delta, minimumScale, 6);
      applyScale(rawWheelScale, 'wheel');
    },
    { passive: false }
  );

  scaleInput.addEventListener('input', () => {
    if (keyboardScale) {
      // Arrow keys are precise nudges. Snapping each 0.01 step back would trap
      // keyboard users at a magnet because the native range repeats that step.
      resetSnapping();
      setActiveCustomDraftPlacement({ ...(getActiveCustomDraftPlacement() || getActiveEffectiveCustomPlacement()), scale: Number(scaleInput.value) });
      syncViewport();
    } else applyScale(Number(scaleInput.value));
  });
  scaleInput.addEventListener('keydown', () => { keyboardScale = true; });
  scaleInput.addEventListener('pointerdown', () => { keyboardScale = false; resetSnapping(); });
  scaleInput.addEventListener('change', resetSnapping);
  scaleInput.addEventListener('blur', () => { keyboardScale = false; resetSnapping(); });

  resetButton?.addEventListener('click', () => {
    resetSnapping();
    setActiveCustomDraftPlacement(createDefaultCustomPlacement());
    syncViewport();
  });

  snapsButton?.setAttribute('aria-pressed', String(getEditorSnapsEnabled('custom')));
  snapsButton?.addEventListener('click', () => {
    setEditorSnapsEnabled('custom', !getEditorSnapsEnabled('custom'));
    resetSnapping();
    snapsButton.setAttribute('aria-pressed', String(getEditorSnapsEnabled('custom')));
  });

  closeButton?.addEventListener('click', () => {
    resetSnapping();
    dismiss.requestClose();
  });

  cancelButton?.addEventListener('click', () => {
    resetSnapping();
    closeCustomPlacementEditor(false);
  });

  applyButton?.addEventListener('click', () => {
    resetSnapping();
    closeCustomPlacementEditor(true);
  });

  window.requestAnimationFrame(syncViewport);
}

function resumeDeferredEditorRender(): void {
  if (editorRenderDeferred) render();
}

function bindEditorDismissGuard(root: HTMLElement, hasChanges: () => boolean, onClose: (save: boolean) => void): EditorDismissGuard {
  const guard = new EditorDismissGuard(root, { hasChanges, onClose, onKeepEditing: resumeDeferredEditorRender });
  editorDismissGuards.push(guard);
  return guard;
}

function bindInterfaceDropzone(dropzone: HTMLDivElement | null, screen: 1 | 2 | 3 = 1): void {
  if (!dropzone) {
    return;
  }

  let dragDepth = 0;

  const resetDropState = (): void => {
    dragDepth = 0;
    dropzone.classList.remove('drag-active');
  };

  const activateDropState = (): void => {
    dropzone.classList.add('drag-active');
  };

  dropzone.addEventListener('dragenter', (event) => {
    if (!hasSupportedInterfacePayload(event)) {
      return;
    }
    event.preventDefault();
    dragDepth += 1;
    activateDropState();
  });

  dropzone.addEventListener('dragover', (event) => {
    if (!hasSupportedInterfacePayload(event)) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    activateDropState();
  });

  dropzone.addEventListener('dragleave', (event) => {
    if (!hasSupportedInterfacePayload(event)) {
      return;
    }
    event.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) {
      dropzone.classList.remove('drag-active');
    }
  });

  dropzone.addEventListener('drop', (event) => {
    if (!hasSupportedInterfacePayload(event)) {
      resetDropState();
      return;
    }
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    resetDropState();
    if (!file) {
      return;
    }
    void handleInterfaceUpload(file, screen);
  });

}

function hasSupportedInterfacePayload(event: DragEvent): boolean {
  const types = event.dataTransfer?.types;
  if (!types) {
    return false;
  }

  if (Array.from(types).includes('Files')) {
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return true;
    }

    const selected = getSelectedTemplate();
    if (selected?.isVideo) {
      return Array.from(files).some((file) => {
        const lowerName = file.name.trim().toLowerCase();
        return (
          file.type.startsWith('video/') ||
          file.type.startsWith('image/') ||
          lowerName.endsWith('.mp4') ||
          lowerName.endsWith('.png') ||
          lowerName.endsWith('.jpg') ||
          lowerName.endsWith('.jpeg')
        );
      });
    }

    return Array.from(files).some((file) => {
      return getUploadedMediaType(file) !== 'unknown';
    });
  }

  return false;
}
